import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { isPublicParishPath } from "../features/public-parish/paths";
import moment from "moment";
import "moment/locale/sv";

// Translation resources
const resources = {
  en: {
    translation: {
      success: "Success",
      error: "Error",
      title: "Title",
      memberRegistration: "Member Registration",
      personalNumber: "Swedish personal identity number (personnummer or samordningsnummer)",
      memberNumber: "Member Number",
      firstName: "First Name",
      middleName: "Middle Name",
      lastName: "Last Name",
      christianName: "Christian Name",
      gender: "Gender",
      male: "Male",
      female: "Female",
      other: "Other",
      genderAskedNotSaid: "Asked - chose not to say",
      genderPreferNotToSay: "Prefer not to say",
      // FR-1.3/FR-1.9/FR-1.17: confession father tri-state + membership type
      confessionKind: "Confession Father",
      confessionInternal: "Internal (select spiritual father)",
      confessionExternal: "External (outside this parish)",
      confessionNoneRecorded: "None recorded",
      selectConfessionKind: "Not set",
      confessionExternalName: "External Confession Father's Name",
      confessionExternalPlace: "External Confession Father's Place",
      confessionExternalNameRequired: "A name is required for an external confession father",
      // R3.1 task 5 - confession father on applications (FR-3.25/FR-3.25a)
      confessionFatherSectionTitle: "Confession Father",
      confessionFatherHelp: "You are asked this for every application - blank is not accepted, since it would look like nobody ever asked.",
      // Public, unauthenticated form - no priest name is ever collected, INTERNAL or EXTERNAL.
      // See ApplicationForm.js's ConfessionDeclarationFields for the full reasoning.
      confessionAtThisChurch: "I have a confession father at this church",
      confessionAtAnotherChurch: "I have a confession father at another church",
      noConfessionFather: "I do not have a confession father",
      confessionChurchName: "Church Name",
      confessionPhoneNumber: "Confession Father's Phone Number",
      confessionDeclarationIsRequired: "Please say whether you have a confession father, and where",
      confessionChurchAndPhoneRequired: "A church name and phone number are required for a confession father at another church",
      confessionCopiedFromApplicantHint: "Filled in from your details further down this form - if blank, fill them in there.",
      declaredConfessionFather: "Declared",
      confessionFatherMissingWarning: "No confession father declared - a pastoral matter for the priest to address, not blocking.",
      confessionFatherOnFileDiscrepancy: "On file: {{onFile}}. This application declares: {{declared}}. The existing record will not be changed.",
      reviewerOverrideOptional: "Reviewer override (optional)",
      noOverride: "No override - use the declared value",
      applicantConfessionFatherTitle: "Applicant's Confession Father",
      partyOneConfessionFatherTitle: "Party One's Confession Father",
      partyTwoConfessionFatherTitle: "Party Two's Confession Father",
      membershipType: "Membership Type",
      parishMember: "Parish Member",
      notRecorded: "Not recorded",
      notKnown: "Not known",
      noneList: "None",
      noMemberLinked: "No member linked",
      attendanceNotTakenYet: "Attendance not taken yet",
      noGuests: "No guests",
      noneCommon: "None",
      notChosen: "Not chosen",
      notApplicable: "Not applicable",
      guest: "Guest",
      selectMembershipType: "Select membership type",
      dateOfBirthRequiredForParishMember: "Date of birth is required for a parish member",
      dateOfBirthStatedUnknown: "Not known (stated by staff)",
      dateOfBirthUnknownToggle: "Date of birth not known",
      nationalIdConsentLabel: "Member has consented to storing this national ID (GDPR Art. 9)",
      nationalIdConsentRequired: "Consent is required before a national ID can be stored",
      // FR-1.6 church transfer
      transfer: "Transfer",
      transferMember: "Transfer Member",
      targetChurch: "Target Church",
      targetChurchRequired: "Select a target church",
      transferDate: "Transfer Date",
      transferDateRequired: "Enter the date of the move - the day they moved, not the day it is recorded.",
      transferDatePlaceholder: "The day they moved",
      transferDateHelp: "The day they actually moved. A receiving parish may ask years later - it is not the day this is recorded, unless that is the day.",
      familyCandidatesLoadFailed: "Couldn't check who moves with them",
      familyCandidatesLoadFailedBody: "This doesn't mean they have no family here - children or a spouse may be moving too. Try again, or add them below.",
      transferReason: "Reason",
      reasonMove: "Move",
      reasonCorrection: "Correction",
      reasonCorrectionHelp: "Use Correction when the church was entered incorrectly at registration, not for an actual move.",
      transferNote: "Note (optional)",
      transferWarningLoseAccess: "This member will no longer appear in your directory after this transfer.",
      transferSuccessful: "Transfer recorded successfully",
      transferSuccessfulRedirect: "Transfer recorded. This member has moved to another church and is no longer in your directory.",
      transferFailed: "Failed to record transfer",
      transferHistory: "Transfer History",
      fromChurch: "From Church",
      toChurch: "To Church",
      recordedBy: "Recorded By",
      noTransferHistory: "No transfers recorded",
      // FR-1.10 member import wizard
      importMembers: "Import Members",
      importSelectFile: "Select .xlsx file",
      importConsentBasis: "Consent Basis",
      importConsentVersion: "Consent Version",
      importConsentHelp: "Applied to every row in this file that includes a personal number.",
      importSubmit: "Upload and Preview",
      importWillCreate: "Will Create",
      importAlreadyImported: "Already Imported",
      importRejected: "Rejected",
      importRowNumber: "Row",
      importStatus: "Status",
      importDuplicateCandidates: "Possible Duplicates",
      importMatchedOn: "matched on",
      importSkipRow: "Skip",
      importBackToUpload: "Back to Upload",
      importCommit: "Commit Import",
      importNoRows: "No rows",
      importUploadFailed: "Failed to preview the import file",
      importBatchExpired: "Your review has expired. Please re-upload the file.",
      importCommitFailed: "Failed to commit the import",
      importCreatedCount: "Created",
      importSkippedCount: "Skipped",
      importFailedCount: "Failed",
      importStartOver: "Import Another File",
      notAuthorized: "You do not have permission to view this page.",
      of: "of",
      // FR-1.11 duplicates review / merge
      duplicatesReview: "Duplicates Review",
      duplicatesSearch: "Search",
      duplicatesCompareSelected: "Compare Selected",
      duplicatesMatchScore: "Match Score",
      duplicatesMatchedFields: "Matched On",
      duplicatesNoResults: "No candidates found",
      duplicatesEnterCriteria: "Enter search criteria above",
      duplicatesSearchFailed: "Failed to search for duplicates",
      duplicatesSearchFailedBody: "This doesn't mean there are none - search again before relying on it.",
      duplicatesField: "Field",
      duplicatesSurvivor: "Survivor",
      duplicatesLosing: "Losing",
      duplicatesUseLosingValue: "Use Losing's Value",
      duplicatesSurvivorIs: "Survivor:",
      duplicatesPersonnummerNote: "Both records have a national ID on file. The survivor's value is kept by default; choosing to use the losing record's value instead will require an active national-ID consent for the survivor.",
      duplicatesReason: "Reason (optional)",
      duplicatesMerge: "Merge",
      duplicatesMergeSuccessful: "Members merged successfully",
      duplicatesMergeFailed: "Failed to merge members",
      duplicatesLoadFailed: "Failed to load member details",
      duplicatesBackToSearch: "Back to Search",
      originPlace: "Origin Place",
      originChurch: "Origin Church",
      // Audit log
      auditLogs: "Audit Log",
      auditLogTable: "Table",
      auditLogRecordId: "Record ID",
      auditLogActor: "Actor Member ID",
      auditLogAction: "Action",
      auditLogAny: "Any",
      auditLogFetchFailed: "Failed to load audit logs",
      auditLogTime: "Time",
      auditLogRecords: "Record(s)",
      auditLogActorName: "Actor",
      auditLogIp: "IP",
      auditLogRevealExport: "Export (unmasked)",
      auditLogRevealSingle: "Reveal",
      auditLogRecordsCount: "records",
      auditLogShowDetails: "Details",
      auditLogHideDetails: "Hide",
      auditLogBefore: "Before",
      auditLogAfter: "After",
      auditLogNoData: "(none)",
      auditLogFieldsSet: "Fields set",
      auditLogFieldsChanged: "Fields changed",
      auditLogAskedWith: "Asked with",
      auditLogNotRecorded: "Not recorded",
      auditLogUnknownNames: "{{count}} name(s) not kept: not known to the system",
      auditLogRegistrationsCount: "registrations",
      auditLogProbeExact: "Lookup (exact number)",
      auditLogProbeSearch: "Lookup (duplicate search)",
      auditLogPurpose: "Purpose",
      auditLogNote: "Note",
      // Reports
      confessionGapReport: "Confession Father Gap",
      reportIncludeMinors: "Include minors (age ≤ 18)",
      reportFetchFailed: "Failed to load report",
      reportNeverCaptured: "Never Captured",
      reportNoneRecorded: "None Recorded",
      servantsRosterReport: "Servants Roster",
      reportAnyStatus: "Any",
      reportStatusActive: "Active",
      reportStatusOnLeave: "On Leave",
      reportStatusReleased: "Released",
      reportCapacity: "Capacity",
      reportDepartment: "Department",
      reportOrdinationRank: "Ordination Rank",
      reportEmployed: "Employed",
      reportEmployer: "Employer",
      reportYes: "Yes",
      reportNo: "No",
      guestCountsReport: "Guest Counts",
      reportPeriodStart: "Period Start",
      reportPeriodEnd: "Period End",
      reportRun: "Run Report",
      reportRunPrompt: "Select a period (optional) and run the report.",
      reportTotalGuests: "Total Guests",
      reportConverted: "Converted",
      reportConversionRate: "Conversion Rate",
      reportByServiceType: "By Service Type",
      reportServiceTypeNote: "Not a partition - a guest linked to more than one service is counted in each.",
      reportServicePriesthood: "Priesthood",
      reportServiceDeaconship: "Deaconship",
      reportServiceSundaySchool: "Sunday School",
      reportServiceParish: "Parish",
      reportServiceYouth: "Youth",
      reportUnassigned: "Unassigned",
      reports: "Reports",
      // FR-1.9 directory - "optional"/"page"/"role" reuse the pre-existing keys further down
      // this same block (meeting-minutes/attendee fields) rather than redefining them; only
      // "youth" needed a distinct key (ageBandYouth) since the existing "youth" already means
      // the congregational Youth service group, not this age band.
      directory: "Directory",
      includeGuests: "Include guests",
      includeInactiveStatuses: "Include deceased and moved away",
      department: "Department",
      ageBand: "Age Band",
      ageBandAny: "Any age",
      ageBandYouth: "Youth (0-18)",
      youngAdult: "Young Adult (19-35)",
      adult: "Adult (36-59)",
      senior: "Senior (60+)",
      missingDateOfBirth: "Missing date of birth",
      searchByPersonalNumber: "Search by personal number",
      applyFilters: "Apply filters",
      resetFilters: "Reset filters",
      totalResults: "results",
      anyChurch: "All churches",
      anyStatus: "Any status",
      anyMembershipType: "Any",
      search: "Search",
      noResults: "No results",
      emailAddress: "Email Address",
      phoneNumber: "Phone Number",
      dateOfBirth: "Date of Birth",
      profession: "Profession",
      register: "Register",
      members: "Members",
      dashboard: "Dashboard",
      churchService: "Church Service",
      baptisms: "Baptisms",
      maritalStatus: "Marital status",
      churchMemberInfoManagementSystem:
        "Church Member Information Management System (CMMS)",
      registerMembers: "Register Members",
      registerEvents: "Register Events (Baptisms, Weddings)",
      getStarted: "Get Started",
      churchName: "Church Name",
      spiritualFathersName: "Spiritual Father's Name",
      address: "Address",
      city: "City",
      savingSuccessful: "You have successfully saved the information",
      savingError:
        "Error saving the information. Please contact the IT department",
      // A date sent with a time of day: only a page loaded before the screens sent days (the owner's decision 1 of 20 Oct
      // 2026, SC3).
      pageOutOfDate: "This page is out of date. Reload it, then save again.",
      // A failure, said with its reference (app/init.js), and the refusals the server codes for these words.
      failureUnexpected: "Something went wrong on the server. Try again later.",
      failureNotSaved: "This change could not be saved.",
      failureNationalIdNotOpened: "Its national ID couldn't be opened; nothing of the row was written.",
      failureAuditNotRecorded:
        "This can't be shown right now: opening it must be recorded in the audit log, and that failed. Nothing was shown. Try again in a moment; if it keeps happening, tell the administrator.",
      failureNationalIdUnavailable:
        "The personal identity number can't be read or stored right now. Nothing else is affected. Try again in a moment; if it keeps happening, tell the administrator.",
      failureSignInNotChecked: "Sign-in could not be checked. Try again later.",
      failureSignInNotRecorded: "Sign-in could not be recorded, so you were not signed in. Try again later.",
      failureReference: "(reference {{reference}})",
      refusalUsernameTaken: "That username is already taken.",
      refusalUsernameLikePersonalNumber: "A username can't contain a personal identity number. Choose another username.",
      refusalUsernameLikeEmailAddress: "A username can't be an e-mail address. Choose another username.",
      refusalChurchCodeTaken: "Another church already has this code.",
      refusalChurchHasMembers: "This church can't be deleted: member records are held under it.",
      refusalChurchServiceHasActivities: "This church service can't be deleted: activities are recorded under it.",
      // Checklist 1.6, the display work (3.5): a national ID shown "on file", the reveal, the approval's number.
      nationalIdOnFilePersonnummer: "Personnummer on file",
      nationalIdOnFileSamordningsnummer: "Samordningsnummer on file",
      nationalIdOnFile: "National ID on file",
      nationalIdUnreadable: "The number on file couldn't be opened. Nothing was shown.",
      revealNationalId: "Show the number",
      revealPurposeLabel: "Why do you need to see the number?",
      revealPurposeChoose: "Choose a purpose",
      revealPurposePersonAsked: "The person, or their guardian, asked",
      revealPurposeCertificateOrRegister: "A certificate or a register entry needs it",
      revealPurposeAuthorityAsked: "A public authority asked",
      revealPurposeCheckingTheRecord: "Telling records apart, or correcting one",
      revealPurposeOther: "Other",
      revealNoteLabel: "Note (needed for Other)",
      revealNoteHelp: "At most 200 characters, and no number. The purpose is kept in the audit log; the note for 12 months.",
      revealConfirm: "Show",
      hideNationalId: "Hide",
      revealPurposeRequired: "Choose why you need to see the number.",
      revealNoteRequired: "Say in the note what the other purpose is.",
      revealNoteTooLong: "The note is at most 200 characters.",
      revealReasonHoldsANumber: "The note can't hold a number: it is kept in the audit log.",
      revealNotAllowed: "You may not see this member's number.",
      revealFailed: "The number couldn't be shown.",
      replaceNationalId: "Replace",
      keepNationalIdOnFile: "Keep the number on file",
      registrationNumberGiven: "A number was given at registration",
      useRegistrationNumber: "Use the number given at registration?",
      useRegistrationNumberYes: "Yes, use it",
      useRegistrationNumberNo: "No",
      useRegistrationNumberChoose: "Choose whether to use the number given at registration.",
      registrationNumberCorrection: "If it was wrong, give the corrected number below. Leave it empty to store none.",
      registrationNumberUsed: "The number given at registration will be stored, sealed. It is not shown here.",
      numbersCompareFailed: "Whether the two records' numbers differ couldn't be checked.",
      membersTableTitle: "Members of the Church",
      apartmentNumber: "Apartment Number",
      postCode: "Post Code",
      country: "Country",
      countryName: "Country",
      // R4 task 6: short, human-chosen, becomes the certificate reference-number prefix (e.g.
      // "MDH", "STG") - never derived from churchName. See docs/decisions.md.
      churchCode: "Church Code",
      churchCodeRequired: "A church code is required for a new church - it becomes the certificate reference-number prefix",
      organisationsnummer: "Organisation Number",
      organisationsnummerRequired: "An organisation number is required for a new church",
      telefon: "Phone",
      churchTimeZone: "Time zone",
      churchTimeZoneHelp: "Notification quiet hours are evaluated in this zone. Choose where the parish is - there is no safe default.",
      activities: "Activities",
      activitiesTitle: "Activities",
      newActivity: "New activity",
      editActivity: "Edit activity",
      noActivities: "No activities yet",
      activitySettings: "Activity settings",
      activityName: "Name",
      activityDescription: "Description",
      activityDepartment: "Department",
      activityType: "Activity type",
      activityTypes: "Activity types",
      activityFundingTag: "Funding tag",
      activityFundingTags: "Funding tags",
      activityFundingTagNone: "None",
      activityFundingTagHelp: "Set independently of the activity type, so a summer camp is not reported as Sunday school.",
      activitySelect: "Select…",
      activityNoTypesYet: "No activity types are defined for this church yet - create one in Activity settings first.",
      activityRecurrence: "Repeats",
      activityRecurrenceHelp: "Tick the weekdays the activity meets. Leave all unticked for an activity with one-off sessions only.",
      activityEveryNWeeks: "Every how many weeks",
      activityWeekdayMO: "Mon",
      activityWeekdayTU: "Tue",
      activityWeekdayWE: "Wed",
      activityWeekdayTH: "Thu",
      activityWeekdayFR: "Fri",
      activityWeekdaySA: "Sat",
      activityWeekdaySU: "Sun",
      activityStartsOn: "Starts on",
      activityEndsOn: "Ends on",
      activityStartsOnRequired: "A start date is required when the activity repeats.",
      activityDefaultStartTime: "Start time",
      activityDefaultEndTime: "End time",
      activityDefaultLocation: "Location",
      activityCapacity: "Capacity",
      activityCapacityHelp: "0 means unlimited. Exceeding capacity warns but never blocks an enrolment.",
      activityUnlimited: "Unlimited",
      activityFee: "Fee",
      activityFeeFree: "Free",
      activityFeePaid: "Paid",
      activityFeeChoiceRequired: "Choose whether the activity is free or paid.",
      activityFeeAmount: "Fee amount",
      activityFeeCurrency: "Currency",
      activitySendInvitations: "Send session invitations",
      activitySendInvitationsHelp: "Off by default. Reminders always go out 24 hours before each session; invitations suit a one-off session or a new term's first session.",
      activityStatus: "Status",
      activityStatusACTIVE: "Active",
      activityStatusARCHIVED: "Archived",
      activityYes: "Yes",
      activityNo: "No",
      activityRequiredFields: "Name, department and activity type are required.",
      activitySaved: "Saved",
      activitySaveFailed: "Could not save",
      activityLoadFailed: "Could not load the activity",
      activityDeactivatedSuffix: "(deactivated)",
      activityLeaders: "Leaders",
      activityNoLeaders: "No leaders assigned",
      activityAddLeader: "Add as leader",
      activityRemoveLeader: "Remove",
      activitySearchMember: "Search a member of this church by name",
      activitySearch: "Search",
      activitySessions: "Sessions",
      activityNoSessions: "No sessions yet",
      activityGenerateSessions: "Generate sessions",
      activityGenerateFrom: "From",
      activityGenerateTo: "To",
      activityGenerateHelp: "At most 366 days per run. Generating the same period again never duplicates a session, and a cancelled date is never recreated.",
      activityGeneratedResult: "Created {{created}}, already existed {{skipped}}",
      activityAddSession: "Add one-off session",
      activitySessionDate: "Date",
      activitySessionOrigin: "Origin",
      activitySessionOriginGENERATED: "Generated",
      activitySessionOriginAD_HOC: "One-off",
      activitySessionStatusSCHEDULED: "Scheduled",
      activitySessionStatusHELD: "Held",
      activitySessionStatusCANCELLED: "Cancelled",
      activityCancelSession: "Cancel session",
      activityNoSessionDay: "No session: {{name}}",
      activityCancelReason: "Reason for cancelling",
      activityCancelReasonRequired: "A reason is required. The session is kept, marked cancelled - never deleted.",
      activityLookupHelp: "Activity types and funding tags belong to your church. Deactivating hides an entry from new activities; activities already using it keep it. Nothing can be deleted.",
      activityLookupCode: "Code",
      activityLookupCodeHelp: "The code is fixed once created - reports group by it. Rename freely; to change a code, create a new entry.",
      activityLookupName: "Name",
      activityLookupSortOrder: "Sort order",
      activityLookupActive: "Active",
      activityLookupDeactivated: "Deactivated",
      activityLookupDeactivate: "Deactivate",
      activityLookupReactivate: "Reactivate",
      activityLookupRename: "Rename",
      activityLookupNoneYet: "None yet",
      activityLookupRequired: "Code and name are required.",
      activityAddType: "Add activity type",
      activityAddFundingTag: "Add funding tag",
      enrolments: "Enrolments",
      enrolMember: "Enrol a member",
      enrolmentAll: "All",
      enrolmentNone: "No enrolments",
      enrolmentMember: "Name",
      enrolmentPeriod: "Period",
      enrolmentPeriodStart: "Period start",
      enrolmentPeriodEnd: "Period end",
      enrolmentOpenEnded: "open-ended",
      enrolmentStatusPENDING: "Pending",
      enrolmentStatusACTIVE: "Active",
      enrolmentStatusWITHDRAWN: "Withdrawn",
      enrolmentMinor: "Minor",
      enrolmentGuardianConsent: "Guardian consent",
      enrolmentConsentOnFile: "On file",
      enrolmentConsentNotGiven: "Not given",
      enrolmentActivateBlocked_GUARDIAN_CONSENT_NOT_GIVEN: "Needs guardian consent first",
      enrolmentActivateBlocked_GUARDIAN_CONSENT_WITHDRAWN: "Guardian consent was withdrawn",
      enrolmentActivateBlocked_NO_DATE_OF_BIRTH: "No date of birth recorded: needs guardian consent, or record the date of birth",
      enrolmentConsentButton: "Guardian consent",
      enrolmentActivate: "Activate",
      enrolmentActivated: "Enrolment activated",
      enrolmentCreated: "Enrolled - the enrolment is pending until it is activated",
      enrolmentChangeMember: "Change",
      enrolmentHelpFree: "Every enrolment starts as pending. A minor - under 18 at the period start, or with no date of birth recorded - needs guardian consent before activation.",
      enrolmentHelpPaid: "This activity charges a fee, so the period needs an end date: the fee period is the enrolment's. A minor needs guardian consent before activation.",
      enrolmentWithdraw: "Withdraw",
      enrolmentWithdrawReason: "Reason for withdrawing",
      enrolmentWithdrawHelp: "A reason is required. Record the decision and who made it - never the family's circumstances. Group leaders see that a child is withdrawn, not why. Nothing is deleted - attendance already recorded stays.",
      enrolmentWithdrawn: "Enrolment withdrawn",
      consentGuardianTitle: "Guardian consent",
      consentOnFile: "Consents on record",
      consentRawGuardian: "Not linked to the register",
      consentRevoke: "Revoke",
      consentRevokeConfirm: "Revoke this consent? An active minor's enrolment returns to pending if no other consent remains. Attendance already recorded is not affected.",
      consentRevokedOn: "Revoked {{date}}",
      consentTextLoadFailed: "Could not load the consent text",
      consentTextShown: "Consent text shown to the guardian ({{version}})",
      consentLanguage_sv: "Swedish",
      consentLanguage_en: "English",
      consentLanguage_am: "Amharic",
      consentAmharicPending: "Amharic is not available for this version yet. Show the guardian the Swedish or English text - the language shown is what is recorded.",
      consentChooseLanguage: "Choose the language the guardian is shown.",
      consentGuardian: "Guardian",
      consentGuardianName: "Guardian's name",
      consentGuardianPhone: "Phone",
      consentGuardianEmail: "Email",
      consentGuardianLanguage: "Language the guardian wants messages in",
      consentFindGuardian: "Find in register",
      consentFindGuardianHelp: "Search the register first - linking the guardian's own member record is the normal case.",
      consentNoCandidates: "No one in the register matches.",
      consentCandidateOtherChurch: "Registered at another church - linking needs a diocese administrator",
      consentLinkThisPerson: "This is the guardian",
      consentLinked: "Linked",
      consentNotInRegister: "The guardian is not in the register",
      consentRawHelp: "Only for a guardian who is genuinely not in the register. These details are kept on the consent alone.",
      consentNoneOfThese: "None of the people listed is this guardian",
      consentRelationship: "Relationship to the child",
      consentRelationshipMOTHER: "Mother",
      consentRelationshipFATHER: "Father",
      consentRelationshipLEGAL_GUARDIAN: "Legal guardian",
      consentRelationshipOTHER: "Other",
      consentRecord: "Record consent",
      consentRecorded: "Consent recorded",
      consentTextTitle: "Guardian consent text",
      consentTextHelp: "The wording guardians consent to, written by this church. A draft is never shown to anyone. Publishing needs Swedish and English, and replaces the current version; after that the text cannot be changed - only an empty Amharic text can be added, once. Any other change is a new version.",
      consentTextNoneYet: "No consent text has been published. Guardian consent cannot be recorded until one is.",
      consentTextCurrent: "Current version: {{version}}",
      consentTextDraft: "Draft ({{version}})",
      consentTextPending: "Not yet written",
      consentTextOptional: "may be added later",
      consentTextNewVersion: "Start a new version",
      consentTextSaveDraft: "Save draft",
      consentTextPublish: "Publish",
      consentTextPublishHelp: "Publishing replaces the current version for new consents. Consents already recorded keep pointing at the version the guardian was shown.",
      consentChangeQuestion: "What kind of change is this?",
      consentChangeEditorialOption: "Editorial: the wording changes, but people who agreed to an earlier version are still covered by what this version says.",
      consentChangeMaterialOption: "Material: what people agree to changes. People who agreed to an earlier version need to be asked again.",
      consentChangeRecordedHelp: "Your choice is recorded with your name, and the audit log keeps what changed since the previous version.",
      consentChange_EDITORIAL: "Editorial change",
      consentChange_MATERIAL: "Material change",
      consentChangeNotRecorded: "Type of change not recorded",
      consentTextActivityMissing: "This text never names the activity a family is consenting to. Add [activity] where its name belongs.",
      consentTextActivityNotNamedOption: "This text deliberately does not name the activity",
      consentTextActivityPartial: "The activity is named in some languages but not all. Add [activity] here too: {{missing}}.",
      consentTextActivityNotNamedBadge: "Does not name the activity",
      consentTextPublishConfirm: "Publish this version? Its Swedish and English text can never be changed afterwards.",
      consentTextPublished: "Consent text published",
      consentTextAddAmharic: "Add the Amharic text",
      consentTextAddAmharicHelp: "Type or paste the text the parish has written. It can be added once; changing it afterwards needs a new version.",
      consentTextAmharicConfirm: "Add this Amharic text? It cannot be changed afterwards except by publishing a new version.",
      consentTextSaveAmharic: "Save Amharic text",
      consentTextEarlier: "Earlier versions ({{count}})",
      attendanceTitle: "Attendance",
      attendanceStatus: "Attendance",
      attendanceStatusATTENDED: "Attended",
      attendanceStatusABSENT: "Absent",
      attendanceNoEnrolees: "No pending or active enrolments",
      attendanceConsentPending: "Consent pending",
      formRequiredLegend: "required",
      formFieldsRequired: "Fill in: {{fields}}",
      attendanceConsentWithdrawn: "Consent withdrawn",
      attendanceConsentWithdrawnHelp: "Their guardian has withdrawn consent, so they can't be marked.",
      attendanceRefusedWithdrawn: "Nothing was saved: {{names}} can't be marked, because their guardian has withdrawn consent. Your other marks are still here - press Save again.",
      attendanceUnsavedLeave: "You have marks on this sheet that aren't saved yet. Leave the page and lose them?",
      attendanceLate: "recorded later",
      attendanceWhenRecorded: "When are these figures being recorded?",
      attendanceOnTheDay: "On the day of the session",
      attendanceLater: "Later, from notes or memory",
      attendanceWhenRecordedHelp: "Say which - figures entered after the day are marked as late entries.",
      attendancePastSessionHelp: "This session is in the past, so these figures are recorded later.",
      attendanceSave: "Save attendance",
      attendanceSaved: "Attendance saved",
      attendanceSavedConsentPending: "Attendance saved. {{count}} enrolee(s) still need guardian consent and are on the consent worklist.",
      attendanceCorrect: "Correct",
      attendanceCorrectionReason: "Reason for the correction",
      attendanceCorrected: "Attendance corrected",
      rosterTitle: "Roster",
      rosterWindow: "Enrolled",
      rosterHeld: "Held",
      rosterConfirmed: "Signed up",
      rosterUnrecorded: "Not recorded",
      rosterRate: "Rate",
      rosterHelp: "The rate counts sessions held while each person was enrolled. Not recorded is shown on its own, never counted as absent; with no held sessions there is no rate.",
      feeTitle: "Fee status",
      feeStatus: "Fee",
      feeStatusPAID: "Paid",
      feeStatusUNPAID: "Unpaid",
      feeStatusWAIVED: "Waived",
      feeStatusNOT_RECORDED: "Not recorded",
      feeRecord: "Record",
      feeRecorded: "Fee status recorded",
      feeHistory: "History",
      feeHelp: "Recorded, not collected - no payment is handled here. Not recorded means nobody has recorded anything yet; it never means unpaid.",
      feeWaiverReason: "Reason for the waiver",
      feeWaiverHint: "Record the decision and who made it - for example \"Waived by parish council decision of 12 September\". Never the family's circumstances.",
      worklistsTitle: "Worklists",
      worklistUnrecordedSessions: "Sessions with no attendance recorded",
      worklistUnrecordedHelp: "Past sessions that were neither recorded nor cancelled. A session counts as held only once attendance is recorded, so each of these needs one or the other.",
      worklistAwaitingConsent: "Attendance awaiting guardian consent",
      worklistAwaitingConsentHelp: "Minors whose attendance was recorded while guardian consent has never been given. Record the consent from the activity's enrolment list.",
      worklistNothing: "Nothing here.",
      activitySettingsChurch: "Church",
      todayTitle: "Today's sessions",
      todayNone: "No sessions today.",
      todayTakeAttendance: "Take attendance",
      todayOpenAttendance: "Open attendance",
      todayRecentUnrecorded: "Earlier sessions not yet recorded",
      todayAllUnrecorded: "All sessions not yet recorded",
      attendanceMarkAllPresent: "Mark the {{count}} not yet marked as attended",
      attendanceMarkAllHelp: "Then change the absentees. Only what you mark is saved - nothing is filled in for you.",
      attendanceProgress: "{{done}} of {{total}} recorded",
      attendanceUnmarkedLeft: "{{count}} not marked yet",
      attendanceChooseWhen: "Say when these figures are being recorded",
      attendanceFutureSession: "This session has not happened yet - attendance can be taken from its day on.",
      attendanceProgressUnsaved: "{{done}} of {{total}} marked - not saved yet",
      activitiesNoneLed: "You don't lead any activities.",
      activitySessionTeacherOnly: "You took one or more sessions of this activity. Open them from the activities page:",
      teacherOfRecordTitle: "Who took this session?",
      teacherOfRecordHelpLeader: "Tick who actually took the session. Pre-filled names are a plan until confirmed. Ticking someone gives them access to this one session: the names of the children enrolled in it, their sign-ups and attendance - which they can record and correct - and whether consent stops a child being marked; nothing else, until staff remove them. Adding a substitute is done by staff.",
      teacherOfRecordHelpStaff: "Tick who actually took the session. Ticking someone gives them access to this one session: the names of the children enrolled in it, their sign-ups and attendance - which they can record and correct - and whether consent stops a child being marked. Not other sessions, and not guardians' details, consents or fees. It lasts until staff remove them from this session, or they leave the parish.",
      teacherOfRecordSourceDEFAULTED: "planned",
      teacherOfRecordSourceRECORDED: "confirmed",
      teacherOfRecordAddSubstitute: "Add a substitute - search a member of this church",
      teacherOfRecordConfirm: "Confirm who took the session",
      teacherOfRecordSaved: "Teacher of record saved",
      enrolmentStartsAfterTerm: "This enrolment starts on {{date}}, after the activity started. Sessions before that date won't include this person - change it if they joined from the start.",
      enrolmentCorrectPeriod: "Correct period",
      enrolmentCorrectionReason: "Reason for the correction",
      enrolmentCorrectPeriodHelp: "For a date entered wrongly. Record what was wrong and who decided - never the family's circumstances. Recorded attendance must stay inside the period. Logged with the old and new dates.",
      enrolmentPeriodCorrected: "Period corrected",
      attendanceNobodyCoversThisDate: "Nobody's enrolment covers this date - see below.",
      attendanceNotOnThisSheet: "Enrolled, but not on this sheet:",
      attendanceEnrolledFrom: "enrolled from {{date}}",
      attendanceEnrolledUntil: "enrolled until {{date}}",
      attendanceFixPeriodStaff: "If that date is wrong, correct the period on the activity page.",
      attendanceFixPeriodLeader: "If that date is wrong, ask an administrator to correct the enrolment.",
      consentTextShownVersion: "Consent text {{version}}",
      consentTextLineBreaksQuestion: "This text looks pasted with its line breaks",
      consentTextLineBreaksIntendedOption: "The line breaks are intended - publish it exactly as written, line by line. (Otherwise, paste the text again without them.)",
      consentTextShortQuestion: "This text is very short",
      consentTextShortCompleteOption: "This short text is complete - it is everything the parish asks people to agree to. (Otherwise, write the full text first.) A text this short does not open registering online.",
      consentTextReadInLanguage: "Language it was read in",
      consentTextChooseLanguage: "Choose the language it was read in",
      consentTextChooseLanguageHelp: "Choose the language the person read it in - it is recorded with the consent.",
      consentTextAmharicPendingPublic: "Amharic is not available for this text yet - please read it in Swedish or English.",
      consentAgreeMembershipSelf: "I have read this text and consent to my personal data being held in the parish's member register on these terms.",
      consentAgreeApplication: "I have read this text and consent to the personal data in this form being processed for this application.",
      consentAgreeRequired: "Please read the consent text and tick the box to agree.",
      myActionItems: "My action items",
      actionItem: "Action item",
      noOutstandingActionItems: "Nothing outstanding right now.",
      failedToFetchActionItems: "Could not load your action items",
      errorUpdatingActionItem: "Could not update the action item",
      consentTextUnavailable: "This form can't be submitted yet: the parish has not published its consent text.",
      addressIsRequired: "An address is required: street, post code and city.",
      consentFromRegistration: "The registrant agreed to the membership text when registering - no consent is taken here.",
      registrationConsentShown: "Agreed at registration to membership text {{version}}, read in {{language}}.",
      registrationChildConsentInPerson: "{{name}} was under 18 when they registered. Their own consent is taken in person or on paper, not from the form - a guardian may read the address they gave.",
      // The approval's reading of the dates of birth, before approving (owner, 18 Oct 2026).
      approvalChildByDateEntered: "By the date of birth entered here, {{name}} was under 18 when they registered. Their own consent is taken in person or on paper, not from the form - a guardian may read the address they gave.",
      approvalChildByRecord: "By the linked record's date of birth, {{name}} was under 18 when they registered. Approving links the account and takes no consent from the form; their own consent is taken in person or on paper, on their record.",
      approvalGuestTakesNoMembershipConsent: "A guest takes no membership consent, so the consent on the form isn't recorded. A personnummer entered for them needs a consent covering it, taken here.",
      approvalDatesDisagreeEntered: "The dates of birth disagree: the registration gave {{given}}; the one entered here is {{other}}.",
      approvalDatesDisagreeRecord: "The dates of birth disagree: the registration gave {{given}}; the linked record holds {{other}}.",
      approvalYoungerCounts: "When they disagree, the one that makes them a child counts.",
      approvalUnderOwnConsentAge: "By the date of birth the registration gave ({{date}}), they were under 12 when they registered. A child's registration is a parent's or guardian's to make, so this one can't be approved: reject it, and a parent registers the child with the parish.",
      pendingUnderOwnConsentAge: "Under 12 when they registered - a parent registers the child with the parish.",
      approvalUnderOwnConsentAgeByDateEntered: "By the date of birth entered here ({{date}}), they were under 12 when they registered. A child's registration is a parent's or guardian's to make, so this one can't be approved: reject it, and a parent registers the child with the parish.",
      approvalUnderOwnConsentAgeByRecord: "By the linked record's date of birth ({{date}}), they were under 12 when they registered. A child's registration is a parent's or guardian's to make, so this one can't be approved: reject it, and a parent registers the child with the parish.",
      approvalAgeChecking: "Checking the dates of birth against the registration…",
      approvalAgeLoadFailed: "Couldn't check the dates of birth against the registration.",
      approvalAgeLoadFailedBody: "Approving waits until they have been checked.",
      approvalAgeRecordOutsideChurch: "The record chosen belongs to another church, so it can't be linked from here, and its date of birth isn't read.",
      approvalAgeRegistrationOutsideChurch: "This registration belongs to another church, so it isn't yours to approve or reject.",
      nationalIdCoveredByInPersonConsent: "Covered by the in-person membership consent recorded below.",
      consentTextsSettingsTitle: "Consent texts",
      consentTextsSettingsHelp: "The wording people agree to, written by this church: the membership text, the application text and the guardian text for activities. Each is one text for the whole parish. Applying for a service is not joining the parish register, so those two texts are separate. A draft is never shown to anyone; publishing needs Swedish and English, and after that the text can't be changed - only an empty Amharic text can be added, once.",
      consentTextMemberTitle: "Membership consent text",
      consentTextMemberHelp: "Shown when someone becomes a parish member: on self-registration, and when consent is taken in person.",
      consentTextMemberNoneYet: "No membership text has been published. Self-registration and in-person membership consent are unavailable until one is.",
      consentTextApplicationTitle: "Application consent text",
      consentTextScopeParishChip: "Whole parish",
      consentTextScopeMember: "For everyone joining the parish register.",
      consentTextScopeApplication: "For everyone applying for a service.",
      consentTextScopeGuardian: "For parents of children taking part in an activity, with the child's activity named in the text.",
      activityConsentTextMoved: "The guardian consent text is now with the parish's other consent texts.",
      activityConsentTextMovedLink: "Open Settings - Consent texts",
      activityConsentTextMovedNoAccess: "The guardian consent text is now under Settings - Consent texts, which the parish office manages.",
      consentTextApplicationHelp: "Shown on the public service application form. It covers the application only, not membership.",
      consentTextApplicationNoneYet: "No application text has been published. The public application form is unavailable until one is.",
      declarationTitle: "Declaration evidence",
      declarationHelp: "How and when membership was approved, and by whom. 'Predates the system' is a valid answer; nothing at all is not.",
      declarationKind: "How was membership approved?",
      declarationChoose: "Choose…",
      declarationKind_SELF_REGISTRATION_APPROVED: "Self-registration, approved by an administrator",
      declarationKind_IN_PERSON_CONSENT: "Consent taken in person",
      declarationKind_PARENT_DECLARATION: "A parent's or guardian's declaration (child under 12)",
      declarationKind_OWN_DECLARATION: "The member's own declaration (from 12)",
      declarationKind_PREDATES_SYSTEM_PAPER_REGISTER: "Predates the system - evidence is in the paper register",
      declarationDeclaredOn: "Date",
      declarationInPersonToday: "A consent taken in person is dated today, with the person here. Given on another day? Don't record it here - send a consent request on paper, for them to sign and date.",
      declarationInPersonAgreed: "The member was shown this text and agreed to it",
      declarationDeclarant: "Who declared for the child",
      declarationDeclarantHelp: "Any member - a legal guardian is valid. Whether they are recorded as a parent is noted, not required.",
      declarationSearchDeclarant: "Search a member by name",
      declarationChange: "Change",
      declarationPaperReference: "Paper register reference (optional)",
      declarationPaperReferenceExample: "e.g. Register 2, p. 14",
      declarationRequired: "Declaration evidence is required for a new parish member - how and when membership was approved.",
      declarationsTitle: "Membership evidence",
      declarationRecordEvidence: "Record evidence",
      declarationNoneYet: "No declaration evidence on record - this member is on the completeness worklist.",
      declarationCurrent: "current",
      declarationRecorded: "Evidence recorded",
      declarationSaveFailed: "Could not record the evidence",
      declarationAppendOnlyHelp: "Evidence is never edited: a correction is a new entry, and the newest one is current.",
      declarationRecordedBy: "recorded by {{name}} on {{date}}",
      declarationDescribeSelfRegistration: "Self-registration approved by {{admin}} (text {{version}})",
      declarationDescribeInPerson: "Consent taken in person (text {{version}}, read in {{language}})",
      declarationDescribeParent: "Declared by {{declarant}}",
      declarationNotRecordedAsParent: "(not recorded as a parent)",
      declarationKind_PARENT_IN_PERSON: "A parent's or guardian's consent, given in person (child under 12)",
      declarationAgeUnknown: "No date of birth is recorded. Who may consent depends on the member's age on the day - record the date of birth to record a consent or a declaration.",
      declarationAgeFailed: "Couldn't check who may consent on that day. Until it can be checked, only a record that predates the system can be entered.",
      declarationAgeBeforeBirth: "That date is before the date of birth.",
      declarationAgeGuardian: "{{age}} on {{date}} - under 12, so a parent or guardian gives the consent, not the child.",
      declarationAgeOwn: "{{age}} on {{date}} - from 12 the member gives their own consent.",
      declarationKindNotForAge: "That choice doesn't fit the member's age on this date - choose again.",
      declarationParentGivingConsent: "Parent or guardian giving the consent",
      declarationParentGivingConsentHelp: "Chosen from the register. Recording the consent doesn't record them as the child's parent - that is offered afterwards, as its own step.",
      declarationParentInPersonAgreed: "The parent or guardian agreed to this text for the child",
      declarationDescribeParentConsent: "Consent {{how}} by {{declarant}}, parent or guardian (text {{version}}, read in {{language}})",
      declarationConsentInPerson: "given in person",
      declarationConsentOnPaper: "given on paper",
      declarationConsentByLink: "given by link",
      declarationParentLinkOffer: "Record {{parent}} as a parent",
      declarationParentLinkConfirm: "Record {{parent}} as {{child}}'s parent? This adds a family link to the register.",
      declarationParentLinkYes: "Record the link",
      declarationParentLinkRecorded: "Parent link recorded",
      declarationParentLinkFailed: "The parent link could not be recorded",
      selfRegistrationUnderAge: "A child under 12 can't register themselves: their membership consent is a parent's or guardian's to give. A parent can register the child with the parish.",
      memberPickerSearchFailed: "The search failed - try again.",
      memberPickerNoMatches: "No one in the register matches that search.",
      memberPickerNotInRegister: "Not in the register?",
      memberPickerRegisterFirst: "Register them first",
      nationalIdConsentLabelParent: "The parent or guardian has consented to storing the child's national ID (GDPR Art. 9)",
      nationalIdConsentAgeUnknown: "Record the date of birth first: whether this consent is the member's own or a parent's depends on their age.",
      nationalIdConsentAgeFailed: "Couldn't check whose consent this is - the national ID can't be stored until it can.",
      nationalIdConsentParentRequired: "Choose the parent or guardian giving the consent - under 12 it is theirs, not the child's.",
      completenessTitle: "Incomplete member records",
      completenessLanguageNotRecorded: "Notified in the parish default",
      completenessLanguageColumn: "Language",
      completenessLanguageAlert: "{{count}} people have been sent messages in the parish's default language because no language is recorded for them - they may not be able to read them. Record the language they read on their member record: that is what stops it.",
      completenessNotifiedInDefault: "Notified in the default {{count}}×",
      completenessLastNotified: "latest {{date}}",
      completenessGuest: "guest",
      completenessRecordLanguage: "Record their language",
      completenessHelp: "Parish members missing an address, a date of birth or declaration evidence. Guests and deceased members are not listed. Nothing here blocks a record - this is where the gaps are counted and chased.",
      completenessStatus: "Status",
      completenessAllStatuses: "All except deceased",
      completenessInScope: "Parish members",
      completenessIncomplete: "Incomplete",
      completenessNoAddress: "No address",
      completenessNoDateOfBirth: "No date of birth",
      completenessNoDeclaration: "No declaration",
      completenessMember: "Member",
      completenessMissing: "missing",
      completenessNothingMissing: "Nothing missing.",
      completenessLoadFailed: "Could not load the worklist",
      memberStatus_ACTIVE: "Active",
      memberStatus_MOVED: "Moved",
      memberStatus_SUSPENDED: "Suspended",
      memberStatusNoLongerRecorded: "{{status}} - no longer recorded; kept until the parish council decides",
      memberStatus_DEACTIVATED: "Inactive",
      memberStatus_TEMPORARY: "Temporary",
      memberStatus_DECEASED: "Deceased",
      maritalStatus_MARRIED: "Married",
      maritalStatus_DIVORCED: "Divorced",
      maritalStatus_WIDOWED: "Widowed",
      maritalStatus_SINGLE: "Single",
      maritalStatus_CHILD: "Child",
      accountStatus_ACTIVE: "Active",
      accountStatus_INACTIVE: "Inactive",
      accountStatus_DELETED: "Deleted",
      accountStatus_PENDING: "Awaiting approval",
      importConsentTextChurch: "Church whose membership text the forms carried",
      importPaperFormsCarriedThisText: "The paper consent forms carried this text, in this language",
      importPredatesSystem: "These records predate the system - the evidence is in the paper register",
      importPredatesSystemHelp: "Recorded as each parish member's declaration evidence. Without it, parish-member rows have no evidence and are refused at the preview.",
      importAddressRequiredHelp: "Every parish-member row needs an address (street, post code and city); rows without one are refused at the preview.",
      userNameIsRequired: "User name is required!",
      passwordIsRequired: "Password is required!",
      userNameOrPasswordIncorrect: "User name or password incorrect.",
      sessionExpired: "Your session has expired. Please log in again.",
      signInFailedStatus: "Signing in failed because of a fault on the server (it answered {{status}}) - not because of your username or password. Try again in a moment; if it keeps happening, tell an administrator.",
      signInNoSession: "The server accepted your username and password but sent no session back - a fault on the server. Try again; if it keeps happening, tell an administrator.",
      signInNoAnswer: "The server didn't answer, so your username and password weren't checked. Check your connection and try again.",
      signInAcceptedThenFailed: "Your username and password were accepted, but the next page couldn't open. Reload the page to continue.",
      login: "Login",
      dontHaveAnAccount:
        "Don't have an account yet? Please email dev@medhanealem.se to get an account.",
      forgotPassword: "Forgot Password?",
      userName: "User name",
      password: "Password",
      loginToSystem: "Login to System",
      appName: "CMMS",
      accessDeniedTitle: "Access Denied",
      accessDeniedBody: "You don't have permission to view this page. If you think this is a mistake, contact an administrator.",
      backToDashboard: "Back to Dashboard",
      backToStart: "Back to the start page",
      editMember: "Edit Member",
      memberDetails: "Member Details",
      edit: "Edit",
      update: "Update",
      updateSuccessful: "Member updated successfully",
      updateError: "Error updating member",
      errorFetchingMember: "Error fetching member details",
      memberLoadFailedTitle: "Couldn't load this member",
      memberLoadFailedBody: "The form isn't shown, so nothing can be saved over the member's record until it loads.",
      memberFormAskedLoadFailedTitle: "Couldn't check what this form asks",
      memberFormAskedLoadFailedBody: "Marital status is shown for everyone until it can; none is saved for anyone under 18.",
      actions: "Actions",
      registerChild: "Register Child",
      relativeParishMemberOnMemberForm: "A relative added here is recorded as a guest. A parish member needs a declaration of membership: register them on the member form, then find them here by name.",
      showFamily: "Show Family",
      familyTree: "Family Tree",
      spouse: "Spouse",
      children: "Children",
      parents: "Parents",
      noFamilyMembers: "No family members found",
      searchingMembers: "Searching members...",
      selectExistingMember: "Select an existing member or create new",
      relationshipRegistered: "Relationship registered successfully",
      errorRegisteringRelationship: "Error registering relationship",
      confirmDeleteMember: "Are you sure you want to delete this member?",
      memberRemoveTitle: "Remove {{name}} from the register?",
      memberRemoveNotRestorable: "The record is marked as removed and is no longer listed. No screen brings it back.",
      memberRemoveKeeps: "Nothing is deleted. These records name this person and stay exactly as they are: {{list}}.",
      memberRemoveNothingRefers: "Nothing else in the register refers to this person.",
      memberRemoveLogin: "Their login is disabled.",
      memberRemoveBaptisms_one: "{{count}} baptism record",
      memberRemoveBaptisms_other: "{{count}} baptism records",
      memberRemoveMarriages_one: "{{count}} marriage record",
      memberRemoveMarriages_other: "{{count}} marriage records",
      memberRemoveConsents_one: "{{count}} consent record",
      memberRemoveConsents_other: "{{count}} consent records",
      memberRemoveOthers_one: "{{count}} other record",
      memberRemoveOthers_other: "{{count}} other records",
      memberRemoveListAnd: " and ",
      memberRemoveQueuedMessages_one: "{{count}} queued message",
      memberRemoveQueuedMessages_other: "{{count}} queued messages",
      memberRemoveOpenRequests_one: "{{count}} open consent request",
      memberRemoveOpenRequests_other: "{{count}} open consent requests",
      memberRemoveCancelled_one: "{{what}} is cancelled.",
      memberRemoveCancelled_other: "{{what}} are cancelled.",
      memberRemoveCancelledBoth: "{{first}} and {{second}} are cancelled.",
      memberChangeQuestion: "Why are you changing this record?",
      memberChangeEnteredInError: "It was entered in error",
      memberChangeDeceased: "The person has died",
      memberChangeTransferHint: "To record a move to another parish, use <1>{{transfer}}</1>.",
      memberDeceasedTitle: "Record {{name}} as deceased?",
      memberDeceasedKeeps: "Nothing is removed: the record is kept, marked as deceased, and everything that names this person stays.",
      // The owner (18 Oct 2026): not undone from here - but a fithat's void puts the status back where the death followed it.
      memberDeceasedFinal: "This can't be undone from here. Where a fithat records the death, voiding the fithat puts the status back.",
      memberDeceasedConfirm: "Record as deceased",
      memberRecordedDeceased: "{{name}} was recorded as deceased.",
      memberRecordDeceasedFailed: "{{name}} could not be recorded as deceased. Nothing was changed.",
      memberRemoveConfirm: "Remove from register",
      memberRemoveImpactFailed: "What removing this person would affect could not be loaded, so nothing has been removed.",
      memberRemovedSuccessfully: "{{name}} was removed from the register.",
      memberRemoveFailed: "{{name}} could not be removed. Nothing was changed.",
      memberChangeOutcomeUnknown: "The server didn't answer, so it isn't known whether {{name}} was changed. Look at the list again before trying again.",
      memberDeletedSuccessfully: "Member deleted successfully",
      errorDeletingMember: "Error deleting member",
      selectDate: "Select Date",
      registerNewMember: "Register New Member",
      close: "Close",
      cancel: "Cancel",
      confirm: "Confirm",
      // Baptism translations
      registerBaptism: "Register Baptism",
      editBaptism: "Edit Baptism",
      correctBaptism: "Correct Baptism Record",
      baptismsListTitle: "Baptisms List",
      registerNewBaptism: "Register New Baptism",
      personBeingBaptized: "Person Being Baptized",
      spiritualFather: "Spiritual Father",
      priest: "Priest",
      selectPriest: "Select a priest",
      baptismDate: "Baptism Date",
      pleaseSelectAllMembers: "Please select all required members",
      baptismCreatedSuccessfully: "Baptism created successfully",
      baptismUpdatedSuccessfully: "Baptism updated successfully",
      errorSavingBaptism: "Error saving baptism",
      errorFetchingBaptism: "Error fetching baptism details",
      failedToFetchBaptisms: "Failed to fetch baptisms",
      confirmDeleteBaptism: "Are you sure you want to delete this baptism?",
      baptismDeletedSuccessfully: "Baptism deleted successfully",
      errorDeletingBaptism: "Error deleting baptism",
      baptismId: "Baptism ID",
      personBaptized: "Person Baptized",
      errorFetchingCertificate: "Error fetching certificate",
      // Marriage translations
      registerMarriage: "Register Marriage",
      editMarriage: "Edit Marriage",
      correctMarriage: "Correct Marriage Record",
      marriagesListTitle: "Marriages List",
      registerNewMarriage: "Register New Marriage",
      firstSpouse: "First Spouse",
      secondSpouse: "Second Spouse",
      marriageDate: "Marriage Date",
      marriageCreatedSuccessfully: "Marriage created successfully",
      marriageUpdatedSuccessfully: "Marriage updated successfully",
      errorSavingMarriage: "Error saving marriage",
      errorFetchingMarriage: "Error fetching marriage details",
      failedToFetchMarriages: "Failed to fetch marriages",
      confirmDeleteMarriage: "Are you sure you want to delete this marriage?",
      marriageDeletedSuccessfully: "Marriage deleted successfully",
      errorDeletingMarriage: "Error deleting marriage",
      marriageId: "Marriage ID",
      marriages: "Marriages",
      certificates: "Certificates",
      englishCertificate: "English Certificate",
      swedishCertificate: "Swedish Certificate",
      amharicCertificate: "Amharic Certificate",
      currentMember: "Current Member",
      parent: "Parent",
      child: "Child",
      membershipCertificate: "Membership Certificate",

      // R4 task 8: fithat register, corrections, worklists, certificate reissue/verification
      fithat: "Funeral",
      fithatRecords: "Funeral Records",
      fithatRecordsListTitle: "Funeral Records List",
      registerNewFithat: "Register New Funeral",
      registerFithat: "Register Funeral",
      correctFithat: "Correct Funeral Record",
      departedPerson: "Departed Person",
      fithatDate: "Funeral Date",
      fithatCreatedSuccessfully: "Funeral record created successfully",
      fithatUpdatedSuccessfully: "Funeral record updated successfully",
      errorSavingFithat: "Error saving funeral record",
      errorFetchingFithat: "Error fetching funeral record details",
      failedToFetchFithatRecords: "Failed to fetch funeral records",
      fithatRecordId: "Funeral Record ID",

      officiatingChurch: "Officiating Church",
      hostingChurch: "Hosting Church",
      spouse1HomeChurch: "First Spouse's Home Church",
      spouse2HomeChurch: "Second Spouse's Home Church",
      baptismalNameLabel: "Baptismal Name (የክርስትና ስም)",
      fatherName: "Father's Name",
      motherName: "Mother's Name",
      baptizedPersonSection: "Person to be Baptized",
      godparentSection: "Godparent",

      correctRecord: "Correct",
      correctionReason: "Reason for Correction",
      correctionReasonRequired: "A reason for this correction is required",
      voidRecordAction: "Void",
      voidReasonLabel: "Reason for Voiding",
      voidReasonRequired: "A reason for voiding this record is required",
      voidVsCorrectionExplanation: "Voiding is different from a correction. Use Correct when the details are wrong but the event happened. Use Void only when this record should never have existed at all - it stays in the system for audit purposes, but is removed from lists and no longer confirmed by certificate verification.",
      recordVoidedSuccessfully: "Record voided",
      errorVoidingRecord: "Error voiding record",
      voidPreviewFailed: "What voiding would put back couldn't be loaded, so it can't be confirmed yet.",
      // A fithat's void (Adisu, 18 Oct 2026): what comes back by itself, and what it stopped, for staff to send again.
      fithatVoidAnotherRecordsTheDeath: "Another fithat still records their death: voiding this one puts nothing back, and nothing it stopped should be sent again.",
      fithatVoidStatusRestored: "Their status goes back to {{status}}, as it was before Deceased followed this fithat.",
      fithatVoidStatusRestoredNone: "Their status goes back to what it was before Deceased followed this fithat: none was recorded.",
      fithatVoidDeceasedStays: "Their record says Deceased, and nothing kept says it followed this fithat, so it stays - and no login comes back with this void.",
      fithatVoidLoginRestored: "The login this fithat ended is put back as it was before.",
      fithatVoidLoginChangedSince: "The login this fithat ended has been changed since, so it is left as it is.",
      fithatVoidLoginStaysEndedMembershipEnded: "The login this fithat ended stays ended: the record was removed from the register since.",
      fithatVoidNotRecorded: "Not all of what was stopped for this person was kept: a fithat recorded before that began to be kept left no list, so what it stopped can't be listed here.",
      fithatVoidStoppedTitle: "What this fithat stopped. Nothing is sent again by itself - send again what should go:",
      fithatVoidStoppedMessage: "{{type}} to {{to}}, due {{due}}",
      fithatVoidStoppedMessageEvent: "- for the event on {{when}}",
      fithatVoidStoppedLinkRequest: "A consent request by link to {{to}}, about {{about}}, sent {{sent}}",
      fithatVoidStoppedPaperRequest: "A consent request on paper to {{to}}, about {{about}}, made {{sent}}",
      fithatVoidStoppedRequestHadExpired: "- its link had already expired",
      fithatVoidStoppedToSomeoneOutside: "someone not in the register",
      fithatVoidStoppedElsewhere: "Something it stopped for a person of another church - not shown here.",
      fithatVoidStoppedNothing: "It stopped no message and no consent request.",
      notificationType_BOOKING_CONFIRMATION: "Booking confirmation",
      notificationType_APPLICATION_STATUS_CHANGE: "Application status change",
      notificationType_APPLICATION_VERIFICATION: "Application verification link",
      notificationType_USER_ACCOUNT_PENDING: "Account request received",
      notificationType_USER_ACCOUNT_APPROVED: "Account approved",
      notificationType_USER_ACCOUNT_DUPLICATE_SIGNUP_ATTEMPT: "Notice of a second registration",
      notificationType_BOOKING_REMINDER: "Booking reminder",
      notificationType_CLERGY_NUDGE: "Reminder to a priest of a waiting booking",
      notificationType_BAPTISM_TIMING_REMINDER: "Baptism timing reminder",
      notificationType_ACTIVITY_SESSION_INVITATION: "Session invitation",
      notificationType_ACTIVITY_SESSION_REMINDER: "Session reminder",
      notificationType_ACTIVITY_SESSION_INVITATION_GUARDIAN: "Session invitation (to a guardian)",
      notificationType_ACTIVITY_SESSION_REMINDER_GUARDIAN: "Session reminder (to a guardian)",
      notificationType_CONSENT_REQUEST: "Consent request",
      notificationType_CONSENT_REQUEST_GUARDIAN: "Consent request (to a guardian)",
      notificationType_CONSENT_RECEIPT: "Consent receipt",
      notificationType_CONSENT_RECEIPT_GUARDIAN: "Consent receipt (to a guardian)",
      notificationType_CONSENT_LINK_RESEND: "Consent link sent again on request",
      notificationType_CONSENT_LINK_RESEND_GUARDIAN: "Consent link sent again on request (to a guardian)",
      notificationType_CONSENT_WITHDRAWN_GUARDIAN: "Consent withdrawn (to a guardian)",
      notificationType_CONSENT_WITHDRAWN: "Consent withdrawn",
      notificationType_CONSENT_WITHDRAWN_STAFF: "Consent withdrawn (to staff)",
      certificateRecordVoided: "This record has been voided",
      viewHistory: "History",
      correctionHistory: "Amendment History",
      errorFetchingCorrectionHistory: "Error fetching amendment history",
      noCorrectionsYet: "No corrections have been recorded yet.",
      correctedBy: "Corrected By",
      correctedAt: "Corrected At",
      viewChanges: "View changes",

      generateCertificate: "Generate",
      reissueCertificate: "Reissue",
      reissueReason: "Reason for Reissue",
      reissueReasonRequired: "A reason for reissuing this certificate is required",
      reissueLanguage: "Language",
      referenceNumberLabel: "Reference No.",
      certificateDisclaimerTitle: "This is not the finished certificate",
      certificateDisclaimerBody: "This document is not valid until it is physically stamped and signed by the issuing church. It confirms the record was created - it is not the finished certificate.",
      confirmAndDownload: "I understand - Download",
      certificateReissuedSuccessfully: "Certificate reissued successfully",
      errorReissuingCertificate: "Error reissuing certificate",

      verifyCertificateTitle: "Verify a Certificate",
      certificateReferenceNumberInput: "Certificate Reference Number",
      verifyCertificateButton: "Verify",
      certificateRecordValid: "This certificate record is genuine.",
      certificateRecordNotFound: "No certificate record was found for this reference number.",
      issuingChurch: "Issuing Church",
      issueDate: "Issue Date",

      pendingReminders: "Pending Baptism Reminders",
      dueForReminder: "Due for Reminder",
      timingUnresolved: "Timing Unresolved",
      timingUnresolvedNote: "Gender is not recorded for these members, so the 40th/80th-day timing can't be computed. Record their gender to resolve.",
      reminderDayCount: "Day",
      dueDate: "Due Date",
      needsAttention: "Needs Attention",
      allRecords: "All",

      pendingEndings: "Pending Marriage Endings",
      endMarriageAction: "End Marriage",
      endReasonLabel: "End Reason",
      spouseDeceased: "Spouse Deceased",
      dissolved: "Dissolved",
      endedAtLabel: "Ended On",
      endedAtOrNotKnownRequired: "Give the day it ended, or tick that it is not known",
      marriageEndDayNotKnownToggle: "The day it ended is not known",
      marriageEndDayNotKnown: "Not known",
      endNoteLabel: "Note (optional)",
      marriageEndedSuccessfully: "Marriage ending recorded successfully",
      errorEndingMarriage: "Error recording marriage ending",
      spouseName: "Spouse",
      deceasedMember: "Deceased Member",

      pendingDeceasedStatus: "Pending Deceased-Status Updates",
      currentStatusLabel: "Current Status",
      updateMemberStatus: "Update Member Status",

      administration: "Administration",
      manageUsers: "Manage Users",
      manageFamily: "Manage Family",
      pendingMinutesApproval: "Pending Minutes Approval",
      failedToFetchPendingMinutes: "Failed to fetch pending minutes",
      noPendingMinutes: "No pending minutes to review",
      submittedBy: "Submitted By",
      submittedAt: "Submitted At",
      meeting: "Meeting",
      view: "View",
      minutesApprovedSuccessfully: "Minutes approved successfully",
      minutesRejectedSuccessfully: "Minutes rejected successfully",
      users: "Users",
      user: "User",
      createUser: "Create User",
      editUser: "Edit User",
      userCreatedSuccessfully: "User created successfully",
      userUpdatedSuccessfully: "User updated successfully",
      errorCreatingUser: "Error creating user",
      errorUpdatingUser: "Error updating user",
      confirmDeleteUser: "Are you sure you want to delete this user?",
      userDeletedSuccessfully: "User deleted successfully",
      errorDeletingUser: "Error deleting user",
      associatedMember: "Associated Member",
      accountStatus: "Account Status",
      roles: "Roles",
      searchMember: "Search for a member...",
      passwordIsRequiredForNew: "Password is required for new users",
      passwordMinLength: "Password must be at least 6 characters",
      usernameMinLength: "Username must be at least 3 characters",
      failedToFetchUsers: "Failed to fetch users",
      failedToFetchRoles: "Failed to fetch roles",
      failedToFetchUserDetails: "Failed to fetch user details",
      atLeastOneRoleRequired: "At least one role is required",
      emailIsRequired: "Email is required",
      leaveBlankToKeepCurrent: "Leave blank to keep current password",
      // R5: self-registration form (Register.js) and the admin pending-accounts/create-account
      // screens.
      usernameIsRequired: "Username is required",
      passwordsDoNotMatch: "Passwords do not match",
      firstNameIsRequired: "First name is required",
      lastNameIsRequired: "Last name is required",
      confirmPassword: "Confirm password",
      registerAccountTitle: "Create an account",
      registerAccountSubtitle: "Submit your details below - an administrator will review and approve your account.",
      personalNumberOptionalHelp: "Optional. If you don't have a Swedish personal number yet, leave this blank - you can still register.",
      registrationSubmissionFailed: "Registration failed - please check the form and try again.",
      registrationSubmittedTitle: "Thank you",
      registrationSubmittedBody: "The parish writes only to an address it knows to be yours. If you have questions, contact the parish.",
      backToLogin: "Back to login",
      forgotPasswordNoOnlineReset: "Passwords can't be reset online yet.",
      forgotPasswordContactAdmin: "Contact your parish administrator. They can set a new password for you.",
      alreadyHaveAnAccount: "Already have an account?",
      pendingAccounts: "Pending Accounts",
      pendingAccountsSubtitle: "Self-registrations awaiting review",
      noPendingAccounts: "No pending accounts",
      pendingAccountsLoadFailed: "Couldn't load the accounts waiting for approval",
      pendingAccountsLoadFailedBody: "This doesn't mean that none are waiting.",
      requestedIdentity: "Requested identity",
      requestedOn: "Requested on",
      rejectAccount: "Reject account",
      registrationRejectionReason: "The reason",
      rejectionReasonOtherParish: "A member of another parish",
      rejectionReasonContactOffice: "Another reason",
      rejectionDeletesRegistration: "The registration, and what they filled in, are deleted.",
      rejectionReasonOneOfTwo: "Choose one of the two reasons.",
      rejectionConfirmTitle: "Reject this registration?",
      rejectionConfirmMessage: "Reject the registration of {{name}}, with the reason: {{reason}}? The registration, and what they filled in, are deleted. This can't be undone.",
      rejectionConfirmYes: "Reject and delete",
      registrationRejectedNoNotice: "The registration is rejected and deleted. No notice was sent: the address they gave isn't verified.",
      registrationRejectedNoticeSent: "The registration is rejected and deleted. They are told by e-mail, in the language they registered in.",
      accountApprovedSuccessfully: "Account approved successfully",
      accountRejectedSuccessfully: "Account rejected successfully",
      errorApprovingAccount: "Failed to approve account",
      errorRejectingAccount: "Failed to reject account",
      failedToFetchPendingAccounts: "Failed to fetch pending accounts",
      consentBasisLabel: "Legal basis for storing this personal number",
      consentBasisSelfSubmitted: "Self-submitted (applicant provided it themselves)",
      consentBasisReligiousBody: "Recorded by the church on the member's behalf",
      consentBasisRequiredForPersonalNumber: "A legal basis is required when a personal number is provided",
      // R6: member self-service screens (My Account section).
      myAccount: "My Account",
      myProfile: "My Profile",
      myApplications: "My Applications",
      myBookings: "My Bookings",
      myCertificates: "My Certificates",
      failedToFetchMyProfile: "Failed to fetch your profile",
      failedToFetchMyBookings: "Failed to fetch your bookings",
      failedToFetchMyCertificates: "Failed to fetch your certificates",
      myProfileReadOnlyNote: "This information is read-only. Contact your church administrator to request a correction.",
      fullName: "Full Name",
      confessionFather: "Confession Father",
      confessionFatherNoneRecorded: "None recorded",
      notificationChannelPreference: "Notification Channel Preference",
      preferredLanguage: "Preferred Language",
      applicationsAppearOnceReviewed: "Your applications appear here once the parish office has reviewed them.",
      noBookingsYet: "You don't have any bookings yet.",
      noCertificatesYet: "You don't have any certificates yet.",
      applicationDate: "Application Date",
      clergyMember: "Clergy Member",
      certificateType: "Certificate Type",
      download: "Download",
      notAuthorizedToManageThisClergyMember: "You can only manage your own availability, or a clergy member's if you're a church administrator.",
      addChurch: "Add Church",
      editChurch: "Edit Church",
      manageChurches: "Manage Churches",
      failedToFetchChurches: "Failed to fetch churches",
      failedToFetchChurchDetails: "Failed to fetch church details",
      churchDeletedSuccessfully: "Church deleted successfully",
      errorDeletingChurch: "Error deleting church",
      churchAddedSuccessfully: "Church added successfully",
      churchUpdatedSuccessfully: "Church updated successfully",
      errorSavingChurch: "Error saving church",
      confirmDeleteChurch: "Are you sure you want to delete this church?",
      failedToFetchCountries: "Failed to fetch countries",
      maritalStatusesLoadFailed: "The marital statuses couldn't be loaded",
      selectCountry: "Select country",
      pleaseFieldAllRequired: "Please fill all required fields",
      street: "Street",
      // Employee translations
      addEmployee: "Add Employee",
      editEmployee: "Edit Employee",
      manageEmployees: "Manage Church Employees",
      memberFullName: "Member Full Name",
      professionType: "Profession Type",
      failedToFetchEmployees: "Failed to fetch employees",
      failedToFetchEmployeeDetails: "Failed to fetch employee details",
      employeeDeletedSuccessfully: "Employee deleted successfully",
      errorDeletingEmployee: "Error deleting employee",
      employeeAddedSuccessfully: "Employee added successfully",
      employeeUpdatedSuccessfully: "Employee updated successfully",
      errorSavingEmployee: "Error saving employee",
      confirmDeleteEmployee: "Are you sure you want to delete this employee?",
      searchByName: "Search by name...",
      // Service Member translations
      registerServiceMember: "Register Service Member",
      manageServiceMembers: "Manage Service Members",
      serviceName: "Service Name",
      selectService: "Select Service",
      failedToFetchServiceMembers: "Failed to fetch service members",
      failedToFetchServices: "Failed to fetch services",
      serviceMemberDeletedSuccessfully: "Service member deleted successfully",
      errorDeletingServiceMember: "Error deleting service member",
      serviceMemberRegisteredSuccessfully: "Service member registered successfully",
      errorRegisteringServiceMember: "Error registering service member",
      confirmDeleteServiceMember: "Are you sure you want to delete this service member registration?",
      failedToFetchDetails: "Failed to fetch details",
      memberServiceUpdated: "Member service updated successfully",
      memberServiceAdded: "Member service added successfully",
      errorSavingDetails: "Error saving details",
      editServiceMember: "Edit Service Member",
      beginDate: "Begin Date",
      endDate: "End Date",
      areYouSureYouWantToDeleteThisMember: "Are you sure you want to delete this member?",
      // Church Service Management translations
      manageChurchServices: "Manage Church Services",
      addService: "Add Service",
      editService: "Edit Service",
      selectServiceType: "Select Service Type",
      partOfSundaySchool: "Part of the Sunday school",
      partOfSundaySchoolHelp: "Its activities are classes, and the Sunday school's staff are responsible for its members (everyone aged 4 to 35 is under the Sunday school).",
      // The public pages' staff screens (session 7)
      publicPageMenu: "Public page",
      roleHelpPublicEditor: "Edits the church's public page: its profile, services, feasts and fasts. Opens no member, family or booking.",
      publicContentSaveFailed: "It wasn't saved.",
      publicContentReasonRequired: "Required.",
      publicContentReasonInvalid: "Not accepted as written.",
      publicContentReasonPersonnummer: "Looks like a personal identity number, which a public text never holds.",
      publicContentReasonAddress: "Holds an e-mail address, which this text never holds.",
      publicContentReasonPersonsAddress: "That is a person's address, not the church's.",
      publicContentReasonDomains: "Not under the church's own domains.",
      publicContentReasonAdministrator: "Only an administrator can set this.",
      publicContentReasonTooLong: "Too long.",
      publicCalendarTitle: "Public calendar",
      publicCalendarChurchFailed: "Which church this is couldn't be loaded.",
      publicCalendarHelp: "Services, feasts and fasts shown on the public page. Classes and their breaks are set under Classes on the public page.",
      publicCalendarYear: "Year",
      publicCalendarYearOption: "{{year}} (Ethiopian) · {{from}} – {{to}}",
      publicCalendarNew: "New event",
      publicCalendarEvents: "Events",
      publicCalendarLoadFailed: "The calendar's events couldn't be loaded.",
      publicCalendarNone: "No events in {{year}}.",
      publicCalendarPublished: "Published",
      publicCalendarDraft: "Draft",
      publicCalendarKindService: "Service",
      publicCalendarKindFeast: "Feast",
      publicCalendarKindFast: "Fast",
      publicCalendarKindOther: "Other",
      publicCalendarRepeatsOnceOn: "Once, {{date}}",
      publicCalendarRepeatsWeeklyOn: "Every week, {{weekday}}",
      publicCalendarRepeatsMonthlyOn: "Every Ethiopian month, day {{day}}",
      publicCalendarRepeatsYearlyOn: "Every year, {{month}} {{day}}",
      publicCalendarRuleLoadFailed: "This event couldn't be loaded.",
      publicCalendarEditor: "Edit event",
      publicCalendarEditing: "Edit: {{title}}",
      publicCalendarNewRule: "New event (a draft until published)",
      publicCalendarTitles: "Title",
      publicCalendarLines: "Line under the title",
      publicCalendarKind: "Kind",
      publicCalendarRepeats: "Repeats",
      publicCalendarRepeatOnce: "Once, on a date",
      publicCalendarRepeatWeekly: "Every week on",
      publicCalendarRepeatMonthly: "Every Ethiopian month on day",
      publicCalendarRepeatYearly: "Every year on",
      publicCalendarDate: "Date",
      publicCalendarWeekday: "Weekday",
      publicCalendarEthMonth: "Ethiopian month",
      publicCalendarEthDay: "Day",
      publicCalendarChoose: "Choose",
      publicCalendarStarts: "Starts",
      publicCalendarEnds: "Ends (optional)",
      publicCalendarFor: "For",
      publicCalendarWholeYear: "The whole year {{year}} ({{from}} – {{to}})",
      publicCalendarFrom: "From",
      publicCalendarUntil: "Until",
      publicCalendarNextDates: "Next dates",
      publicCalendarNoNextDates: "No dates to come.",
      publicCalendarCancelled: "Cancelled dates",
      publicCalendarNoneCancelled: "None cancelled.",
      publicCalendarReasonShown: "Reason shown on the public page: {{reason}}",
      publicCalendarUndo: "Undo",
      publicCalendarCancelOne: "Cancel one date, with a reason",
      publicCalendarCancelWhich: "The date",
      publicCalendarReason: "Reason shown on the public page",
      publicCalendarCancelDate: "Cancel this date",
      publicCalendarPublish: "Publish",
      publicCalendarUnpublish: "Unpublish",
      publicProfileTitle: "The church's public page",
      publicProfileHelp: "What the public page says about the church. A text left empty in a language is shown in another.",
      publicProfileLoadFailed: "The church's public page couldn't be loaded.",
      publicProfileNameAm: "The church's name in Amharic",
      publicProfileTagline: "Tagline",
      publicProfileWelcome: "Welcome",
      publicProfileFirstVisit: "First visit",
      publicProfileDirections: "Getting here",
      publicProfileDiocese: "Diocese line",
      publicProfileEmail: "Public e-mail",
      publicProfileMapUrl: "Map link (https)",
      publicProfilePhoneShown: "Show the church's phone number",
      publicProfileYes: "Yes",
      publicProfileNo: "No",
      publicProfileNotAnswered: "Not answered yet",
      publicProfileByAppointment: "By appointment",
      publicProfileCardTitle: "Title",
      publicProfileCardLine: "Line",
      publicProfileAddCard: "Add a card",
      publicProfileRemoveCard: "Remove this card",
      publicProfileSaved: "Saved.",
      publicProfilePublished: "Published.",
      publicProfileUnpublished: "Unpublished.",
      publicProfileDomains: "The church's own e-mail domains",
      publicProfileDomainsHelp: "The public e-mail, and a department's contact, must be under one of these. Set by an administrator.",
      classCalendarTitle: "Classes on the public page",
      classCalendarHelp: "What the public page says about the Sunday school's classes, and the days without classes. A class's term, weekday and sessions are set in Activities.",
      classCalendarLoadFailed: "The classes couldn't be loaded.",
      classCalendarDepartments: "Departments",
      classCalendarNoDepartments: "No Sunday-school department.",
      classCalendarClasses: "Classes",
      classCalendarNoClasses: "No active class.",
      classCalendarShown: "Shown on the public page",
      classCalendarPublicName: "Public name",
      classCalendarLine: "Line",
      classCalendarContact: "Contact e-mail",
      classCalendarSaved: "Saved.",
      classCalendarTerm: "Term: {{from}} – {{to}}",
      classCalendarAgeFrom: "Ages from",
      classCalendarAgeTo: "Ages to",
      classCalendarDays: "Days without classes",
      classCalendarNoDays: "None.",
      classCalendarRename: "Change the public name",
      classCalendarRemove: "Remove",
      classCalendarRemoveConfirm_one: "Removing it brings back {{count}} class session it alone called off.",
      classCalendarRemoveConfirm_other: "Removing it brings back {{count}} class sessions it alone called off.",
      classCalendarAddDay: "Add a day without classes",
      classCalendarDayClass: "Class",
      classCalendarDayInternalName: "Internal name (not shown)",
      classCalendarDayPublicName: "Public reason",
      classCalendarDayAdded_one: "Added: {{count}} class session called off.",
      classCalendarDayAdded_other: "Added: {{count}} class sessions called off.",
      priesthood: "Priesthood",
      deaconship: "Deaconship",
      sundaySchoolService: "Sunday School",
      serviceAddedSuccessfully: "Service added successfully",
      serviceUpdatedSuccessfully: "Service updated successfully",
      serviceDeletedSuccessfully: "Service deleted successfully",
      errorAddingService: "Error adding service",
      errorUpdatingService: "Error updating service",
      errorDeletingService: "Error deleting service",
      confirmDeleteService: "Are you sure you want to delete this church service?",
      failedToFetchChurchServices: "Failed to fetch church services",
      noServicesFound: "No services found",
      failedToFetchServiceDetails: "Failed to fetch service details",
      // Dashboard translations
      activeMembers: "Members",
      employees: "Employees",
      currentActiveMembers: "Current active members",
      totalEmployees: "Total employees",
      totalBaptisms: "Total baptisms",
      totalMarriages: "Total marriages",
      sundaySchoolParticipants: "Sunday school participants",
      totalDeacons: "Total deacons",
      activeMembersByYear: "Records added by year",
      activeMembersByYearNote: "The year a membership began is not recorded yet.",
      employeesByYear: "Employees by Year",
      baptismsByYear: "Baptisms by Year",
      marriagesByYear: "Marriages by Year",
      sundaySchoolByYear: "Sunday School by Year",
      deaconsByYear: "Deacons by Year",
      failedToFetchDashboardStats: "Failed to fetch dashboard statistics",
      export: "Export",
      rowsPerPage: "Rows per page",
      prev: "Prev",
      next: "Next",
      PROFILE_SETTINGS: "Profile Settings",
      LOGOUT: "Logout",
      selectColumnsToExport: "Select Columns to Export",
      exportSuccessful: "Export successful",
      exportFailed: "Export failed",
      selectMaritalStatus: "Select Marital Status",
      selectGender: "Select Gender",
      selectSpiritualFather: "Select Spiritual Father",
      selectChurch: "Select Church",
      selectStatus: "Select Status",
      status: "Status",
      ACTIVE: "Active",
      INACTIVE: "Inactive",
      MOVED: "Moved",
      DECEASED: "Deceased",
      // Meeting Minutes translations
      meetings: "Meetings",
      meetingsListTitle: "Meetings List",
      addMeeting: "Add Meeting",
      editMeeting: "Edit Meeting",
      scheduleMeeting: "Schedule Meeting",
      meetingTitle: "Meeting Title",
      meetingDate: "Meeting Date",
      startTime: "Start Time",
      endTime: "End Time",
      location: "Location",
      chair: "Chair",
      secretary: "Secretary",
      selectChair: "Select Chair",
      selectSecretary: "Select Secretary",
      noMembersForServiceType: "No members registered for this service type",
      enterDescription: "Enter meeting description...",
      loading: "Loading",
      meetingCreatedSuccessfully: "Meeting created successfully",
      meetingUpdatedSuccessfully: "Meeting updated successfully",
      meetingDeletedSuccessfully: "Meeting deleted successfully",
      errorSavingMeeting: "Error saving meeting",
      errorFetchingMeeting: "Error fetching meeting details",
      failedToFetchMeetings: "Failed to fetch meetings",
      confirmDeleteMeeting: "Are you sure you want to delete this meeting?",
      editMinutes: "Edit Minutes",
      viewMinutes: "View Minutes",
      meetingMinutes: "Meeting Minutes",
      attendance: "Attendance",
      agenda: "Agenda",
      decisions: "Decisions",
      actionItems: "Action Items",
      addAttendee: "Add Attendee",
      attendeeName: "Attendee Name",
      attendeeRole: "Role",
      present: "Present",
      absent: "Absent",
      addAgendaItem: "Add Agenda Item",
      agendaTitle: "Agenda Item Title",
      agendaNotes: "Notes",
      agendaDecision: "Decision",
      showPreview: "Show Preview",
      hidePreview: "Hide Preview",
      addDecision: "Add Decision",
      decisionText: "Decision",
      task: "Task",
      responsible: "Responsible",
      pending: "Pending",
      inProgress: "In Progress",
      completed: "Completed",
      nextMeetingDate: "Next Meeting Date",
      nextMeetingLocation: "Next Meeting Location",
      print: "Print",
      exportPdf: "Export PDF",
      minutesLockedBy: "Minutes are being edited by",
      lockExpired: "Your editing lock has expired",
      acquiringLock: "Acquiring editing lock...",
      youHaveEditingLock: "You have the editing lock",
      viewingOnly: "Viewing only - another user is editing",
      releaseLock: "Release Lock",
      notEditing: "Not Editing",
      clickToStartEditing: "Click the button to start editing",
      startEditing: "Start Editing",
      viewOnly: "View Only",
      viewOnlyMode: "Another user is editing this document",
      lockExpiresAt: "Lock expires at",
      youAreEditing: "You are editing",
      preview: "Preview",
      editor: "Editor",
      additionalNotes: "Additional Notes",
      signature: "Signature",
      chairSignature: "Chair Signature",
      secretarySignature: "Secretary Signature",
      readOnlyAccess: "You have read-only access to this page",
      backToList: "Back to List",
      downloadPdf: "Download PDF",
      pdfDownloaded: "PDF downloaded successfully",
      failedToDownloadPdf: "Failed to download PDF",
      connected: "Connected",
      disconnected: "Disconnected",
      meetingNotFound: "Meeting not found",
      // Approval workflow
      pendingApproval: "Pending Approval",
      approved: "Approved",
      rejected: "Rejected",
      approveMinutesConfirmation: "Are you sure you want to approve these meeting minutes?",
      rejectMinutesConfirmation: "Are you sure you want to reject these meeting minutes?",
      approvedOn: "Approved on",
      optional: "optional",
      addYourNotesHere: "Add your notes here",
      sendApprovalRequests: "Send Approval Requests",
      sendApprovalEmails: "Send approval emails to all attendees",
      // Service types
      serviceType: "Service Type",
      parish: "Parish",
      sundaySchool: "Sunday School",
      youth: "Youth",
      choir: "Choir",
      deacons: "Deacons",
      cancelled: "Cancelled",
      allStatuses: "All Statuses",
      noMeetingsFound: "No meetings found",
      date: "Date",
      time: "Time",
      showingOf: "Showing {{count}} of {{total}}",
      page: "Page",
      delete: "Delete",
      editing: "Editing",
      chooseMember: "Choose Member",
      role: "Role",
      attendee: "Attendee",
      chairperson: "Chairperson",
      invited: "Invited",
      confirmed: "Confirmed",
      declined: "Declined",
      saveNow: "Save Now",
      startTypingMinutes: "Start typing minutes...",
      attendees: "Attendees",
      failedToReleaseLock: "Failed to release lock",
      lockLost: "Your editing lock has been lost",
      titleAndDateRequired: "Title and date are required",
      createMeeting: "Create Meeting",
      minutesSaved: "Minutes saved successfully",
      errorSavingMinutes: "Error saving minutes",
      lockAcquired: "You now have the editing lock",
      lockReleased: "Editing lock released",
      lockRefreshed: "Lock refreshed",
      errorAcquiringLock: "Error acquiring lock",
      errorReleasingLock: "Error releasing lock",
      errorRefreshingLock: "Error refreshing lock",
      DRAFT: "Draft",
      IN_PROGRESS: "In Progress",
      FINALIZED: "Finalized",
      APPROVED: "Approved",
      ARCHIVED: "Archived",
      // Action Items Manager translations
      total: "Total",
      overdue: "Overdue",
      noActionItems: "No action items yet",
      titleAndAgendaRequired: "Title and agenda item are required",
      errorSavingActionItem: "Error saving action item",
      confirmDeleteActionItem: "Are you sure you want to delete this action item?",
      selectAgendaItem: "Select Agenda Item",
      agendaItem: "Agenda Item",
      actionItemTitle: "Action Item Title",
      actionItemDescription: "Description",
      assignee: "Assignee",
      selectAssignee: "Select Assignee",
      priority: "Priority",
      low: "Low",
      medium: "Medium",
      high: "High",
      urgent: "Urgent",
      startProgress: "Start Progress",
      markComplete: "Mark Complete",
      editActionItem: "Edit Action Item",
      add: "Add",
      save: "Save",
      // Meeting Minutes Approval translations
      minutesEditor: "Minutes Editor",
      formalApproval: "Formal Approval",
      noFormalMinutesYet: "No Formal Minutes Created",
      createFormalMinutesDescription: "Create a formal meeting minutes document that can be submitted for approval.",
      createDraftMinutes: "Create Draft Minutes",
      minutesPreview: "Minutes Preview",
      version: "Version",
      lastUpdated: "Last Updated",
      submitForApproval: "Submit for Approval",
      approve: "Approve",
      reject: "Reject",
      approveMinutes: "Approve Minutes",
      rejectMinutes: "Reject Minutes",
      approvalNotes: "Approval Notes",
      rejectionReason: "Rejection Reason",
      optionalApprovalNotes: "Optional notes (e.g., minor corrections made)",
      provideRejectionReason: "Please provide a reason for rejection",
      confirmApprove: "Confirm Approval",
      confirmReject: "Confirm Rejection",
      errorCreatingDraft: "Error creating draft minutes",
      errorSubmittingApproval: "Error submitting for approval",
      errorProcessingApproval: "Error processing approval",
      aiGeneratedSummary: "AI Generated Summary",
      aiSummary: "AI Summary",
      generateAiSummary: "Generate AI Summary",
      aiSummaryComingSoon: "AI Summary feature coming soon!",
      by: "by",
      "minutesStatus.DRAFT": "Draft - Ready for editing",
      "minutesStatus.PENDING_APPROVAL": "Pending Approval - Awaiting review",
      "minutesStatus.APPROVED": "Approved - Final version",
      "minutesStatus.REJECTED": "Rejected - Needs revision",
      "minutesStatus.ARCHIVED": "Archived",
      // Meeting Enhancement Translations (SR-01 to SR-10)
      // SR-01: Recurring Meeting
      recurringPattern: "Recurring Pattern",
      weekly: "Weekly",
      monthly: "Monthly",
      quarterly: "Quarterly",
      lastSundayOfMonth: "Last Sunday of Month",
      firstSundayOfMonth: "First Sunday of Month",
      suggestedNextDate: "Suggested Next Meeting Date",
      // SR-02: Agenda Builder
      agendaItems: "Agenda Items",
      addAgendaItemsOrUseTemplate: "Add agenda items or use a template to get started",
      noAgendaItemsYet: "No agenda items yet",
      agendaItemTitle: "Agenda Item Title",
      agendaItemDescription: "Description/Background",
      whoWillPresent: "Who will present this item?",
      addFromTemplate: "Add from Template",
      openingPrayer: "Opening Prayer",
      approvalOfPreviousMinutes: "Approval of Previous Minutes",
      mattersArising: "Matters Arising",
      reportsAndUpdates: "Reports and Updates",
      newBusiness: "New Business",
      anyOtherBusiness: "Any Other Business (AOB)",
      closingPrayerAndAdjournment: "Closing Prayer and Adjournment",
      items: "items",
      minutes: "minutes",
      min: "min",
      presenter: "Presenter",
      estimatedDuration: "Est. Duration",
      // SR-03: Attachments
      attachments: "Attachments",
      addAttachment: "Add Attachment",
      uploadFile: "Upload File",
      downloadFile: "Download File",
      fileDescription: "File Description",
      // SR-04: Quorum
      quorumMet: "Quorum Met",
      quorumNotMet: "Quorum Not Met",
      membersPresent: "members present",
      totalMembers: "Total Members",
      quorumRequirement: "Quorum Requirement",
      needMoreMembers: "Need {{count}} more members for quorum",
      quorumType: "Quorum Type",
      percentage: "Percentage",
      fixedNumber: "Fixed Number",
      // SR-05: Chair/Secretary Distinction (already have chair/secretary)
      // SR-06: Matters Arising
      loadingMattersArising: "Loading matters arising",
      fromMeeting: "From Meeting",
      noMattersArising: "No Matters Arising",
      allPreviousActionItemsResolved: "All action items from the previous meeting have been resolved",
      reviewStatus: "Review Status",
      notReviewed: "Not Reviewed",
      stillPending: "Still Pending",
      carryForward: "Carry Forward",
      markCompleted: "Mark Completed",
      originalDescription: "Original Description",
      updateStatus: "Update Status",
      reviewNotes: "Review Notes",
      addNotesAboutProgress: "Add notes about progress or updates",
      // SR-07: Inline Action Items
      addActionItem: "Add Action Item",
      newActionItem: "New Action Item",
      noActionItemsYet: "No action items for this agenda item",
      assignTo: "Assign To",
      selectMember: "Select Member",
      optionalDetails: "Optional details",
      addItem: "Add Item",
      // SR-08: Decision/Voting
      decisionType: "Decision Type",
      consensus: "Consensus",
      majorityVote: "Majority Vote",
      unanimous: "Unanimous",
      deferred: "Deferred",
      consensusDescription: "All members agreed without formal vote",
      majorityVoteDescription: "Decision made by majority vote",
      unanimousDescription: "All members voted in favor",
      deferredDescription: "Decision postponed to future meeting",
      voteCount: "Vote Count",
      totalVoters: "Total Voters",
      votesFor: "Votes For",
      votesAgainst: "Votes Against",
      abstentions: "Abstentions",
      motionBy: "Motion By",
      secondedBy: "Seconded By",
      whoProposedMotion: "Who proposed the motion?",
      whoSecondedMotion: "Who seconded the motion?",
      motionPassed: "Motion PASSED",
      motionFailed: "Motion FAILED",
      inFavor: "in favor",
      votes: "votes",
      // SR-09: Dynamic Header (parish logo/name from settings)
      parishHeader: "Parish Header",
      // SR-10: Digital Signatures
      signatures: "Signatures",
      signedBy: "Signed by",
      signatureNotYetProvided: "Signature not yet provided",
      waitingForSignature: "Waiting for signature",
      clickToSign: "Click to sign as",
      signNow: "Sign Now",
      signAbovePrompt: "Sign in the box above using your mouse or touch",
      saveSignature: "Save Signature",
      clearSignature: "Clear Signature",
      toBeSignedBy: "To be signed by",
      signedOn: "Signed on",
      required: "Required",
      fullyApproved: "Fully Approved",
      bothSignaturesRequired: "Both Chair and Secretary signatures are required",
      chairSignatureRequired: "Chair signature is required",
      secretarySignatureRequired: "Secretary signature is required",
      // Meeting Status
      scheduled: "Scheduled",
      liveInSession: "Live / In Session",
      review: "Under Review",
      archived: "Archived",
      discussed: "Discussed",

      // R3 task 7 - shared fields across the four new screens
      subject: "Subject",
      service: "Service",
      telephone: "Telephone",
      email: "Email",
      province: "Province/Region",
      postalCode: "Postal Code",
      apartmentNo: "Apartment No.",
      referenceCode: "Reference Code",
      decisionReason: "Decision Reason",
      requestedBy: "Requested By",
      requestedClergy: "Requested Clergy",
      scheduledAt: "Scheduled At",
      logisticsNote: "Logistics Note",
      dayOfWeekLabel: "Day of Week",

      // R3 task 7 - public application form (FR-3.9a)
      applyForServiceTitle: "Apply for a Church Service",
      applyForServiceSubtitle: "Baptism, matrimony, funeral, confession, consultation, and more.",
      churchIsRequired: "Please select a church",
      applicantNameIsRequired: "Your name is required",
      serviceIsRequired: "Please select a service or describe the one you need",
      consentIsRequired: "You must accept the consent statement to submit this application",
      childNameIsRequired: "The child's name is required",
      childDobIsRequired: "The child's date of birth is required",
      godparentNameIsRequired: "A godparent's name is required",
      applicantChristianNameIsRequired: "Your የክርስትና ስም (Christian name) is required",
      atLeastOneSubjectIsRequired: "At least one subject is required",
      subjectFullNameIsRequired: "Every subject's full name is required",
      subjectChristianNameIsRequired: "Every subject's የክርስትና ስም (Christian name) is required - it is the name spoken in the prayer",
      // R3.1 task 4 - wedding application section (FR-3.24)
      weddingPartyNameIsRequired: "Both parties' ዓለማዊ ስም (given name) is required",
      weddingPartyChristianNameIsRequired: "Both parties' የክርስትና ስም (Christian name) is required",
      // Phone/email/address are individually optional - an elderly party or one still in
      // Ethiopia may genuinely have no email - but the office needs at least one way to reach
      // them, so at least one of the three is required.
      weddingPartyContactMethodIsRequired: "Please provide at least one way to contact each party (phone, email, or address)",
      weddingPartyContactMethodHelp: "Provide at least one of phone, email, or address for this party.",
      phoneNumberIsInvalid: "Please enter a valid phone number",
      weddingApplicantRoleTitle: "Are you one of the two people getting married?",
      iAmNotDirectlyAParty: "No, I'm applying on their behalf",
      iAmPartyOne: "Yes, I am Party One",
      iAmPartyTwo: "Yes, I am Party Two",
      iAmAlsoASubject: "I am also one of the people this request is for",
      applicationSubmissionFailed: "We couldn't submit your application. Please try again.",
      applicationSubmittedTitle: "Application Submitted",
      applicationSubmittedCheckEmail: "Please check your email to verify your application.",
      otherServiceNotListed: "The service I need isn't listed",
      // Public form's service-specific panel header - names the actual selected catalog entry
      // rather than a generic label, so the header itself confirms the choice back to the
      // applicant. Review.js keeps its own static baptismDetailsTitle/weddingDetailsTitle/
      // subjectsSectionTitle below - different screen, different context.
      serviceSectionDetailsTitle: "{{service}} Details",
      baptismDetailsTitle: "Baptism Details",
      // R3.1 task 4 - wedding application section (FR-3.24): both parties captured as free text
      // at intake, matched against the member registry only at review.
      weddingDetailsTitle: "Wedding Details",
      partyOneTitle: "Party One",
      partyTwoTitle: "Party Two",
      partyName: "ዓለማዊ ስም (Given Name)",
      // R3.1 task 3 - subjects array (FR-3.12b/FR-3.12c): fithat/prayer requests may name many
      // subjects, each needing their own የክርስትና ስም since it is the name spoken in the prayer.
      subjectsSectionTitle: "Subjects",
      applicantChristianName: "Your የክርስትና ስም (Christian Name)",
      applicantChristianNameHelp: "You are also named in the prayer, as the one asking - this is the name spoken for you.",
      subjectFullName: "Full Name",
      subjectChristianName: "የክርስትና ስም (Christian Name)",
      livingOrDeparted: "Living or Departed",
      living: "Living",
      departed: "Departed",
      addAnotherSubject: "Add Another Subject",
      remove: "Remove",
      // Pulled forward from task 6 (FR-3.22) - declared payment, no file upload.
      paymentSectionTitle: "Payment",
      amountPaid: "Amount Paid",
      payerName: "Payer's Name",
      estimatedTotal: "Estimated Total",
      estimatedTotalHelp: "Estimated at the guest rate - the confirmed amount depends on membership, resolved when the parish reviews your application.",
      consentStatement: "I consent to the processing of the personal data provided in this form for the purpose of this application.",
      submitApplication: "Submit Application",
      applicantName: "Applicant Name",
      applicantEmail: "Applicant Email",
      applicantRelation: "Relationship to Subject",
      serviceNeededDate: "Date Needed",
      serviceOtherText: "Describe the Service Needed",
      childName: "Child's Name",
      childDob: "Child's Date of Birth",
      godparentName: "Godparent's Name",
      selectChurchFirst: "Select a church first",
      checkApplicationStatus: "Check Application Status",

      // R3 task 7 - verification page (FR-3.15)
      verificationTokenMissing: "This verification link is missing its token",
      verifyingApplication: "Verifying your application...",
      applicationVerifiedTitle: "Application Verified",
      applicationVerifiedBody: "Thank you - your application has been verified and is now with the parish for review.",
      verificationFailedTitle: "Verification Failed",
      verificationFailed: "This verification link is invalid or has expired",

      // R3 task 7 - status lookup page (FR-3.17)
      referenceCodeIsRequired: "Please enter your reference code",
      applicationNotFound: "No application was found for that reference code",
      checkStatus: "Check Status",
      yourResponse: "Your Response",
      responseRequired: "Please enter a response",
      responseFailed: "Failed to submit your response",
      submitResponse: "Submit Response",
      applicationStatus: {
        PENDING_VERIFICATION: "Pending Verification",
        SUBMITTED: "Submitted",
        UNDER_REVIEW: "Under Review",
        INFO_REQUESTED: "More Information Requested",
        APPROVED: "Approved",
        REJECTED: "Rejected",
        ABANDONED: "Abandoned",
      },

      // R3 task 7 - review queue (FR-3.13/FR-3.16)
      serviceApplications: "Service Applications",
      failedToFetchApplications: "Failed to load service applications",
      queueWarningsColumn: "Warnings",
      subjectCountBadge: "{{count}} subjects",
      subjectCountBadgeHelp: "This application names more than one subject",
      queueWarningConfessionFather: "No Confession Father",
      queueWarningConfessionFatherHelp: "The applicant or a wedding party declared they have no confession father on file",
      queueWarningConfessionNeedsResolution: "Confession Father Needs Resolving",
      queueWarningConfessionNeedsResolutionHelp: "Declared \"at this church\" - pick the specific priest before or during approval",
      queueWarningPaymentNotDeclared: "No Payment Declared",
      queueWarningPaymentNotDeclaredHelp: "This is a paid service and no payment has been declared yet",
      queueWarningPaymentUnderpaid: "Likely Underpaid",
      queueWarningPaymentUnderpaidHelp: "The declared payment is below even the cheapest possible rate for this application",
      queueWarningPossibleDuplicate: "Possible Duplicate",
      queueWarningPossibleDuplicateHelp: "The applicant's name closely matches an existing member",
      reviewApplication: "Review Application",
      failedToFetchApplication: "Failed to load the application",
      applicationDetails: "Application Details",
      applicantResolutionTitle: "Applicant",
      subjectResolutionTitle: "Subject",
      godparentResolutionTitle: "Godparent",
      partyOneResolutionTitle: "Party One",
      partyTwoResolutionTitle: "Party Two",
      bookingDetailsTitle: "Booking Details",
      applicantResolutionRequired: "Please resolve the applicant before approving",
      subjectResolutionRequired: "Please resolve the subject before approving",
      godparentResolutionRequired: "Please resolve the godparent before approving",
      weddingPartyResolutionRequired: "Please resolve both parties before approving",
      serviceOverride: "Service to Book",
      serviceOverrideHelp: "This application used the free-text 'Other Services' option - choose which catalog service to book against.",
      serviceOverrideRequired: "Please choose which service to book",
      confirmedTotalTitle: "Payment Reconciliation",
      resolvedTotalLabel: "Resolved total",
      paymentStatusLabel: "Payment Status",
      paymentStatusConfirmed: "Confirmed",
      paymentStatusUnderpaid: "Underpaid",
      paymentStatusUnpaid: "Unpaid",
      selectPaymentStatus: "Select payment status",
      paymentStatusRequired: "Please record a payment status before approving",
      paymentMismatchWarning: "Declared {{declared}} - total due {{total}} - {{difference}} short",
      noPriceSnapshotNote: "This application has no price snapshot on file - reconciliation must be done manually.",
      selectClergyOptional: "Select clergy (optional)",
      reviewClergyLoadFailed: "The clergy list couldn't be loaded",
      reviewClergyBookingsOff: "Choosing the clergy comes with bookings, which aren't switched on here.",
      reviewFathersLoadFailed: "The list of priests couldn't be loaded",
      applicationApproved: "Application approved",
      approvalFailed: "Failed to approve the application",
      rejectionReasonRequired: "A rejection reason is required",
      applicationRejected: "Application rejected",
      rejectionFailed: "Failed to reject the application",
      requestInfo: "Request Info",
      requestInfoNote: "What's missing",
      requestInfoNoteRequired: "Please describe what information is needed",
      confirmRequestInfo: "Send Request",
      applicantRecordLinked: "The application will appear on My Applications for the member linked above: {{name}}.",
      applicantRecordHint: "Link the applicant's member record above, so the application appears on their My Applications, or say they have none. A rejection or a request for more information creates no record.",
      applicantHasNoMemberRecord: "The applicant has no member record",
      applicantRecordRequired: "Link the applicant's member record above, or tick that they have none",
      applicationInfoRequested: "Request for more information sent",
      requestInfoFailed: "Failed to request more information",
      requestInfoSectionTitle: "Requested Information",
      applicantResponseLabel: "Applicant's Response",
      noResponseYet: "No response yet",
      // R3.1 manual-testing fixes - person picker (FR-3.16): a reviewer picks by name, never
      // types or sees a raw database id.
      selected: "Selected",
      changeSelection: "Change",
      noPersonSelectedYet: "No person selected yet - see suggestions or search below.",
      possibleMatches: "Possible matches",
      searchAgain: "Search again",
      searchExistingPeople: "Search for an existing person",
      noResultsFound: "No results found",
      resolutionActionLabel: "How should this person be resolved?",
      selectAction: "Select an action",
      resolutionAction: {
        LINK_EXISTING: "Link to an existing member",
        CREATE_MEMBER: "Create a new member",
        CREATE_GUEST: "Create a new guest",
        DESCRIPTIVE_ONLY: "Record descriptively (no member/guest record)",
      },
      existingMemberId: "Existing Member ID",
      descriptiveName: "Descriptive Name",
      descriptiveDob: "Descriptive Date of Birth",
      descriptiveNote: "Descriptive Note",
      checkForDuplicates: "Check for Duplicates",
      nameRequiredForDuplicateCheck: "Enter a name before checking for duplicates",
      nameAsGiven: "Name as given: {{name}} - enter each part in the fields below",
      duplicateCheckFailed: "Failed to check for duplicate candidates",
      noDuplicateCandidatesFound: "No likely duplicates found",
      matchScore: "Match Score",
      possibleMatchElsewhere: "Possible match at another church",
      contactDioceseToConfirm: "contact a diocese admin to confirm",

      // R3 task 7 - bookings
      bookings: "Bookings",
      failedToFetchBookings: "Failed to load bookings",
      bookingStatus: {
        REQUESTED: "Requested",
        CONFIRMED: "Confirmed",
        COMPLETED: "Completed",
        CANCELLED: "Cancelled",
      },
      // R3.1 task 4 - marriage-recording reminder: a wedding booking whose date has passed but
      // no Marriage row has been recorded yet for that exact couple (FR-5.1's "already married"
      // check would otherwise silently miss it).
      needsMarriageRecorded: "Record Marriage",
      needsMarriageRecordedHelp: "This wedding's scheduled date has passed but the marriage hasn't been recorded yet - click to record it.",
      editBooking: "Edit Booking",
      failedToFetchBooking: "Failed to load the booking",
      bookingUpdatedSuccessfully: "Booking updated successfully",
      errorUpdatingBooking: "Failed to update the booking",
      clergyQueue: "Clergy Queue",
      failedToFetchClergyQueue: "Failed to load your clergy queue",
      clergyQueueEmpty: "You have no pending bookings awaiting action",
      respond: "Respond",

      // R3 task 7 - clergy availability (FR-3.7)
      manageClergyAvailability: "Clergy Availability",
      searchClergyMember: "Search Clergy Member",
      newAvailabilityWindow: "New Availability Window",
      addAvailabilityWindow: "Add Window",
      startAndEndTimeRequired: "Start and end time are required",
      availabilityWindowAdded: "Availability window added",
      errorAddingAvailabilityWindow: "Failed to add the availability window",
      errorDeletingAvailabilityWindow: "Failed to delete the availability window",
      failedToFetchAvailability: "Failed to load availability windows",
      dayOfWeek: {
        MONDAY: "Monday",
        TUESDAY: "Tuesday",
        WEDNESDAY: "Wednesday",
        THURSDAY: "Thursday",
        FRIDAY: "Friday",
        SATURDAY: "Saturday",
        SUNDAY: "Sunday",
      },

      // R3 task 7 - self-service consultation booking
      bookConsultation: "Book a Consultation",
      failedToLoadBookingSetup: "Failed to load booking setup",
      consultationServiceNotConfigured: "Consultation is not configured for your church yet",
      failedToFetchBookableClergy: "Failed to load available clergy",
      failedToFetchSlots: "Failed to load available time slots",
      selectAClergyMember: "Select a Clergy Member",
      selectClergy: "Select clergy",
      noBookableClergyAtYourChurch: "No clergy at your church currently have open consultation times",
      previousWeek: "Previous Week",
      nextWeek: "Next Week",
      noOpenSlotsThisWeek: "No open slots this week",
      consultationBooked: "Consultation booked",
      consultationBookedTitle: "Consultation Booked",
      consultationBookedBody: "Your consultation with {{clergy}} is confirmed for {{date}}.",
      bookAnother: "Book Another",
      bookingFailed: "Failed to book this slot - it may have just been taken",

      // R3 task 7 - sidebar
      servicesAndBookings: "Services & Bookings",

      // R3.1 task 2 - service catalog admin screen (FR-3.23)
      serviceCatalog: "Service Catalog",
      addServiceCatalogEntry: "Add Service",
      editServiceCatalogEntry: "Edit Service",
      failedToFetchServiceCatalog: "Failed to load the service catalog",
      failedToFetchServiceCatalogEntry: "Failed to load this service catalog entry",
      serviceCatalogCode: "Code",
      serviceCatalogPrice: "Member / Guest Price",
      serviceCatalogPricingBasis: "Pricing Basis",
      serviceCatalogCurrency: "Currency",
      serviceCatalogMemberPrice: "Member Price",
      serviceCatalogGuestPrice: "Guest Price",
      serviceCatalogIsFree: "This service is free",
      selectPricingBasis: "Select pricing basis",
      pricingBasis: {
        PER_SUBJECT: "Per subject",
        PER_APPLICATION: "Per application",
      },
      serviceCatalogSubjectMode: "Subject Mode",
      selectSubjectMode: "Select subject mode",
      subjectMode: {
        SELF_ONLY: "Self only - no subjects section",
        SINGLE: "Single subject",
        MANY: "Many subjects",
      },
      free: "Free",
      active: "Active",
      retired: "Retired",
      retire: "Retire",
      activate: "Activate",
      confirmRetireServiceCatalogEntry: "Retire this service? It will no longer be offered on new applications, but existing bookings and applications keep referencing it.",
      confirmActivateServiceCatalogEntry: "Activate this service again? It will become available on new applications.",
      serviceCatalogEntryRetired: "Service retired",
      serviceCatalogEntryActivated: "Service activated",
      serviceCatalogEntryUpdated: "Service catalog entry updated",
      serviceCatalogEntryAdded: "Service catalog entry added",
      errorUpdatingServiceCatalogEntry: "Failed to update this service catalog entry",
      errorAddingServiceCatalogEntry: "Failed to add this service catalog entry",
      // R5: family graph view (FamilyTreeView.js)
      errorFetchingFamilyData: "Error fetching family data",
      familyTreeDepth: "Depth",
      showGodparents: "Show godparents",
      showConfessionFather: "Show confession father",
      familyLegendParent: "Parent",
      familyLegendChild: "Child",
      familyLegendSpouse: "Spouse",
      familyLegendSibling: "Sibling",
      familyLegendPending: "Pending",
      familyLegendDisputed: "Disputed",
      "familyEdgeStatus.pending": "pending",
      "familyEdgeStatus.disputed": "disputed",
      // R5/FR-2.3: self-declared household relationships (My Family / Pending Confirmations)
      pendingConfirmations: "Pending Confirmations",
      pendingConfirmationsHelp: "Other members have declared you as their parent. Confirm if this is correct, or dispute it if it isn't.",
      noPendingConfirmations: "You have no pending confirmations.",
      householdPendingClaim: "{{name}} has declared you as their parent",
      householdConfirmSuccessful: "Confirmed",
      householdDisputeSuccessful: "Disputed",
      householdActionFailed: "Failed to record your response",
      dispute: "Dispute",
      // R5/FR-5.1-5.10: marriage impediment resubmit-with-reasons flow (ImpedimentPanel.js)
      proceedWithReasons: "Proceed with reasons",
      impedimentAbsoluteTitle: "This marriage cannot be recorded",
      impedimentAbsoluteBody: "The following do not accept an override, under any circumstances:",
      impedimentInsufficientTitle: "Could not be verified",
      impedimentFoundTitle: "Impediment found",
      impedimentReviewTitle: "Requires clergy review",
      impedimentSpouse1EdgesKnown: "Spouse 1 - family tree edges on file",
      impedimentSpouse2EdgesKnown: "Spouse 2 - family tree edges on file",
      impedimentOverrideReasonLabel: "Reason for proceeding despite this",
      "impedimentType.ACTIVE_MARRIAGE": "Active marriage on record",
      "impedimentType.UNDER_CIVIL_AGE": "Under civil age of marriage",
      "impedimentType.SAME_SEX": "Same sex",
      "impedimentType.CONSANGUINITY": "Consanguinity",
      "impedimentType.SPIRITUAL_KINSHIP": "Spiritual kinship",
      "impedimentType.UNCONFIRMED_DECLARED_LINK": "Unconfirmed declared relationship relied on",
      "impedimentType.CATECHUMEN": "Baptismal status unconfirmed",
      "impedimentType.PRIOR_MARRIAGE_NOT_PROPERLY_CLOSED": "Prior marriage not properly closed",
      "impedimentType.MISSING_DATA": "Missing data",
      // Consent step A3 - the public consent page (ConsentAnswer.js)
      consentPageGuardianTitle: "{{child}} in {{className}}",
      consentPageMembershipTitle: "{{child}}'s membership record",
      consentPageChangedMeanwhile: "This request changed while you were answering, so this time your answers weren't taken. The page now shows where it stands.",
      consentPageOffline: "We couldn't reach the parish's system. Check your internet connection and try again.",
      consentPageTooManyTries: "Too many tries in a short time. Wait a few minutes and try again.",
      consentPageServerTrouble: "The parish's system answered, but couldn't do this just now. Try again in a little while. If it keeps happening, contact the parish.",
      consentPageNotShownTitle: "This page couldn't be shown just now",
      consentPageRefusedTitle: "This page can't be shown",
      consentPageRefused: "The parish's system didn't accept this link. Contact the parish.",
      consentPageUnknownStateTitle: "This request can't be shown here",
      consentPageRejected: "Your answers could not be saved. Please open the link from your email again and start over. If this keeps happening, contact the parish.",
      consentPageNoTokenTitle: "This link is incomplete",
      consentPageNoTokenBody: "Part of the link is missing. Open the email from the parish and tap the link again. If you copied the link, make sure you copied all of it.",
      consentPageNormalBrowser: "If it still doesn't work, open the link in your normal web browser, such as Safari or Chrome.",
      consentPageLoading: "One moment",
      consentPageLoadingBody: "Opening your request…",
      consentPageUnreachableTitle: "We can't reach the parish's system",
      consentPageTryAgain: "Try again",
      consentPageInvalidTitle: "This link doesn't work",
      consentPageInvalidBody: "It may have been copied only in part, or it isn't a link from the parish. Open the email from the parish and tap the link again. If it still doesn't work, contact the parish.",
      consentPageClosedTitle: "This request is closed",
      consentPageClosedBody: "{{parish}} has withdrawn this request. Nothing more is needed from you.",
      consentPageSupersededTitle: "A newer link has been sent",
      consentPageSupersededBody: "{{parish}} has sent you a newer email about this. Please use the link in the most recent email.",
      consentPageSentElsewhereTitle: "This link is no longer active",
      consentPageSentElsewhereBody: "This request has been sent to another guardian. If you need to answer it yourself, please contact the parish.",
      // Consent step A4 - the withdrawal page (ConsentWithdraw.js)
      consentWithdrawTitle: "Withdraw your consent?",
      consentWithdrawGivenOn: "Given on",
      consentWithdrawWhatHappensGuardian: "If you withdraw it, {{child}} won't take part in {{className}} on this consent from now on. Attendance already recorded stays as it is.",
      consentWithdrawWhatHappensMembership: "If you withdraw it, the parish no longer has this consent for {{child}}'s membership record from now on. What was recorded before stays as it is.",
      consentWithdrawTold: "{{child}}'s recorded guardians and the parish will be told that it was withdrawn.",
      consentWithdrawConfirm: "Withdraw my consent",
      consentWithdrawKeep: "Keep my consent",
      consentWithdrawWithdrawing: "Withdrawing…",
      consentWithdrawFailed: "The withdrawal could not be saved. Check your internet connection and try again. If it keeps failing, contact the parish.",
      consentWithdrawFailedServer: "The withdrawal could not be saved just now. Try again in a little while. If it keeps failing, contact the parish.",
      consentWithdrawRefused: "The parish's system didn't accept the withdrawal. Contact the parish to withdraw your consent.",
      // Finding 38 - the member page's consents, and recording a withdrawal (WithdrawalForm.js)
      consentsTitle: "Consents",
      consentsNone: "No consent is on record for this member.",
      consentsLoadFailed: "The consents could not be loaded.",
      consentPurposeMembership: "Membership",
      consentPurposeActivity: "Activity: {{activity}}",
      consentState_STANDING: "standing",
      consentState_WITHDRAWN: "withdrawn",
      consentState_SUPERSEDED: "replaced by a newer consent",
      consentGivenOn: "Given {{date}}",
      consentGivenOnNotRecorded: "The day it was given is not recorded",
      consentGivenByMember: "by the member themselves",
      consentGivenByGuardian: "by {{name}}, a guardian",
      consentGivenByGuardianUnnamed: "by a guardian",
      consentAnsweredAs: "Answered on the consent page by someone confirming they are {{name}}.",
      consentGivenByNotRecorded: "who gave it is not recorded",
      consentRecordedOn: "recorded {{date}}",
      consentRowReligiousBody: "The parish's own basis, GDPR Art. 9(2)(d), recorded {{date}} - not a consent: nobody gave it, so there is nothing to withdraw.",
      consentRecordWithdrawal: "Record a withdrawal",
      withdrawalFormTitle: "Record a withdrawal",
      withdrawalNoEvidenceNeeded: "Record what you have. Nothing more is needed - no scan, no signature, no second confirmation: your word is the record, and what you attest says how you know.",
      withdrawalNowTheChildsOwn: "This consent is now the child's own - they have turned 12 - so by link only they may withdraw it. A parent's report is still recorded here, naming the parent.",
      withdrawalHowQuestion: "How did it reach you?",
      withdrawalChannelOption_IN_PERSON: "In person",
      withdrawalChannelOption_PHONE: "By phone",
      withdrawalChannelOption_LETTER: "By letter",
      withdrawalChannelOption_EMAIL: "By e-mail to the parish",
      withdrawalWhoQuestion: "Who withdrew it?",
      withdrawalGiverOption_THE_PERSON: "{{name}} themselves",
      withdrawalGiverOption_GUARDIAN_IN_REGISTER: "A guardian in the register",
      withdrawalGiverOption_NAMED_PERSON: "Someone not in the register",
      withdrawalGiverOption_NOT_KNOWN: "Not known",
      withdrawalChooseGuardian: "Choose the guardian",
      withdrawalNoGuardianOnRecord: "No guardian is on record for them - choose Someone not in the register and give the name.",
      withdrawalGiverName: "Their name",
      withdrawalGiverRelationship: "Relationship (if given)",
      withdrawalDayQuestion: "The day they withdrew",
      withdrawalDayNotKnown: "Not known",
      withdrawalDayHint: "Leave it empty if nobody said.",
      withdrawalAttestQuestion: "What can you say?",
      withdrawalAttestationOption_HEARD_IT_FROM_THEM: "I heard it from them myself, in person or on the phone",
      withdrawalAttestationOption_HOLDS_THEIR_WRITING: "I have their letter or e-mail",
      withdrawalAttestationOption_PASSED_ON_TO_ME: "It was passed on to me - I did not hear it from them myself",
      withdrawalWhoWillBeTold: "Who will be told:",
      withdrawalToldGuardianOutside: "a guardian outside the register, by e-mail",
      withdrawalToldStaff: "the parish's administrators ({{count}})",
      withdrawalToldGeneral: "The guardians on record and the parish's administrators will be told.",
      withdrawalDoesNothingToMembership: "Recording this withdrawal does not end the membership itself.",
      withdrawalSubmit: "Record the withdrawal",
      withdrawalFailed: "The withdrawal could not be recorded.",
      withdrawalLine: "Withdrawn {{how}} - {{who}} - {{day}}",
      withdrawalChannel_IN_PERSON: "in person",
      withdrawalChannel_PHONE: "by phone",
      withdrawalChannel_LETTER: "by letter",
      withdrawalChannel_EMAIL: "by e-mail to the parish",
      withdrawalChannel_LINK: "by the withdrawal link",
      withdrawalChannel_NOT_RECORDED: "(how it arrived was not recorded)",
      withdrawalGiver_THE_PERSON: "{{name}} themselves",
      withdrawalGiver_GUARDIAN_IN_REGISTER: "{{name}}, a guardian",
      withdrawalGiver_NAMED_PERSON: "{{name}}, not in the register",
      withdrawalGiver_NOT_KNOWN: "who withdrew it is not known",
      withdrawalGiver_LINK_HOLDER: "whoever held the link",
      withdrawalGiver_NOT_RECORDED: "who withdrew it was not recorded",
      withdrawalDay: "the day they withdrew: {{date}}",
      withdrawalDayStatedUnknown: "the day is not known",
      withdrawalDayNotRecorded: "the day was not recorded",
      withdrawalAttestation_HEARD_IT_FROM_THEM: "heard from them first-hand",
      withdrawalAttestation_HOLDS_THEIR_WRITING: "their letter or e-mail is held",
      withdrawalAttestation_PASSED_ON_TO_ME: "passed on second-hand",
      withdrawalRecordedBy: "recorded {{date}} by {{name}}",
      withdrawalRecordedOn: "recorded {{date}}",
      withdrawalBackfilled: "Recorded before withdrawals had a record of their own: what was known then, and nothing more.",
      // The profile page until it loads a real profile (owner, 15 Oct 2026)
      consentWithdrawKeptTitle: "Nothing has changed",
      consentWithdrawKeptBody: "Your consent stands. You can close this page. The link in your email still works if you change your mind.",
      consentWithdrawDoneTitle: "Your consent is withdrawn",
      consentWithdrawDoneBody: "{{child}}'s recorded guardians and the parish have been told. If this was a mistake, contact the parish: consent can be given again.",
      consentWithdrawAlreadyTitle: "This consent is already withdrawn",
      consentWithdrawAlreadyBody: "Nothing more is needed. If you have questions, contact the parish.",
      consentWithdrawNowOwnTitle: "This is now {{child}}'s own consent",
      consentWithdrawNowOwnBody: "{{child}} is 12 or over, so their membership consent is theirs to give or withdraw, not a parent's. If they want to withdraw it, they can contact the parish.",
      consentPageNewLinkSentTitle: "A new link is on its way",
      consentPageNewLinkSentBody: "We've sent a new link to the email address the parish has for you. It can take a few minutes. Check your spam folder too.",
      consentPageExpiredTitle: "This link has expired",
      consentPageExpiredCanRenew: "Links stop working after a while, for your safety. We can send a new one to the email address the parish has for you.",
      consentPageSending: "Sending…",
      consentPageSendNewLink: "Send me a new link",
      consentPageNewLinkRecentlySent: "A new link was sent a few minutes ago. Check your email, including the spam folder. If nothing arrives, contact the parish.",
      consentPageExpiredContactParish: "Please contact {{parish}} to get a new link.",
      consentPageThanksTitle: "Thank you. Your answers are saved",
      consentPageAlreadyAnsweredTitle: "You have already answered",
      consentPageNothingToAnswerTitle: "There is nothing to answer right now",
      consentPageIntroTitle: "A question about {{child}}",
      consentPageIntroAsking: "{{parish}} is asking for your answer about {{child}}.",
      consentPageIntroOne: "There is one question.",
      consentPageIntroMany: "There are {{count}} questions. You answer each one on its own: you can say yes to one and no to another.",
      consentPageIntroNothingChosen: "Nothing is chosen for you. Before anything is sent, you will see all your answers and can change them.",
      consentPageBegin: "Start",
      consentPageQuestionOf: "Question {{number}} of {{count}}",
      consentPageTextVersion: "Text version {{version}}",
      consentPageDoYouAgree: "Do you agree?",
      consentPageYes: "Yes, I agree",
      consentPageNo: "No, I do not agree",
      consentPageChooseFirst: "Please choose yes or no before you continue.",
      consentPageContinue: "Continue",
      consentPageBack: "Back",
      consentPageReviewTitle: "Check your answers",
      consentPageChange: "Change this answer",
      consentPageWithdrawLater: "If you say yes, you can withdraw it later. The receipt tells you how.",
      consentPageIAm: "I am {{name}}",
      consentPageIAmHelp: "This link is for {{name}}. If you are not {{name}}, don't answer - let the parish know it reached you.",
      consentPageIAmFirst: "Tick that you are {{name}} before sending - only they may answer.",
      consentPageSendAnswers: "Send my answers",
      consentPageNowOwnConsent: "{{child}} is now old enough to answer this themselves, so it is no longer asked of you.",
      consentPageReworded: "The parish has changed the wording of this. You will get a new request for it.",
      consentPageInPerson: "This can no longer be answered by link: the parish takes it from you in person or on paper.",
      consentPageNotAnswerableOther: "This can't be answered here. Please ask the parish about it.",
      consentPageDateBoth: "{{gregorian}} (Ethiopian calendar: {{ethiopian}})",
      consentPageThanksBody: "This is your receipt. A copy has also been sent to your email.",
      consentPageAnsweredOn: "Answered on",
      consentPageDocRequest: "Request for consent",
      consentPageRequestedOn: "Requested on",
      consentPageDocReceipt: "Receipt of your answers",
      consentPageCopyProduced: "This copy produced",
      consentPageWithdrawOnPaper: "To withdraw this consent, contact the parish at {{contact}}, or use the link in the email this receipt came from.",
      consentPageWithdrawOnPaperNoContact: "To withdraw this consent, contact the parish, or use the link in the email this receipt came from.",
      consentPageOutcomeWithdrawn: "You agreed, and later withdrew it",
      consentPageOutcomeGranted: "You agreed",
      consentPageOutcomeRefused: "You did not agree",
      consentPageOutcomeNotAnswered: "Not answered",
      consentPageWithdrawnOn: "Withdrawn on",
      consentPageTextYouAgreedTo: "The text you agreed to:",
      consentPageWithdrawLink: "Withdraw this consent",
      consentPageKeepThis: "Keep this receipt. It shows exactly what you agreed to and when.",
      consentPagePrint: "Print or save this receipt",
      // Shown on every page before any page-specific text: the loader, the error boundary, <noscript> (scripts/noscript.js)
      loadingApplication: "Loading…",
      errorBoundaryTitle: "Something went wrong",
      errorBoundaryBody: "An unexpected error occurred. Try again, or reload the page.",
      errorBoundaryTryAgain: "Try again",
      errorBoundaryReload: "Reload the page",
      noscriptOpenInBrowser: "This page can't open here. Please open the link in your normal web browser, such as Safari or Chrome.",
      // Consent step A3c - the chase list (ConsentChase.js)
      chaseTitle: "Consent status",
      chaseLoadFailed: "The consent status could not be loaded.",
      chaseSendFailed: "Nothing was sent. Try again.",
      chaseGrantFailed: "That change could not be saved.",
      chaseCurrentText: "A new link shows guardian consent text {{version}}.",
      chaseAdultsNotListed: "Adults enrolled, not listed (no guardian consent asked): {{count}}.",
      chaseSummary: "Summary",
      chaseBulkSend: "Prepare links for everyone not asked, not answering or reworded ({{count}})",
      chaseBulkHelp: "Nothing is sent until you have seen who each link goes to. Refusals and withdrawals are never chased.",
      chaseChildren: "Children",
      chaseNoChildren: "No children are enrolled.",
      chaseSentTo: "Sent to {{name}}",
      chaseBy: "By {{name}}",
      chasePreviousClosed: "An earlier request was closed without an answer.",
      chaseAlsoAsked: "Also asked in the same request: {{purpose}} -",
      chaseStatusNotAsked: "Not asked",
      chaseStatusAsked: "Asked, link still valid",
      chaseStatusNoResponse: "No response, link expired",
      chaseStatusReworded: "Wording changed, needs a new link",
      chaseStatusGranted: "Consent given",
      chaseStatusInPerson: "Consent recorded in person",
      chaseStatusRefused: "Refused",
      chaseStatusWithdrawn: "Withdrawn",
      chaseStatusNowOwnConsent: "Now the child's own to answer",
      chaseActionSend: "Send a link",
      chaseActionResend: "Send the link again",
      chaseActionReplace: "Send a link with the new wording",
      chaseChoiceStaffEntered: "named by staff",
      chaseChoiceOnlyParent: "the only recorded parent who can be reached",
      chaseChoiceLanguageHasText: "the only parent whose language the text is published in",
      chaseChoiceFirstAlphabetically: "first by name - more than one parent can be reached",
      chaseChoiceChangedInPreview: "chosen in the preview instead",
      chaseCaptureLink: "via the consent link",
      chaseCapturePaper: "on paper",
      chaseCaptureInPerson: "in person",
      chaseCaptureSelfRegistration: "on the registration form",
      chaseCaptureImport: "from the old register",
      chasePurposeMembership: "membership",
      chasePurposeGuardian: "guardian consent",
      chaseWhoCanChase: "Who can chase this consent",
      chaseNoHolders: "Only staff.",
      chaseScope: "For",
      chaseScopeActivity: "this activity",
      chaseScopeChurch: "every activity of the church",
      chaseGrantedOn: "since {{date}}, by {{name}}",
      chaseGrant: "Let them chase",
      chaseRevoke: "Remove",
      chaseGrantHelp: "They see each child's consent status and can send links to parents already in the register. They never see email addresses, and can't add a recipient or cancel a request.",
      chaseBackToActivity: "Back to the activity",
      chasePreviewTitle: "Check before sending",
      chasePreviewHelp: "Each link goes to one parent, never to both. Check who, and change it if needed.",
      chaseSendTo: "Send to",
      chaseNotReachable: "{{name}} can't be reached by link: {{reason}}",
      chaseUnreachableNoEmail: "no email address recorded",
      chaseUnreachableNoLanguage: "no language recorded",
      chaseUnreachableNoText: "the consent text isn't published in their language",
      chaseUnreachableAddressNotProven: "no proof is recorded that the address is theirs",
      chaseCantSend: "Can't be sent ({{count}})",
      chaseProblemNoParent: "no parent is recorded",
      chaseProblemNoReachableParent: "no recorded parent can be reached by link",
      chaseProblemRecipientNotReachable: "the parent it was sent to can't be reached by link - to ask someone else, staff cancel the request on the child's record",
      chaseProblemNoText: "No guardian consent text is published.",
      chaseProblemNothingToSend: "nothing to send",
      chaseConfirmSend: "Send ({{count}})",
      chaseResultsTitle: "What was sent",
      chaseOutcomeSent: "link sent",
      chaseOutcomeResent: "link sent again",
      chaseOutcomeReplaced: "new link with the new wording sent",
      chaseOutcomeSkipped: "not sent",
      chaseYourLists: "Consent you can chase",
      chaseYourListsHelp: "The parish has asked you to follow up guardian consent for these activities.",
      dataModeSyntheticBanner: "Test data only - no real person's data may be entered here.",
      // The church's data-protection contact (AddChurch.js) and the public pages' contact line
      churchDpContactTitle: "Data-protection contact",
      churchDpContactHelp: "Who people contact to withdraw consent or ask about their personal data. Consent texts show the email (and phone) where they say [contact]; each text names the role in its own words. Role and email are needed together. Left empty, [contact] shows the church's phone number instead.",
      churchDpContactRole: "Role",
      churchDpContactRoleExample: "e.g. Data protection contact",
      churchDpContactEmail: "Email",
      churchDpContactPhone: "Phone (optional)",
      consentPageParishContact: "Parish contact: {{contact}}",
      consentPageClassNowCalled: "This class is now called {{name}}.",
      // M12 screen batch, item 1: refused pages (LoadProblem.js) and role help (RegisterUser.js)
      activityRefusedTitle: "This class isn't one of yours",
      activityRefusedBody: "You can open the classes you lead, and a session you took. If you should lead this class, ask the parish office to assign you to it.",
      activityNotFoundTitle: "This class or session doesn't exist",
      activityNotFoundBody: "It may have been removed, or the link is incomplete.",
      activityTryAgain: "Try again",
      activityServerSaid: "Reason given: {{message}}",
      loadFailedTitle: "This couldn't be loaded",
      loadRefusedTitle: "You don't have access to this",
      loadNotFoundTitle: "This doesn't exist, or no longer does",
      loadServerSaid: "Reason given: {{message}}",
      loadTryAgain: "Try again",
      loadBack: "Back",
      addMemberManually: "Add a member",
      addressInformation: "Address",
      ageUnknown: "age unknown",
      agendaLocked: "Agenda is locked - minutes submitted",
      approvedBy: "Approved by",
      back: "Back",
      backToMembers: "Back to members",
      church: "Church",
      confirmDeleteChurchService: "Delete this church service?",
      contactAndProfessional: "Contact and work",
      createdDate: "Created",
      decision: "Decision",
      description: "Description",
      displayName: "Display name",
      errorAddingAttendee: "Error adding attendee",
      errorApprovingMinutes: "Error approving minutes",
      errorReopeningMinutes: "Error reopening minutes",
      errorSavingAgendaItem: "Error saving agenda item",
      errorSubmittingMinutes: "Error submitting minutes",
      errorUpdatingAttendee: "Error updating attendee",
      failedToFetchMember: "Couldn't load this member",
      groupTransferPartial: "{{transferred}} transferred, {{excluded}} could not be transferred:",
      groupTransferSuccessful: "{{count}} members transferred successfully",
      invalidMemberId: "Invalid member ID",
      loadingMemberData: "Loading the member…",
      meetingDetails: "Meeting",
      meetingRequiredFields: "Title, date and church are required",
      meetingUpdated: "Meeting updated",
      memberNotFound: "Member not found",
      memberNotFoundMessage: "This member doesn't exist, or no longer does.",
      minutesApproved: "Minutes approved",
      minutesReopened: "Minutes reopened",
      minutesReopenedNote: "Reopened for editing - the text below is the last submitted version until resubmission",
      minutesSubmitted: "Minutes submitted for approval",
      name: "Name",
      newAgendaItem: "New agenda item",
      noFamilyOnRecord: "No family relationships on record for this member - family links are only captured from baptism registrations, so this may reflect missing data rather than the member having no family. You can still add people below.",
      noMinutesYet: "No minutes submitted yet",
      notes: "Notes",
      personalInformation: "Personal details",
      quorum: "Quorum",
      quorumRequired: "Quorum required",
      reasonForReopening: "Reason for reopening",
      reopen: "Reopen",
      transferRoleChild: "Child",
      transferRoleManual: "Added",
      transferRolePrimary: "Primary",
      transferRoleSpouse: "Spouse",
      updatedDate: "Updated",
      whoIsMoving: "Who is moving",
      formFieldUnnamed: "another field",
      featuresLoadFailedTitle: "Couldn't check which parts of the system are switched on",
      featuresLoadFailedBody: "This page belongs to a part of the system that may be switched off here, and the check failed.",
      featuresLoadFailedMenu: "Some menu entries may be missing until this works.",
      identityLoadFailedTitle: "Couldn't check who is signed in",
      identityLoadFailedBody: "This is a failure, not a change to your access: whatever depends on your role - choosing a church, for one - is missing from the screens until this works.",
      moduleOffTitle: "Not switched on here",
      moduleOffBody: "Nothing has gone wrong: this part of the system is switched off at this parish for now. Ask the parish office if you expected to find it.",
      moduleOffToSignIn: "Go to sign in",
      moduleOffApplications: "Service applications are not in use here yet.",
      moduleOffBookings: "Bookings, clergy availability and the service catalogue are not in use here yet.",
      moduleOffActivities: "Activities and attendance are not in use here yet.",
      moduleOffRegisters: "The baptism, marriage and fithat registers and certificates are not in use here yet.",
      moduleOffMeetings: "Council meetings and action items are not in use here yet.",
      // The public page (public pages, session 6)
      moduleOffPublicPages: "The church's public page is not in use here yet.",
      // /register while self-registration is closed (the owner's answer 3 of 3 Nov 2026)
      registrationClosedTitle: "Registration isn't open yet",
      registrationClosedContact: "To ask about becoming a member, contact the church:",
      registrationClosedContactFailed: "The church's contact couldn't be loaded",
      // The public pages' staff screens: a super-administrator's choice of church (session 8)
      publicContentChurch: "Church",
      publicContentChurchChoose: "Choose a church",
      publicContentChurchesFailed: "The churches to choose from couldn't be loaded",
      publicContentFieldPublic: "Public: anyone can read this on the church's page. Name no person here - no teacher, priest, member or staff member.",
      publicParishCheckFailedTitle: "Couldn't check whether this page is switched on",
      publicParishCheckFailedBody: "The page may well be there; the check failed.",
      publicParishNoSuchPage: "There is no church page at this address",
      publicParishChurchFailedBody: "The church's page couldn't be loaded.",
      publicParishOpenMenu: "Open the menu",
      publicParishCloseMenu: "Close the menu",
      publicParishNavLabel: "The page's sections",
      publicParishFooterNav: "The parish",
      publicParishLanguage: "Language",
      publicParishNavCalendar: "Calendar",
      publicParishNavServices: "Services",
      publicParishNavEducation: "Education",
      publicParishNavVisit: "Visit us",
      publicParishWelcome: "Welcome to {{name}}",
      publicParishSeeCalendar: "This month's calendar",
      publicParishPlanVisit: "Plan your visit",
      publicParishComingNext: "Coming next",
      publicParishComingUp: "Coming up",
      publicParishComingFailed: "What comes next couldn't be loaded.",
      publicParishNothingComing: "Nothing is published for the coming weeks.",
      publicParishNextService: "Next service",
      publicParishNextFeast: "Next feast",
      publicParishNextFast: "Fast",
      publicParishFastBegins: "begins {{date}}",
      publicParishCalendarTitle: "Calendar",
      publicParishCalendarFailed: "This month's calendar couldn't be loaded",
      publicParishCalendarKind: "Calendar",
      publicParishEthiopian: "Ethiopian",
      publicParishGregorian: "Gregorian",
      publicParishPreviousMonth: "Previous month",
      publicParishNextMonth: "Next month",
      publicParishEthiopianSpan: "{{from}} – {{to}} {{year}} · the small date is the Gregorian",
      publicParishGregorianSpan: "{{months}} · the small date is the Ethiopian",
      publicParishKindService: "Service",
      publicParishKindFeast: "Feast",
      publicParishKindFast: "Fast",
      publicParishKindClasses: "Classes",
      publicParishKindNoClasses: "No classes",
      publicParishNoClassesChip: "No classes",
      publicParishCancelled: "Cancelled",
      publicParishNothingThisMonth: "Nothing is published for this month.",
      publicParishServicesTitle: "Regular services",
      publicParishServicesFailed: "The regular services couldn't be loaded.",
      publicParishNoServices: "No regular services are published.",
      publicParishEveryWeekday: "Every {{weekday}}",
      publicParishEveryEthiopianMonth: "Day {{day}} of every Ethiopian month",
      publicParishNextOn: "Next: {{date}}",
      publicParishByAppointment: "By appointment",
      publicParishEducationFailed: "The classes couldn't be loaded.",
      publicParishNoEducation: "No classes are published.",
      publicParishNoClasses: "No classes are published for this department.",
      publicParishNoClassesOn: "No classes on {{date}}",
      publicParishThisTerm: "This term",
      publicParishTerm: "Term",
      publicParishBreaks: "No classes on",
      publicParishNoBreaks: "No days without classes are published.",
      publicParishQuestions: "Questions?",
      publicParishAges: "Ages {{from}}–{{to}}",
      publicParishAgesFrom: "From age {{from}}",
      publicParishStarts: "Starts {{date}}",
      publicParishNextClass: "Next class: {{date}}",
      publicParishFree: "Free of charge",
      publicParishVisitTitle: "Visit us",
      publicParishAddress: "Address",
      publicParishOpenMap: "Open the map",
      publicParishGettingHere: "Getting here",
      publicParishContact: "Contact",
      publicParishFirstVisit: "First visit?",
      publicParishOrgNumber: "Org. no. {{number}}",
      ownAccountRolesLocked: "Your own roles and status are changed by another administrator, never by you.",
      roleWarningSharedAddress_one: "One other member shares this account's e-mail address. Whoever reads that inbox receives this account's sign-in links.",
      roleWarningSharedAddress_other: "{{count}} other members share this account's e-mail address. Whoever reads that inbox receives this account's sign-in links.",
      roleWarningSharedAddressUnknown: "Couldn't check whether other members share this account's e-mail address - whoever reads that inbox receives this account's sign-in links.",
      nameHasTitle: "A title is not part of a name - write the name without it.",
      roleHeldNotGrantable: "Held by this account - not yours to give or take away.",
      accountRefusedSelfChange: "No account changes its own roles or status - another administrator does.",
      accountRefusedHoldsMore: "This account holds more than yours, so you can't change it.",
      accountRefusedOutsideChurch: "This account belongs to another church.",
      transferRefusedFromAnotherChurch: "This member belongs to another church: a transfer is started by the member's own church, or by a super-administrator.",
      accountRefusedRolesOffFirst: "This login administers its church. Take its administrator roles off first; then the member can move.",
      memberWriteOwnChurchOnly: "Members are entered and changed by an administrator of their own church.",
      deathLoginNotTheCallersToEnd: "Recording this death ends the person's login, which isn't yours to end: a super-administrator ends the login first, then the death is recorded here.",
      voidLoginNotTheCallersToPutBack: "Voiding this fithat puts back the person's login, which isn't yours to change - a super-administrator voids it.",
      correctionLoginNotTheCallersToPutBack: "Correcting this fithat puts back the login of the person it named before, which isn't yours to change - a super-administrator corrects it.",
      voidStatusNotTheCallersToPutBack: "Voiding this fithat puts back the status of a member of another church, which isn't yours to change - a super-administrator voids it.",
      correctionStatusNotTheCallersToPutBack: "Correcting this fithat puts back the status of the person it named before, a member of another church, which isn't yours to change - a super-administrator corrects it.",
      accountRefusedGrantAboveCaller: "You can't give a role that stands above your own.",
      accountRefusedAdministersNone: "Your account doesn't administer accounts.",
      accountRefusedAddressHasLogin: "This member's e-mail address is already another login's. A family can share an address, but only one login can use it - give this member an address of its own on the member record.",
      accountRefusedMemberHasLogin: "This member already has a login, and a member has only one: use that one rather than opening another.",
      changePassword: "Change password",
      currentPassword: "Current password",
      newPassword: "New password",
      repeatNewPassword: "New password again",
      passwordRuleHint: "At least 12 characters, at most 72 bytes (72 letters A to Z, fewer of most others), and not a common password.",
      passwordTooShort: "A password has at least 12 characters.",
      passwordTooLong: "A password has at most 72 bytes: 72 letters A to Z, fewer of most others.",
      passwordCommon: "This password is on a list of common passwords: choose another.",
      ownPasswordWrong: "The current password is not right.",
      tooManyAttempts: "Too many wrong passwords: try again within 15 minutes.",
      signInRefused: "The user name or password is wrong, or there have been too many attempts: try again within 15 minutes.",
      ownPasswordChanged: "Your password is changed. Every other session has ended.",
      ownPasswordFailed: "The password could not be changed.",
      signOutEverywhere: "Sign out everywhere",
      signOutEverywhereHelp: "Ends every session of this account, on every device, this one too.",
      signOutEverywhereFailed: "Signing out everywhere did not work.",
      accountRefusedMemberLeftRegister: "This member has left the register - removed, or merged into another record - so no login on it is made active. Open one on the record that stays.",
      accountRefusedMemberDeceased: "This member's death is recorded, so no login on the record is made active. If it was recorded in error, void the fithat - that gives back the login it ended - or correct the status on the member record.",
      mergeRefusedAddressOfALogin: "The record that stays has a login, and a merge doesn't change its e-mail address. Keep its own address, or have its church change the address on the record.",
      accountRefusedMemberHasNoAddress: "This member has no e-mail address, and a login's links go to its member's address. Add one on the member record first.",
      accountRefusedPendingByReviewOnly: "A registration waiting for review leaves pending only by approval or rejection, on the registrations screen.",
      loginAddressIsMembers: "A login's address is its member's: change it on the member record.",
      memberHasNoAddress: "None on the member record",
      useRegistrationAddress: "Put the address given at registration on the member record (the login's links go there)",
      mergeRefusedDeathAndActiveLogin: "The record being merged away has a death recorded, and the record that stays has an active login. Settle it first: if the death was recorded in error, void the fithat or correct the status; otherwise end the login.",
      mergeRefusedChaseGrantInForce_one: "The record being merged away may still chase consent under {{count}} grant, and a merge never moves one. Remove it first, under \"Who can chase this consent\"; if it should go on, give it to the record that stays. Then merge.",
      mergeRefusedChaseGrantInForce_other: "The record being merged away may still chase consent under {{count}} grants, and a merge never moves one. Remove them first, under \"Who can chase this consent\"; if they should go on, give them to the record that stays. Then merge.",
      mergeRefusedActionItemOtherChurch_one: "The record being merged away is assigned {{count}} action item from another church's meeting, and a merge never gives a church's task to another church's record. Have that church reassign it first, then merge.",
      mergeRefusedActionItemOtherChurch_other: "The record being merged away is assigned {{count}} action items from another church's meetings, and a merge never gives a church's task to another church's record. Have that church reassign them first, then merge.",
      mergeRefusedDeclarationBetweenThem_one: "These two records are linked by {{count}} parent's or guardian's declaration: one is recorded as declaring for the other. They can't be the same person, so they are not merged.",
      mergeRefusedDeclarationBetweenThem_other: "These two records are linked by {{count}} parent's or guardian's declarations: one is recorded as declaring for the other. They can't be the same person, so they are not merged.",
      accountRefusedOwnPasswordOnForm: "Your own password is changed on Change password, in the menu at the top, which asks for the current one - not on this form.",
      ownPasswordElsewhere: "Your own password is changed on its own page, which asks for the current one:",
      // Account package step 5: nobody types a password for anyone - a link to the member's address sets it.
      passwordByLinkOnOpening: "No password is typed here: once the account is opened, a link to choose one goes to the member's address. It works once, for 72 hours.",
      passwordStateUnknown: "Whether this account has a password could not be read.",
      accountHasPassword: "This account has a password.",
      accountHasNoPassword: "This account has no password yet: nobody can sign in with it until its link is used.",
      passwordLinkLastSent: "A link that still works was made {{sent}}, as its e-mail went out, and works until {{until}}.",
      passwordLinkClearsPassword: "Sending a link clears the current password at once: it stops working, and every session of the account ends.",
      passwordLinkEndsEarlierOnes: "Sending a new link ends any earlier one.",
      sendPasswordLink: "Send a set-password link",
      passwordLinkQueued: "The link is on its way to the member's address.",
      passwordLinkNotSent: "The link could not be sent.",
      accountRefusedPasswordByLinkOnly: "Nobody types a password for anyone: send a set-password link instead.",
      accountRefusedPasswordLinkNotActive: "A set-password link goes only to an active account.",
      setPasswordTitle: "Choose your password",
      setPasswordChecking: "Checking the link…",
      setPasswordFor: "For the login {{username}}. This link works once, until {{until}}.",
      setPasswordNew: "New password",
      setPasswordRepeat: "The same password again",
      setPasswordRuleHint: "At least 12 characters. A long phrase you will remember is good.",
      setPasswordMismatch: "The two passwords are not the same.",
      setPasswordSave: "Set my password",
      setPasswordDone: "Your password is set. Sign in with your username and the password you chose.",
      setPasswordGoToSignIn: "Go to sign in",
      setPasswordLinkUsed: "This link has already been used.",
      setPasswordLinkExpired: "This link has expired: it worked for 72 hours.",
      setPasswordLinkEnded: "This link no longer works: a newer link was sent, or the login was changed.",
      setPasswordLinkUnknown: "This link does not work. Check that the whole link was copied from the e-mail.",
      setPasswordAskOffice: "Ask the parish office for a new link.",
      setPasswordTryAgain: "Try again",
      moduleHeldBackTitle: "Not on these screens yet",
      moduleHeldBackBody: "This part of the system is switched on, but some of its screens don't yet say when they fail to load, and a screen that fails without saying so can mislead. They stay hidden until they do. Ask the parish office if you expected to find it.",
      modulesHeldBack: "Held back on these screens although the server has them switched on: some of their screens don't yet say when they fail to load. The deploy refuses that, so they were switched on another way:",
      enrolmentsRefusedTitle: "You can't see this class's enrolments",
      enrolmentsLoadFailed: "The enrolments couldn't be loaded",
      nonSessionDatesRefusedTitle: "You can't see this church's days without sessions",
      nonSessionDatesLoadFailed: "The days without sessions couldn't be loaded",
      activityBackToList: "Back to Activities",
      roleHelpIntro: "A class leader or volunteer needs only USER. Leading a class comes from being assigned to it on the activity's page, not from a role. A role that sounds right, such as ROLE_SUNDAY_SCHOOL, opens every member of that service.",
      roleHelpSuperAdmin: "Everything, in every church.",
      roleHelpAdmin: "Everything in this church, including members, activities and settings.",
      roleHelpUser: "Their own record, plus the classes they are assigned to lead. Enough for a class leader.",
      roleHelpServiceRole: "Every member of this service in the church. Not needed to lead a class.",
      roleHelpPriesthood: "A priest's own confession children, and issuing certificates. Not needed to lead a class.",
      // FR-12.7b - the printable blank marking sheet (PrintSheetButton.js)
      activityPrintSheet: "Print blank sheet",
      activityPrintSheetHelp: "A paper sheet for this session, for when there is no working phone. Enter it here afterwards, then destroy the paper.",
      activityPrintSheetFailed: "The sheet could not be produced.",
      activityLoading: "Loading…",
      closeMenu: "Close menu",
      // Consent step B1 - an interpreter on an in-person consent (InterpreterField.js)
      consentInterpreterUsed: "An interpreter informed them",
      consentGivenInPersonLabel: "I confirm the person is here with me now and has been informed of this text",
      consentNotInPersonHint: "Not here with you now? Don't record it here - send a consent request by link or on paper. A consent given on an earlier day goes on paper.",
      consentGuardianGivenInPersonLabel: "I confirm the guardian is here with me now and has been informed of this text",
      consentInterpreterLanguage: "Interpreter's language",
      consentInterpreterLanguageExample: "e.g. Tigrinya",
      consentViaInterpreter: "through an interpreter ({{language}})",
      // M12 item 3c - the refusals a recorder can meet (recorderError.js)
      recorderErrorAlreadyRecorded: "Some of these children were already recorded, perhaps from another phone. The sheet now shows what was saved. Check it, and correct a row if it's wrong.",
      recorderErrorSessionCancelled: "This session was cancelled ({{reason}}), so attendance can't be recorded for it.",
      recorderErrorSessionCancelledNoReason: "This session was cancelled, so attendance can't be recorded for it.",
      recorderErrorConsentRevoked: "A guardian has withdrawn consent for one of these children, so their attendance can't be recorded. Ask the parish office.",
      recorderErrorCorrectionReason: "Say why you're changing it: a correction needs a reason.",
      recorderErrorCorrectionNoChange: "It's already recorded that way. There's nothing to change.",
      recorderErrorTeacherRequired: "Tick at least one person who took this session.",
      recorderErrorNotYours: "You can't record this session any more. You may no longer be assigned to this class. Ask the parish office.",
      recorderErrorOffline: "No connection, so nothing was saved. What you entered is still on the screen: try again when you have signal.",
      recorderErrorOfflineLoad: "No connection, so this couldn't be loaded. Try again when you have signal.",
      // M12 - days without sessions (NonSessionDatesPanel.js)
      nonSessionDatesTitle: "Days without sessions",
      nonSessionDatesHelp: "Days the church's activities don't meet, such as Gena, Fasika or a public holiday. Generating sessions skips them, so they never appear as sessions nobody held. Add each year's dates; the Ethiopian Orthodox ones move.",
      nonSessionDatesNone: "No days without sessions yet.",
      nonSessionDateDate: "Date",
      nonSessionDateName: "Name",
      nonSessionDateNameExample: "e.g. Gena",
      nonSessionDateScope: "For",
      nonSessionDateWholeChurch: "the whole church",
      nonSessionDateCancelScheduled: "Also cancel sessions already scheduled that day",
      nonSessionDateAdd: "Add",
      nonSessionDateRemove: "Remove",
      nonSessionDateRequired: "A date and a name are required.",
      nonSessionDateAddedCancelled: "Saved. Sessions already scheduled that day that were cancelled: {{count}}.",
      activityGeneratedExcluded: "Not created (days without sessions): {{days}}.",
      chaseActionOtherParent: "Send to another parent instead",
      chaseOtherParentNote: "No answer came from {{name}}. The request goes to this parent instead, and the expired link to {{name}} stops working.",
      chaseOutcomeSentToOther: "sent to the other parent instead",
      chaseStatusOnPaper: "On paper - form to hand over",
      chaseStatusReturnedUnanswered: "Form back, this left blank",
      chaseProblemOnPaper: "the form is out on paper - print it again rather than send a link",
      chaseUnreachableNoForm: "the paper form's own wording isn't translated into their language yet",
      chaseOutcomeOnPaper: "paper form made",
      chasePaperFailed: "The paper form couldn't be made",
      chasePrintFailed: "The form couldn't be printed",
      chaseNotPrintedYet: "Not printed yet.",
      chaseReturnedUnansweredHelp: "The signed form came back with this left blank, so it can't be recorded on that form. Staff cancel that request on the child's record, then ask again.",
      chaseReturnedBlankOn: "Returned the form with this left blank, signed {{date}}.",
      chaseReturnedUnansweredClosedHelp: "They were asked, and left this blank - perhaps on purpose. That request is closed. Ask again only if there is a reason to.",
      chaseAskAgainByLink: "Ask again by link",
      chaseAskAgainOnPaper: "Ask again on paper",
      chasePrintAgain: "Print again",
      chasePrintForm: "Print the form",
      chasePaperForm: "Paper form",
      chasePaperTitle: "Ask on paper",
      chasePaperConfirm: "Make and print the form",
      chasePaperNoParent: "No recorded parent can be given a paper form: none has a recorded language with the consent text and the form in it. Record the consent in person, through an interpreter if they need one.",
      chaseNotOnPaper: "{{name}} can't be given a paper form: {{reason}}",
      chasePaperHelp: "The form is printed in the parent's recorded language, with the consent text as it reads now. Hand it over; when it comes back signed, record it on the child's record. No email is sent.",
      consentRequestsTitle: "Consent requests",
      memberLanguageLabel: "Language they read",
      memberLanguageNotKnown: "Not known",
      memberLanguageHelp: "Consent links and paper forms are sent in this language. Leave it as not known rather than guess - nothing is sent in a language nobody recorded.",
      consentRequestAsk: "Ask for consent",
      consentRequestNewTitle: "Ask for consent",
      consentRequestOptionsFailed: "What can be asked couldn't be loaded",
      consentRequestSaveFailed: "The consent request couldn't be made",
      consentRequestItems: "What is asked",
      consentRequestRecipient: "Who is asked",
      consentRequestDelivery: "How it reaches them",
      consentRequestAnsweredBySelf: "{{name}} answers this themself - 12 or over.",
      consentRequestAnsweredByParent: "A parent answers this.",
      consentRequestMixed: "A child's own consent and a parent's can't be asked in one request - ask them separately.",
      consentRequestSelf: "themself",
      consentRequestParent: "parent",
      consentRequestOnlyTheirOwn: "From 12, {{name}}'s membership consent is their own - a parent can't give it.",
      consentRequestOnlyAParent: "A parent answers what is chosen.",
      consentRequestNoParentRecorded: "No parent is recorded for this member. Record the relationship first to ask a parent.",
      consentRequestByLinkOption: "By link - sent by email",
      consentRequestOnPaperOption: "On paper - a form printed now and handed over",
      consentRequestMakeAndSend: "Make the request",
      consentRequestMakeAndPrint: "Make it and print the form",
      consentRequestBlocked_NO_DATE_OF_BIRTH: "No date of birth is recorded - who answers depends on whether they are 12 or over. Record it first.",
      consentRequestBlocked_OPEN_REQUEST: "Already asked in an open request - send that link again, or cancel it first.",
      consentRequestBlocked_NO_EMAIL: "No email address is recorded - use paper.",
      consentRequestBlocked_ADDRESS_NOT_PROVEN: "No proof is recorded that this address is theirs - use paper.",
      consentRequestBlocked_NO_LANGUAGE: "No language is recorded for them - record the language they read first.",
      consentRequestBlocked_NO_FORM_IN_LANGUAGE: "The paper form's own wording isn't translated into their language yet.",
      consentRequestBlocked_CHILD_ADDRESS_SHARED: "Their address is also a parent's, or another adult's, on record - a child's own consent is taken in person or on paper, never by link.",
      consentRequestBlocked_CHILD_ADDRESS_NOT_KNOWN_OWN: "No parent is on record to show the address is theirs alone - a child's own consent is taken in person or on paper.",
      consentRequestsNone: "No consent has been asked of anyone about this member.",
      consentRequestsLoadFailed: "The consent requests couldn't be loaded",
      consentRequestOnPaper: "On paper",
      consentRequestByLink: "By link",
      consentRequestTo: "to {{name}}",
      consentRequestMade: "Made {{date}}, reference {{ref}}",
      consentRequestPrinted: "first printed {{date}}",
      consentRequestNotPrinted: "not printed yet",
      consentRequestStatus_OPEN: "open",
      consentRequestStatus_EXPIRED: "link expired",
      consentRequestStatus_COMPLETED: "every item answered",
      consentRequestStatus_CANCELLED: "cancelled",
      consentRequestStatus_REPLACED: "replaced by a newer request",
      consentItemOutcome_PENDING: "not answered",
      consentItemOutcome_GRANTED: "yes",
      consentItemOutcome_REFUSED: "no",
      consentRequestItemGuardian: "Guardian consent - {{activity}}",
      consentRequestItemMembership: "Membership",
      consentRequestPrint: "Print the form",
      consentRequestPrintAgain: "Print again",
      consentRequestPrintFailed: "The form couldn't be printed",
      consentRequestRecordReturn: "Record the signed form",
      consentRequestResend: "Send the link again",
      consentRequestCancel: "Cancel the request",
      consentRequestCancelConfirm: "Cancel this request? Its link stops working. Anything already answered stays answered.",
      consentRequestCancelPaperConfirm: "Cancel this request? A form already handed over can no longer be recorded. Anything already answered stays answered.",
      consentRequestActionFailed: "That couldn't be done",
      consentRequestPaperHelp: "Record the form only once it is back and signed. The scan becomes the record; the paper is destroyed once the scan is checked.",
      consentRequestReturnedForm: "The returned form",
      consentReturnTitle: "Record the signed form",
      consentReturnSave: "Record it",
      consentReturnSaveFailed: "The returned form couldn't be saved",
      consentReturnHelp: "Copy what the paper says - nothing more. The form was printed for {{name}}.",
      consentReturnSignedOn: "Date signed",
      consentReturnSignedOnHelp: "The date written next to the signature.",
      consentReturnSignedByRecipient: "Signed by the person it was printed for",
      consentReturnSignedByRecipientHelp: "{{name}}. If someone else signed it, don't record it: cancel the request and print a new form for them.",
      consentReturnAnswers: "Answers on the form",
      consentReturnAnswersHelp: "Each as ticked on the paper. A box nobody ticked is left blank - it is not a no. On a form that asks more than one thing, at least one must be answered. A form that asks one thing and comes back with it blank is recorded as returned unanswered.",
      consentReturnYes: "Yes",
      consentReturnNo: "No",
      consentReturnLeftBlank: "Left blank",
      consentReturnScans: "Scan of the signed form",
      consentReturnScansHelp: "A PDF, or a photo of each page (JPEG or PNG): at most {{files}} files, {{each}} MB each and {{total}} MB together.",
      consentReturnTooManyFiles: "At most {{max}} files - this is {{count}}. Scan the pages into one PDF.",
      consentReturnFileTooBig: "{{name}} is over {{max}} MB. Scan it again at a lower resolution.",
      consentReturnTotalTooBig: "Together the files are over {{max}} MB.",
      consentReturnFileType: "{{name}} isn't a PDF, JPEG or PNG.",
      consentReturnLoadFailed: "The returned form couldn't be loaded",
      consentReturnRecorded: "Signed {{signedOn}}. Recorded by {{name}} on {{date}}.",
      consentReturnScansHeading: "Scans",
      consentReturnScanPage: "Page {{page}}",
      consentReturnScanReplaced: "replaced",
      consentReturnOpenScan: "Open",
      consentReturnOpenFailed: "That couldn't be opened",
      consentReturnAddScans: "Add a better or missing scan",
      consentReturnAddScansHelp: "Before it is checked. Tick the pages a new scan replaces; leave all unticked for a missing page. Nothing is overwritten - a replaced scan is kept and marked.",
      consentReturnReplaces: "Replaces",
      consentReturnOneScanReplaces: "Replace pages with one new scan at a time - one file, for one page or for all the pages it shows - or add pages without ticking any.",
      consentReturnAddScansSave: "Add the scans",
      consentReturnReceiptHeading: "The family's receipt",
      consentReturnReceiptEmailed: "Emailed on {{date}}.",
      consentReturnReceiptPrinted: "Printed on {{date}} by {{name}}.",
      consentReturnReceiptToPrint: "No email address is held for them - print the receipt and give it to them.",
      consentReturnReceiptNothingAnswered: "Nothing on the form was answered, so no receipt was emailed. Print the receipt and give it to them - it shows the form came back blank.",
      consentReturnPrintReceipt: "Print the receipt",
      consentReturnPrintReceiptAgain: "Print the receipt again",
      consentReturnMarkChecked: "The scans match the paper",
      consentReturnMarkCheckedHelp: "Compare every scan with the paper first. Once confirmed, the scan is the record and the paper is destroyed.",
      consentReturnCheckedWaitsForReceipt: "This waits until the family has their receipt - emailed or printed.",
      consentReturnChecked: "Checked on {{date}} by {{name}}.",
      consentReturnDestroyPaper: "Destroy the paper now - the scan is the record.",
      chaseFilterFollowUp: "To follow up ({{count}})",
      chaseFilterEveryone: "Everyone ({{count}})",
      chaseNothingToFollowUp: "Nobody needs a follow-up right now.",
      chaseBackToActivities: "Back to your activities",
      signInSecondStepTitle: "Two-step sign-in",
      signInSecondStepHint: "Your password is right. Now the code your authenticator app shows.",
      signInSecondStepRecoveryHint: "One of your recovery codes, in place of a code from the app. Each works once.",
      signInSecondStepCode: "Code from your authenticator app",
      signInSecondStepRecovery: "Recovery code",
      signInSecondStepUseApp: "Use a code from the app instead",
      signInSecondStepUseRecovery: "Use a recovery code instead",
      signInSecondStepContinue: "Sign in",
      signInSecondStepStartAgain: "Start again with your password",
      signInSecondStepCodeRequired: "Type the code.",
      signInSecondStepRefused: "The code is not right, or there have been too many attempts: try again within 15 minutes. After five minutes, start again with your password.",
      signInSecondStepLocked: "Two-step sign-in is locked after ten wrong codes. Open the link sent to your address to unlock it.",
      signInSetupSent: "Your login now needs two-step sign-in. We have sent a link to your address to set it up: open it, then sign in.",
      signInSetupNotSent: "Your login now needs two-step sign-in, and no link to set it up could be sent to your address. Ask the parish office.",
      signInSetupAgainSent: "You signed in with a recovery code. If your phone or app is lost, open the link we have sent to your address to set up the app again; the one you have keeps working until then.",
      signInSetupAgainNotSent: "You signed in with a recovery code. If your phone or app is lost, ask the parish office for a way to set up the app again.",
      signInContinue: "Continue",
      secondStepNeeded: "Two-step sign-in is now needed for your account. Please sign in again.",
      secondStepCodeRefused: "The code is not right.",
      secondStepSetupTitle: "Set up two-step sign-in",
      secondStepSetupChecking: "Checking the link…",
      secondStepLinkEnded: "This link no longer works",
      secondStepTryAgain: "Try again",
      secondStepSetupAskAgain: "To get a new one, sign in again, or ask the parish office.",
      secondStepSetupPasswordHint: "First your password, to show this is your login.",
      secondStepSetupPassword: "Your password",
      secondStepContinue: "Continue",
      secondStepSetupRefused: "The password is not right, or there have been too many attempts: try again within 15 minutes.",
      secondStepSetupScan: "Add your login to an authenticator app: scan the code, or type the key.",
      secondStepSetupQr: "QR code for your authenticator app",
      secondStepSetupKey: "Key",
      secondStepSetupAccount: "In the app it is called {{label}}.",
      secondStepSetupConfirmCode: "The code the app shows now",
      secondStepSetupConfirm: "Confirm",
      secondStepSetupDone: "Two-step sign-in is on.",
      secondStepGoToSignIn: "Now sign in",
      recoveryCodesTitle: "Your recovery codes",
      recoveryCodesAdvice: "Each of these ten codes works once, in place of a code from your app. Keep them away from your phone - on paper, or in a password manager. Anyone with one of them and your password can sign in as you. Making new codes ends these.",
      recoveryCodesOnce: "They are shown only now.",
      recoveryCodesPrint: "Print",
      recoveryCodesDownload: "Download",
      secondStepReopenTitle: "Unlock two-step sign-in",
      secondStepReopenWhy: "Two-step sign-in was locked after ten wrong codes in a row. If it was you, unlock it here. If it wasn't, someone may know your password: change it, and tell the parish office.",
      secondStepReopenButton: "Unlock",
      secondStepReopenDone: "Two-step sign-in is unlocked",
      secondStepReopenAskOffice: "While two-step sign-in is still locked, signing in with your password sends a new link, at most once a day. Or ask the parish office.",
      ownSecondStepTitle: "Two-step sign-in",
      ownSecondStepLoadFailed: "Two-step sign-in's state could not be loaded",
      ownSecondStepOn: "Two-step sign-in is on for your login.",
      ownSecondStepOff: "Two-step sign-in is off for your login.",
      ownSecondStepLocked: "It is locked after ten wrong codes: open the link sent to your address to unlock it.",
      ownSecondStepLeft: "Recovery codes left: {{count}}.",
      ownSecondStepFewLeft: "Few are left: make new ones.",
      ownSecondStepTurnOn: "Turn on",
      ownSecondStepLinkSent: "We have sent a link to your address to set it up.",
      ownSecondStepNewCodes: "Make new recovery codes",
      ownSecondStepNewCodesHint: "With a code from your app. The recovery codes you have now stop working.",
      ownSecondStepTurnOff: "Turn off",
      ownSecondStepTurnOffHint: "With a code from your app. Every session of your login ends, this one too.",
      ownSecondStepMustKeep: "Your login must keep two-step sign-in: what your account does needs it.",
      ownSecondStepTooMany: "There have been too many attempts: try again within 15 minutes.",
      ownSecondStepFailed: "That could not be done just now.",
      ownSecondStepTurnedOff: "Two-step sign-in is off for your login. Please sign in again.",
      setPasswordSecondStep: "Your login has two-step sign-in: give the code from your app, or a recovery code.",
      setPasswordThenSetup: "Your password is set. Now set up two-step sign-in",
      resetSecondStep: "Reset two-step sign-in",
      resetSecondStepConfirm: "Only after checking who asks - in person, or by a call to the number on the member record. The second step and its recovery codes are removed, and every session of this login ends.",
      resetSecondStepDone: "Two-step sign-in was reset, and the person is told by e-mail.",
      resetSecondStepNotOn: "Two-step sign-in is not on for this login.",
      resetSecondStepFailed: "Two-step sign-in could not be reset.",
      secondStepLinkNotOpened: "This page holds no link",
      secondStepLinkOpenAgain: "Open the link again from your e-mail, the whole of it. Once read, it is taken out of the address bar, so a reload loses it - the link itself still works.",
      secondStepCodeSixDigits: "A code from the app is six digits.",
      ownSecondStepLinkNotSent: "No setup link could be sent to your address. Ask the parish office.",
      resetSecondStepDoneNotTold: "Two-step sign-in was reset. The person could not be told by e-mail - their address isn't proven: tell them yourself.",
      setPasswordSetupEnded: "Two-step sign-in was not set up: its link ended first. Sign in, and a new link is sent to your address.",
    },
  },
  sv: {
    translation: {
      success: "Framgång",
      error: "Fel",
      title: "Titel",
      memberRegistration: "Medlemsregistrering",
      personalNumber: "Personnummer eller samordningsnummer",
      memberNumber: "Medlemsnummer",
      firstName: "Förnamn",
      middleName: "Mellannamn",
      lastName: "Efternamn",
      christianName: "Kristet namn",
      gender: "Kön",
      male: "Man",
      female: "Kvinna",
      other: "Annat",
      genderAskedNotSaid: "Tillfrågad - ville inte uppge",
      genderPreferNotToSay: "Vill inte uppge",
      confessionKind: "Biktfader",
      confessionInternal: "Intern (välj biktfader)",
      confessionExternal: "Extern (utanför denna församling)",
      confessionNoneRecorded: "Ingen registrerad",
      selectConfessionKind: "Inte angivet",
      confessionExternalName: "Extern biktfaders namn",
      confessionExternalPlace: "Extern biktfaders plats",
      confessionExternalNameRequired: "Ett namn krävs för en extern biktfader",
      confessionFatherSectionTitle: "Biktfader",
      confessionFatherHelp: "Du tillfrågas om detta vid varje ansökan - tomt accepteras inte, eftersom det skulle se ut som att ingen någonsin frågat.",
      confessionAtThisChurch: "Jag har en biktfader i denna församling",
      confessionAtAnotherChurch: "Jag har en biktfader i en annan församling",
      noConfessionFather: "Jag har ingen biktfader",
      confessionChurchName: "Församlingens namn",
      confessionPhoneNumber: "Biktfaderns telefonnummer",
      confessionDeclarationIsRequired: "Ange om du har en biktfader, och var",
      confessionChurchAndPhoneRequired: "Församlingens namn och telefonnummer krävs för en biktfader i en annan församling",
      confessionCopiedFromApplicantHint: "Fylls i från dina uppgifter längre ner i formuläret - om de är tomma, fyll i dem där.",
      declaredConfessionFather: "Uppgivet",
      confessionFatherMissingWarning: "Ingen biktfader uppgiven - en pastoral fråga för prästen att ta upp, blockerar inte.",
      confessionFatherOnFileDiscrepancy: "Registrerat: {{onFile}}. Denna ansökan uppger: {{declared}}. Den befintliga uppgiften ändras inte.",
      reviewerOverrideOptional: "Granskarens ändring (valfritt)",
      noOverride: "Ingen ändring - använd det uppgivna värdet",
      applicantConfessionFatherTitle: "Sökandens biktfader",
      partyOneConfessionFatherTitle: "Part ett biktfader",
      partyTwoConfessionFatherTitle: "Part två biktfader",
      membershipType: "Medlemstyp",
      parishMember: "Församlingsmedlem",
      notRecorded: "Ej registrerat",
      notKnown: "Okänt",
      noneList: "Inga",
      noMemberLinked: "Ingen medlem kopplad",
      attendanceNotTakenYet: "Närvaro inte tagen ännu",
      noGuests: "Inga gäster",
      noneCommon: "Ingen",
      notChosen: "Inget valt",
      notApplicable: "Ej tillämpligt",
      guest: "Gäst",
      selectMembershipType: "Välj medlemstyp",
      dateOfBirthRequiredForParishMember: "Födelsedatum krävs för en församlingsmedlem",
      dateOfBirthStatedUnknown: "Okänt (angivet av personal)",
      dateOfBirthUnknownToggle: "Födelsedatum okänt",
      nationalIdConsentLabel: "Medlemmen har samtyckt till att detta personnummer lagras (GDPR art. 9)",
      nationalIdConsentRequired: "Samtycke krävs innan ett personnummer kan lagras",
      // FR-1.6 församlingsöverföring
      transfer: "Överför",
      transferMember: "Överför medlem",
      targetChurch: "Ny församling",
      targetChurchRequired: "Välj en ny församling",
      transferDate: "Överföringsdatum",
      transferDateRequired: "Ange datumet för flytten - dagen de flyttade, inte dagen det registreras.",
      transferDatePlaceholder: "Dagen de flyttade",
      transferDateHelp: "Dagen de faktiskt flyttade. En mottagande församling kan fråga om det flera år senare - det är inte dagen detta registreras, om det inte är samma dag.",
      familyCandidatesLoadFailed: "Det gick inte att kontrollera vem som flyttar med",
      familyCandidatesLoadFailedBody: "Det betyder inte att personen saknar familj här – barn eller make/maka kan också flytta. Försök igen, eller lägg till dem nedan.",
      transferReason: "Orsak",
      reasonMove: "Flytt",
      reasonCorrection: "Rättelse",
      reasonCorrectionHelp: "Använd Rättelse när församlingen angavs fel vid registreringen, inte för en faktisk flytt.",
      transferNote: "Anteckning (valfritt)",
      transferWarningLoseAccess: "Denna medlem kommer inte längre att synas i din katalog efter denna överföring.",
      transferSuccessful: "Överföringen registrerades",
      transferSuccessfulRedirect: "Överföringen registrerades. Medlemmen har flyttat till en annan församling och finns inte längre i din katalog.",
      transferFailed: "Det gick inte att registrera överföringen",
      transferHistory: "Överföringshistorik",
      fromChurch: "Från församling",
      toChurch: "Till församling",
      recordedBy: "Registrerad av",
      noTransferHistory: "Inga överföringar registrerade",
      // FR-1.10 medlemsimport
      importMembers: "Importera medlemmar",
      importSelectFile: "Välj .xlsx-fil",
      importConsentBasis: "Samtyckesgrund",
      importConsentVersion: "Samtyckesversion",
      importConsentHelp: "Gäller alla rader i denna fil som innehåller ett personnummer.",
      importSubmit: "Ladda upp och förhandsgranska",
      importWillCreate: "Skapas",
      importAlreadyImported: "Redan importerad",
      importRejected: "Avvisad",
      importRowNumber: "Rad",
      importStatus: "Status",
      importDuplicateCandidates: "Möjliga dubbletter",
      importMatchedOn: "matchade på",
      importSkipRow: "Hoppa över",
      importBackToUpload: "Tillbaka till uppladdning",
      importCommit: "Genomför import",
      importNoRows: "Inga rader",
      importUploadFailed: "Det gick inte att förhandsgranska importfilen",
      importBatchExpired: "Din granskning har gått ut. Ladda upp filen igen.",
      importCommitFailed: "Det gick inte att genomföra importen",
      importCreatedCount: "Skapade",
      importSkippedCount: "Överhoppade",
      importFailedCount: "Misslyckade",
      importStartOver: "Importera en ny fil",
      notAuthorized: "Du har inte behörighet att visa denna sida.",
      of: "av",
      // FR-1.11 dubblettgranskning / sammanslagning
      duplicatesReview: "Dubblettgranskning",
      duplicatesSearch: "Sök",
      duplicatesCompareSelected: "Jämför valda",
      duplicatesMatchScore: "Matchningspoäng",
      duplicatesMatchedFields: "Matchade på",
      duplicatesNoResults: "Inga kandidater hittades",
      duplicatesEnterCriteria: "Ange sökkriterier ovan",
      duplicatesSearchFailed: "Det gick inte att söka efter dubbletter",
      duplicatesSearchFailedBody: "Det betyder inte att det inte finns några – sök igen innan du förlitar dig på det.",
      duplicatesField: "Fält",
      duplicatesSurvivor: "Behålls",
      duplicatesLosing: "Tas bort",
      duplicatesUseLosingValue: "Använd det borttagna postens värde",
      duplicatesSurvivorIs: "Behålls:",
      duplicatesPersonnummerNote: "Båda posterna har ett personnummer angivet. Den kvarvarande postens värde behålls som standard; att välja att använda den borttagna postens värde istället kräver ett aktivt samtycke för personnummer hos den kvarvarande posten.",
      duplicatesReason: "Orsak (valfritt)",
      duplicatesMerge: "Slå ihop",
      duplicatesMergeSuccessful: "Medlemmarna slogs ihop",
      duplicatesMergeFailed: "Det gick inte att slå ihop medlemmarna",
      duplicatesLoadFailed: "Det gick inte att läsa in medlemsuppgifterna",
      duplicatesBackToSearch: "Tillbaka till sökning",
      originPlace: "Ursprungsplats",
      originChurch: "Ursprungsförsamling",
      // Granskningslogg
      auditLogs: "Granskningslogg",
      auditLogTable: "Tabell",
      auditLogRecordId: "Post-ID",
      auditLogActor: "Utförare (medlems-ID)",
      auditLogAction: "Åtgärd",
      auditLogAny: "Alla",
      auditLogFetchFailed: "Det gick inte att läsa in granskningsloggen",
      auditLogTime: "Tid",
      auditLogRecords: "Post(er)",
      auditLogActorName: "Utförare",
      auditLogIp: "IP",
      auditLogRevealExport: "Export (avmaskerad)",
      auditLogRevealSingle: "Visning",
      auditLogRecordsCount: "poster",
      auditLogShowDetails: "Detaljer",
      auditLogHideDetails: "Dölj",
      auditLogBefore: "Före",
      auditLogAfter: "Efter",
      auditLogNoData: "(inget)",
      auditLogFieldsSet: "Angivna fält",
      auditLogFieldsChanged: "Ändrade fält",
      auditLogAskedWith: "Frågat med",
      auditLogNotRecorded: "Inte registrerat",
      auditLogUnknownNames: "{{count}} namn sparades inte: okända för systemet",
      auditLogRegistrationsCount: "registreringar",
      auditLogProbeExact: "Sökning (exakt nummer)",
      auditLogProbeSearch: "Sökning (dubblettsökning)",
      auditLogPurpose: "Syfte",
      auditLogNote: "Anteckning",
      // Rapporter
      confessionGapReport: "Biktfaderslucka",
      reportIncludeMinors: "Inkludera minderåriga (ålder ≤ 18)",
      reportFetchFailed: "Det gick inte att läsa in rapporten",
      reportNeverCaptured: "Aldrig registrerat",
      reportNoneRecorded: "Inget registrerat",
      servantsRosterReport: "Tjänstgöringslista",
      reportAnyStatus: "Alla",
      reportStatusActive: "Aktiv",
      reportStatusOnLeave: "Tjänstledig",
      reportStatusReleased: "Entledigad",
      reportCapacity: "Roll",
      reportDepartment: "Avdelning",
      reportOrdinationRank: "Vigningsgrad",
      reportEmployed: "Anställd",
      reportEmployer: "Arbetsgivare",
      reportYes: "Ja",
      reportNo: "Nej",
      guestCountsReport: "Gästantal",
      reportPeriodStart: "Periodstart",
      reportPeriodEnd: "Periodslut",
      reportRun: "Kör rapport",
      reportRunPrompt: "Välj en period (valfritt) och kör rapporten.",
      reportTotalGuests: "Totalt antal gäster",
      reportConverted: "Konverterade",
      reportConversionRate: "Konverteringsgrad",
      reportByServiceType: "Per tjänstetyp",
      reportServiceTypeNote: "Inte en partition - en gäst kopplad till fler än en tjänst räknas i vardera.",
      reportServicePriesthood: "Prästerskap",
      reportServiceDeaconship: "Diakonat",
      reportServiceSundaySchool: "Söndagsskola",
      reportServiceParish: "Församling",
      reportServiceYouth: "Ungdom",
      reportUnassigned: "Ej tilldelad",
      reports: "Rapporter",
      directory: "Katalog",
      includeGuests: "Inkludera gäster",
      includeInactiveStatuses: "Inkludera avlidna och utflyttade",
      department: "Avdelning",
      ageBand: "Åldersgrupp",
      ageBandAny: "Alla åldrar",
      ageBandYouth: "Ungdom (0-18)",
      youngAdult: "Ung vuxen (19-35)",
      adult: "Vuxen (36-59)",
      senior: "Senior (60+)",
      missingDateOfBirth: "Saknar födelsedatum",
      searchByPersonalNumber: "Sök på personnummer",
      applyFilters: "Tillämpa filter",
      resetFilters: "Återställ filter",
      totalResults: "resultat",
      anyChurch: "Alla kyrkor",
      anyStatus: "Alla statusar",
      anyMembershipType: "Alla",
      search: "Sök",
      noResults: "Inga resultat",
      emailAddress: "E-postadress",
      phoneNumber: "Telefonnummer",
      dateOfBirth: "Födelsedatum",
      profession: "Yrke",
      register: "Registrera",
      members: "Medlemmar",
      dashboard: "Instrumentbräda",
      churchService: "Kyrktjänst",
      baptisms: "Dop",
      maritalStatus: "Civilstånd",
      churchMemberInfoManagementSystem:
        "Kyrkans medlemsinformationshanteringssystem (CMMS)",
      registerMembers: "Registrera Medlemmar",
      registerEvents: "Registrera Händelser (Dop, Bröllop)",
      getStarted: "Komma igång",
      churchName: "Kyrkans Namn",
      spiritualFathersName: "Andlig Faders Namn",
      address: "Adress",
      city: "Stad",
      savingSuccessful: "Du har framgångsrikt sparat informationen",
      savingError:
        "Fel vid sparande av informationen. Vänligen kontakta IT-avdelningen",
      // FOR THE SWEDISH REVIEW (the owner's order of 23 Oct 2026): the 12 keys below, not yet read by a Swedish speaker.
      failureUnexpected: "Något gick fel på servern. Försök igen senare.",
      failureNotSaved: "Ändringen kunde inte sparas.",
      failureNationalIdNotOpened: "Radens id-nummer kunde inte öppnas; inget av raden skrevs.",
      failureAuditNotRecorded:
        "Det här kan inte visas just nu: att det öppnas måste registreras i granskningsloggen, och det misslyckades. Ingenting visades. Försök igen om en stund; händer det igen, säg till administratören.",
      failureNationalIdUnavailable:
        "Id-numret kan inte läsas eller sparas just nu. Inget annat påverkas. Försök igen om en stund; händer det igen, säg till administratören.",
      failureSignInNotChecked: "Inloggningen kunde inte kontrolleras. Försök igen senare.",
      failureSignInNotRecorded: "Inloggningen kunde inte registreras, så du loggades inte in. Försök igen senare.",
      failureReference: "(referens {{reference}})",
      refusalUsernameTaken: "Användarnamnet är redan upptaget.",
      refusalUsernameLikePersonalNumber: "Ett användarnamn kan inte innehålla ett personnummer. Välj ett annat användarnamn.",
      refusalUsernameLikeEmailAddress: "Ett användarnamn kan inte vara en e-postadress. Välj ett annat användarnamn.",
      refusalChurchCodeTaken: "En annan kyrka har redan den här koden.",
      refusalChurchHasMembers: "Kyrkan kan inte tas bort: det finns medlemsuppgifter registrerade under den.",
      refusalChurchServiceHasActivities: "Kyrkotjänsten kan inte tas bort: det finns aktiviteter registrerade under den.",
      pageOutOfDate: "Sidan är inaktuell. Ladda om den och spara sedan igen.",
      // Checklist 1.6, the display work (3.5): a national ID shown "on file", the reveal, the approval's number.
      // FOR THE SWEDISH REVIEW (the owner's order of 23 Oct 2026): written here, not yet read by a Swedish speaker -
      // the 25 keys below, and pageOutOfDate just above. The list, with each one's English: cmms's
      // docs/swedish-review.md (the owner's decision 3 of 23 Oct 2026); a key read takes its row off it.
      nationalIdOnFilePersonnummer: "Personnummer finns registrerat",
      nationalIdOnFileSamordningsnummer: "Samordningsnummer finns registrerat",
      nationalIdOnFile: "Id-nummer finns registrerat",
      nationalIdUnreadable: "Det registrerade numret kunde inte öppnas. Ingenting visades.",
      revealNationalId: "Visa numret",
      revealPurposeLabel: "Varför behöver du se numret?",
      revealPurposeChoose: "Välj ett syfte",
      revealPurposePersonAsked: "Personen, eller dess vårdnadshavare, bad om det",
      revealPurposeCertificateOrRegister: "Ett intyg eller en registerpost behöver det",
      revealPurposeAuthorityAsked: "En myndighet bad om det",
      revealPurposeCheckingTheRecord: "Skilja poster åt, eller rätta en",
      revealPurposeOther: "Annat",
      revealNoteLabel: "Anteckning (behövs för Annat)",
      revealNoteHelp: "Högst 200 tecken, och inget nummer. Syftet sparas i granskningsloggen; anteckningen i 12 månader.",
      revealConfirm: "Visa",
      hideNationalId: "Dölj",
      revealPurposeRequired: "Välj varför du behöver se numret.",
      revealNoteRequired: "Skriv i anteckningen vilket det andra syftet är.",
      revealNoteTooLong: "Anteckningen får vara högst 200 tecken.",
      revealReasonHoldsANumber: "Anteckningen får inte innehålla ett nummer: den sparas i granskningsloggen.",
      revealNotAllowed: "Du får inte se den här medlemmens nummer.",
      revealFailed: "Numret kunde inte visas.",
      replaceNationalId: "Ersätt",
      keepNationalIdOnFile: "Behåll det registrerade numret",
      registrationNumberGiven: "Ett nummer angavs vid registreringen",
      useRegistrationNumber: "Använda numret som angavs vid registreringen?",
      useRegistrationNumberYes: "Ja, använd det",
      useRegistrationNumberNo: "Nej",
      useRegistrationNumberChoose: "Välj om numret som angavs vid registreringen ska användas.",
      registrationNumberCorrection: "Om det var fel, ange det rättade numret nedan. Lämna fältet tomt för att inte spara något.",
      registrationNumberUsed: "Numret som angavs vid registreringen sparas krypterat. Det visas inte här.",
      numbersCompareFailed: "Det gick inte att kontrollera om de två posternas nummer skiljer sig.",
      membersTableTitle: "Medlemmar av kyrkan",
      apartmentNumber: "Lägenhetsnummer",
      postCode: "Postnummer",
      country: "Land",
      churchCode: "Kyrkokod",
      churchCodeRequired: "En kyrkokod krävs för en ny kyrka - den blir prefixet för certifikatets referensnummer",
      organisationsnummer: "Organisationsnummer",
      organisationsnummerRequired: "Ett organisationsnummer krävs för en ny kyrka",
      telefon: "Telefon",
      churchTimeZone: "Tidszon",
      churchTimeZoneHelp: "Tysta timmar för aviseringar räknas i den här tidszonen. Välj där församlingen finns - det finns inget säkert standardval.",
      activities: "Aktiviteter",
      activitiesTitle: "Aktiviteter",
      newActivity: "Ny aktivitet",
      editActivity: "Redigera aktivitet",
      noActivities: "Inga aktiviteter ännu",
      activitySettings: "Aktivitetsinställningar",
      activityName: "Namn",
      activityDescription: "Beskrivning",
      activityDepartment: "Avdelning",
      activityType: "Aktivitetstyp",
      activityTypes: "Aktivitetstyper",
      activityFundingTag: "Bidragskategori",
      activityFundingTags: "Bidragskategorier",
      activityFundingTagNone: "Ingen",
      activityFundingTagHelp: "Anges oberoende av aktivitetstypen, så att ett sommarläger inte redovisas som söndagsskola.",
      activitySelect: "Välj…",
      activityNoTypesYet: "Inga aktivitetstyper finns för den här kyrkan ännu - skapa en under Aktivitetsinställningar först.",
      activityRecurrence: "Upprepas",
      activityRecurrenceHelp: "Markera de veckodagar aktiviteten träffas. Lämna alla omarkerade för en aktivitet med enbart enstaka tillfällen.",
      activityEveryNWeeks: "Var hur många veckor",
      activityWeekdayMO: "Mån",
      activityWeekdayTU: "Tis",
      activityWeekdayWE: "Ons",
      activityWeekdayTH: "Tor",
      activityWeekdayFR: "Fre",
      activityWeekdaySA: "Lör",
      activityWeekdaySU: "Sön",
      activityStartsOn: "Startar",
      activityEndsOn: "Slutar",
      activityStartsOnRequired: "Ett startdatum krävs när aktiviteten upprepas.",
      activityDefaultStartTime: "Starttid",
      activityDefaultEndTime: "Sluttid",
      activityDefaultLocation: "Plats",
      activityCapacity: "Kapacitet",
      activityCapacityHelp: "0 betyder obegränsat. Att överskrida kapaciteten ger en varning men stoppar aldrig en inskrivning.",
      activityUnlimited: "Obegränsat",
      activityFee: "Avgift",
      activityFeeFree: "Kostnadsfri",
      activityFeePaid: "Avgiftsbelagd",
      activityFeeChoiceRequired: "Välj om aktiviteten är kostnadsfri eller avgiftsbelagd.",
      activityFeeAmount: "Avgiftsbelopp",
      activityFeeCurrency: "Valuta",
      activitySendInvitations: "Skicka inbjudningar till tillfällen",
      activitySendInvitationsHelp: "Av som standard. Påminnelser skickas alltid 24 timmar före varje tillfälle; inbjudningar passar ett enstaka tillfälle eller första tillfället på en ny termin.",
      activityStatus: "Status",
      activityStatusACTIVE: "Aktiv",
      activityStatusARCHIVED: "Arkiverad",
      activityYes: "Ja",
      activityNo: "Nej",
      activityRequiredFields: "Namn, avdelning och aktivitetstyp krävs.",
      activitySaved: "Sparat",
      activitySaveFailed: "Det gick inte att spara",
      activityLoadFailed: "Det gick inte att läsa in aktiviteten",
      activityDeactivatedSuffix: "(inaktiverad)",
      activityLeaders: "Ledare",
      activityNoLeaders: "Inga ledare tilldelade",
      activityAddLeader: "Lägg till som ledare",
      activityRemoveLeader: "Ta bort",
      activitySearchMember: "Sök en medlem i den här kyrkan på namn",
      activitySearch: "Sök",
      activitySessions: "Tillfällen",
      activityNoSessions: "Inga tillfällen ännu",
      activityGenerateSessions: "Generera tillfällen",
      activityGenerateFrom: "Från",
      activityGenerateTo: "Till",
      activityGenerateHelp: "Högst 366 dagar per körning. Att generera samma period igen skapar aldrig dubbletter, och ett inställt datum återskapas aldrig.",
      activityGeneratedResult: "Skapade {{created}}, fanns redan {{skipped}}",
      activityAddSession: "Lägg till enstaka tillfälle",
      activitySessionDate: "Datum",
      activitySessionOrigin: "Ursprung",
      activitySessionOriginGENERATED: "Genererat",
      activitySessionOriginAD_HOC: "Enstaka",
      activitySessionStatusSCHEDULED: "Planerat",
      activitySessionStatusHELD: "Genomfört",
      activitySessionStatusCANCELLED: "Inställt",
      activityCancelSession: "Ställ in tillfälle",
      activityNoSessionDay: "Inget tillfälle: {{name}}",
      activityCancelReason: "Anledning till inställningen",
      activityCancelReasonRequired: "En anledning krävs. Tillfället sparas och markeras som inställt - det raderas aldrig.",
      activityLookupHelp: "Aktivitetstyper och bidragskategorier hör till din kyrka. Inaktivering döljer en post för nya aktiviteter; aktiviteter som redan använder den behåller den. Inget kan raderas.",
      activityLookupCode: "Kod",
      activityLookupCodeHelp: "Koden kan inte ändras när den skapats - rapporter grupperas på den. Namnet kan ändras fritt; för en ny kod, skapa en ny post.",
      activityLookupName: "Namn",
      activityLookupSortOrder: "Sorteringsordning",
      activityLookupActive: "Aktiv",
      activityLookupDeactivated: "Inaktiverad",
      activityLookupDeactivate: "Inaktivera",
      activityLookupReactivate: "Återaktivera",
      activityLookupRename: "Byt namn",
      activityLookupNoneYet: "Inga ännu",
      activityLookupRequired: "Kod och namn krävs.",
      activityAddType: "Lägg till aktivitetstyp",
      activityAddFundingTag: "Lägg till bidragskategori",
      enrolments: "Inskrivningar",
      enrolMember: "Skriv in en medlem",
      enrolmentAll: "Alla",
      enrolmentNone: "Inga inskrivningar",
      enrolmentMember: "Namn",
      enrolmentPeriod: "Period",
      enrolmentPeriodStart: "Periodens start",
      enrolmentPeriodEnd: "Periodens slut",
      enrolmentOpenEnded: "tills vidare",
      enrolmentStatusPENDING: "Väntande",
      enrolmentStatusACTIVE: "Aktiv",
      enrolmentStatusWITHDRAWN: "Avslutad",
      enrolmentMinor: "Minderårig",
      enrolmentGuardianConsent: "Vårdnadshavares samtycke",
      enrolmentConsentOnFile: "Finns",
      enrolmentConsentNotGiven: "Inte givet",
      enrolmentActivateBlocked_GUARDIAN_CONSENT_NOT_GIVEN: "Behöver vårdnadshavares samtycke först",
      enrolmentActivateBlocked_GUARDIAN_CONSENT_WITHDRAWN: "Vårdnadshavares samtycke har återkallats",
      enrolmentActivateBlocked_NO_DATE_OF_BIRTH: "Inget födelsedatum registrerat: behöver vårdnadshavares samtycke, eller registrera födelsedatumet",
      enrolmentConsentButton: "Vårdnadshavares samtycke",
      enrolmentActivate: "Aktivera",
      enrolmentActivated: "Inskrivningen är aktiverad",
      enrolmentCreated: "Inskriven - inskrivningen väntar tills den aktiveras",
      enrolmentChangeMember: "Byt",
      enrolmentHelpFree: "Varje inskrivning börjar som väntande. En minderårig - under 18 vid periodens start, eller utan registrerat födelsedatum - behöver vårdnadshavares samtycke före aktivering.",
      enrolmentHelpPaid: "Aktiviteten är avgiftsbelagd, så perioden behöver ett slutdatum: avgiftsperioden är inskrivningens. En minderårig behöver vårdnadshavares samtycke före aktivering.",
      enrolmentWithdraw: "Avsluta",
      enrolmentWithdrawReason: "Anledning till avslutet",
      enrolmentWithdrawHelp: "En anledning krävs. Ange beslutet och vem som fattade det - aldrig familjens omständigheter. Gruppledare ser att ett barn har avslutats, inte varför. Inget raderas - redan registrerad närvaro finns kvar.",
      enrolmentWithdrawn: "Inskrivningen är avslutad",
      consentGuardianTitle: "Vårdnadshavares samtycke",
      consentOnFile: "Registrerade samtycken",
      consentRawGuardian: "Inte kopplad till registret",
      consentRevoke: "Återkalla",
      consentRevokeConfirm: "Återkalla detta samtycke? En aktiv minderårigs inskrivning går tillbaka till väntande om inget annat samtycke finns kvar. Redan registrerad närvaro påverkas inte.",
      consentRevokedOn: "Återkallat {{date}}",
      consentTextLoadFailed: "Det gick inte att läsa in samtyckestexten",
      consentTextShown: "Samtyckestext som visas för vårdnadshavaren ({{version}})",
      consentLanguage_sv: "Svenska",
      consentLanguage_en: "Engelska",
      consentLanguage_am: "Amhariska",
      consentAmharicPending: "Amhariska finns ännu inte för den här versionen. Visa vårdnadshavaren den svenska eller engelska texten - det språk som visas är det som registreras.",
      consentChooseLanguage: "Välj det språk vårdnadshavaren får se.",
      consentGuardian: "Vårdnadshavare",
      consentGuardianName: "Vårdnadshavarens namn",
      consentGuardianPhone: "Telefon",
      consentGuardianEmail: "E-post",
      consentGuardianLanguage: "Språk vårdnadshavaren vill få meddelanden på",
      consentFindGuardian: "Sök i registret",
      consentFindGuardianHelp: "Sök i registret först - att koppla vårdnadshavarens egen medlemspost är det normala.",
      consentNoCandidates: "Ingen i registret matchar.",
      consentCandidateOtherChurch: "Registrerad i en annan kyrka - koppling kräver en stiftsadministratör",
      consentLinkThisPerson: "Detta är vårdnadshavaren",
      consentLinked: "Kopplad",
      consentNotInRegister: "Vårdnadshavaren finns inte i registret",
      consentRawHelp: "Endast för en vårdnadshavare som verkligen inte finns i registret. Uppgifterna sparas enbart på samtycket.",
      consentNoneOfThese: "Ingen av de listade personerna är denna vårdnadshavare",
      consentRelationship: "Relation till barnet",
      consentRelationshipMOTHER: "Mor",
      consentRelationshipFATHER: "Far",
      consentRelationshipLEGAL_GUARDIAN: "Särskilt förordnad vårdnadshavare",
      consentRelationshipOTHER: "Annan",
      consentRecord: "Registrera samtycke",
      consentRecorded: "Samtycket är registrerat",
      consentTextTitle: "Samtyckestext för vårdnadshavare",
      consentTextHelp: "Den text vårdnadshavare samtycker till, skriven av denna kyrka. Ett utkast visas aldrig för någon. Publicering kräver svenska och engelska och ersätter den gällande versionen; därefter kan texten inte ändras - endast en tom amharisk text kan läggas till, en gång. Alla andra ändringar blir en ny version.",
      consentTextNoneYet: "Ingen samtyckestext har publicerats. Vårdnadshavares samtycke kan inte registreras förrän en finns.",
      consentTextCurrent: "Gällande version: {{version}}",
      consentTextDraft: "Utkast ({{version}})",
      consentTextPending: "Ännu inte skriven",
      consentTextOptional: "kan läggas till senare",
      consentTextNewVersion: "Påbörja en ny version",
      consentTextSaveDraft: "Spara utkast",
      consentTextPublish: "Publicera",
      consentTextPublishHelp: "Publicering ersätter den gällande versionen för nya samtycken. Redan registrerade samtycken pekar fortfarande på den version vårdnadshavaren fick se.",
      consentChangeQuestion: "Vilken sorts ändring är det här?",
      consentChangeEditorialOption: "Redaktionell: formuleringen ändras, men den som godkände en tidigare version omfattas fortfarande av det som står i den här versionen.",
      consentChangeMaterialOption: "Väsentlig: det man samtycker till ändras. Den som godkände en tidigare version behöver tillfrågas igen.",
      consentChangeRecordedHelp: "Ditt val sparas med ditt namn, och granskningsloggen sparar vad som har ändrats sedan föregående version.",
      consentChange_EDITORIAL: "Redaktionell ändring",
      consentChange_MATERIAL: "Väsentlig ändring",
      consentChangeNotRecorded: "Typ av ändring inte registrerad",
      consentTextActivityMissing: "Texten nämner aldrig den aktivitet familjen samtycker till. Lägg till [activity] där namnet hör hemma.",
      consentTextActivityNotNamedOption: "Texten nämner medvetet inte aktiviteten",
      consentTextActivityPartial: "Aktiviteten nämns på vissa språk men inte på alla. Lägg till [activity] även här: {{missing}}.",
      consentTextActivityNotNamedBadge: "Nämner inte aktiviteten",
      consentTextPublishConfirm: "Publicera denna version? Dess svenska och engelska text kan aldrig ändras efteråt.",
      consentTextPublished: "Samtyckestexten är publicerad",
      consentTextAddAmharic: "Lägg till den amhariska texten",
      consentTextAddAmharicHelp: "Skriv eller klistra in den text församlingen har skrivit. Den kan läggas till en gång; att ändra den efteråt kräver en ny version.",
      consentTextAmharicConfirm: "Lägga till denna amhariska text? Den kan inte ändras efteråt annat än genom att publicera en ny version.",
      consentTextSaveAmharic: "Spara amharisk text",
      consentTextEarlier: "Tidigare versioner ({{count}})",
      attendanceTitle: "Närvaro",
      attendanceStatus: "Närvaro",
      attendanceStatusATTENDED: "Närvarande",
      attendanceStatusABSENT: "Frånvarande",
      attendanceNoEnrolees: "Inga väntande eller aktiva inskrivningar",
      attendanceConsentPending: "Samtycke saknas",
      formRequiredLegend: "obligatoriskt",
      formFieldsRequired: "Fyll i: {{fields}}",
      attendanceConsentWithdrawn: "Samtycke återkallat",
      attendanceConsentWithdrawnHelp: "Vårdnadshavaren har återkallat sitt samtycke, så barnet kan inte markeras.",
      attendanceRefusedWithdrawn: "Inget sparades: {{names}} kan inte markeras, eftersom vårdnadshavaren har återkallat sitt samtycke. Dina andra markeringar finns kvar - tryck på Spara igen.",
      attendanceUnsavedLeave: "Du har markeringar på listan som inte är sparade. Lämna sidan och förlora dem?",
      attendanceLate: "registrerad i efterhand",
      attendanceWhenRecorded: "När registreras dessa uppgifter?",
      attendanceOnTheDay: "Samma dag som tillfället",
      attendanceLater: "I efterhand, från anteckningar eller minne",
      attendanceWhenRecordedHelp: "Ange vilket - uppgifter som förs in efter dagen markeras som efterregistrerade.",
      attendancePastSessionHelp: "Detta tillfälle har passerat, så uppgifterna registreras i efterhand.",
      attendanceSave: "Spara närvaro",
      attendanceSaved: "Närvaron är sparad",
      attendanceSavedConsentPending: "Närvaron är sparad. {{count}} inskrivna saknar fortfarande vårdnadshavares samtycke och finns på arbetslistan för samtycken.",
      attendanceCorrect: "Rätta",
      attendanceCorrectionReason: "Anledning till rättelsen",
      attendanceCorrected: "Närvaron är rättad",
      rosterTitle: "Närvarolista",
      rosterWindow: "Inskriven",
      rosterHeld: "Genomförda",
      rosterConfirmed: "Anmäld",
      rosterUnrecorded: "Ej registrerat",
      rosterRate: "Andel",
      rosterHelp: "Andelen räknar tillfällen som genomförts medan personen var inskriven. Ej registrerat visas för sig och räknas aldrig som frånvaro; utan genomförda tillfällen finns ingen andel.",
      feeTitle: "Avgiftsstatus",
      feeStatus: "Avgift",
      feeStatusPAID: "Betald",
      feeStatusUNPAID: "Obetald",
      feeStatusWAIVED: "Efterskänkt",
      feeStatusNOT_RECORDED: "Ej registrerad",
      feeRecord: "Registrera",
      feeRecorded: "Avgiftsstatus är registrerad",
      feeHistory: "Historik",
      feeHelp: "Registreras, tas inte in - ingen betalning hanteras här. Ej registrerad betyder att ingen har registrerat något ännu; det betyder aldrig obetald.",
      feeWaiverReason: "Anledning till efterskänkningen",
      feeWaiverHint: "Ange beslutet och vem som fattade det - till exempel \"Efterskänkt enligt församlingsrådets beslut 12 september\". Aldrig familjens omständigheter.",
      worklistsTitle: "Arbetslistor",
      worklistUnrecordedSessions: "Tillfällen utan registrerad närvaro",
      worklistUnrecordedHelp: "Passerade tillfällen som varken registrerats eller ställts in. Ett tillfälle räknas som genomfört först när närvaron registrerats, så vart och ett av dessa behöver det ena eller det andra.",
      worklistAwaitingConsent: "Närvaro som väntar på vårdnadshavares samtycke",
      worklistAwaitingConsentHelp: "Minderåriga vars närvaro registrerats fast vårdnadshavares samtycke aldrig lämnats. Registrera samtycket från aktivitetens inskrivningslista.",
      worklistNothing: "Inget här.",
      activitySettingsChurch: "Kyrka",
      todayTitle: "Dagens tillfällen",
      todayNone: "Inga tillfällen i dag.",
      todayTakeAttendance: "Ta närvaro",
      todayOpenAttendance: "Öppna närvaron",
      todayRecentUnrecorded: "Tidigare tillfällen som inte registrerats",
      todayAllUnrecorded: "Alla tillfällen som inte registrerats",
      attendanceMarkAllPresent: "Markera de {{count}} som inte markerats som närvarande",
      attendanceMarkAllHelp: "Ändra sedan de frånvarande. Endast det du markerar sparas - inget fylls i åt dig.",
      attendanceProgress: "{{done}} av {{total}} registrerade",
      attendanceUnmarkedLeft: "{{count}} inte markerade ännu",
      attendanceChooseWhen: "Ange när uppgifterna registreras",
      attendanceFutureSession: "Detta tillfälle har inte ägt rum ännu - närvaro kan tas från och med dagen för tillfället.",
      attendanceProgressUnsaved: "{{done}} av {{total}} markerade - inte sparat ännu",
      activitiesNoneLed: "Du leder inga aktiviteter.",
      activitySessionTeacherOnly: "Du har hållit ett eller flera tillfällen i den här aktiviteten. Öppna dem från aktivitetssidan:",
      teacherOfRecordTitle: "Vem höll det här tillfället?",
      teacherOfRecordHelpLeader: "Markera vem som faktiskt höll tillfället. Förifyllda namn är en plan tills de bekräftas. Den du markerar får tillgång till just det här tillfället: namnen på barnen som är inskrivna i det, deras anmälningar och närvaro - som de kan registrera och rätta - och om ett samtycke hindrar att ett barn markeras; inget annat, tills personal tar bort dem. En vikarie läggs till av personal.",
      teacherOfRecordHelpStaff: "Markera vem som faktiskt höll tillfället. Den du markerar får tillgång till just det här tillfället: namnen på barnen som är inskrivna i det, deras anmälningar och närvaro - som de kan registrera och rätta - och om ett samtycke hindrar att ett barn markeras. Inte andra tillfällen, och inte vårdnadshavarnas uppgifter, samtycken eller avgifter. Det gäller tills personal tar bort dem från tillfället, eller de lämnar församlingen.",
      teacherOfRecordSourceDEFAULTED: "planerad",
      teacherOfRecordSourceRECORDED: "bekräftad",
      teacherOfRecordAddSubstitute: "Lägg till en vikarie - sök en medlem i den här kyrkan",
      teacherOfRecordConfirm: "Bekräfta vem som höll tillfället",
      teacherOfRecordSaved: "Ansvarig ledare sparad",
      enrolmentStartsAfterTerm: "Inskrivningen börjar {{date}}, efter att aktiviteten startade. Tillfällen före det datumet tar inte med personen - ändra om personen var med från början.",
      enrolmentCorrectPeriod: "Rätta period",
      enrolmentCorrectionReason: "Anledning till rättelsen",
      enrolmentCorrectPeriodHelp: "För ett felaktigt angivet datum. Ange vad som var fel och vem som beslutade - aldrig familjens omständigheter. Registrerad närvaro måste ligga inom perioden. Loggas med gamla och nya datum.",
      enrolmentPeriodCorrected: "Perioden är rättad",
      attendanceNobodyCoversThisDate: "Ingen inskrivning omfattar det här datumet - se nedan.",
      attendanceNotOnThisSheet: "Inskrivna, men inte på den här listan:",
      attendanceEnrolledFrom: "inskriven från {{date}}",
      attendanceEnrolledUntil: "inskriven till {{date}}",
      attendanceFixPeriodStaff: "Om datumet är fel, rätta perioden på aktivitetssidan.",
      attendanceFixPeriodLeader: "Om datumet är fel, be en administratör rätta inskrivningen.",
      consentTextShownVersion: "Samtyckestext {{version}}",
      consentTextLineBreaksQuestion: "Den här texten ser ut att vara inklistrad med sina radbrytningar",
      consentTextLineBreaksIntendedOption: "Radbrytningarna är avsiktliga - publicera den exakt som den är skriven, rad för rad. (Annars: klistra in texten igen utan dem.)",
      consentTextShortQuestion: "Den här texten är mycket kort",
      consentTextShortCompleteOption: "Den här korta texten är fullständig - den är allt som församlingen ber om samtycke till. (Annars: skriv hela texten först.) En så kort text öppnar inte registrering på nätet.",
      consentTextReadInLanguage: "Språk den lästes på",
      consentTextChooseLanguage: "Välj språket den lästes på",
      consentTextChooseLanguageHelp: "Välj språket personen läste den på - det registreras med samtycket.",
      consentTextAmharicPendingPublic: "Amhariska finns ännu inte för den här texten - läs den på svenska eller engelska.",
      consentAgreeMembershipSelf: "Jag har läst texten och samtycker till att mina personuppgifter förs in i församlingens medlemsregister på dessa villkor.",
      consentAgreeApplication: "Jag har läst texten och samtycker till att personuppgifterna i formuläret behandlas för den här ansökan.",
      consentAgreeRequired: "Läs samtyckestexten och kryssa i rutan för att samtycka.",
      myActionItems: "Mina åtgärdspunkter",
      actionItem: "Åtgärdspunkt",
      noOutstandingActionItems: "Inget utestående just nu.",
      failedToFetchActionItems: "Det gick inte att hämta dina åtgärdspunkter",
      errorUpdatingActionItem: "Det gick inte att uppdatera åtgärdspunkten",
      consentTextUnavailable: "Formuläret kan inte skickas ännu: församlingen har inte publicerat sin samtyckestext.",
      addressIsRequired: "En adress krävs: gatuadress, postnummer och ort.",
      consentFromRegistration: "Den som registrerade sig godkände medlemstexten vid registreringen - inget samtycke tas här.",
      registrationConsentShown: "Godkände vid registreringen medlemstext {{version}}, läst på {{language}}.",
      registrationChildConsentInPerson: "{{name}} var under 18 år vid registreringen. Det egna samtycket tas personligen eller på papper, inte från formuläret - en vårdnadshavare kan läsa adressen som angavs.",
      approvalChildByDateEntered: "Enligt födelsedatumet som angetts här var {{name}} under 18 år vid registreringen. Det egna samtycket tas personligen eller på papper, inte från formuläret - en vårdnadshavare kan läsa adressen som angavs.",
      approvalChildByRecord: "Enligt den länkade postens födelsedatum var {{name}} under 18 år vid registreringen. Godkännandet länkar kontot och tar inget samtycke från formuläret; det egna samtycket tas personligen eller på papper och förs in i personens post.",
      approvalGuestTakesNoMembershipConsent: "En gäst lämnar inget medlemssamtycke, så samtycket i formuläret registreras inte. Ett personnummer som anges för gästen kräver ett samtycke som täcker det, och det tas här.",
      approvalDatesDisagreeEntered: "Födelsedatumen stämmer inte överens: registreringen angav {{given}}, det som angetts här är {{other}}.",
      approvalDatesDisagreeRecord: "Födelsedatumen stämmer inte överens: registreringen angav {{given}}, den länkade posten har {{other}}.",
      approvalYoungerCounts: "När de inte stämmer överens gäller det datum som gör personen till barn.",
      approvalUnderOwnConsentAge: "Enligt födelsedatumet i registreringen ({{date}}) var personen under 12 år vid registreringen. Ett barns registrering görs av en förälder eller vårdnadshavare, så den här kan inte godkännas: avslå den, så registrerar en förälder barnet hos församlingen.",
      pendingUnderOwnConsentAge: "Under 12 år vid registreringen - en förälder registrerar barnet hos församlingen.",
      approvalUnderOwnConsentAgeByDateEntered: "Enligt födelsedatumet som angetts här ({{date}}) var personen under 12 år vid registreringen. Ett barns registrering görs av en förälder eller vårdnadshavare, så den här kan inte godkännas: avslå den, så registrerar en förälder barnet hos församlingen.",
      approvalUnderOwnConsentAgeByRecord: "Enligt den länkade postens födelsedatum ({{date}}) var personen under 12 år vid registreringen. Ett barns registrering görs av en förälder eller vårdnadshavare, så den här kan inte godkännas: avslå den, så registrerar en förälder barnet hos församlingen.",
      approvalAgeChecking: "Kontrollerar födelsedatumen mot registreringen…",
      approvalAgeLoadFailed: "Det gick inte att kontrollera födelsedatumen mot registreringen.",
      approvalAgeLoadFailedBody: "Godkännandet väntar tills de har kontrollerats.",
      approvalAgeRecordOutsideChurch: "Den valda posten hör till en annan församling, så den kan inte kopplas härifrån, och dess födelsedatum läses inte.",
      approvalAgeRegistrationOutsideChurch: "Den här registreringen hör till en annan församling, så det är inte du som godkänner eller avvisar den.",
      nationalIdCoveredByInPersonConsent: "Omfattas av det personliga medlemssamtycke som registreras nedan.",
      consentTextsSettingsTitle: "Samtyckestexter",
      consentTextsSettingsHelp: "Den text människor godkänner, skriven av denna kyrka: medlemstexten, ansökningstexten och texten för vårdnadshavare i aktiviteter. Var och en är en text för hela församlingen. Att ansöka om en tjänst är inte att gå med i församlingens register, så de två texterna är skilda. Ett utkast visas aldrig för någon; publicering kräver svenska och engelska, och därefter kan texten inte ändras - endast en tom amharisk text kan läggas till, en gång.",
      consentTextMemberTitle: "Samtyckestext för medlemskap",
      consentTextMemberHelp: "Visas när någon blir medlem i församlingen: vid självregistrering och när samtycke tas personligen.",
      consentTextMemberNoneYet: "Ingen medlemstext har publicerats. Självregistrering och personligt medlemssamtycke är inte möjligt förrän en finns.",
      consentTextApplicationTitle: "Samtyckestext för ansökningar",
      consentTextScopeParishChip: "Hela församlingen",
      consentTextScopeMember: "För alla som går med i församlingens register.",
      consentTextScopeApplication: "För alla som ansöker om en tjänst.",
      consentTextScopeGuardian: "För föräldrar till barn som deltar i en aktivitet, med barnets aktivitet angiven i texten.",
      activityConsentTextMoved: "Samtyckestexten för vårdnadshavare finns nu bland församlingens övriga samtyckestexter.",
      activityConsentTextMovedLink: "Öppna Inställningar - Samtyckestexter",
      activityConsentTextMovedNoAccess: "Samtyckestexten för vårdnadshavare finns nu under Inställningar - Samtyckestexter, som församlingsexpeditionen sköter.",
      consentTextApplicationHelp: "Visas på det offentliga ansökningsformuläret för tjänster. Den gäller bara ansökan, inte medlemskap.",
      consentTextApplicationNoneYet: "Ingen ansökningstext har publicerats. Det offentliga ansökningsformuläret är inte tillgängligt förrän en finns.",
      declarationTitle: "Underlag för medlemskap",
      declarationHelp: "Hur och när medlemskapet godkändes, och av vem. 'Äldre än systemet' är ett giltigt svar; inget alls är det inte.",
      declarationKind: "Hur godkändes medlemskapet?",
      declarationChoose: "Välj…",
      declarationKind_SELF_REGISTRATION_APPROVED: "Självregistrering, godkänd av administratör",
      declarationKind_IN_PERSON_CONSENT: "Samtycke taget personligen",
      declarationKind_PARENT_DECLARATION: "Förälders eller vårdnadshavares anmälan (barn under 12)",
      declarationKind_OWN_DECLARATION: "Medlemmens egen anmälan (från 12 år)",
      declarationKind_PREDATES_SYSTEM_PAPER_REGISTER: "Äldre än systemet - underlaget finns i pappersregistret",
      declarationDeclaredOn: "Datum",
      declarationInPersonToday: "Ett samtycke som tas personligen dateras i dag, med personen här. Lämnat en annan dag? Registrera det inte här - skicka en samtyckesbegäran på papper, som de skriver under och daterar.",
      declarationInPersonAgreed: "Medlemmen fick se texten och godkände den",
      declarationDeclarant: "Vem anmälde barnet",
      declarationDeclarantHelp: "Vilken medlem som helst - en vårdnadshavare är giltig. Om personen är registrerad som förälder noteras, men krävs inte.",
      declarationSearchDeclarant: "Sök en medlem på namn",
      declarationChange: "Byt",
      declarationPaperReference: "Hänvisning till pappersregistret (valfritt)",
      declarationPaperReferenceExample: "t.ex. Register 2, s. 14",
      declarationRequired: "Underlag krävs för en ny församlingsmedlem - hur och när medlemskapet godkändes.",
      declarationsTitle: "Underlag för medlemskap",
      declarationRecordEvidence: "Registrera underlag",
      declarationNoneYet: "Inget underlag registrerat - medlemmen finns på listan över ofullständiga uppgifter.",
      declarationCurrent: "gällande",
      declarationRecorded: "Underlaget är registrerat",
      declarationSaveFailed: "Det gick inte att registrera underlaget",
      declarationAppendOnlyHelp: "Underlag ändras aldrig: en rättelse blir en ny post, och den senaste gäller.",
      declarationRecordedBy: "registrerat av {{name}} den {{date}}",
      declarationDescribeSelfRegistration: "Självregistrering godkänd av {{admin}} (text {{version}})",
      declarationDescribeInPerson: "Samtycke taget personligen (text {{version}}, läst på {{language}})",
      declarationDescribeParent: "Anmäld av {{declarant}}",
      declarationNotRecordedAsParent: "(inte registrerad som förälder)",
      declarationKind_PARENT_IN_PERSON: "Förälders eller vårdnadshavares samtycke, lämnat på plats (barn under 12)",
      declarationAgeUnknown: "Inget födelsedatum är registrerat. Vem som får samtycka beror på medlemmens ålder den dagen - registrera födelsedatumet för att kunna registrera ett samtycke eller en anmälan.",
      declarationAgeFailed: "Det gick inte att kontrollera vem som får samtycka den dagen. Tills det går kan bara en uppgift som är äldre än systemet registreras.",
      declarationAgeBeforeBirth: "Det datumet är före födelsedatumet.",
      declarationAgeGuardian: "{{age}} år den {{date}} - under 12, så en förälder eller vårdnadshavare samtycker, inte barnet.",
      declarationAgeOwn: "{{age}} år den {{date}} - från 12 lämnar medlemmen sitt eget samtycke.",
      declarationKindNotForAge: "Det valet passar inte medlemmens ålder detta datum - välj igen.",
      declarationParentGivingConsent: "Förälder eller vårdnadshavare som samtycker",
      declarationParentGivingConsentHelp: "Väljs ur registret. Att samtycket registreras gör dem inte till barnets förälder i registret - det erbjuds efteråt, som ett eget steg.",
      declarationParentInPersonAgreed: "Föräldern eller vårdnadshavaren godkände denna text för barnet",
      declarationDescribeParentConsent: "Samtycke {{how}} av {{declarant}}, förälder eller vårdnadshavare (text {{version}}, läst på {{language}})",
      declarationConsentInPerson: "lämnat på plats",
      declarationConsentOnPaper: "lämnat på papper",
      declarationConsentByLink: "lämnat via länk",
      declarationParentLinkOffer: "Registrera {{parent}} som förälder",
      declarationParentLinkConfirm: "Registrera {{parent}} som förälder till {{child}}? Det lägger till en familjelänk i registret.",
      declarationParentLinkYes: "Registrera länken",
      declarationParentLinkRecorded: "Förälderlänken är registrerad",
      declarationParentLinkFailed: "Förälderlänken kunde inte registreras",
      selfRegistrationUnderAge: "Ett barn under 12 kan inte registrera sig självt: samtycket till medlemskap lämnas av en förälder eller vårdnadshavare. En förälder kan registrera barnet hos församlingen.",
      memberPickerSearchFailed: "Sökningen misslyckades - försök igen.",
      memberPickerNoMatches: "Ingen i registret matchar sökningen.",
      memberPickerNotInRegister: "Finns inte i registret?",
      memberPickerRegisterFirst: "Registrera personen först",
      nationalIdConsentLabelParent: "Föräldern eller vårdnadshavaren har samtyckt till att barnets personnummer lagras (GDPR art. 9)",
      nationalIdConsentAgeUnknown: "Registrera födelsedatumet först: om samtycket är medlemmens eget eller en förälders beror på åldern.",
      nationalIdConsentAgeFailed: "Det gick inte att kontrollera vems samtycke detta är - personnumret kan inte lagras förrän det går.",
      nationalIdConsentParentRequired: "Välj den förälder eller vårdnadshavare som samtycker - under 12 är samtycket deras, inte barnets.",
      completenessTitle: "Ofullständiga medlemsuppgifter",
      completenessLanguageNotRecorded: "Aviserade på församlingens standardspråk",
      completenessLanguageColumn: "Språk",
      completenessLanguageAlert: "{{count}} personer har fått meddelanden på församlingens standardspråk eftersom inget språk är registrerat för dem - de kanske inte kan läsa dem. Registrera språket de läser på deras medlemspost: det är det som stoppar det.",
      completenessNotifiedInDefault: "Aviserad på standardspråket {{count}} gånger",
      completenessLastNotified: "senast {{date}}",
      completenessGuest: "gäst",
      completenessRecordLanguage: "Registrera deras språk",
      completenessHelp: "Församlingsmedlemmar som saknar adress, födelsedatum eller underlag för medlemskap. Gäster och avlidna visas inte. Inget här blockerar en post - här räknas och följs luckorna upp.",
      completenessStatus: "Status",
      completenessAllStatuses: "Alla utom avlidna",
      completenessInScope: "Församlingsmedlemmar",
      completenessIncomplete: "Ofullständiga",
      completenessNoAddress: "Saknar adress",
      completenessNoDateOfBirth: "Saknar födelsedatum",
      completenessNoDeclaration: "Saknar underlag",
      completenessMember: "Medlem",
      completenessMissing: "saknas",
      completenessNothingMissing: "Inget saknas.",
      completenessLoadFailed: "Det gick inte att läsa in listan",
      memberStatus_ACTIVE: "Aktiv",
      memberStatus_MOVED: "Flyttad",
      memberStatus_SUSPENDED: "Avstängd",
      memberStatusNoLongerRecorded: "{{status}} - registreras inte längre; behålls tills församlingsrådet beslutar",
      memberStatus_DEACTIVATED: "Vilande",
      memberStatus_TEMPORARY: "Tillfällig",
      memberStatus_DECEASED: "Avliden",
      maritalStatus_MARRIED: "Gift",
      maritalStatus_DIVORCED: "Skild",
      maritalStatus_WIDOWED: "Änka eller änkling",
      maritalStatus_SINGLE: "Ogift",
      maritalStatus_CHILD: "Barn",
      accountStatus_ACTIVE: "Aktivt",
      accountStatus_INACTIVE: "Inaktivt",
      accountStatus_DELETED: "Borttaget",
      accountStatus_PENDING: "Väntar på godkännande",
      importConsentTextChurch: "Kyrka vars medlemstext formulären hade",
      importPaperFormsCarriedThisText: "Pappersformulären hade den här texten, på det här språket",
      importPredatesSystem: "Posterna är äldre än systemet - underlaget finns i pappersregistret",
      importPredatesSystemHelp: "Registreras som underlag för varje församlingsmedlem. Utan det saknar medlemsraderna underlag och avvisas vid förhandsgranskningen.",
      importAddressRequiredHelp: "Varje församlingsmedlem behöver en adress (gatuadress, postnummer och ort); rader utan avvisas vid förhandsgranskningen.",
      userNameIsRequired: "Användarnamn krävs!",
      passwordIsRequired: "Lösenord krävs!",
      userNameOrPasswordIncorrect: "Användarnamn eller lösenord är felaktigt.",
      sessionExpired: "Din session har gått ut. Vänligen logga in igen.",
      signInFailedStatus: "Inloggningen misslyckades på grund av ett fel på servern (den svarade {{status}}) – inte på grund av ditt användarnamn eller lösenord. Försök igen om en stund; om det fortsätter, kontakta en administratör.",
      signInNoSession: "Servern godtog ditt användarnamn och lösenord men skickade ingen session tillbaka – ett fel på servern. Försök igen; om det fortsätter, kontakta en administratör.",
      signInNoAnswer: "Servern svarade inte, så ditt användarnamn och lösenord kontrollerades inte. Kontrollera din anslutning och försök igen.",
      signInAcceptedThenFailed: "Ditt användarnamn och lösenord godtogs, men nästa sida kunde inte öppnas. Ladda om sidan för att fortsätta.",
      login: "Logga in",
      dontHaveAnAccount:
        "Har du inte ett konto än? Vänligen mejla dev@medhanealem.se för att få ett konto.",
      forgotPassword: "Glömt lösenord?",
      userName: "Användarnamn",
      password: "Lösenord",
      loginToSystem: "Logga in till systemet",
      appName: "CMMS",
      accessDeniedTitle: "Åtkomst nekad",
      accessDeniedBody: "Du har inte behörighet att visa denna sida. Om du tror att detta är ett misstag, kontakta en administratör.",
      backToDashboard: "Tillbaka till instrumentpanelen",
      backToStart: "Tillbaka till startsidan",
      editMember: "Redigera medlem",
      memberDetails: "Medlemsdetaljer",
      edit: "Redigera",
      update: "Uppdatera",
      updateSuccessful: "Medlem uppdaterades framgångsrikt",
      updateError: "Fel vid uppdatering av medlem",
      errorFetchingMember: "Fel vid hämtning av medlemsinformation",
      memberLoadFailedTitle: "Det gick inte att läsa in medlemmen",
      memberLoadFailedBody: "Formuläret visas inte, så inget kan sparas över medlemmens uppgifter förrän de har lästs in.",
      memberFormAskedLoadFailedTitle: "Det gick inte att kontrollera vad formuläret frågar efter",
      memberFormAskedLoadFailedBody: "Civilstånd visas för alla tills det går; inget sparas för någon under 18.",
      actions: "Åtgärder",
      registerChild: "Registrera barn",
      relativeParishMemberOnMemberForm: "En anhörig som läggs till här registreras som gäst. En församlingsmedlem behöver en medlemsförklaring: registrera hen i medlemsformuläret och sök sedan fram hen här med namn.",
      showFamily: "Visa familj",
      familyTree: "Familjeträd",
      spouse: "Make/Maka",
      children: "Barn",
      parents: "Föräldrar",
      noFamilyMembers: "Inga familjemedlemmar hittades",
      searchingMembers: "Söker medlemmar...",
      selectExistingMember: "Välj en befintlig medlem eller skapa ny",
      relationshipRegistered: "Relation registrerad framgångsrikt",
      errorRegisteringRelationship: "Fel vid registrering av relation",
      confirmDeleteMember: "Är du säker på att du vill ta bort den här medlemmen?",
      memberRemoveTitle: "Ta bort {{name}} ur registret?",
      memberRemoveNotRestorable: "Posten markeras som borttagen och visas inte längre. Ingen skärm tar tillbaka den.",
      memberRemoveKeeps: "Ingenting raderas. Följande uppgifter nämner personen och lämnas oförändrade: {{list}}.",
      memberRemoveNothingRefers: "Inget annat i registret hänvisar till personen.",
      memberRemoveLogin: "Inloggningen stängs av.",
      memberRemoveBaptisms_one: "{{count}} dop",
      memberRemoveBaptisms_other: "{{count}} dop",
      memberRemoveMarriages_one: "{{count}} vigsel",
      memberRemoveMarriages_other: "{{count}} vigslar",
      memberRemoveConsents_one: "{{count}} samtycke",
      memberRemoveConsents_other: "{{count}} samtycken",
      memberRemoveOthers_one: "{{count}} övrig uppgift",
      memberRemoveOthers_other: "{{count}} övriga uppgifter",
      memberRemoveListAnd: " och ",
      memberRemoveQueuedMessages_one: "{{count}} köat meddelande",
      memberRemoveQueuedMessages_other: "{{count}} köade meddelanden",
      memberRemoveOpenRequests_one: "{{count}} öppen samtyckesförfrågan",
      memberRemoveOpenRequests_other: "{{count}} öppna samtyckesförfrågningar",
      memberRemoveCancelled_one: "{{what}} avbryts.",
      memberRemoveCancelled_other: "{{what}} avbryts.",
      memberRemoveCancelledBoth: "{{first}} och {{second}} avbryts.",
      memberChangeQuestion: "Varför ändrar du den här posten?",
      memberChangeEnteredInError: "Den registrerades av misstag",
      memberChangeDeceased: "Personen har avlidit",
      memberChangeTransferHint: "För att registrera en flytt till en annan församling, använd <1>{{transfer}}</1>.",
      memberDeceasedTitle: "Registrera {{name}} som avliden?",
      memberDeceasedKeeps: "Ingenting tas bort: posten behålls, markerad som avliden, och allt som nämner personen finns kvar.",
      memberDeceasedFinal: "Detta kan inte ångras härifrån. Om en fithat registrerar dödsfallet återställer en ogiltigförklaring av fithaten statusen.",
      memberDeceasedConfirm: "Registrera som avliden",
      memberRecordedDeceased: "{{name}} registrerades som avliden.",
      memberRecordDeceasedFailed: "{{name}} kunde inte registreras som avliden. Ingenting ändrades.",
      memberRemoveConfirm: "Ta bort ur registret",
      memberRemoveImpactFailed: "Det gick inte att hämta vad borttagningen påverkar, så ingenting har tagits bort.",
      memberRemovedSuccessfully: "{{name}} togs bort ur registret.",
      memberRemoveFailed: "{{name}} kunde inte tas bort. Ingenting ändrades.",
      memberChangeOutcomeUnknown: "Servern svarade inte, så det är inte känt om {{name}} ändrades. Titta i listan igen innan du försöker på nytt.",
      memberDeletedSuccessfully: "Medlem borttagen framgångsrikt",
      errorDeletingMember: "Fel vid borttagning av medlem",
      selectDate: "Välj datum",
      registerNewMember: "Registrera ny medlem",
      close: "Stäng",
      cancel: "Avbryt",
      confirm: "Bekräfta",
      // Baptism translations
      registerBaptism: "Registrera dop",
      editBaptism: "Redigera dop",
      correctBaptism: "Korrigera dopanteckning",
      baptismsListTitle: "Doplista",
      registerNewBaptism: "Registrera nytt dop",
      personBeingBaptized: "Person som döps",
      spiritualFather: "Andlig fader",
      priest: "Präst",
      selectPriest: "Välj präst",
      baptismDate: "Dopdatum",
      pleaseSelectAllMembers: "Välj alla nödvändiga medlemmar",
      baptismCreatedSuccessfully: "Dop skapat framgångsrikt",
      baptismUpdatedSuccessfully: "Dop uppdaterat framgångsrikt",
      errorSavingBaptism: "Fel vid sparande av dop",
      errorFetchingBaptism: "Fel vid hämtning av dopdetaljer",
      failedToFetchBaptisms: "Misslyckades med att hämta dop",
      confirmDeleteBaptism: "Är du säker på att du vill ta bort detta dop?",
      baptismDeletedSuccessfully: "Dop borttaget framgångsrikt",
      errorDeletingBaptism: "Fel vid borttagning av dop",
      baptismId: "Dop-ID",
      personBaptized: "Person döpt",
      errorFetchingCertificate: "Fel vid hämtning av certifikat",
      // Marriage translations
      registerMarriage: "Registrera äktenskap",
      editMarriage: "Redigera äktenskap",
      correctMarriage: "Korrigera äktenskapsanteckning",
      marriagesListTitle: "Äktenskapslista",
      registerNewMarriage: "Registrera nytt äktenskap",
      firstSpouse: "Första maken/makan",
      secondSpouse: "Andra maken/makan",
      marriageDate: "Äktenskapsdatum",
      marriageCreatedSuccessfully: "Äktenskap skapat framgångsrikt",
      marriageUpdatedSuccessfully: "Äktenskap uppdaterat framgångsrikt",
      errorSavingMarriage: "Fel vid sparande av äktenskap",
      errorFetchingMarriage: "Fel vid hämtning av äktenskapsdetaljer",
      failedToFetchMarriages: "Misslyckades med att hämta äktenskap",
      confirmDeleteMarriage: "Är du säker på att du vill ta bort detta äktenskap?",
      marriageDeletedSuccessfully: "Äktenskap borttaget framgångsrikt",
      errorDeletingMarriage: "Fel vid borttagning av äktenskap",
      marriageId: "Äktenskaps-ID",
      marriages: "Äktenskap",
      certificates: "Certifikat",
      englishCertificate: "Engelskt certifikat",
      swedishCertificate: "Svenskt certifikat",
      amharicCertificate: "Amhariskt certifikat",
      currentMember: "Nuvarande medlem",
      parent: "Förälder",
      child: "Barn",
      membershipCertificate: "Medlemsbevis",

      // R4 task 8: fithat register, corrections, worklists, certificate reissue/verification
      fithat: "Fithat",
      fithatRecords: "Fithat-poster",
      fithatRecordsListTitle: "Fithat-postlista",
      registerNewFithat: "Registrera ny fithat",
      registerFithat: "Registrera fithat",
      correctFithat: "Korrigera fithat-post",
      departedPerson: "Avliden person",
      fithatDate: "Fithat-datum",
      fithatCreatedSuccessfully: "Fithat-post skapad",
      fithatUpdatedSuccessfully: "Fithat-post uppdaterad",
      errorSavingFithat: "Fel vid sparande av fithat-post",
      errorFetchingFithat: "Fel vid hämtning av fithat-post",
      failedToFetchFithatRecords: "Misslyckades med att hämta fithat-poster",
      fithatRecordId: "Fithat-post-ID",

      officiatingChurch: "Officierande kyrka",
      hostingChurch: "Värdkyrka",
      spouse1HomeChurch: "Första makens hemkyrka",
      spouse2HomeChurch: "Andra makens hemkyrka",
      baptismalNameLabel: "Dopnamn (የክርስትና ስም)",
      fatherName: "Faderns namn",
      motherName: "Moderns namn",
      baptizedPersonSection: "Person som ska döpas",
      godparentSection: "Gudförälder",

      correctRecord: "Korrigera",
      correctionReason: "Anledning till korrigering",
      correctionReasonRequired: "En anledning till denna korrigering krävs",
      voidRecordAction: "Ogiltigförklara",
      voidReasonLabel: "Anledning till ogiltigförklaring",
      voidReasonRequired: "En anledning till att ogiltigförklara denna post krävs",
      voidVsCorrectionExplanation: "Att ogiltigförklara skiljer sig från en korrigering. Använd Korrigera när uppgifterna är fel men händelsen ägde rum. Använd Ogiltigförklara endast när denna post aldrig borde ha funnits - den finns kvar i systemet för granskningsändamål, men tas bort från listor och bekräftas inte längre vid intygsverifiering.",
      recordVoidedSuccessfully: "Posten ogiltigförklarad",
      errorVoidingRecord: "Fel vid ogiltigförklaring av posten",
      voidPreviewFailed: "Det gick inte att hämta vad ogiltigförklaringen skulle återställa, så den kan inte bekräftas än.",
      fithatVoidAnotherRecordsTheDeath: "En annan fithat registrerar fortfarande personens död: att ogiltigförklara den här återställer ingenting, och inget som den stoppade ska skickas igen.",
      fithatVoidStatusRestored: "Personens status återgår till {{status}}, som den var innan Avliden följde på denna fithat.",
      fithatVoidStatusRestoredNone: "Personens status återgår till vad den var innan Avliden följde på denna fithat: ingen var registrerad.",
      fithatVoidDeceasedStays: "Personens post säger Avliden, och inget som sparats säger att det följde på denna fithat, så det står kvar - och ingen inloggning kommer tillbaka med ogiltigförklaringen.",
      fithatVoidLoginRestored: "Inloggningen som denna fithat stängde av återställs som den var innan.",
      fithatVoidLoginChangedSince: "Inloggningen som denna fithat stängde av har ändrats sedan dess, så den lämnas som den är.",
      fithatVoidLoginStaysEndedMembershipEnded: "Inloggningen som denna fithat stängde av förblir avstängd: posten har tagits bort ur registret sedan dess.",
      fithatVoidNotRecorded: "Inte allt som stoppades för personen sparades: en fithat som registrerades innan det började sparas lämnade ingen lista, så det den stoppade kan inte listas här.",
      fithatVoidStoppedTitle: "Det här stoppade denna fithat. Ingenting skickas igen av sig självt - skicka igen det som ska iväg:",
      fithatVoidStoppedMessage: "{{type}} till {{to}}, skulle skickas {{due}}",
      fithatVoidStoppedMessageEvent: "- för händelsen {{when}}",
      fithatVoidStoppedLinkRequest: "En begäran om samtycke via länk till {{to}}, om {{about}}, skickad {{sent}}",
      fithatVoidStoppedPaperRequest: "En begäran om samtycke på papper till {{to}}, om {{about}}, gjord {{sent}}",
      fithatVoidStoppedRequestHadExpired: "- länken hade redan gått ut",
      fithatVoidStoppedToSomeoneOutside: "någon som inte finns i registret",
      fithatVoidStoppedElsewhere: "Något som den stoppade för en person i en annan kyrka - visas inte här.",
      fithatVoidStoppedNothing: "Den stoppade inget meddelande och ingen begäran om samtycke.",
      notificationType_BOOKING_CONFIRMATION: "Bokningsbekräftelse",
      notificationType_APPLICATION_STATUS_CHANGE: "Ändrad status för ansökan",
      notificationType_APPLICATION_VERIFICATION: "Länk för att bekräfta ansökan",
      notificationType_USER_ACCOUNT_PENDING: "Kontoansökan mottagen",
      notificationType_USER_ACCOUNT_APPROVED: "Kontot godkänt",
      notificationType_USER_ACCOUNT_DUPLICATE_SIGNUP_ATTEMPT: "Meddelande om en andra registrering",
      notificationType_BOOKING_REMINDER: "Bokningspåminnelse",
      notificationType_CLERGY_NUDGE: "Påminnelse till en präst om en väntande bokning",
      notificationType_BAPTISM_TIMING_REMINDER: "Påminnelse om tid för dop",
      notificationType_ACTIVITY_SESSION_INVITATION: "Inbjudan till ett tillfälle",
      notificationType_ACTIVITY_SESSION_REMINDER: "Påminnelse om ett tillfälle",
      notificationType_ACTIVITY_SESSION_INVITATION_GUARDIAN: "Inbjudan till ett tillfälle (till en vårdnadshavare)",
      notificationType_ACTIVITY_SESSION_REMINDER_GUARDIAN: "Påminnelse om ett tillfälle (till en vårdnadshavare)",
      notificationType_CONSENT_REQUEST: "Begäran om samtycke",
      notificationType_CONSENT_REQUEST_GUARDIAN: "Begäran om samtycke (till en vårdnadshavare)",
      notificationType_CONSENT_RECEIPT: "Kvitto på samtycke",
      notificationType_CONSENT_RECEIPT_GUARDIAN: "Kvitto på samtycke (till en vårdnadshavare)",
      notificationType_CONSENT_LINK_RESEND: "Samtyckeslänk skickad igen på begäran",
      notificationType_CONSENT_LINK_RESEND_GUARDIAN: "Samtyckeslänk skickad igen på begäran (till en vårdnadshavare)",
      notificationType_CONSENT_WITHDRAWN_GUARDIAN: "Samtycke återkallat (till en vårdnadshavare)",
      notificationType_CONSENT_WITHDRAWN: "Samtycke återkallat",
      notificationType_CONSENT_WITHDRAWN_STAFF: "Samtycke återkallat (till personalen)",
      certificateRecordVoided: "Denna post har ogiltigförklarats",
      viewHistory: "Historik",
      correctionHistory: "Ändringshistorik",
      errorFetchingCorrectionHistory: "Fel vid hämtning av ändringshistorik",
      noCorrectionsYet: "Inga korrigeringar har registrerats ännu.",
      correctedBy: "Korrigerad av",
      correctedAt: "Korrigerad den",
      viewChanges: "Visa ändringar",

      generateCertificate: "Generera",
      reissueCertificate: "Utfärda igen",
      reissueReason: "Anledning till nyutfärdande",
      reissueReasonRequired: "En anledning till nyutfärdandet krävs",
      reissueLanguage: "Språk",
      referenceNumberLabel: "Referensnr",
      certificateDisclaimerTitle: "Detta är inte det färdiga certifikatet",
      certificateDisclaimerBody: "Detta dokument är inte giltigt förrän det är fysiskt stämplat och undertecknat av den utfärdande kyrkan. Det bekräftar att posten skapades - det är inte det färdiga certifikatet.",
      confirmAndDownload: "Jag förstår - Ladda ner",
      certificateReissuedSuccessfully: "Certifikatet utfärdades igen",
      errorReissuingCertificate: "Fel vid nyutfärdande av certifikat",

      verifyCertificateTitle: "Verifiera ett certifikat",
      certificateReferenceNumberInput: "Certifikatets referensnummer",
      verifyCertificateButton: "Verifiera",
      certificateRecordValid: "Denna certifikatpost är äkta.",
      certificateRecordNotFound: "Ingen certifikatpost hittades för detta referensnummer.",
      issuingChurch: "Utfärdande kyrka",
      issueDate: "Utfärdandedatum",

      pendingReminders: "Väntande dopåminnelser",
      dueForReminder: "Förfallen för påminnelse",
      timingUnresolved: "Tidpunkt olöst",
      timingUnresolvedNote: "Kön är inte registrerat för dessa medlemmar, så tidpunkten för 40:e/80:e dagen kan inte beräknas. Registrera deras kön för att lösa detta.",
      reminderDayCount: "Dag",
      dueDate: "Förfallodatum",
      needsAttention: "Kräver uppmärksamhet",
      allRecords: "Alla",

      pendingEndings: "Väntande äktenskapsavslut",
      endMarriageAction: "Avsluta äktenskap",
      endReasonLabel: "Anledning till avslut",
      spouseDeceased: "Make/maka avliden",
      dissolved: "Upplöst",
      endedAtLabel: "Avslutat den",
      endedAtOrNotKnownRequired: "Ange dagen då det upphörde, eller markera att den är okänd",
      marriageEndDayNotKnownToggle: "Dagen då det upphörde är okänd",
      marriageEndDayNotKnown: "Okänd",
      endNoteLabel: "Anteckning (valfritt)",
      marriageEndedSuccessfully: "Äktenskapsavslut registrerat",
      errorEndingMarriage: "Fel vid registrering av äktenskapsavslut",
      spouseName: "Make/maka",
      deceasedMember: "Avliden medlem",

      pendingDeceasedStatus: "Väntande statusuppdateringar för avlidna",
      currentStatusLabel: "Nuvarande status",
      updateMemberStatus: "Uppdatera medlemsstatus",

      administration: "Administration",
      manageUsers: "Hantera användare",
      manageFamily: "Hantera familj",
      pendingMinutesApproval: "Väntande protokollgodkännande",
      failedToFetchPendingMinutes: "Kunde inte hämta väntande protokoll",
      noPendingMinutes: "Inga väntande protokoll att granska",
      submittedBy: "Skickad av",
      submittedAt: "Skickad",
      meeting: "Möte",
      view: "Visa",
      minutesApprovedSuccessfully: "Protokoll godkänt",
      minutesRejectedSuccessfully: "Protokoll avvisat",
      users: "Användare",
      user: "Användare",
      createUser: "Skapa användare",
      editUser: "Redigera användare",
      userCreatedSuccessfully: "Användare skapad",
      userUpdatedSuccessfully: "Användare uppdaterad",
      errorCreatingUser: "Fel vid skapande av användare",
      errorUpdatingUser: "Fel vid uppdatering av användare",
      confirmDeleteUser: "Är du säker på att du vill ta bort den här användaren?",
      userDeletedSuccessfully: "Användare borttagen",
      errorDeletingUser: "Fel vid borttagning av användare",
      associatedMember: "Associerad medlem",
      accountStatus: "Kontostatus",
      roles: "Roller",
      searchMember: "Sök efter en medlem...",
      passwordIsRequiredForNew: "Lösenord krävs för nya användare",
      passwordMinLength: "Lösenordet måste vara minst 6 tecken",
      usernameMinLength: "Användarnamnet måste vara minst 3 tecken",
      failedToFetchUsers: "Misslyckades med att hämta användare",
      failedToFetchRoles: "Misslyckades med att hämta roller",
      failedToFetchUserDetails: "Misslyckades med att hämta användardetaljer",
      atLeastOneRoleRequired: "Minst en roll krävs",
      emailIsRequired: "E-post krävs",
      leaveBlankToKeepCurrent: "Lämna tomt för att behålla nuvarande lösenord",
      usernameIsRequired: "Användarnamn krävs",
      passwordsDoNotMatch: "Lösenorden matchar inte",
      firstNameIsRequired: "Förnamn krävs",
      lastNameIsRequired: "Efternamn krävs",
      confirmPassword: "Bekräfta lösenord",
      registerAccountTitle: "Skapa ett konto",
      registerAccountSubtitle: "Fyll i dina uppgifter nedan - en administratör granskar och godkänner ditt konto.",
      personalNumberOptionalHelp: "Valfritt. Om du inte har ett svenskt personnummer ännu kan du lämna detta tomt - du kan ändå registrera dig.",
      registrationSubmissionFailed: "Registreringen misslyckades - kontrollera formuläret och försök igen.",
      registrationSubmittedTitle: "Tack",
      registrationSubmittedBody: "Församlingen skriver bara till en adress som den vet är din. Kontakta församlingen om du har frågor.",
      backToLogin: "Tillbaka till inloggning",
      forgotPasswordNoOnlineReset: "Lösenord kan inte återställas på nätet ännu.",
      forgotPasswordContactAdmin: "Kontakta församlingens administratör. Hen kan ge dig ett nytt lösenord.",
      alreadyHaveAnAccount: "Har du redan ett konto?",
      pendingAccounts: "Väntande konton",
      pendingAccountsSubtitle: "Självregistreringar som väntar på granskning",
      noPendingAccounts: "Inga väntande konton",
      pendingAccountsLoadFailed: "Det gick inte att läsa in kontona som väntar på godkännande",
      pendingAccountsLoadFailedBody: "Det betyder inte att inga väntar.",
      requestedIdentity: "Begärd identitet",
      requestedOn: "Begärt den",
      rejectAccount: "Avvisa konto",
      registrationRejectionReason: "Orsaken",
      rejectionReasonOtherParish: "Medlem i en annan församling",
      rejectionReasonContactOffice: "En annan orsak",
      rejectionDeletesRegistration: "Registreringen och uppgifterna i den raderas.",
      rejectionReasonOneOfTwo: "Välj en av de två orsakerna.",
      rejectionConfirmTitle: "Avvisa registreringen?",
      rejectionConfirmMessage: "Avvisa registreringen från {{name}}, med orsaken: {{reason}}? Registreringen och uppgifterna i den raderas. Det går inte att ångra.",
      rejectionConfirmYes: "Avvisa och radera",
      registrationRejectedNoNotice: "Registreringen är avvisad och raderad. Inget meddelande skickades: adressen som angavs är inte verifierad.",
      registrationRejectedNoticeSent: "Registreringen är avvisad och raderad. Personen får veta det via e-post, på det språk registreringen gjordes på.",
      accountApprovedSuccessfully: "Kontot godkändes",
      accountRejectedSuccessfully: "Kontot avvisades",
      errorApprovingAccount: "Det gick inte att godkänna kontot",
      errorRejectingAccount: "Det gick inte att avvisa kontot",
      failedToFetchPendingAccounts: "Det gick inte att hämta väntande konton",
      consentBasisLabel: "Rättslig grund för att lagra detta personnummer",
      consentBasisSelfSubmitted: "Självregistrerat (sökanden har själv uppgett det)",
      consentBasisReligiousBody: "Registrerat av kyrkan för medlemmens räkning",
      consentBasisRequiredForPersonalNumber: "En rättslig grund krävs när ett personnummer anges",
      myAccount: "Mitt konto",
      myProfile: "Min profil",
      myApplications: "Mina ansökningar",
      myBookings: "Mina bokningar",
      myCertificates: "Mina intyg",
      failedToFetchMyProfile: "Det gick inte att hämta din profil",
      failedToFetchMyBookings: "Det gick inte att hämta dina bokningar",
      failedToFetchMyCertificates: "Det gick inte att hämta dina intyg",
      myProfileReadOnlyNote: "Denna information är skrivskyddad. Kontakta din kyrkoadministratör för att begära en rättelse.",
      fullName: "Fullständigt namn",
      confessionFather: "Biktfader",
      confessionFatherNoneRecorded: "Inget registrerat",
      notificationChannelPreference: "Preferens för aviseringskanal",
      preferredLanguage: "Föredraget språk",
      applicationsAppearOnceReviewed: "Dina ansökningar visas här när församlingsexpeditionen har granskat dem.",
      noBookingsYet: "Du har inga bokningar ännu.",
      noCertificatesYet: "Du har inga intyg ännu.",
      applicationDate: "Ansökningsdatum",
      clergyMember: "Präst",
      certificateType: "Intygstyp",
      download: "Ladda ner",
      notAuthorizedToManageThisClergyMember: "Du kan bara hantera din egen tillgänglighet, eller en prästs om du är kyrkoadministratör.",
      addChurch: "Lägg till kyrka",
      editChurch: "Redigera kyrka",
      manageChurches: "Hantera kyrkor",
      failedToFetchChurches: "Misslyckades med att hämta kyrkor",
      failedToFetchChurchDetails: "Misslyckades med att hämta kyrkodetaljer",
      churchDeletedSuccessfully: "Kyrkan togs bort framgångsrikt",
      selectCountry: "Välj land",
      errorDeletingChurch: "Fel vid borttagning av kyrka",
      churchAddedSuccessfully: "Kyrka tillagd framgångsrikt",
      churchUpdatedSuccessfully: "Kyrka uppdaterad framgångsrikt",
      errorSavingChurch: "Fel vid sparande av kyrka",
      confirmDeleteChurch: "Är du säker på att du vill ta bort denna kyrka?",
      failedToFetchCountries: "Misslyckades med att hämta länder",
      maritalStatusesLoadFailed: "Civilstånden kunde inte hämtas",
      pleaseFieldAllRequired: "Vänligen fyll i alla obligatoriska fält",
      street: "Gata",
      countryName: "Land",
      // Employee translations
      addEmployee: "Lägg till anställd",
      editEmployee: "Redigera anställd",
      manageEmployees: "Hantera kyrkanställda",
      memberFullName: "Medlemmens fullständiga namn",
      professionType: "Yrkestyp",
      failedToFetchEmployees: "Misslyckades med att hämta anställda",
      failedToFetchEmployeeDetails: "Misslyckades med att hämta anställningsuppgifter",
      employeeDeletedSuccessfully: "Anställd borttagen framgångsrikt",
      errorDeletingEmployee: "Fel vid borttagning av anställd",
      employeeAddedSuccessfully: "Anställd tillagd framgångsrikt",
      employeeUpdatedSuccessfully: "Anställd uppdaterad framgångsrikt",
      errorSavingEmployee: "Fel vid sparande av anställd",
      confirmDeleteEmployee: "Är du säker på att du vill ta bort denna anställd?",
      searchByName: "Sök efter namn...",
      // Service Member translations
      registerServiceMember: "Registrera tjänstemedlem",
      manageServiceMembers: "Hantera tjänstemedlemmar",
      serviceName: "Tjänstens namn",
      selectService: "Välj tjänst",
      failedToFetchServiceMembers: "Misslyckades med att hämta tjänstemedlemmar",
      failedToFetchServices: "Misslyckades med att hämta tjänster",
      serviceMemberDeletedSuccessfully: "Tjänstemedlem borttagen framgångsrikt",
      errorDeletingServiceMember: "Fel vid borttagning av tjänstemedlem",
      serviceMemberRegisteredSuccessfully: "Tjänstemedlem registrerad framgångsrikt",
      errorRegisteringServiceMember: "Fel vid registrering av tjänstemedlem",
      confirmDeleteServiceMember: "Är du säker på att du vill ta bort denna tjänstemedlemsregistrering?",
      failedToFetchDetails: "Misslyckades med att hämta detaljer",
      memberServiceUpdated: "Tjänstemedlemsinformation uppdaterad",
      memberServiceAdded: "Tjänstemedlem tillagd",
      errorSavingDetails: "Fel vid sparande av detaljer",
      editServiceMember: "Redigera tjänstemedlem",
      beginDate: "Startdatum",
      endDate: "Slutdatum",
      areYouSureYouWantToDeleteThisMember: "Är du säker på att du vill ta bort den här medlemmen?",
      // Church Service Management translations
      manageChurchServices: "Hantera kyrkotjänster",
      addService: "Lägg till tjänst",
      editService: "Redigera tjänst",
      selectServiceType: "Välj tjänstetyp",
      partOfSundaySchool: "Del av söndagsskolan",
      partOfSundaySchoolHelp: "Dess aktiviteter är klasser, och söndagsskolans personal ansvarar för dess medlemmar (alla mellan 4 och 35 år hör till söndagsskolan).",
      // The public pages' staff screens (session 7)
      publicPageMenu: "Offentlig sida",
      roleHelpPublicEditor: "Redigerar församlingens offentliga sida: dess profil, gudstjänster, högtider och fastor. Ger ingen åtkomst till medlemmar, familjer eller bokningar.",
      publicContentSaveFailed: "Det sparades inte.",
      publicContentReasonRequired: "Måste fyllas i.",
      publicContentReasonInvalid: "Godtas inte som det är skrivet.",
      publicContentReasonPersonnummer: "Ser ut som ett personnummer, och det står aldrig i en offentlig text.",
      publicContentReasonAddress: "Innehåller en e-postadress, och det gör den här texten aldrig.",
      publicContentReasonPersonsAddress: "Det är en persons adress, inte församlingens.",
      publicContentReasonDomains: "Inte under församlingens egna domäner.",
      publicContentReasonAdministrator: "Bara en administratör kan ange detta.",
      publicContentReasonTooLong: "För lång.",
      publicCalendarTitle: "Offentlig kalender",
      publicCalendarChurchFailed: "Vilken församling det gäller kunde inte läsas in.",
      publicCalendarHelp: "Gudstjänster, högtider och fastor som visas på den offentliga sidan. Lektioner och deras uppehåll anges under Lektioner på den offentliga sidan.",
      publicCalendarYear: "År",
      publicCalendarYearOption: "{{year}} (etiopiskt) · {{from}} – {{to}}",
      publicCalendarNew: "Ny händelse",
      publicCalendarEvents: "Händelser",
      publicCalendarLoadFailed: "Kalenderns händelser kunde inte läsas in.",
      publicCalendarNone: "Inga händelser under {{year}}.",
      publicCalendarPublished: "Publicerad",
      publicCalendarDraft: "Utkast",
      publicCalendarKindService: "Gudstjänst",
      publicCalendarKindFeast: "Högtid",
      publicCalendarKindFast: "Fasta",
      publicCalendarKindOther: "Annat",
      publicCalendarRepeatsOnceOn: "En gång, {{date}}",
      publicCalendarRepeatsWeeklyOn: "Varje vecka, {{weekday}}",
      publicCalendarRepeatsMonthlyOn: "Varje etiopisk månad, dag {{day}}",
      publicCalendarRepeatsYearlyOn: "Varje år, {{month}} {{day}}",
      publicCalendarRuleLoadFailed: "Händelsen kunde inte läsas in.",
      publicCalendarEditor: "Redigera händelse",
      publicCalendarEditing: "Redigera: {{title}}",
      publicCalendarNewRule: "Ny händelse (ett utkast tills den publiceras)",
      publicCalendarTitles: "Titel",
      publicCalendarLines: "Rad under titeln",
      publicCalendarKind: "Typ",
      publicCalendarRepeats: "Upprepas",
      publicCalendarRepeatOnce: "En gång, på ett datum",
      publicCalendarRepeatWeekly: "Varje vecka på",
      publicCalendarRepeatMonthly: "Varje etiopisk månad på dag",
      publicCalendarRepeatYearly: "Varje år på",
      publicCalendarDate: "Datum",
      publicCalendarWeekday: "Veckodag",
      publicCalendarEthMonth: "Etiopisk månad",
      publicCalendarEthDay: "Dag",
      publicCalendarChoose: "Välj",
      publicCalendarStarts: "Börjar",
      publicCalendarEnds: "Slutar (valfritt)",
      publicCalendarFor: "Gäller",
      publicCalendarWholeYear: "Hela året {{year}} ({{from}} – {{to}})",
      publicCalendarFrom: "Från",
      publicCalendarUntil: "Till och med",
      publicCalendarNextDates: "Nästa datum",
      publicCalendarNoNextDates: "Inga kommande datum.",
      publicCalendarCancelled: "Inställda datum",
      publicCalendarNoneCancelled: "Inga inställda.",
      publicCalendarReasonShown: "Skäl som visas på den offentliga sidan: {{reason}}",
      publicCalendarUndo: "Ångra",
      publicCalendarCancelOne: "Ställ in ett datum, med ett skäl",
      publicCalendarCancelWhich: "Datumet",
      publicCalendarReason: "Skäl som visas på den offentliga sidan",
      publicCalendarCancelDate: "Ställ in datumet",
      publicCalendarPublish: "Publicera",
      publicCalendarUnpublish: "Avpublicera",
      publicProfileTitle: "Församlingens offentliga sida",
      publicProfileHelp: "Det den offentliga sidan säger om församlingen. En text som lämnas tom på ett språk visas på ett annat.",
      publicProfileLoadFailed: "Församlingens offentliga sida kunde inte läsas in.",
      publicProfileNameAm: "Församlingens namn på amhariska",
      publicProfileTagline: "Underrubrik",
      publicProfileWelcome: "Välkommen",
      publicProfileFirstVisit: "Första besöket",
      publicProfileDirections: "Hitta hit",
      publicProfileDiocese: "Stiftsrad",
      publicProfileEmail: "Offentlig e-post",
      publicProfileMapUrl: "Kartlänk (https)",
      publicProfilePhoneShown: "Visa församlingens telefonnummer",
      publicProfileYes: "Ja",
      publicProfileNo: "Nej",
      publicProfileNotAnswered: "Inte besvarat än",
      publicProfileByAppointment: "Efter överenskommelse",
      publicProfileCardTitle: "Titel",
      publicProfileCardLine: "Rad",
      publicProfileAddCard: "Lägg till ett kort",
      publicProfileRemoveCard: "Ta bort kortet",
      publicProfileSaved: "Sparat.",
      publicProfilePublished: "Publicerad.",
      publicProfileUnpublished: "Avpublicerad.",
      publicProfileDomains: "Församlingens egna e-postdomäner",
      publicProfileDomainsHelp: "Den offentliga e-postadressen, och en avdelnings kontakt, måste ligga under någon av dessa. Anges av en administratör.",
      classCalendarTitle: "Lektioner på den offentliga sidan",
      classCalendarHelp: "Det den offentliga sidan säger om söndagsskolans lektioner, och dagarna utan lektioner. En lektions termin, veckodag och tillfällen anges under Aktiviteter.",
      classCalendarLoadFailed: "Lektionerna kunde inte läsas in.",
      classCalendarDepartments: "Avdelningar",
      classCalendarNoDepartments: "Ingen avdelning i söndagsskolan.",
      classCalendarClasses: "Lektioner",
      classCalendarNoClasses: "Ingen aktiv lektion.",
      classCalendarShown: "Visas på den offentliga sidan",
      classCalendarPublicName: "Offentligt namn",
      classCalendarLine: "Rad",
      classCalendarContact: "Kontakt (e-post)",
      classCalendarSaved: "Sparat.",
      classCalendarTerm: "Termin: {{from}} – {{to}}",
      classCalendarAgeFrom: "Ålder från",
      classCalendarAgeTo: "Ålder till",
      classCalendarDays: "Dagar utan lektioner",
      classCalendarNoDays: "Inga.",
      classCalendarRename: "Ändra det offentliga namnet",
      classCalendarRemove: "Ta bort",
      classCalendarRemoveConfirm_one: "Om dagen tas bort återkommer {{count}} lektionstillfälle som bara den ställde in.",
      classCalendarRemoveConfirm_other: "Om dagen tas bort återkommer {{count}} lektionstillfällen som bara den ställde in.",
      classCalendarAddDay: "Lägg till en dag utan lektioner",
      classCalendarDayClass: "Lektion",
      classCalendarDayInternalName: "Internt namn (visas inte)",
      classCalendarDayPublicName: "Offentligt skäl",
      classCalendarDayAdded_one: "Tillagd: {{count}} lektionstillfälle inställt.",
      classCalendarDayAdded_other: "Tillagd: {{count}} lektionstillfällen inställda.",
      priesthood: "Prästerskap",
      deaconship: "Diakonat",
      sundaySchoolService: "Söndagsskola",
      serviceAddedSuccessfully: "Tjänst tillagd framgångsrikt",
      serviceUpdatedSuccessfully: "Tjänst uppdaterad framgångsrikt",
      serviceDeletedSuccessfully: "Tjänst borttagen framgångsrikt",
      errorAddingService: "Fel vid tillägg av tjänst",
      errorUpdatingService: "Fel vid uppdatering av tjänst",
      errorDeletingService: "Fel vid borttagning av tjänst",
      confirmDeleteService: "Är du säker på att du vill ta bort denna kyrkotjänst?",
      failedToFetchChurchServices: "Misslyckades med att hämta kyrkotjänster",
      noServicesFound: "Inga tjänster hittades",
      failedToFetchServiceDetails: "Misslyckades med att hämta tjänstedetaljer",
      // Dashboard translations
      activeMembers: "Medlemmar",
      employees: "Anställda",
      currentActiveMembers: "Nuvarande aktiva medlemmar",
      totalEmployees: "Totalt antal anställda",
      totalBaptisms: "Totalt antal dop",
      totalMarriages: "Totalt antal äktenskap",
      sundaySchoolParticipants: "Deltagare i söndagsskolan",
      totalDeacons: "Totalt antal diakoner",
      activeMembersByYear: "Poster tillagda per år",
      activeMembersByYearNote: "Året då ett medlemskap började registreras inte ännu.",
      employeesByYear: "Anställda per år",
      baptismsByYear: "Dop per år",
      marriagesByYear: "Äktenskap per år",
      sundaySchoolByYear: "Söndagsskola per år",
      deaconsByYear: "Diakoner per år",
      failedToFetchDashboardStats: "Misslyckades med att hämta instrumentbrädesstatistik",
      export: "Exportera",
      rowsPerPage: "Rader per sida",
      prev: "Föreg",
      next: "Nästa",
      PROFILE_SETTINGS: "Profilinställningar",
      LOGOUT: "Logga ut",
      selectColumnsToExport: "Välj kolumner att exportera",
      exportSuccessful: "Export lyckades",
      exportFailed: "Export misslyckades",
      selectMaritalStatus: "Välj civilstånd",
      selectGender: "Välj kön",
      selectSpiritualFather: "Välj andlig fader",
      selectChurch: "Välj kyrka",
      selectStatus: "Välj status",
      status: "Status",
      ACTIVE: "Aktiv",
      INACTIVE: "Inaktiv",
      MOVED: "Flyttad",
      DECEASED: "Avliden",
      // Meeting Minutes translations (Swedish)
      meetings: "Möten",
      meetingsListTitle: "Möteslista",
      addMeeting: "Lägg till möte",
      editMeeting: "Redigera möte",
      scheduleMeeting: "Schemalägg möte",
      meetingTitle: "Mötestitel",
      meetingDate: "Mötesdatum",
      startTime: "Starttid",
      endTime: "Sluttid",
      location: "Plats",
      chair: "Ordförande",
      secretary: "Sekreterare",
      selectChair: "Välj ordförande",
      selectSecretary: "Välj sekreterare",
      noMembersForServiceType: "Inga medlemmar registrerade för denna tjänstetyp",
      enterDescription: "Ange mötesbeskrivning...",
      loading: "Laddar",
      meetingCreatedSuccessfully: "Möte skapat framgångsrikt",
      meetingUpdatedSuccessfully: "Möte uppdaterat framgångsrikt",
      meetingDeletedSuccessfully: "Möte borttaget framgångsrikt",
      errorSavingMeeting: "Fel vid sparande av möte",
      errorFetchingMeeting: "Fel vid hämtning av mötesdetaljer",
      failedToFetchMeetings: "Misslyckades med att hämta möten",
      confirmDeleteMeeting: "Är du säker på att du vill ta bort detta möte?",
      editMinutes: "Redigera protokoll",
      viewMinutes: "Visa protokoll",
      meetingMinutes: "Mötesprotokoll",
      attendance: "Närvaro",
      agenda: "Dagordning",
      decisions: "Beslut",
      actionItems: "Åtgärdspunkter",
      addAttendee: "Lägg till deltagare",
      attendeeName: "Deltagarens namn",
      attendeeRole: "Roll",
      present: "Närvarande",
      absent: "Frånvarande",
      addAgendaItem: "Lägg till dagordningspunkt",
      showPreview: "Visa förhandsgranskning",
      hidePreview: "Dölj förhandsgranskning",
      agendaTitle: "Dagordningspunktens titel",
      agendaNotes: "Anteckningar",
      agendaDecision: "Beslut",
      addDecision: "Lägg till beslut",
      decisionText: "Beslut",
      addActionItem: "Lägg till åtgärdspunkt",
      task: "Uppgift",
      responsible: "Ansvarig",
      pending: "Väntande",
      inProgress: "Pågående",
      completed: "Slutförd",
      nextMeetingDate: "Nästa mötesdatum",
      nextMeetingLocation: "Nästa mötesplats",
      print: "Skriv ut",
      exportPdf: "Exportera PDF",
      minutesLockedBy: "Protokollet redigeras av",
      lockExpired: "Din redigeringslås har gått ut",
      acquiringLock: "Hämtar redigeringslås...",
      youHaveEditingLock: "Du har redigeringslåset",
      viewingOnly: "Endast visning - en annan användare redigerar",
      releaseLock: "Frigör lås",
      notEditing: "Redigerar inte",
      clickToStartEditing: "Klicka på knappen för att börja redigera",
      startEditing: "Börja redigera",
      viewOnly: "Endast visning",
      viewOnlyMode: "En annan användare redigerar detta dokument",
      lockExpiresAt: "Låset går ut kl",
      youAreEditing: "Du redigerar",
      preview: "Förhandsgranska",
      editor: "Redigerare",
      additionalNotes: "Ytterligare anteckningar",
      signature: "Signatur",
      chairSignature: "Ordförandens signatur",
      secretarySignature: "Sekreterarens signatur",
      readOnlyAccess: "Du har endast läsbehörighet till denna sida",
      backToList: "Tillbaka till listan",
      downloadPdf: "Ladda ner PDF",
      pdfDownloaded: "PDF nedladdad framgångsrikt",
      failedToDownloadPdf: "Misslyckades att ladda ner PDF",
      connected: "Ansluten",
      disconnected: "Frånkopplad",
      meetingNotFound: "Mötet hittades inte",
      failedToReleaseLock: "Misslyckades att frigöra lås",
      lockLost: "Ditt redigeringslås har gått förlorat",
      titleAndDateRequired: "Titel och datum krävs",
      createMeeting: "Skapa möte",
      minutesSaved: "Protokoll sparat framgångsrikt",
      // Approval workflow (Swedish)
      pendingApproval: "Väntar på godkännande",
      approved: "Godkänd",
      rejected: "Avvisad",
      approveMinutesConfirmation: "Är du säker på att du vill godkänna detta mötesprotokoll?",
      rejectMinutesConfirmation: "Är du säker på att du vill avvisa detta mötesprotokoll?",
      approvedOn: "Godkänd den",
      optional: "frivillig",
      addYourNotesHere: "Lägg till dina anteckningar här",
      sendApprovalRequests: "Skicka godkännande förfrågningar",
      sendApprovalEmails: "Skicka godkännande e-post till alla deltagare",
      // Service types (Swedish)
      serviceType: "Tjänstetyp",
      parish: "Församling",
      sundaySchool: "Söndagsskola",
      youth: "Ungdom",
      choir: "Kör",
      deacons: "Diakoner",
      scheduled: "Schemalagd",
      cancelled: "Inställd",
      allStatuses: "Alla statusar",
      noMeetingsFound: "Inga möten hittades",
      date: "Datum",
      time: "Tid",
      showingOf: "Visar {{count}} av {{total}}",
      page: "Sida",
      delete: "Radera",
      editing: "Redigering",
      chooseMember: "Välj medlem",
      selectMember: "Välj medlem",
      role: "Roll",
      attendee: "Deltagare",
      chairperson: "Ordförande",
      invited: "Inbjuden",
      confirmed: "Bekräftad",
      declined: "Avböjd",
      saveNow: "Spara nu",
      startTypingMinutes: "Börja skriva protokoll...",
      attendees: "Deltagare",
      errorSavingMinutes: "Fel vid sparande av protokoll",
      lockAcquired: "Du har nu redigeringslåset",
      lockReleased: "Redigeringslås frigjort",
      lockRefreshed: "Lås uppdaterat",
      errorAcquiringLock: "Fel vid hämtning av lås",
      errorReleasingLock: "Fel vid frigivning av lås",
      errorRefreshingLock: "Fel vid uppdatering av lås",
      DRAFT: "Utkast",
      IN_PROGRESS: "Pågående",
      FINALIZED: "Slutförd",
      APPROVED: "Godkänd",
      ARCHIVED: "Arkiverad",
      // Action Items Manager translations
      total: "Totalt",
      overdue: "Försenade",
      noActionItems: "Inga åtgärdspunkter ännu",
      titleAndAgendaRequired: "Titel och dagordningspunkt krävs",
      errorSavingActionItem: "Fel vid sparande av åtgärdspunkt",
      confirmDeleteActionItem: "Är du säker på att du vill ta bort denna åtgärdspunkt?",
      selectAgendaItem: "Välj dagordningspunkt",
      agendaItem: "Dagordningspunkt",
      actionItemTitle: "Åtgärdspunktens titel",
      actionItemDescription: "Beskrivning",
      assignee: "Ansvarig",
      selectAssignee: "Välj ansvarig",
      priority: "Prioritet",
      low: "Låg",
      medium: "Medel",
      high: "Hög",
      urgent: "Brådskande",
      startProgress: "Starta arbete",
      markComplete: "Markera som klar",
      editActionItem: "Redigera åtgärdspunkt",
      add: "Lägg till",
      save: "Spara",
      // Meeting Minutes Approval translations
      minutesEditor: "Protokollredigerare",
      formalApproval: "Formellt godkännande",
      noFormalMinutesYet: "Inget formellt protokoll skapat",
      createFormalMinutesDescription: "Skapa ett formellt mötesprotokoll som kan skickas för godkännande.",
      createDraftMinutes: "Skapa utkast",
      minutesPreview: "Förhandsgranskning",
      version: "Version",
      lastUpdated: "Senast uppdaterad",
      submitForApproval: "Skicka för godkännande",
      approve: "Godkänn",
      reject: "Avvisa",
      approveMinutes: "Godkänn protokoll",
      rejectMinutes: "Avvisa protokoll",
      approvalNotes: "Godkännandeanteckningar",
      rejectionReason: "Avslagsorsak",
      optionalApprovalNotes: "Valfria anteckningar",
      provideRejectionReason: "Ange orsak för avslag",
      confirmApprove: "Bekräfta godkännande",
      confirmReject: "Bekräfta avslag",
      errorCreatingDraft: "Fel vid skapande av utkast",
      errorSubmittingApproval: "Fel vid inskickning för godkännande",
      errorProcessingApproval: "Fel vid behandling av godkännande",
      aiGeneratedSummary: "AI-genererad sammanfattning",
      aiSummary: "AI-sammanfattning",
      generateAiSummary: "Generera AI-sammanfattning",
      aiSummaryComingSoon: "AI-sammanfattning kommer snart!",
      by: "av",
      "minutesStatus.DRAFT": "Utkast - Redo för redigering",
      "minutesStatus.PENDING_APPROVAL": "Väntar på godkännande",
      "minutesStatus.APPROVED": "Godkänt - Slutgiltig version",
      "minutesStatus.REJECTED": "Avslagen - Behöver revision",
      "minutesStatus.ARCHIVED": "Arkiverad",

      // R3 task 7 - shared fields across the four new screens
      subject: "Ärende",
      service: "Tjänst",
      telephone: "Telefon",
      email: "E-post",
      province: "Region/Län",
      postalCode: "Postnummer",
      apartmentNo: "Lägenhetsnummer",
      referenceCode: "Referenskod",
      decisionReason: "Beslutsmotivering",
      requestedBy: "Begärd av",
      requestedClergy: "Begärd präst",
      scheduledAt: "Bokad tid",
      logisticsNote: "Logistikanteckning",
      dayOfWeekLabel: "Veckodag",

      // R3 task 7 - public application form (FR-3.9a)
      applyForServiceTitle: "Ansök om en kyrklig tjänst",
      applyForServiceSubtitle: "Dop, vigsel, fithat, bikt, samtal och mer.",
      churchIsRequired: "Välj en församling",
      applicantNameIsRequired: "Ditt namn krävs",
      serviceIsRequired: "Välj en tjänst eller beskriv den du behöver",
      consentIsRequired: "Du måste godkänna samtyckesförklaringen för att skicka in ansökan",
      childNameIsRequired: "Barnets namn krävs",
      childDobIsRequired: "Barnets födelsedatum krävs",
      godparentNameIsRequired: "En fadders/gudmors namn krävs",
      applicantChristianNameIsRequired: "Ditt kristna namn (የክርስትና ስም) krävs",
      atLeastOneSubjectIsRequired: "Minst en person krävs",
      subjectFullNameIsRequired: "Varje persons fullständiga namn krävs",
      subjectChristianNameIsRequired: "Varje persons kristna namn (የክርስትና ስም) krävs - det är namnet som uttalas i bönen",
      // R3.1 task 4 - bröllopsansökan (FR-3.24)
      weddingPartyNameIsRequired: "Båda parters ዓለማዊ ስም (borgerliga namn) krävs",
      weddingPartyChristianNameIsRequired: "Båda parters የክርስትና ስም (kristna namn) krävs",
      weddingPartyContactMethodIsRequired: "Ange minst ett sätt att kontakta varje part (telefon, e-post eller adress)",
      weddingPartyContactMethodHelp: "Ange minst ett av telefon, e-post eller adress för denna part.",
      phoneNumberIsInvalid: "Ange ett giltigt telefonnummer",
      weddingApplicantRoleTitle: "Är du en av de två som gifter sig?",
      iAmNotDirectlyAParty: "Nej, jag ansöker å deras vägnar",
      iAmPartyOne: "Ja, jag är Part Ett",
      iAmPartyTwo: "Ja, jag är Part Två",
      iAmAlsoASubject: "Jag är också en av de personer som denna begäran gäller",
      applicationSubmissionFailed: "Vi kunde inte skicka in din ansökan. Försök igen.",
      applicationSubmittedTitle: "Ansökan inskickad",
      applicationSubmittedCheckEmail: "Kontrollera din e-post för att verifiera din ansökan.",
      otherServiceNotListed: "Tjänsten jag behöver finns inte med i listan",
      serviceSectionDetailsTitle: "{{service}} - uppgifter",
      baptismDetailsTitle: "Dopuppgifter",
      // R3.1 task 4 - bröllopsansökan (FR-3.24)
      weddingDetailsTitle: "Bröllopsuppgifter",
      partyOneTitle: "Part Ett",
      partyTwoTitle: "Part Två",
      partyName: "ዓለማዊ ስም (Borgerligt namn)",
      // R3.1 task 3 - subjects array (FR-3.12b/FR-3.12c)
      subjectsSectionTitle: "Personer",
      applicantChristianName: "Ditt kristna namn (የክርስትና ስም)",
      applicantChristianNameHelp: "Du nämns också i bönen, som den som ber - detta är namnet som uttalas för dig.",
      subjectFullName: "Fullständigt namn",
      subjectChristianName: "Kristet namn (የክርስትና ስም)",
      livingOrDeparted: "Levande eller avliden",
      living: "Levande",
      departed: "Avliden",
      addAnotherSubject: "Lägg till ytterligare en person",
      remove: "Ta bort",
      paymentSectionTitle: "Betalning",
      amountPaid: "Betalt belopp",
      payerName: "Betalarens namn",
      estimatedTotal: "Uppskattat totalbelopp",
      estimatedTotalHelp: "Uppskattat till gästpris - det bekräftade beloppet beror på medlemskap, som fastställs när församlingen granskar din ansökan.",
      consentStatement: "Jag samtycker till att de personuppgifter som lämnas i detta formulär behandlas för syftet med denna ansökan.",
      submitApplication: "Skicka ansökan",
      applicantName: "Sökandens namn",
      applicantEmail: "Sökandens e-post",
      applicantRelation: "Relation till ärendet",
      serviceNeededDate: "Önskat datum",
      serviceOtherText: "Beskriv den önskade tjänsten",
      childName: "Barnets namn",
      childDob: "Barnets födelsedatum",
      godparentName: "Fadder/gudmors namn",
      selectChurchFirst: "Välj först en församling",
      checkApplicationStatus: "Kontrollera ansökans status",

      // R3 task 7 - verification page (FR-3.15)
      verificationTokenMissing: "Denna verifieringslänk saknar sin token",
      verifyingApplication: "Verifierar din ansökan...",
      applicationVerifiedTitle: "Ansökan verifierad",
      applicationVerifiedBody: "Tack - din ansökan har verifierats och ligger nu hos församlingen för granskning.",
      verificationFailedTitle: "Verifiering misslyckades",
      verificationFailed: "Denna verifieringslänk är ogiltig eller har gått ut",

      // R3 task 7 - status lookup page (FR-3.17)
      referenceCodeIsRequired: "Ange din referenskod",
      applicationNotFound: "Ingen ansökan hittades för den referenskoden",
      checkStatus: "Kontrollera status",
      yourResponse: "Ditt svar",
      responseRequired: "Ange ett svar",
      responseFailed: "Det gick inte att skicka ditt svar",
      submitResponse: "Skicka svar",
      "applicationStatus.PENDING_VERIFICATION": "Väntar på verifiering",
      "applicationStatus.SUBMITTED": "Inskickad",
      "applicationStatus.UNDER_REVIEW": "Under granskning",
      "applicationStatus.INFO_REQUESTED": "Mer information begärd",
      "applicationStatus.APPROVED": "Godkänd",
      "applicationStatus.REJECTED": "Avslagen",
      "applicationStatus.ABANDONED": "Övergiven",

      // R3 task 7 - review queue (FR-3.13/FR-3.16)
      serviceApplications: "Tjänsteansökningar",
      failedToFetchApplications: "Det gick inte att hämta tjänsteansökningar",
      queueWarningsColumn: "Varningar",
      subjectCountBadge: "{{count}} personer",
      subjectCountBadgeHelp: "Denna ansökan avser fler än en person",
      queueWarningConfessionFather: "Ingen biktfader",
      queueWarningConfessionFatherHelp: "Sökanden eller en brudpart har uppgett att de saknar biktfader i registret",
      queueWarningConfessionNeedsResolution: "Biktfader behöver fastställas",
      queueWarningConfessionNeedsResolutionHelp: "Uppgav \"i denna församling\" - välj rätt präst före eller vid godkännande",
      queueWarningPaymentNotDeclared: "Ingen betalning angiven",
      queueWarningPaymentNotDeclaredHelp: "Detta är en betaltjänst och ingen betalning har angetts ännu",
      queueWarningPaymentUnderpaid: "Troligen underbetald",
      queueWarningPaymentUnderpaidHelp: "Det uppgivna beloppet understiger även den billigaste möjliga taxan för denna ansökan",
      queueWarningPossibleDuplicate: "Möjlig dubblett",
      queueWarningPossibleDuplicateHelp: "Sökandens namn liknar starkt en befintlig medlem",
      reviewApplication: "Granska ansökan",
      failedToFetchApplication: "Det gick inte att hämta ansökan",
      applicationDetails: "Ansökningsuppgifter",
      applicantResolutionTitle: "Sökande",
      subjectResolutionTitle: "Ärende",
      godparentResolutionTitle: "Fadder/Gudmor",
      partyOneResolutionTitle: "Part Ett",
      partyTwoResolutionTitle: "Part Två",
      bookingDetailsTitle: "Bokningsuppgifter",
      applicantResolutionRequired: "Lös sökandens identitet innan du godkänner",
      subjectResolutionRequired: "Lös ärendets identitet innan du godkänner",
      godparentResolutionRequired: "Lös fadderns/gudmorns identitet innan du godkänner",
      weddingPartyResolutionRequired: "Lös båda parters identitet innan du godkänner",
      serviceOverride: "Tjänst att boka",
      serviceOverrideHelp: "Denna ansökan använde fritextalternativet 'Andra tjänster' - välj vilken katalogtjänst bokningen ska gälla.",
      serviceOverrideRequired: "Välj vilken tjänst som ska bokas",
      confirmedTotalTitle: "Betalningsavstämning",
      resolvedTotalLabel: "Fastställt totalbelopp",
      paymentStatusLabel: "Betalningsstatus",
      paymentStatusConfirmed: "Bekräftad",
      paymentStatusUnderpaid: "Underbetald",
      paymentStatusUnpaid: "Obetald",
      selectPaymentStatus: "Välj betalningsstatus",
      paymentStatusRequired: "Ange en betalningsstatus innan du godkänner",
      paymentMismatchWarning: "Uppgivet {{declared}} - totalt att betala {{total}} - {{difference}} för lite",
      noPriceSnapshotNote: "Denna ansökan saknar prisuppgift i systemet - avstämningen måste göras manuellt.",
      selectClergyOptional: "Välj präst (valfritt)",
      reviewClergyLoadFailed: "Prästlistan kunde inte läsas in",
      reviewClergyBookingsOff: "Val av präst hör till bokningar, som inte är påslagna här.",
      reviewFathersLoadFailed: "Listan över präster kunde inte läsas in",
      applicationApproved: "Ansökan godkänd",
      approvalFailed: "Det gick inte att godkänna ansökan",
      rejectionReasonRequired: "En avslagsmotivering krävs",
      applicationRejected: "Ansökan avslagen",
      rejectionFailed: "Det gick inte att avslå ansökan",
      requestInfo: "Begär komplettering",
      requestInfoNote: "Vad som saknas",
      requestInfoNoteRequired: "Ange vilken information som behövs",
      confirmRequestInfo: "Skicka begäran",
      applicantRecordLinked: "Ansökan visas under Mina ansökningar för medlemmen som länkats ovan: {{name}}.",
      applicantRecordHint: "Länka sökandens medlemspost ovan, så att ansökan visas under deras Mina ansökningar, eller ange att de inte har någon. Ett avslag eller en begäran om komplettering skapar ingen post.",
      applicantHasNoMemberRecord: "Sökanden har ingen medlemspost",
      applicantRecordRequired: "Länka sökandens medlemspost ovan, eller markera att de inte har någon",
      applicationInfoRequested: "Begäran om komplettering skickad",
      requestInfoFailed: "Det gick inte att begära komplettering",
      requestInfoSectionTitle: "Begärd information",
      applicantResponseLabel: "Sökandens svar",
      noResponseYet: "Inget svar än",
      selected: "Vald",
      changeSelection: "Ändra",
      noPersonSelectedYet: "Ingen person vald ännu - se förslag eller sök nedan.",
      possibleMatches: "Möjliga träffar",
      searchAgain: "Sök igen",
      searchExistingPeople: "Sök efter en befintlig person",
      noResultsFound: "Inga resultat hittades",
      resolutionActionLabel: "Hur ska denna person hanteras?",
      selectAction: "Välj en åtgärd",
      "resolutionAction.LINK_EXISTING": "Länka till befintlig medlem",
      "resolutionAction.CREATE_MEMBER": "Skapa ny medlem",
      "resolutionAction.CREATE_GUEST": "Skapa ny gäst",
      "resolutionAction.DESCRIPTIVE_ONLY": "Registrera beskrivande (ingen medlems-/gästpost)",
      existingMemberId: "Befintligt medlems-ID",
      descriptiveName: "Beskrivande namn",
      descriptiveDob: "Beskrivande födelsedatum",
      descriptiveNote: "Beskrivande anteckning",
      checkForDuplicates: "Kontrollera dubbletter",
      nameRequiredForDuplicateCheck: "Ange ett namn innan du kontrollerar dubbletter",
      nameAsGiven: "Namn som det angavs: {{name}} - fyll i varje del i fälten nedan",
      duplicateCheckFailed: "Det gick inte att kontrollera dubblettkandidater",
      noDuplicateCandidatesFound: "Inga sannolika dubbletter hittades",
      matchScore: "Träffsäkerhet",
      possibleMatchElsewhere: "Möjlig träff i en annan församling",
      contactDioceseToConfirm: "kontakta en stiftsadministratör för att bekräfta",

      // R3 task 7 - bookings
      bookings: "Bokningar",
      failedToFetchBookings: "Det gick inte att hämta bokningar",
      "bookingStatus.REQUESTED": "Begärd",
      "bookingStatus.CONFIRMED": "Bekräftad",
      "bookingStatus.COMPLETED": "Genomförd",
      "bookingStatus.CANCELLED": "Avbokad",
      needsMarriageRecorded: "Registrera vigsel",
      needsMarriageRecordedHelp: "Detta bröllops planerade datum har passerat men vigseln har inte registrerats än - klicka för att registrera den.",
      editBooking: "Redigera bokning",
      failedToFetchBooking: "Det gick inte att hämta bokningen",
      bookingUpdatedSuccessfully: "Bokningen uppdaterades",
      errorUpdatingBooking: "Det gick inte att uppdatera bokningen",
      clergyQueue: "Prästkö",
      failedToFetchClergyQueue: "Det gick inte att hämta din prästkö",
      clergyQueueEmpty: "Du har inga väntande bokningar som kräver åtgärd",
      respond: "Svara",

      // R3 task 7 - clergy availability (FR-3.7)
      manageClergyAvailability: "Prästers tillgänglighet",
      searchClergyMember: "Sök präst",
      newAvailabilityWindow: "Nytt tillgänglighetsfönster",
      addAvailabilityWindow: "Lägg till fönster",
      startAndEndTimeRequired: "Start- och sluttid krävs",
      availabilityWindowAdded: "Tillgänglighetsfönster tillagt",
      errorAddingAvailabilityWindow: "Det gick inte att lägga till tillgänglighetsfönstret",
      errorDeletingAvailabilityWindow: "Det gick inte att ta bort tillgänglighetsfönstret",
      failedToFetchAvailability: "Det gick inte att hämta tillgänglighetsfönster",
      "dayOfWeek.MONDAY": "Måndag",
      "dayOfWeek.TUESDAY": "Tisdag",
      "dayOfWeek.WEDNESDAY": "Onsdag",
      "dayOfWeek.THURSDAY": "Torsdag",
      "dayOfWeek.FRIDAY": "Fredag",
      "dayOfWeek.SATURDAY": "Lördag",
      "dayOfWeek.SUNDAY": "Söndag",

      // R3 task 7 - self-service consultation booking
      bookConsultation: "Boka ett samtal",
      failedToLoadBookingSetup: "Det gick inte att läsa in bokningsinställningarna",
      consultationServiceNotConfigured: "Samtal är inte konfigurerat för din församling ännu",
      failedToFetchBookableClergy: "Det gick inte att hämta tillgängliga präster",
      failedToFetchSlots: "Det gick inte att hämta lediga tider",
      selectAClergyMember: "Välj en präst",
      selectClergy: "Välj präst",
      noBookableClergyAtYourChurch: "Ingen präst i din församling har lediga samtalstider just nu",
      previousWeek: "Föregående vecka",
      nextWeek: "Nästa vecka",
      noOpenSlotsThisWeek: "Inga lediga tider denna vecka",
      consultationBooked: "Samtal bokat",
      consultationBookedTitle: "Samtal bokat",
      consultationBookedBody: "Ditt samtal med {{clergy}} är bekräftat till {{date}}.",
      bookAnother: "Boka en till",
      bookingFailed: "Det gick inte att boka denna tid - den kan just ha blivit tagen",

      // R3 task 7 - sidebar
      servicesAndBookings: "Tjänster & Bokningar",

      // R3.1 task 2 - service catalog admin screen (FR-3.23)
      serviceCatalog: "Tjänstekatalog",
      addServiceCatalogEntry: "Lägg till tjänst",
      editServiceCatalogEntry: "Redigera tjänst",
      failedToFetchServiceCatalog: "Det gick inte att hämta tjänstekatalogen",
      failedToFetchServiceCatalogEntry: "Det gick inte att hämta denna katalogpost",
      serviceCatalogCode: "Kod",
      serviceCatalogPrice: "Medlems-/Gästpris",
      serviceCatalogPricingBasis: "Prisgrund",
      serviceCatalogCurrency: "Valuta",
      serviceCatalogMemberPrice: "Medlemspris",
      serviceCatalogGuestPrice: "Gästpris",
      serviceCatalogIsFree: "Denna tjänst är kostnadsfri",
      selectPricingBasis: "Välj prisgrund",
      pricingBasis: {
        PER_SUBJECT: "Per person",
        PER_APPLICATION: "Per ansökan",
      },
      serviceCatalogSubjectMode: "Personläge",
      selectSubjectMode: "Välj personläge",
      subjectMode: {
        SELF_ONLY: "Endast sökanden - inget personavsnitt",
        SINGLE: "En person",
        MANY: "Flera personer",
      },
      free: "Kostnadsfri",
      active: "Aktiv",
      retired: "Avregistrerad",
      retire: "Avregistrera",
      activate: "Aktivera",
      confirmRetireServiceCatalogEntry: "Avregistrera denna tjänst? Den erbjuds inte längre på nya ansökningar, men befintliga bokningar och ansökningar fortsätter att referera till den.",
      confirmActivateServiceCatalogEntry: "Aktivera denna tjänst igen? Den blir tillgänglig för nya ansökningar.",
      serviceCatalogEntryRetired: "Tjänsten avregistrerad",
      serviceCatalogEntryActivated: "Tjänsten aktiverad",
      serviceCatalogEntryUpdated: "Katalogposten uppdaterad",
      serviceCatalogEntryAdded: "Katalogposten tillagd",
      errorUpdatingServiceCatalogEntry: "Det gick inte att uppdatera denna katalogpost",
      errorAddingServiceCatalogEntry: "Det gick inte att lägga till denna katalogpost",
      // R5: family graph view (FamilyTreeView.js)
      errorFetchingFamilyData: "Fel vid hämtning av familjedata",
      familyTreeDepth: "Djup",
      showGodparents: "Visa faddrar",
      showConfessionFather: "Visa skriftefader",
      familyLegendParent: "Förälder",
      familyLegendChild: "Barn",
      familyLegendSpouse: "Make/maka",
      familyLegendSibling: "Syskon",
      familyLegendPending: "Väntande",
      familyLegendDisputed: "Bestridd",
      "familyEdgeStatus.pending": "väntande",
      "familyEdgeStatus.disputed": "bestridd",
      // R5/FR-2.3: self-declared household relationships (My Family / Pending Confirmations)
      pendingConfirmations: "Väntande bekräftelser",
      pendingConfirmationsHelp: "Andra medlemmar har angett dig som sin förälder. Bekräfta om det stämmer, eller bestrid om det inte gör det.",
      noPendingConfirmations: "Du har inga väntande bekräftelser.",
      householdPendingClaim: "{{name}} har angett dig som sin förälder",
      householdConfirmSuccessful: "Bekräftat",
      householdDisputeSuccessful: "Bestridd",
      householdActionFailed: "Det gick inte att registrera ditt svar",
      dispute: "Bestrid",
      // R5/FR-5.1-5.10: marriage impediment resubmit-with-reasons flow (ImpedimentPanel.js)
      proceedWithReasons: "Fortsätt med angivna skäl",
      impedimentAbsoluteTitle: "Detta äktenskap kan inte registreras",
      impedimentAbsoluteBody: "Följande godtar inget undantag, under några omständigheter:",
      impedimentInsufficientTitle: "Kunde inte verifieras",
      impedimentFoundTitle: "Hinder funnet",
      impedimentReviewTitle: "Kräver granskning av präst",
      impedimentSpouse1EdgesKnown: "Make/maka 1 - kända släktband i systemet",
      impedimentSpouse2EdgesKnown: "Make/maka 2 - kända släktband i systemet",
      impedimentOverrideReasonLabel: "Skäl för att fortsätta trots detta",
      "impedimentType.ACTIVE_MARRIAGE": "Aktivt äktenskap registrerat",
      "impedimentType.UNDER_CIVIL_AGE": "Under civilrättslig äktenskapsålder",
      "impedimentType.SAME_SEX": "Samma kön",
      "impedimentType.CONSANGUINITY": "Blodsband (konsanguinitet)",
      "impedimentType.SPIRITUAL_KINSHIP": "Andligt släktskap",
      "impedimentType.UNCONFIRMED_DECLARED_LINK": "Obekräftad angiven relation användes",
      "impedimentType.CATECHUMEN": "Dopstatus obekräftad",
      "impedimentType.PRIOR_MARRIAGE_NOT_PROPERLY_CLOSED": "Tidigare äktenskap inte korrekt avslutat",
      "impedimentType.MISSING_DATA": "Saknade uppgifter",
      // Consent step A3 - the public consent page (ConsentAnswer.js)
      consentPageGuardianTitle: "{{child}} i {{className}}",
      consentPageMembershipTitle: "Medlemsuppgifter för {{child}}",
      consentPageChangedMeanwhile: "Förfrågan ändrades medan du svarade, så den här gången togs dina svar inte emot. Sidan visar nu hur det ser ut.",
      consentPageOffline: "Vi kunde inte nå församlingens system. Kontrollera din internetanslutning och försök igen.",
      consentPageTooManyTries: "För många försök på kort tid. Vänta några minuter och försök igen.",
      consentPageServerTrouble: "Församlingens system svarade men kunde inte göra detta just nu. Försök igen om en stund. Kontakta församlingen om det fortsätter att hända.",
      consentPageNotShownTitle: "Sidan kunde inte visas just nu",
      consentPageRefusedTitle: "Sidan kan inte visas",
      consentPageRefused: "Församlingens system godtog inte den här länken. Kontakta församlingen.",
      consentPageUnknownStateTitle: "Förfrågan kan inte visas här",
      consentPageRejected: "Dina svar kunde inte sparas. Öppna länken i ditt mejl igen och börja om. Kontakta församlingen om det fortsätter att hända.",
      consentPageNoTokenTitle: "Länken är ofullständig",
      consentPageNoTokenBody: "En del av länken saknas. Öppna mejlet från församlingen och tryck på länken igen. Om du kopierade länken, se till att du fick med hela.",
      consentPageNormalBrowser: "Fungerar det fortfarande inte? Öppna länken i din vanliga webbläsare, till exempel Safari eller Chrome.",
      consentPageLoading: "Ett ögonblick",
      consentPageLoadingBody: "Din förfrågan öppnas …",
      consentPageUnreachableTitle: "Vi når inte församlingens system",
      consentPageTryAgain: "Försök igen",
      consentPageInvalidTitle: "Länken fungerar inte",
      consentPageInvalidBody: "Den kan ha kopierats bara till en del, eller så kommer den inte från församlingen. Öppna mejlet från församlingen och tryck på länken igen. Kontakta församlingen om det fortfarande inte fungerar.",
      consentPageClosedTitle: "Förfrågan är stängd",
      consentPageClosedBody: "{{parish}} har dragit tillbaka förfrågan. Du behöver inte göra något mer.",
      consentPageSupersededTitle: "En nyare länk har skickats",
      consentPageSupersededBody: "{{parish}} har skickat ett nyare mejl om detta. Använd länken i det senaste mejlet.",
      consentPageSentElsewhereTitle: "Länken gäller inte längre",
      consentPageSentElsewhereBody: "Förfrågan har skickats till en annan vårdnadshavare. Om du själv behöver svara, kontakta församlingen.",
      // Consent step A4 - the withdrawal page (ConsentWithdraw.js)
      consentWithdrawTitle: "Återkalla ditt samtycke?",
      consentWithdrawGivenOn: "Givet den",
      consentWithdrawWhatHappensGuardian: "Om du återkallar det deltar {{child}} inte längre i {{className}} med stöd av detta samtycke. Närvaro som redan registrerats ligger kvar.",
      consentWithdrawWhatHappensMembership: "Om du återkallar det har församlingen inte längre detta samtycke för {{child}}s medlemsuppgifter. Det som registrerats tidigare ligger kvar.",
      consentWithdrawTold: "{{child}}s registrerade vårdnadshavare och församlingen får veta att det återkallats.",
      consentWithdrawConfirm: "Återkalla mitt samtycke",
      consentWithdrawKeep: "Behåll mitt samtycke",
      consentWithdrawWithdrawing: "Återkallar …",
      consentWithdrawFailed: "Återkallelsen kunde inte sparas. Kontrollera din internetanslutning och försök igen. Kontakta församlingen om det fortsätter att misslyckas.",
      consentWithdrawFailedServer: "Återkallelsen kunde inte sparas just nu. Försök igen om en stund. Kontakta församlingen om det fortsätter att misslyckas.",
      consentWithdrawRefused: "Församlingens system godtog inte återkallelsen. Kontakta församlingen för att återkalla ditt samtycke.",
      // Finding 38 - the member page's consents, and recording a withdrawal (WithdrawalForm.js)
      consentsTitle: "Samtycken",
      consentsNone: "Inget samtycke finns registrerat för den här medlemmen.",
      consentsLoadFailed: "Samtyckena kunde inte hämtas.",
      consentPurposeMembership: "Medlemskap",
      consentPurposeActivity: "Aktivitet: {{activity}}",
      consentState_STANDING: "gäller",
      consentState_WITHDRAWN: "återkallat",
      consentState_SUPERSEDED: "ersatt av ett nyare samtycke",
      consentGivenOn: "Givet {{date}}",
      consentGivenOnNotRecorded: "Dagen då det gavs är inte registrerad",
      consentGivenByMember: "av medlemmen själv",
      consentGivenByGuardian: "av {{name}}, vårdnadshavare",
      consentGivenByGuardianUnnamed: "av en vårdnadshavare",
      consentAnsweredAs: "Besvarat på samtyckessidan av någon som bekräftade att de är {{name}}.",
      consentGivenByNotRecorded: "vem som gav det är inte registrerat",
      consentRecordedOn: "registrerat {{date}}",
      consentRowReligiousBody: "Församlingens egen grund, dataskyddsförordningen artikel 9.2 d, registrerad {{date}} - inte ett samtycke: ingen gav det, så det finns inget att återkalla.",
      consentRecordWithdrawal: "Registrera ett återkallande",
      withdrawalFormTitle: "Registrera ett återkallande",
      withdrawalNoEvidenceNeeded: "Registrera det du har. Mer behövs inte - ingen skanning, ingen underskrift, ingen ny bekräftelse: ditt ord är underlaget, och det du intygar säger hur du vet.",
      withdrawalNowTheChildsOwn: "Samtycket är nu barnets eget - barnet har fyllt 12 - så via länk kan bara barnet återkalla det. En förälders anmälan registreras ändå här, med föräldern angiven.",
      withdrawalHowQuestion: "Hur kom det till dig?",
      withdrawalChannelOption_IN_PERSON: "Personligen",
      withdrawalChannelOption_PHONE: "Per telefon",
      withdrawalChannelOption_LETTER: "Per brev",
      withdrawalChannelOption_EMAIL: "Per e-post till församlingen",
      withdrawalWhoQuestion: "Vem återkallade det?",
      withdrawalGiverOption_THE_PERSON: "{{name}} själv",
      withdrawalGiverOption_GUARDIAN_IN_REGISTER: "En vårdnadshavare i registret",
      withdrawalGiverOption_NAMED_PERSON: "Någon som inte finns i registret",
      withdrawalGiverOption_NOT_KNOWN: "Okänt",
      withdrawalChooseGuardian: "Välj vårdnadshavare",
      withdrawalNoGuardianOnRecord: "Ingen vårdnadshavare finns registrerad - välj Någon som inte finns i registret och ange namnet.",
      withdrawalGiverName: "Personens namn",
      withdrawalGiverRelationship: "Relation (om den angavs)",
      withdrawalDayQuestion: "Dagen då samtycket återkallades",
      withdrawalDayNotKnown: "Okänd",
      withdrawalDayHint: "Lämna tomt om ingen sa det.",
      withdrawalAttestQuestion: "Vad kan du intyga?",
      withdrawalAttestationOption_HEARD_IT_FROM_THEM: "Jag hörde det från personen själv, personligen eller per telefon",
      withdrawalAttestationOption_HOLDS_THEIR_WRITING: "Jag har personens brev eller e-post",
      withdrawalAttestationOption_PASSED_ON_TO_ME: "Det fördes vidare till mig - jag hörde det inte själv",
      withdrawalWhoWillBeTold: "Får besked:",
      withdrawalToldGuardianOutside: "en vårdnadshavare utanför registret, per e-post",
      withdrawalToldStaff: "församlingens administratörer ({{count}})",
      withdrawalToldGeneral: "Registrerade vårdnadshavare och församlingens administratörer får besked.",
      withdrawalDoesNothingToMembership: "Att registrera återkallandet avslutar inte själva medlemskapet.",
      withdrawalSubmit: "Registrera återkallandet",
      withdrawalFailed: "Återkallandet kunde inte registreras.",
      withdrawalLine: "Återkallat {{how}} - {{who}} - {{day}}",
      withdrawalChannel_IN_PERSON: "personligen",
      withdrawalChannel_PHONE: "per telefon",
      withdrawalChannel_LETTER: "per brev",
      withdrawalChannel_EMAIL: "per e-post till församlingen",
      withdrawalChannel_LINK: "via återkallelselänken",
      withdrawalChannel_NOT_RECORDED: "(hur det kom in registrerades inte)",
      withdrawalGiver_THE_PERSON: "{{name}} själv",
      withdrawalGiver_GUARDIAN_IN_REGISTER: "{{name}}, vårdnadshavare",
      withdrawalGiver_NAMED_PERSON: "{{name}}, inte i registret",
      withdrawalGiver_NOT_KNOWN: "vem som återkallade är okänt",
      withdrawalGiver_LINK_HOLDER: "den som hade länken",
      withdrawalGiver_NOT_RECORDED: "vem som återkallade registrerades inte",
      withdrawalDay: "dagen för återkallandet: {{date}}",
      withdrawalDayStatedUnknown: "dagen är okänd",
      withdrawalDayNotRecorded: "dagen registrerades inte",
      withdrawalAttestation_HEARD_IT_FROM_THEM: "hört direkt från personen",
      withdrawalAttestation_HOLDS_THEIR_WRITING: "personens brev eller e-post finns",
      withdrawalAttestation_PASSED_ON_TO_ME: "fört vidare i andra hand",
      withdrawalRecordedBy: "registrerat {{date}} av {{name}}",
      withdrawalRecordedOn: "registrerat {{date}}",
      withdrawalBackfilled: "Registrerat innan återkallanden fick en egen post: det som var känt då, och inget mer.",
      // The profile page until it loads a real profile (owner, 15 Oct 2026)
      consentWithdrawKeptTitle: "Inget har ändrats",
      consentWithdrawKeptBody: "Ditt samtycke gäller. Du kan stänga sidan. Länken i ditt mejl fungerar fortfarande om du ändrar dig.",
      consentWithdrawDoneTitle: "Ditt samtycke är återkallat",
      consentWithdrawDoneBody: "{{child}}s registrerade vårdnadshavare och församlingen har fått veta. Om detta var ett misstag, kontakta församlingen: samtycke kan ges igen.",
      consentWithdrawAlreadyTitle: "Detta samtycke är redan återkallat",
      consentWithdrawAlreadyBody: "Inget mer behövs. Kontakta församlingen om du har frågor.",
      consentWithdrawNowOwnTitle: "Detta är nu {{child}}s eget samtycke",
      consentWithdrawNowOwnBody: "{{child}} är 12 år eller äldre, så samtycket till medlemskapet är deras eget att ge eller återkalla, inte en förälders. Om de vill återkalla det kan de kontakta församlingen.",
      consentPageNewLinkSentTitle: "En ny länk är på väg",
      consentPageNewLinkSentBody: "Vi har skickat en ny länk till den e-postadress församlingen har till dig. Det kan ta några minuter. Titta även i skräpposten.",
      consentPageExpiredTitle: "Länken har gått ut",
      consentPageExpiredCanRenew: "Länkar slutar fungera efter en tid, för din säkerhet. Vi kan skicka en ny till den e-postadress församlingen har till dig.",
      consentPageSending: "Skickar …",
      consentPageSendNewLink: "Skicka en ny länk till mig",
      consentPageNewLinkRecentlySent: "En ny länk skickades för några minuter sedan. Titta i din e-post, även i skräpposten. Kontakta församlingen om inget kommer fram.",
      consentPageExpiredContactParish: "Kontakta {{parish}} för att få en ny länk.",
      consentPageThanksTitle: "Tack. Dina svar är sparade",
      consentPageAlreadyAnsweredTitle: "Du har redan svarat",
      consentPageNothingToAnswerTitle: "Det finns inget att svara på just nu",
      consentPageIntroTitle: "En fråga om {{child}}",
      consentPageIntroAsking: "{{parish}} ber om ditt svar om {{child}}.",
      consentPageIntroOne: "Det är en fråga.",
      consentPageIntroMany: "Det är {{count}} frågor. Du svarar på var och en för sig: du kan säga ja till en och nej till en annan.",
      consentPageIntroNothingChosen: "Inget är förvalt. Innan något skickas ser du alla dina svar och kan ändra dem.",
      consentPageBegin: "Börja",
      consentPageQuestionOf: "Fråga {{number}} av {{count}}",
      consentPageTextVersion: "Textversion {{version}}",
      consentPageDoYouAgree: "Samtycker du?",
      consentPageYes: "Ja, jag samtycker",
      consentPageNo: "Nej, jag samtycker inte",
      consentPageChooseFirst: "Välj ja eller nej innan du fortsätter.",
      consentPageContinue: "Fortsätt",
      consentPageBack: "Tillbaka",
      consentPageReviewTitle: "Kontrollera dina svar",
      consentPageChange: "Ändra svaret",
      consentPageWithdrawLater: "Om du säger ja kan du återkalla det senare. Kvittot visar hur.",
      consentPageIAm: "Jag är {{name}}",
      consentPageIAmHelp: "Den här länken är till {{name}}. Om du inte är {{name}}, svara inte - meddela församlingen att den nådde dig.",
      consentPageIAmFirst: "Kryssa i att du är {{name}} innan du skickar - bara den personen får svara.",
      consentPageSendAnswers: "Skicka mina svar",
      consentPageNowOwnConsent: "{{child}} är nu gammal nog att svara på detta själv, så du får inte längre frågan.",
      consentPageReworded: "Församlingen har ändrat formuleringen. Du får en ny förfrågan om den.",
      consentPageInPerson: "Detta kan inte längre besvaras via länken: församlingen tar emot ditt svar personligen eller på papper.",
      consentPageNotAnswerableOther: "Detta kan inte besvaras här. Fråga församlingen om det.",
      consentPageDateBoth: "{{gregorian}} (etiopisk kalender: {{ethiopian}})",
      consentPageThanksBody: "Detta är ditt kvitto. En kopia har också skickats till din e-post.",
      consentPageAnsweredOn: "Besvarad den",
      consentPageDocRequest: "Begäran om samtycke",
      consentPageRequestedOn: "Begärd den",
      consentPageDocReceipt: "Kvitto på dina svar",
      consentPageCopyProduced: "Den här kopian skapades",
      consentPageWithdrawOnPaper: "För att återkalla samtycket, kontakta församlingen på {{contact}} eller använd länken i det e-postmeddelande som kvittot kom med.",
      consentPageWithdrawOnPaperNoContact: "För att återkalla samtycket, kontakta församlingen eller använd länken i det e-postmeddelande som kvittot kom med.",
      consentPageOutcomeWithdrawn: "Du samtyckte och återkallade det senare",
      consentPageOutcomeGranted: "Du samtyckte",
      consentPageOutcomeRefused: "Du samtyckte inte",
      consentPageOutcomeNotAnswered: "Inte besvarad",
      consentPageWithdrawnOn: "Återkallad den",
      consentPageTextYouAgreedTo: "Texten du samtyckte till:",
      consentPageWithdrawLink: "Återkalla detta samtycke",
      consentPageKeepThis: "Spara kvittot. Det visar exakt vad du samtyckte till och när.",
      consentPagePrint: "Skriv ut eller spara kvittot",
      // Shown on every page before any page-specific text: the loader, the error boundary, <noscript> (scripts/noscript.js)
      loadingApplication: "Laddar …",
      errorBoundaryTitle: "Något gick fel",
      errorBoundaryBody: "Ett oväntat fel inträffade. Försök igen eller ladda om sidan.",
      errorBoundaryTryAgain: "Försök igen",
      errorBoundaryReload: "Ladda om sidan",
      noscriptOpenInBrowser: "Sidan kan inte öppnas här. Öppna länken i din vanliga webbläsare, till exempel Safari eller Chrome.",
      // Consent step A3c - the chase list (ConsentChase.js)
      chaseTitle: "Samtyckesstatus",
      chaseLoadFailed: "Samtyckesstatusen kunde inte hämtas.",
      chaseSendFailed: "Inget skickades. Försök igen.",
      chaseGrantFailed: "Ändringen kunde inte sparas.",
      chaseCurrentText: "En ny länk visar vårdnadshavarsamtycke, text {{version}}.",
      chaseAdultsNotListed: "Vuxna inskrivna, visas inte (inget vårdnadshavarsamtycke): {{count}}.",
      chaseSummary: "Sammanfattning",
      chaseBulkSend: "Förbered länkar till alla som inte tillfrågats, inte svarat eller fått ny text ({{count}})",
      chaseBulkHelp: "Inget skickas förrän du sett vem varje länk går till. Nej och återkallelser jagas aldrig.",
      chaseChildren: "Barn",
      chaseNoChildren: "Inga barn är inskrivna.",
      chaseSentTo: "Skickad till {{name}}",
      chaseBy: "Av {{name}}",
      chasePreviousClosed: "En tidigare förfrågan stängdes utan svar.",
      chaseAlsoAsked: "Också i samma förfrågan: {{purpose}} -",
      chaseStatusNotAsked: "Inte tillfrågad",
      chaseStatusAsked: "Tillfrågad, länken gäller",
      chaseStatusNoResponse: "Inget svar, länken har gått ut",
      chaseStatusReworded: "Texten ändrad, behöver ny länk",
      chaseStatusGranted: "Samtycke givet",
      chaseStatusInPerson: "Samtycke registrerat på plats",
      chaseStatusRefused: "Nej",
      chaseStatusWithdrawn: "Återkallat",
      chaseStatusNowOwnConsent: "Nu barnets eget att svara på",
      chaseActionSend: "Skicka en länk",
      chaseActionResend: "Skicka länken igen",
      chaseActionReplace: "Skicka en länk med den nya texten",
      chaseChoiceStaffEntered: "angiven av personal",
      chaseChoiceOnlyParent: "den enda registrerade föräldern som kan nås",
      chaseChoiceLanguageHasText: "den enda föräldern vars språk texten finns på",
      chaseChoiceFirstAlphabetically: "först i bokstavsordning - fler än en förälder kan nås",
      chaseChoiceChangedInPreview: "vald i förhandsgranskningen i stället",
      chaseCaptureLink: "via samtyckeslänken",
      chaseCapturePaper: "på papper",
      chaseCaptureInPerson: "på plats",
      chaseCaptureSelfRegistration: "i registreringsformuläret",
      chaseCaptureImport: "från det gamla registret",
      chasePurposeMembership: "medlemskap",
      chasePurposeGuardian: "vårdnadshavarsamtycke",
      chaseWhoCanChase: "Vem kan påminna om detta samtycke",
      chaseNoHolders: "Bara personal.",
      chaseScope: "Gäller",
      chaseScopeActivity: "denna aktivitet",
      chaseScopeChurch: "alla kyrkans aktiviteter",
      chaseGrantedOn: "sedan {{date}}, av {{name}}",
      chaseGrant: "Låt dem påminna",
      chaseRevoke: "Ta bort",
      chaseGrantHelp: "De ser varje barns samtyckesstatus och kan skicka länkar till föräldrar som redan finns i registret. De ser aldrig e-postadresser och kan inte lägga till en mottagare eller avbryta en förfrågan.",
      chaseBackToActivity: "Tillbaka till aktiviteten",
      chasePreviewTitle: "Kontrollera innan du skickar",
      chasePreviewHelp: "Varje länk går till en förälder, aldrig till båda. Kontrollera vem och ändra vid behov.",
      chaseSendTo: "Skicka till",
      chaseNotReachable: "{{name}} kan inte nås via länk: {{reason}}",
      chaseUnreachableNoEmail: "ingen e-postadress registrerad",
      chaseUnreachableNoLanguage: "inget språk registrerat",
      chaseUnreachableNoText: "samtyckestexten finns inte på deras språk",
      chaseUnreachableAddressNotProven: "inget registrerat visar att adressen är deras",
      chaseCantSend: "Kan inte skickas ({{count}})",
      chaseProblemNoParent: "ingen förälder är registrerad",
      chaseProblemNoReachableParent: "ingen registrerad förälder kan nås via länk",
      chaseProblemRecipientNotReachable: "föräldern den skickades till kan inte nås via länk - för att fråga någon annan avbryter personalen begäran på barnets post",
      chaseProblemNoText: "Ingen samtyckestext för vårdnadshavare är publicerad.",
      chaseProblemNothingToSend: "inget att skicka",
      chaseConfirmSend: "Skicka ({{count}})",
      chaseResultsTitle: "Vad som skickades",
      chaseOutcomeSent: "länk skickad",
      chaseOutcomeResent: "länken skickad igen",
      chaseOutcomeReplaced: "ny länk med den nya texten skickad",
      chaseOutcomeSkipped: "inte skickad",
      chaseYourLists: "Samtycken du kan påminna om",
      chaseYourListsHelp: "Församlingen har bett dig följa upp vårdnadshavarsamtycke för dessa aktiviteter.",
      dataModeSyntheticBanner: "Endast testdata - inga verkliga personers uppgifter får registreras här.",
      // The church's data-protection contact (AddChurch.js) and the public pages' contact line
      churchDpContactTitle: "Kontakt för dataskydd",
      churchDpContactHelp: "Den som man kontaktar för att återkalla samtycke eller fråga om sina personuppgifter. Samtyckestexterna visar e-postadressen (och telefonnumret) där de säger [contact]; varje text anger rollen med egna ord. Roll och e-post behövs tillsammans. Lämnas fälten tomma visar [contact] kyrkans telefonnummer i stället.",
      churchDpContactRole: "Roll",
      churchDpContactRoleExample: "t.ex. Kontaktperson för dataskydd",
      churchDpContactEmail: "E-post",
      churchDpContactPhone: "Telefon (valfritt)",
      consentPageParishContact: "Församlingens kontakt: {{contact}}",
      consentPageClassNowCalled: "Den här klassen heter nu {{name}}.",
      // M12 screen batch, item 1: refused pages (LoadProblem.js) and role help (RegisterUser.js)
      activityRefusedTitle: "Den här klassen är inte din",
      activityRefusedBody: "Du kan öppna klasserna du leder och tillfällen du har hållit. Om du ska leda den här klassen, be församlingsexpeditionen att lägga till dig som ledare.",
      activityNotFoundTitle: "Den här klassen eller det här tillfället finns inte",
      activityNotFoundBody: "Den kan ha tagits bort, eller så är länken ofullständig.",
      activityTryAgain: "Försök igen",
      activityServerSaid: "Angiven orsak: {{message}}",
      loadFailedTitle: "Det gick inte att läsa in detta",
      loadRefusedTitle: "Du har inte åtkomst till detta",
      loadNotFoundTitle: "Detta finns inte, eller finns inte längre",
      loadServerSaid: "Angiven orsak: {{message}}",
      loadTryAgain: "Försök igen",
      loadBack: "Tillbaka",
      addMemberManually: "Lägg till en medlem",
      addressInformation: "Adress",
      ageUnknown: "ålder okänd",
      agendaLocked: "Dagordningen är låst - protokollet är inlämnat",
      approvedBy: "Godkänt av",
      back: "Tillbaka",
      backToMembers: "Tillbaka till medlemmar",
      church: "Kyrka",
      confirmDeleteChurchService: "Ta bort den här kyrkliga tjänsten?",
      contactAndProfessional: "Kontakt och yrke",
      createdDate: "Skapad",
      decision: "Beslut",
      description: "Beskrivning",
      displayName: "Visningsnamn",
      errorAddingAttendee: "Det gick inte att lägga till deltagaren",
      errorApprovingMinutes: "Det gick inte att godkänna protokollet",
      errorReopeningMinutes: "Det gick inte att öppna protokollet igen",
      errorSavingAgendaItem: "Det gick inte att spara punkten på dagordningen",
      errorSubmittingMinutes: "Det gick inte att lämna in protokollet",
      errorUpdatingAttendee: "Det gick inte att uppdatera deltagaren",
      failedToFetchMember: "Det gick inte att läsa in medlemmen",
      groupTransferPartial: "{{transferred}} flyttade, {{excluded}} kunde inte flyttas:",
      groupTransferSuccessful: "{{count}} medlemmar flyttades",
      invalidMemberId: "Ogiltigt medlems-id",
      loadingMemberData: "Läser in medlemmen …",
      meetingDetails: "Möte",
      meetingRequiredFields: "Titel, datum och kyrka måste anges",
      meetingUpdated: "Mötet uppdaterades",
      memberNotFound: "Medlemmen hittades inte",
      memberNotFoundMessage: "Medlemmen finns inte, eller finns inte längre.",
      minutesApproved: "Protokollet godkändes",
      minutesReopened: "Protokollet öppnades igen",
      minutesReopenedNote: "Öppnat igen för redigering - texten nedan är den senast inlämnade versionen tills den lämnas in igen",
      minutesSubmitted: "Protokollet lämnades in för godkännande",
      name: "Namn",
      newAgendaItem: "Ny punkt på dagordningen",
      noFamilyOnRecord: "Inga familjerelationer finns registrerade för den här medlemmen - familjelänkar registreras bara från dopanmälningar, så det kan bero på uppgifter som saknas snarare än att medlemmen saknar familj. Du kan ändå lägga till personer nedan.",
      noMinutesYet: "Inget protokoll inlämnat ännu",
      notes: "Anteckningar",
      personalInformation: "Personuppgifter",
      quorum: "Beslutförhet",
      quorumRequired: "Krav på beslutförhet",
      reasonForReopening: "Skäl att öppna igen",
      reopen: "Öppna igen",
      transferRoleChild: "Barn",
      transferRoleManual: "Tillagd",
      transferRolePrimary: "Huvudperson",
      transferRoleSpouse: "Make/maka",
      updatedDate: "Uppdaterad",
      whoIsMoving: "Vem flyttar",
      formFieldUnnamed: "ett annat fält",
      featuresLoadFailedTitle: "Det gick inte att kontrollera vilka delar av systemet som är påslagna",
      featuresLoadFailedBody: "Den här sidan hör till en del av systemet som kan vara avstängd här, och kontrollen misslyckades.",
      featuresLoadFailedMenu: "Vissa menyval kan saknas tills detta fungerar.",
      identityLoadFailedTitle: "Det gick inte att kontrollera vem som är inloggad",
      identityLoadFailedBody: "Det här är ett fel, inte en ändring av din behörighet: allt som beror på din roll – till exempel att välja kyrka – saknas på sidorna tills detta fungerar.",
      moduleOffTitle: "Inte påslaget här",
      moduleOffBody: "Inget har gått fel: den här delen av systemet är avstängd i den här församlingen tills vidare. Fråga församlingsexpeditionen om du väntade dig att hitta den.",
      moduleOffToSignIn: "Till inloggningen",
      moduleOffApplications: "Ansökningar om kyrkliga tjänster används inte här ännu.",
      moduleOffBookings: "Bokningar, prästernas tillgänglighet och tjänstekatalogen används inte här ännu.",
      moduleOffActivities: "Aktiviteter och närvaro används inte här ännu.",
      moduleOffRegisters: "Dop-, vigsel- och fithatregistren och intygen används inte här ännu.",
      moduleOffMeetings: "Rådsmöten och åtgärdspunkter används inte här ännu.",
      // The public page (public pages, session 6)
      moduleOffPublicPages: "Församlingens offentliga sida används inte här ännu.",
      // /register while self-registration is closed (the owner's answer 3 of 3 Nov 2026)
      registrationClosedTitle: "Registreringen är inte öppen än",
      registrationClosedContact: "Kontakta församlingen om du vill bli medlem:",
      registrationClosedContactFailed: "Församlingens kontaktuppgifter kunde inte läsas in",
      // The public pages' staff screens: a super-administrator's choice of church (session 8)
      publicContentChurch: "Församling",
      publicContentChurchChoose: "Välj en församling",
      publicContentChurchesFailed: "Församlingarna att välja bland kunde inte läsas in",
      publicContentFieldPublic: "Offentligt: vem som helst kan läsa detta på församlingens sida. Nämn ingen person här – ingen lärare, präst, medlem eller anställd.",
      publicParishCheckFailedTitle: "Det gick inte att kontrollera om den här sidan är påslagen",
      publicParishCheckFailedBody: "Sidan kan mycket väl finnas; kontrollen misslyckades.",
      publicParishNoSuchPage: "Det finns ingen församlingssida på den här adressen",
      publicParishChurchFailedBody: "Församlingens sida kunde inte läsas in.",
      publicParishOpenMenu: "Öppna menyn",
      publicParishCloseMenu: "Stäng menyn",
      publicParishNavLabel: "Sidans delar",
      publicParishFooterNav: "Församlingen",
      publicParishLanguage: "Språk",
      publicParishNavCalendar: "Kalender",
      publicParishNavServices: "Gudstjänster",
      publicParishNavEducation: "Undervisning",
      publicParishNavVisit: "Besök oss",
      publicParishWelcome: "Välkommen till {{name}}",
      publicParishSeeCalendar: "Månadens kalender",
      publicParishPlanVisit: "Planera ditt besök",
      publicParishComingNext: "Härnäst",
      publicParishComingUp: "Kommande",
      publicParishComingFailed: "Det som kommer härnäst kunde inte läsas in.",
      publicParishNothingComing: "Inget är publicerat för de kommande veckorna.",
      publicParishNextService: "Nästa gudstjänst",
      publicParishNextFeast: "Nästa högtid",
      publicParishNextFast: "Fasta",
      publicParishFastBegins: "börjar {{date}}",
      publicParishCalendarTitle: "Kalender",
      publicParishCalendarFailed: "Månadens kalender kunde inte läsas in",
      publicParishCalendarKind: "Kalender",
      publicParishEthiopian: "Etiopisk",
      publicParishGregorian: "Gregoriansk",
      publicParishPreviousMonth: "Föregående månad",
      publicParishNextMonth: "Nästa månad",
      publicParishEthiopianSpan: "{{from}} – {{to}} {{year}} · det lilla datumet är det gregorianska",
      publicParishGregorianSpan: "{{months}} · det lilla datumet är det etiopiska",
      publicParishKindService: "Gudstjänst",
      publicParishKindFeast: "Högtid",
      publicParishKindFast: "Fasta",
      publicParishKindClasses: "Lektioner",
      publicParishKindNoClasses: "Inga lektioner",
      publicParishNoClassesChip: "Inga lektioner",
      publicParishCancelled: "Inställt",
      publicParishNothingThisMonth: "Inget är publicerat för den här månaden.",
      publicParishServicesTitle: "Regelbundna gudstjänster",
      publicParishServicesFailed: "De regelbundna gudstjänsterna kunde inte läsas in.",
      publicParishNoServices: "Inga regelbundna gudstjänster är publicerade.",
      publicParishEveryWeekday: "Varje {{weekday}}",
      publicParishEveryEthiopianMonth: "Dag {{day}} i varje etiopisk månad",
      publicParishNextOn: "Nästa: {{date}}",
      publicParishByAppointment: "Efter överenskommelse",
      publicParishEducationFailed: "Lektionerna kunde inte läsas in.",
      publicParishNoEducation: "Inga lektioner är publicerade.",
      publicParishNoClasses: "Inga lektioner är publicerade för den här avdelningen.",
      publicParishNoClassesOn: "Inga lektioner {{date}}",
      publicParishThisTerm: "Den här terminen",
      publicParishTerm: "Termin",
      publicParishBreaks: "Inga lektioner",
      publicParishNoBreaks: "Inga dagar utan lektioner är publicerade.",
      publicParishQuestions: "Frågor?",
      publicParishAges: "{{from}}–{{to}} år",
      publicParishAgesFrom: "Från {{from}} år",
      publicParishStarts: "Börjar {{date}}",
      publicParishNextClass: "Nästa lektion: {{date}}",
      publicParishFree: "Kostnadsfritt",
      publicParishVisitTitle: "Besök oss",
      publicParishAddress: "Adress",
      publicParishOpenMap: "Öppna kartan",
      publicParishGettingHere: "Hitta hit",
      publicParishContact: "Kontakt",
      publicParishFirstVisit: "Första besöket?",
      publicParishOrgNumber: "Org.nr {{number}}",
      ownAccountRolesLocked: "Dina egna roller och din status ändras av en annan administratör, aldrig av dig själv.",
      roleWarningSharedAddress_one: "En annan medlem delar det här kontots e-postadress. Den som läser den inkorgen får kontots inloggningslänkar.",
      roleWarningSharedAddress_other: "{{count}} andra medlemmar delar det här kontots e-postadress. Den som läser den inkorgen får kontots inloggningslänkar.",
      roleWarningSharedAddressUnknown: "Det gick inte att kontrollera om andra medlemmar delar det här kontots e-postadress - den som läser den inkorgen får kontots inloggningslänkar.",
      nameHasTitle: "En titel är inte en del av namnet - skriv namnet utan den.",
      roleHeldNotGrantable: "Innehas av det här kontot - inte din att ge eller ta bort.",
      accountRefusedSelfChange: "Inget konto ändrar sina egna roller eller sin status - det gör en annan administratör.",
      accountRefusedHoldsMore: "Det här kontot har mer behörighet än ditt, så du kan inte ändra det.",
      accountRefusedOutsideChurch: "Det här kontot hör till en annan församling.",
      transferRefusedFromAnotherChurch: "Den här medlemmen hör till en annan församling: en överföring påbörjas av medlemmens egen församling, eller av en superadministratör.",
      accountRefusedRolesOffFirst: "Inloggningen administrerar sin församling. Ta först bort dess administratörsroller; sedan kan medlemmen flyttas.",
      memberWriteOwnChurchOnly: "Medlemmar registreras och ändras av en administratör i den egna församlingen.",
      deathLoginNotTheCallersToEnd: "Att registrera dödsfallet stänger personens inloggning, som inte är din att stänga: en superadministratör stänger först inloggningen, sedan registreras dödsfallet här.",
      voidLoginNotTheCallersToPutBack: "Att ogiltigförklara fithaten återställer personens inloggning, som inte är din att ändra - en superadministratör ogiltigförklarar den.",
      correctionLoginNotTheCallersToPutBack: "Att rätta fithaten återställer inloggningen för den person den tidigare angav, som inte är din att ändra - en superadministratör rättar den.",
      voidStatusNotTheCallersToPutBack: "Att ogiltigförklara fithaten återställer statusen för en medlem i en annan kyrka, som inte är din att ändra - en superadministratör ogiltigförklarar den.",
      correctionStatusNotTheCallersToPutBack: "Att rätta fithaten återställer statusen för den person den tidigare angav, en medlem i en annan kyrka, som inte är din att ändra - en superadministratör rättar den.",
      accountRefusedGrantAboveCaller: "Du kan inte ge en roll som står över din egen.",
      accountRefusedAdministersNone: "Ditt konto administrerar inga konton.",
      accountRefusedAddressHasLogin: "Den här medlemmens e-postadress är redan en annan inloggnings. En familj kan dela en adress, men bara en inloggning kan använda den - ge medlemmen en egen adress på medlemsposten.",
      accountRefusedMemberHasLogin: "Den här medlemmen har redan en inloggning, och en medlem har bara en: använd den i stället för att öppna en ny.",
      changePassword: "Byt lösenord",
      currentPassword: "Nuvarande lösenord",
      newPassword: "Nytt lösenord",
      repeatNewPassword: "Nytt lösenord igen",
      passwordRuleHint: "Minst 12 tecken, högst 72 byte (72 bokstäver A till Z, färre av de flesta andra), och inte ett vanligt lösenord.",
      passwordTooShort: "Ett lösenord har minst 12 tecken.",
      passwordTooLong: "Ett lösenord har högst 72 byte: 72 bokstäver A till Z, färre av de flesta andra.",
      passwordCommon: "Det här lösenordet finns på en lista över vanliga lösenord: välj ett annat.",
      ownPasswordWrong: "Det nuvarande lösenordet stämmer inte.",
      tooManyAttempts: "För många felaktiga lösenord: försök igen inom 15 minuter.",
      signInRefused: "Användarnamnet eller lösenordet är fel, eller så har det gjorts för många försök: försök igen inom 15 minuter.",
      ownPasswordChanged: "Ditt lösenord är bytt. Alla andra inloggningar har avslutats.",
      ownPasswordFailed: "Lösenordet kunde inte bytas.",
      signOutEverywhere: "Logga ut överallt",
      signOutEverywhereHelp: "Avslutar alla inloggningar på det här kontot, på alla enheter, även den här.",
      signOutEverywhereFailed: "Utloggningen överallt fungerade inte.",
      accountRefusedMemberLeftRegister: "Den här medlemmen har lämnat registret - borttagen, eller ihopslagen med en annan post - så ingen inloggning på den görs aktiv. Öppna en på den post som finns kvar.",
      accountRefusedMemberDeceased: "Den här medlemmens dödsfall är registrerat, så ingen inloggning på posten görs aktiv. Om det registrerades av misstag: ogiltigförklara fithaten - det ger tillbaka inloggningen den avslutade - eller rätta statusen på medlemsposten.",
      mergeRefusedAddressOfALogin: "Posten som finns kvar har en inloggning, och en sammanslagning ändrar inte dess e-postadress. Behåll dess egen adress, eller låt dess församling ändra adressen på posten.",
      accountRefusedMemberHasNoAddress: "Den här medlemmen har ingen e-postadress, och en inloggnings länkar går till dess medlems adress. Lägg till en på medlemsposten först.",
      accountRefusedPendingByReviewOnly: "En registrering som väntar på granskning lämnar väntande läge bara genom godkännande eller avslag, på skärmen för registreringar.",
      loginAddressIsMembers: "En inloggnings adress är dess medlems: ändra den på medlemsposten.",
      memberHasNoAddress: "Ingen på medlemsposten",
      useRegistrationAddress: "Lägg adressen som angavs vid registreringen på medlemsposten (inloggningens länkar går dit)",
      mergeRefusedDeathAndActiveLogin: "Posten som slås ihop har ett registrerat dödsfall, och posten som finns kvar har en aktiv inloggning. Lös det först: om dödsfallet registrerades av misstag, ogiltigförklara fithaten eller rätta statusen; annars avsluta inloggningen.",
      mergeRefusedChaseGrantInForce_one: "Posten som slås ihop får fortfarande påminna om samtycke enligt {{count}} behörighet, och en sammanslagning flyttar aldrig en sådan. Ta bort den först, under \"Vem kan påminna om detta samtycke\"; ska den fortsätta, ge den till posten som finns kvar. Slå sedan ihop.",
      mergeRefusedChaseGrantInForce_other: "Posten som slås ihop får fortfarande påminna om samtycke enligt {{count}} behörigheter, och en sammanslagning flyttar aldrig en sådan. Ta bort dem först, under \"Vem kan påminna om detta samtycke\"; ska de fortsätta, ge dem till posten som finns kvar. Slå sedan ihop.",
      mergeRefusedActionItemOtherChurch_one: "Posten som slås ihop har {{count}} åtgärdspunkt från en annan församlings möte, och en sammanslagning ger aldrig en församlings uppgift till en annan församlings post. Låt den församlingen ge den till någon annan först, och slå sedan ihop.",
      mergeRefusedActionItemOtherChurch_other: "Posten som slås ihop har {{count}} åtgärdspunkter från en annan församlings möten, och en sammanslagning ger aldrig en församlings uppgift till en annan församlings post. Låt den församlingen ge dem till någon annan först, och slå sedan ihop.",
      mergeRefusedDeclarationBetweenThem_one: "De två posterna hör ihop genom {{count}} förälders eller vårdnadshavares anmälan: den ena är registrerad som den som anmält den andra. De kan inte vara samma person, så de slås inte ihop.",
      mergeRefusedDeclarationBetweenThem_other: "De två posterna hör ihop genom {{count}} förälders eller vårdnadshavares anmälningar: den ena är registrerad som den som anmält den andra. De kan inte vara samma person, så de slås inte ihop.",
      accountRefusedOwnPasswordOnForm: "Ditt eget lösenord byts under Byt lösenord, i menyn högst upp, som frågar efter det nuvarande - inte i det här formuläret.",
      ownPasswordElsewhere: "Ditt eget lösenord byts på en egen sida, som frågar efter det nuvarande:",
      passwordByLinkOnOpening: "Inget lösenord skrivs här: när kontot har öppnats går en länk för att välja ett till medlemmens adress. Den fungerar en gång, i 72 timmar.",
      passwordStateUnknown: "Det gick inte att läsa om kontot har ett lösenord.",
      accountHasPassword: "Kontot har ett lösenord.",
      accountHasNoPassword: "Kontot har inget lösenord ännu: ingen kan logga in med det förrän länken har använts.",
      passwordLinkLastSent: "En länk som fortfarande fungerar skapades {{sent}}, när e-postmeddelandet skickades, och fungerar till {{until}}.",
      passwordLinkClearsPassword: "En ny länk tar bort det nuvarande lösenordet direkt: det slutar fungera, och alla inloggningar på kontot avslutas.",
      passwordLinkEndsEarlierOnes: "En ny länk gör tidigare länkar ogiltiga.",
      sendPasswordLink: "Skicka en länk för att välja lösenord",
      passwordLinkQueued: "Länken skickas till medlemmens adress.",
      passwordLinkNotSent: "Länken kunde inte skickas.",
      accountRefusedPasswordByLinkOnly: "Ingen skriver ett lösenord åt någon annan: skicka en länk för att välja lösenord i stället.",
      accountRefusedPasswordLinkNotActive: "En länk för att välja lösenord skickas bara till ett aktivt konto.",
      setPasswordTitle: "Välj ditt lösenord",
      setPasswordChecking: "Kontrollerar länken…",
      setPasswordFor: "För inloggningen {{username}}. Länken fungerar en gång, till {{until}}.",
      setPasswordNew: "Nytt lösenord",
      setPasswordRepeat: "Samma lösenord igen",
      setPasswordRuleHint: "Minst 12 tecken. En lång fras som du kommer ihåg är bra.",
      setPasswordMismatch: "De två lösenorden är inte likadana.",
      setPasswordSave: "Spara mitt lösenord",
      setPasswordDone: "Ditt lösenord är sparat. Logga in med ditt användarnamn och lösenordet du valde.",
      setPasswordGoToSignIn: "Till inloggningen",
      setPasswordLinkUsed: "Den här länken har redan använts.",
      setPasswordLinkExpired: "Den här länken har gått ut: den gällde i 72 timmar.",
      setPasswordLinkEnded: "Den här länken fungerar inte längre: en nyare länk har skickats, eller så har inloggningen ändrats.",
      setPasswordLinkUnknown: "Den här länken fungerar inte. Kontrollera att hela länken kopierades från e-postmeddelandet.",
      setPasswordAskOffice: "Be församlingsexpeditionen om en ny länk.",
      setPasswordTryAgain: "Försök igen",
      moduleHeldBackTitle: "Inte på de här sidorna ännu",
      moduleHeldBackBody: "Den här delen av systemet är påslagen, men några av dess sidor säger ännu inte till när de inte går att läsa in, och en sida som misslyckas utan att säga det kan vilseleda. De hålls dolda tills de gör det. Fråga församlingsexpeditionen om du väntade dig att hitta den.",
      modulesHeldBack: "Tillbakahållna på de här sidorna fast servern har dem påslagna: några av deras sidor säger ännu inte till när de inte går att läsa in. Driftsättningen vägrar det, så de slogs på på annat sätt:",
      enrolmentsRefusedTitle: "Du kan inte se den här klassens inskrivningar",
      enrolmentsLoadFailed: "Inskrivningarna kunde inte läsas in",
      nonSessionDatesRefusedTitle: "Du kan inte se församlingens dagar utan tillfällen",
      nonSessionDatesLoadFailed: "Dagarna utan tillfällen kunde inte läsas in",
      activityBackToList: "Tillbaka till Aktiviteter",
      roleHelpIntro: "En klassledare eller volontär behöver bara USER. Att leda en klass kommer av att man läggs till som ledare på aktivitetens sida, inte av en roll. En roll som låter rätt, till exempel ROLE_SUNDAY_SCHOOL, öppnar alla medlemmar i den verksamheten.",
      roleHelpSuperAdmin: "Allt, i alla kyrkor.",
      roleHelpAdmin: "Allt i den här kyrkan, även medlemmar, aktiviteter och inställningar.",
      roleHelpUser: "Sin egen uppgift, plus klasserna de läggs till som ledare för. Räcker för en klassledare.",
      roleHelpServiceRole: "Alla medlemmar i den här verksamheten i kyrkan. Behövs inte för att leda en klass.",
      roleHelpPriesthood: "En prästs egna biktbarn, och att utfärda intyg. Behövs inte för att leda en klass.",
      // FR-12.7b - the printable blank marking sheet (PrintSheetButton.js)
      activityPrintSheet: "Skriv ut tom lista",
      activityPrintSheetHelp: "En papperslista för det här tillfället, för när ingen telefon fungerar. För in den här efteråt och förstör sedan papperet.",
      activityPrintSheetFailed: "Listan kunde inte skapas.",
      activityLoading: "Laddar…",
      closeMenu: "Stäng menyn",
      // Consent step B1 - an interpreter on an in-person consent (InterpreterField.js)
      consentInterpreterUsed: "En tolk informerade personen",
      consentGivenInPersonLabel: "Jag bekräftar att personen är här hos mig nu och har informerats om denna text",
      consentNotInPersonHint: "Inte här hos dig nu? Registrera det inte här - skicka en samtyckesbegäran via länk eller på papper. Ett samtycke som lämnades en tidigare dag går på papper.",
      consentGuardianGivenInPersonLabel: "Jag bekräftar att vårdnadshavaren är här hos mig nu och har informerats om denna text",
      consentInterpreterLanguage: "Tolkens språk",
      consentInterpreterLanguageExample: "t.ex. tigrinja",
      consentViaInterpreter: "via tolk ({{language}})",
      // M12 item 3c - the refusals a recorder can meet (recorderError.js)
      recorderErrorAlreadyRecorded: "Några av barnen var redan registrerade, kanske från en annan telefon. Listan visar nu det som sparades. Kontrollera den och rätta en rad om den är fel.",
      recorderErrorSessionCancelled: "Det här tillfället är inställt ({{reason}}), så närvaro kan inte registreras för det.",
      recorderErrorSessionCancelledNoReason: "Det här tillfället är inställt, så närvaro kan inte registreras för det.",
      recorderErrorConsentRevoked: "En vårdnadshavare har återkallat samtycket för ett av barnen, så deras närvaro kan inte registreras. Fråga församlingsexpeditionen.",
      recorderErrorCorrectionReason: "Skriv varför du ändrar: en rättelse behöver en anledning.",
      recorderErrorCorrectionNoChange: "Det är redan registrerat så. Det finns inget att ändra.",
      recorderErrorTeacherRequired: "Kryssa i minst en person som höll tillfället.",
      recorderErrorNotYours: "Du kan inte registrera det här tillfället längre. Du kanske inte längre är ledare för klassen. Fråga församlingsexpeditionen.",
      recorderErrorOffline: "Ingen anslutning, så inget sparades. Det du fyllde i finns kvar på skärmen: försök igen när du har täckning.",
      recorderErrorOfflineLoad: "Ingen anslutning, så detta kunde inte hämtas. Försök igen när du har täckning.",
      // M12 - days without sessions (NonSessionDatesPanel.js)
      nonSessionDatesTitle: "Dagar utan tillfällen",
      nonSessionDatesHelp: "Dagar då kyrkans aktiviteter inte träffas, till exempel Gena, Fasika eller en helgdag. När tillfällen skapas hoppas de över, så de visas aldrig som tillfällen som ingen höll. Lägg till varje års datum; de etiopisk-ortodoxa flyttar.",
      nonSessionDatesNone: "Inga dagar utan tillfällen ännu.",
      nonSessionDateDate: "Datum",
      nonSessionDateName: "Namn",
      nonSessionDateNameExample: "t.ex. Gena",
      nonSessionDateScope: "Gäller",
      nonSessionDateWholeChurch: "hela kyrkan",
      nonSessionDateCancelScheduled: "Ställ också in tillfällen som redan finns den dagen",
      nonSessionDateAdd: "Lägg till",
      nonSessionDateRemove: "Ta bort",
      nonSessionDateRequired: "Datum och namn krävs.",
      nonSessionDateAddedCancelled: "Sparat. Redan inplanerade tillfällen den dagen som ställdes in: {{count}}.",
      activityGeneratedExcluded: "Inte skapade (dagar utan tillfällen): {{days}}.",
      chaseActionOtherParent: "Skicka till en annan förälder i stället",
      chaseOtherParentNote: "Inget svar kom från {{name}}. Förfrågan går till denna förälder i stället, och den utgångna länken till {{name}} slutar gälla.",
      chaseOutcomeSentToOther: "skickad till den andra föräldern i stället",
      chaseStatusOnPaper: "På papper - blankett att lämna över",
      chaseStatusReturnedUnanswered: "Blanketten tillbaka, detta lämnat tomt",
      chaseProblemOnPaper: "blanketten är ute på papper - skriv ut den igen i stället för att skicka en länk",
      chaseUnreachableNoForm: "pappersblankettens egen text är ännu inte översatt till deras språk",
      chaseOutcomeOnPaper: "pappersblankett skapad",
      chasePaperFailed: "Pappersblanketten kunde inte skapas",
      chasePrintFailed: "Blanketten kunde inte skrivas ut",
      chaseNotPrintedYet: "Inte utskriven än.",
      chaseReturnedUnansweredHelp: "Den underskrivna blanketten kom tillbaka med detta lämnat tomt, så det kan inte registreras på den blanketten. Personalen avbryter den begäran på barnets post och frågar sedan igen.",
      chaseReturnedBlankOn: "Lämnade tillbaka blanketten med detta tomt, underskriven {{date}}.",
      chaseReturnedUnansweredClosedHelp: "De har tillfrågats och lämnade detta tomt - kanske med avsikt. Den begäran är avslutad. Fråga igen bara om det finns skäl till det.",
      chaseAskAgainByLink: "Fråga igen via länk",
      chaseAskAgainOnPaper: "Fråga igen på papper",
      chasePrintAgain: "Skriv ut igen",
      chasePrintForm: "Skriv ut blanketten",
      chasePaperForm: "Pappersblankett",
      chasePaperTitle: "Fråga på papper",
      chasePaperConfirm: "Skapa och skriv ut blanketten",
      chasePaperNoParent: "Ingen registrerad förälder kan få en pappersblankett: ingen har ett registrerat språk som samtyckestexten och blanketten finns på. Registrera samtycket personligen, med tolk om de behöver.",
      chaseNotOnPaper: "{{name}} kan inte få en pappersblankett: {{reason}}",
      chasePaperHelp: "Blanketten skrivs ut på förälderns registrerade språk, med samtyckestexten som den lyder nu. Lämna över den; när den kommer tillbaka underskriven, registrera den på barnets post. Inget e-postmeddelande skickas.",
      consentRequestsTitle: "Samtyckesbegäranden",
      memberLanguageLabel: "Språk de läser",
      memberLanguageNotKnown: "Okänt",
      memberLanguageHelp: "Samtyckeslänkar och pappersblanketter skickas på detta språk. Lämna det som okänt hellre än att gissa - inget skickas på ett språk som ingen har registrerat.",
      consentRequestAsk: "Be om samtycke",
      consentRequestNewTitle: "Be om samtycke",
      consentRequestOptionsFailed: "Det gick inte att hämta vad som kan efterfrågas",
      consentRequestSaveFailed: "Samtyckesbegäran kunde inte skapas",
      consentRequestItems: "Vad som efterfrågas",
      consentRequestRecipient: "Vem som tillfrågas",
      consentRequestDelivery: "Hur den når dem",
      consentRequestAnsweredBySelf: "{{name}} svarar själv på detta - 12 år eller äldre.",
      consentRequestAnsweredByParent: "En förälder svarar på detta.",
      consentRequestMixed: "Barnets eget samtycke och en förälders kan inte efterfrågas i samma begäran - fråga dem var för sig.",
      consentRequestSelf: "själv",
      consentRequestParent: "förälder",
      consentRequestOnlyTheirOwn: "Från 12 år är {{name}}s samtycke till medlemskap deras eget - en förälder kan inte ge det.",
      consentRequestOnlyAParent: "En förälder svarar på det som är valt.",
      consentRequestNoParentRecorded: "Ingen förälder är registrerad för den här medlemmen. Registrera relationen först för att fråga en förälder.",
      consentRequestByLinkOption: "Via länk - skickas med e-post",
      consentRequestOnPaperOption: "På papper - en blankett som skrivs ut nu och lämnas över",
      consentRequestMakeAndSend: "Skapa begäran",
      consentRequestMakeAndPrint: "Skapa den och skriv ut blanketten",
      consentRequestBlocked_NO_DATE_OF_BIRTH: "Inget födelsedatum är registrerat - vem som svarar beror på om de är 12 år eller äldre. Registrera det först.",
      consentRequestBlocked_OPEN_REQUEST: "Redan efterfrågat i en öppen begäran - skicka den länken igen, eller avbryt den först.",
      consentRequestBlocked_NO_EMAIL: "Ingen e-postadress är registrerad - använd papper.",
      consentRequestBlocked_ADDRESS_NOT_PROVEN: "Inget registrerat visar att adressen är deras - använd papper.",
      consentRequestBlocked_NO_LANGUAGE: "Inget språk är registrerat för dem - registrera först det språk de läser.",
      consentRequestBlocked_NO_FORM_IN_LANGUAGE: "Pappersblankettens egen text är ännu inte översatt till deras språk.",
      consentRequestBlocked_CHILD_ADDRESS_SHARED: "Adressen är också en förälders, eller en annan vuxens, i registret - ett barns eget samtycke tas personligen eller på papper, aldrig via länk.",
      consentRequestBlocked_CHILD_ADDRESS_NOT_KNOWN_OWN: "Ingen förälder finns i registret som visar att adressen bara är barnets egen - ett barns eget samtycke tas personligen eller på papper.",
      consentRequestsNone: "Ingen har tillfrågats om samtycke för den här medlemmen.",
      consentRequestsLoadFailed: "Samtyckesbegärandena kunde inte hämtas",
      consentRequestOnPaper: "På papper",
      consentRequestByLink: "Via länk",
      consentRequestTo: "till {{name}}",
      consentRequestMade: "Skapad {{date}}, referens {{ref}}",
      consentRequestPrinted: "först utskriven {{date}}",
      consentRequestNotPrinted: "inte utskriven än",
      consentRequestStatus_OPEN: "öppen",
      consentRequestStatus_EXPIRED: "länken har gått ut",
      consentRequestStatus_COMPLETED: "alla punkter besvarade",
      consentRequestStatus_CANCELLED: "avbruten",
      consentRequestStatus_REPLACED: "ersatt av en nyare begäran",
      consentItemOutcome_PENDING: "inte besvarad",
      consentItemOutcome_GRANTED: "ja",
      consentItemOutcome_REFUSED: "nej",
      consentRequestItemGuardian: "Vårdnadshavares samtycke - {{activity}}",
      consentRequestItemMembership: "Medlemskap",
      consentRequestPrint: "Skriv ut blanketten",
      consentRequestPrintAgain: "Skriv ut igen",
      consentRequestPrintFailed: "Blanketten kunde inte skrivas ut",
      consentRequestRecordReturn: "Registrera den underskrivna blanketten",
      consentRequestResend: "Skicka länken igen",
      consentRequestCancel: "Avbryt begäran",
      consentRequestCancelConfirm: "Avbryta den här begäran? Länken slutar fungera. Det som redan är besvarat förblir besvarat.",
      consentRequestCancelPaperConfirm: "Avbryta den här begäran? En blankett som redan lämnats ut kan då inte längre registreras. Det som redan är besvarat förblir besvarat.",
      consentRequestActionFailed: "Det gick inte att genomföra",
      consentRequestPaperHelp: "Registrera blanketten först när den är tillbaka och underskriven. Skanningen blir handlingen; pappret förstörs när skanningen är kontrollerad.",
      consentRequestReturnedForm: "Den återlämnade blanketten",
      consentReturnTitle: "Registrera den underskrivna blanketten",
      consentReturnSave: "Registrera",
      consentReturnSaveFailed: "Den återlämnade blanketten kunde inte sparas",
      consentReturnHelp: "För över det som står på pappret - inget mer. Blanketten skrevs ut till {{name}}.",
      consentReturnSignedOn: "Datum för underskrift",
      consentReturnSignedOnHelp: "Datumet som står bredvid underskriften.",
      consentReturnSignedByRecipient: "Underskriven av den person den skrevs ut till",
      consentReturnSignedByRecipientHelp: "{{name}}. Om någon annan har skrivit under, registrera den inte: avbryt begäran och skriv ut en ny blankett till dem.",
      consentReturnAnswers: "Svar på blanketten",
      consentReturnAnswersHelp: "Var och en som den är kryssad på pappret. En ruta som ingen kryssat i lämnas tom - det är inte ett nej. På en blankett som frågar om mer än en sak måste minst en vara besvarad. En blankett som frågar om en sak och kommer tillbaka med den tom registreras som återlämnad obesvarad.",
      consentReturnYes: "Ja",
      consentReturnNo: "Nej",
      consentReturnLeftBlank: "Lämnad tom",
      consentReturnScans: "Skanning av den underskrivna blanketten",
      consentReturnScansHelp: "En PDF, eller ett foto av varje sida (JPEG eller PNG): högst {{files}} filer, {{each}} MB per fil och {{total}} MB sammanlagt.",
      consentReturnTooManyFiles: "Högst {{max}} filer - det här är {{count}}. Skanna sidorna till en PDF.",
      consentReturnFileTooBig: "{{name}} är större än {{max}} MB. Skanna den igen med lägre upplösning.",
      consentReturnTotalTooBig: "Tillsammans är filerna större än {{max}} MB.",
      consentReturnFileType: "{{name}} är inte en PDF, JPEG eller PNG.",
      consentReturnLoadFailed: "Den återlämnade blanketten kunde inte hämtas",
      consentReturnRecorded: "Underskriven {{signedOn}}. Registrerad av {{name}} den {{date}}.",
      consentReturnScansHeading: "Skanningar",
      consentReturnScanPage: "Sida {{page}}",
      consentReturnScanReplaced: "ersatt",
      consentReturnOpenScan: "Öppna",
      consentReturnOpenFailed: "Det kunde inte öppnas",
      consentReturnAddScans: "Lägg till en bättre eller saknad skanning",
      consentReturnAddScansHelp: "Innan den kontrolleras. Kryssa i sidorna som en ny skanning ersätter; lämna alla tomma för en saknad sida. Inget skrivs över - en ersatt skanning sparas och markeras.",
      consentReturnReplaces: "Ersätter",
      consentReturnOneScanReplaces: "Ersätt sidor med en ny skanning i taget - en fil, för en sida eller för alla sidor den visar - eller lägg till sidor utan att kryssa i någon.",
      consentReturnAddScansSave: "Lägg till skanningarna",
      consentReturnReceiptHeading: "Familjens kvitto",
      consentReturnReceiptEmailed: "Skickat via e-post den {{date}}.",
      consentReturnReceiptPrinted: "Utskrivet den {{date}} av {{name}}.",
      consentReturnReceiptToPrint: "Ingen e-postadress finns för dem - skriv ut kvittot och ge det till dem.",
      consentReturnReceiptNothingAnswered: "Inget på blanketten var besvarat, så inget kvitto skickades via e-post. Skriv ut kvittot och ge det till dem - det visar att blanketten kom tillbaka tom.",
      consentReturnPrintReceipt: "Skriv ut kvittot",
      consentReturnPrintReceiptAgain: "Skriv ut kvittot igen",
      consentReturnMarkChecked: "Skanningarna stämmer med pappret",
      consentReturnMarkCheckedHelp: "Jämför varje skanning med pappret först. När det är bekräftat är skanningen handlingen och pappret förstörs.",
      consentReturnCheckedWaitsForReceipt: "Detta väntar tills familjen har fått sitt kvitto - via e-post eller utskrivet.",
      consentReturnChecked: "Kontrollerad den {{date}} av {{name}}.",
      consentReturnDestroyPaper: "Förstör pappret nu - skanningen är handlingen.",
      chaseFilterFollowUp: "Att följa upp ({{count}})",
      chaseFilterEveryone: "Alla ({{count}})",
      chaseNothingToFollowUp: "Ingen behöver följas upp just nu.",
      chaseBackToActivities: "Tillbaka till dina aktiviteter",
      signInSecondStepTitle: "Tvåstegsinloggning",
      signInSecondStepHint: "Lösenordet stämmer. Ange nu koden som din autentiseringsapp visar.",
      signInSecondStepRecoveryHint: "En av dina återställningskoder, i stället för en kod från appen. Var och en fungerar en gång.",
      signInSecondStepCode: "Kod från din autentiseringsapp",
      signInSecondStepRecovery: "Återställningskod",
      signInSecondStepUseApp: "Använd en kod från appen i stället",
      signInSecondStepUseRecovery: "Använd en återställningskod i stället",
      signInSecondStepContinue: "Logga in",
      signInSecondStepStartAgain: "Börja om med ditt lösenord",
      signInSecondStepCodeRequired: "Skriv koden.",
      signInSecondStepRefused: "Koden stämmer inte, eller så har det gjorts för många försök: försök igen inom 15 minuter. Efter fem minuter, börja om med ditt lösenord.",
      signInSecondStepLocked: "Tvåstegsinloggningen är låst efter tio felaktiga koder. Öppna länken som skickats till din adress för att låsa upp den.",
      signInSetupSent: "Din inloggning behöver nu tvåstegsinloggning. Vi har skickat en länk till din adress för att ställa in den: öppna den och logga sedan in.",
      signInSetupNotSent: "Din inloggning behöver nu tvåstegsinloggning, och ingen länk för att ställa in den kunde skickas till din adress. Kontakta församlingsexpeditionen.",
      signInSetupAgainSent: "Du loggade in med en återställningskod. Om telefonen eller appen har försvunnit, öppna länken vi har skickat till din adress för att ställa in appen igen; den du har fortsätter att fungera tills dess.",
      signInSetupAgainNotSent: "Du loggade in med en återställningskod. Om telefonen eller appen har försvunnit, be församlingsexpeditionen om ett sätt att ställa in appen igen.",
      signInContinue: "Fortsätt",
      secondStepNeeded: "Tvåstegsinloggning behövs nu för ditt konto. Logga in igen.",
      secondStepCodeRefused: "Koden stämmer inte.",
      secondStepSetupTitle: "Konfigurera tvåstegsinloggning",
      secondStepSetupChecking: "Kontrollerar länken…",
      secondStepLinkEnded: "Den här länken fungerar inte längre",
      secondStepTryAgain: "Försök igen",
      secondStepSetupAskAgain: "Logga in igen för att få en ny, eller kontakta församlingsexpeditionen.",
      secondStepSetupPasswordHint: "Först ditt lösenord, för att visa att det är din inloggning.",
      secondStepSetupPassword: "Ditt lösenord",
      secondStepContinue: "Fortsätt",
      secondStepSetupRefused: "Lösenordet stämmer inte, eller så har det gjorts för många försök: försök igen inom 15 minuter.",
      secondStepSetupScan: "Lägg till din inloggning i en autentiseringsapp: skanna koden eller skriv in nyckeln.",
      secondStepSetupQr: "QR-kod för din autentiseringsapp",
      secondStepSetupKey: "Nyckel",
      secondStepSetupAccount: "I appen heter den {{label}}.",
      secondStepSetupConfirmCode: "Koden som appen visar nu",
      secondStepSetupConfirm: "Bekräfta",
      secondStepSetupDone: "Tvåstegsinloggning är på.",
      secondStepGoToSignIn: "Logga in nu",
      recoveryCodesTitle: "Dina återställningskoder",
      recoveryCodesAdvice: "Var och en av de här tio koderna fungerar en gång, i stället för en kod från appen. Förvara dem skilt från telefonen – på papper eller i en lösenordshanterare. Den som har en av dem och ditt lösenord kan logga in som du. Om du skapar nya koder slutar de här att gälla.",
      recoveryCodesOnce: "De visas bara nu.",
      recoveryCodesPrint: "Skriv ut",
      recoveryCodesDownload: "Ladda ned",
      secondStepReopenTitle: "Lås upp tvåstegsinloggningen",
      secondStepReopenWhy: "Tvåstegsinloggningen låstes efter tio felaktiga koder i rad. Om det var du, lås upp den här. Om det inte var du kan någon känna till ditt lösenord: byt det och kontakta församlingsexpeditionen.",
      secondStepReopenButton: "Lås upp",
      secondStepReopenDone: "Tvåstegsinloggningen är upplåst",
      secondStepReopenAskOffice: "Så länge tvåstegsinloggningen är låst skickar en inloggning med ditt lösenord en ny länk, högst en gång om dagen. Eller kontakta församlingsexpeditionen.",
      ownSecondStepTitle: "Tvåstegsinloggning",
      ownSecondStepLoadFailed: "Tvåstegsinloggningens läge kunde inte hämtas",
      ownSecondStepOn: "Tvåstegsinloggning är på för din inloggning.",
      ownSecondStepOff: "Tvåstegsinloggning är av för din inloggning.",
      ownSecondStepLocked: "Den är låst efter tio felaktiga koder: öppna länken som skickats till din adress för att låsa upp den.",
      ownSecondStepLeft: "Återställningskoder kvar: {{count}}.",
      ownSecondStepFewLeft: "Få återstår: skapa nya.",
      ownSecondStepTurnOn: "Slå på",
      ownSecondStepLinkSent: "Vi har skickat en länk till din adress för att ställa in den.",
      ownSecondStepNewCodes: "Skapa nya återställningskoder",
      ownSecondStepNewCodesHint: "Med en kod från din app. De återställningskoder du har nu slutar att gälla.",
      ownSecondStepTurnOff: "Stäng av",
      ownSecondStepTurnOffHint: "Med en kod från din app. Alla sessioner för din inloggning avslutas, även den här.",
      ownSecondStepMustKeep: "Din inloggning måste behålla tvåstegsinloggningen: det ditt konto gör kräver den.",
      ownSecondStepTooMany: "Det har gjorts för många försök: försök igen inom 15 minuter.",
      ownSecondStepFailed: "Det gick inte att göra just nu.",
      ownSecondStepTurnedOff: "Tvåstegsinloggning är av för din inloggning. Logga in igen.",
      setPasswordSecondStep: "Din inloggning har tvåstegsinloggning: ange koden från din app eller en återställningskod.",
      setPasswordThenSetup: "Ditt lösenord är valt. Konfigurera nu tvåstegsinloggning",
      resetSecondStep: "Återställ tvåstegsinloggning",
      resetSecondStepConfirm: "Bara efter att ha kontrollerat vem som frågar – personligen eller genom ett samtal till numret i medlemsregistret. Tvåstegsinloggningen och dess återställningskoder tas bort, och alla sessioner för inloggningen avslutas.",
      resetSecondStepDone: "Tvåstegsinloggningen återställdes, och personen får besked via e-post.",
      resetSecondStepNotOn: "Tvåstegsinloggning är inte på för den här inloggningen.",
      resetSecondStepFailed: "Tvåstegsinloggningen kunde inte återställas.",
      secondStepLinkNotOpened: "Sidan har ingen länk",
      secondStepLinkOpenAgain: "Öppna länken igen från e-postmeddelandet, hela länken. När den har lästs tas den bort ur adressfältet, så en omladdning tappar den – själva länken fungerar fortfarande.",
      secondStepCodeSixDigits: "En kod från appen är sex siffror.",
      ownSecondStepLinkNotSent: "Ingen länk kunde skickas till din adress. Kontakta församlingsexpeditionen.",
      resetSecondStepDoneNotTold: "Tvåstegsinloggningen återställdes. Personen kunde inte få besked via e-post – adressen är inte bekräftad: meddela personen själv.",
      setPasswordSetupEnded: "Tvåstegsinloggningen ställdes inte in: länken slutade gälla först. Logga in, så skickas en ny länk till din adress.",
    },
  },
  am: {
    translation: {
      success: "ተሳክትቷል",
      error: "ስህተት",
      title: "ርዕስ",
      memberRegistration: "አባል መመዝግቢያ",
      memberNumber: "አባል ቁጥር",
      firstName: "ስም",
      middleName: "አባት ስም",
      lastName: "የአያት ስም",
      christianName: "ክርስትና ስም",
      gender: "ጾታ",
      male: "ወንድ",
      female: "ሴት",
      other: "ሌላ",
      // Liturgical/administrative terms here (confession father, membership type, etc.) need a
      // native speaker, not a machine translation - a plausible-but-wrong term in a church admin
      // tool reads as authoritative and is worse than an honest untranslated string. Do not
      // replace with machine translation; route to a native speaker for review.
      confessionKind: "የንስሐ አባት",
      confessionInternal: "የውስጥ (የንስሐ አባት ይምረጡ)",
      confessionExternal: "የውጭ (ከዚህ አጥቢያ ውጭ)",
      confessionNoneRecorded: "አልተመዘገበም",
      selectConfessionKind: "አልተመረጠም",
      confessionExternalName: "የውጭ የንስሐ አባት ስም",
      confessionExternalPlace: "የውጭ የንስሐ አባት አድራሻ/ቦታ",
      confessionExternalNameRequired: "ለውጭ የንስሐ አባት ስም ማካተት ያስፈልጋል",
      confessionFatherSectionTitle: "የንስሐ አባት",
      confessionFatherHelp: "ይህ ጥያቄ በእያንዳንዱ ማመልከቻ ላይ ይጠየቃል - ማንም እንዳልጠየቀ ስለሚያስመስለው ባዶ መተው ተቀባይነት የለውም።",
      confessionAtThisChurch: "በዚህ ቤተ ክርስቲያን የንስሐ አባት አለኝ",
      confessionAtAnotherChurch: "በሌላ ቤተ ክርስቲያን የንስሐ አባት አለኝ",
      noConfessionFather: "የንስሐ አባት የለኝም",
      confessionChurchName: "የቤተ ክርስቲያኑ ስም",
      confessionPhoneNumber: "የንስሐ አባት ስልክ ቁጥር",
      confessionDeclarationIsRequired: "እባክዎ የንስሐ አባት እንዳለዎት ወይም እንደሌለዎት፣ እና የት እንደሆነ ይግለጹ",
      confessionChurchAndPhoneRequired: "በሌላ ቤተ ክርስቲያን ላለ የንስሐ አባት የቤተ ክርስቲያኑ ስም እና ስልክ ቁጥር ያስፈልጋሉ",
      confessionCopiedFromApplicantHint: "ከዚህ ቅጽ ግርጌ ካሉት የእርስዎ መረጃዎች ይሞላል - ባዶ ከሆኑ እዚያ ይሙሉ።",
      declaredConfessionFather: "የተገለጸ",
      confessionFatherMissingWarning: "የንስሐ አባት አልተገለጸም - በካህኑ ሊታይ የሚገባ መንፈሳዊ ጉዳይ ነው እንጂ አያግድም።",
      confessionFatherOnFileDiscrepancy: "በመዝገብ ላይ፡ {{onFile}}። ይህ ማመልከቻ የሚያሳየው፡ {{declared}}። ነባሩ መዝገብ አይቀየርም።",
      reviewerOverrideOptional: "ገምጋሚ ማስተካከያ (አማራጭ)",
      noOverride: "ምንም ማስተካከያ የለም - የተገለጸውን እሴት ይጠቀሙ",
      applicantConfessionFatherTitle: "የአመልካቹ የንስሐ አባት",
      partyOneConfessionFatherTitle: "የመጀመሪያው ወገን የንስሐ አባት",
      partyTwoConfessionFatherTitle: "የሁለተኛው ወገን የንስሐ አባት",
      membershipType: "የአባልነት ዓይነት",
      parishMember: "የአጥቢያ አባል",
      guest: "እንግዳ",
      selectMembershipType: "የአባልነት ዓይነት ይምረጡ",
      dateOfBirthRequiredForParishMember: "ለአጥቢያ አባል የትውልድ ቀን ማካተት ያስፈልጋል",
      dateOfBirthUnknownToggle: "የትውልድ ቀን አይታወቅም",
      nationalIdConsentLabel: "አባሉ ይህ ብሔራዊ መታወቂያ (GDPR Art. 9) እንዲቀመጥ ፈቅዷል",
      nationalIdConsentRequired: "ብሔራዊ መታወቂያ ከመቀመጡ በፊት ስምምነት ያስፈልጋል",
      // native speaker, not a machine translation.
      transfer: "ማዛወር",
      transferMember: "አባልን ማዛወር",
      targetChurch: "የሚዘዋወሩበት ቤተ ክርስቲያን",
      targetChurchRequired: "የሚዘዋወሩበትን ቤተ ክርስቲያን ይምረጡ",
      transferDate: "የዝውውር ቀን",
      transferReason: "ምክንያት",
      reasonMove: "መኖሪያ መቀየር",
      reasonCorrection: "እርማት",
      reasonCorrectionHelp: "ይህን የሚጠቀሙት ቤተ ክርስቲያኑ በስህተት ከተመዘገበ ነው፣ ለትክክለኛ ቦታ ቅየራ አይደለም።",
      transferNote: "ማስታወሻ (አማራጭ)",
      transferWarningLoseAccess: "ይህ አባል ከዚህ ዝውውር በኋላ በእርስዎ ማውጫ ውስጥ አይገኝም።",
      transferSuccessful: "ዝውውሩ በተሳካ ሁኔታ ተመዝግቧል",
      transferSuccessfulRedirect: "ዝውውሩ ተመዝግቧል። ይህ አባል ወደ ሌላ ቤተ ክርስቲያን ስለተዛወረ በእርስዎ ማውጫ ውስጥ አይገኝም።",
      transferFailed: "ዝውውሩን መመዝገብ አልተሳካም",
      transferHistory: "የዝውውር ታሪክ",
      fromChurch: "ከቤተ ክርስቲያን",
      toChurch: "ወደ ቤተ ክርስቲያን",
      recordedBy: "መዝጋቢ",
      noTransferHistory: "የተመዘገበ ዝውውር የለም",
      // native speaker, not a machine translation.
      importMembers: "አባላትን አስገባ",
      importSelectFile: ".xlsx ፋይል ይምረጡ",
      importConsentBasis: "የስምምነት መሠረት",
      importConsentVersion: "የስምምነት ስሪት",
      importConsentHelp: "በዚህ ፋይል ውስጥ የግል መለያ ቁጥር (personnummer) ላለው እያንዳንዱ ረድፍ ይተገበራል።",
      importSubmit: "ጫን እና አስቀድመህ እይ",
      importWillCreate: "ይፈጥራል",
      importAlreadyImported: "ቀደም ሲል የገባ",
      importRejected: "ውድቅ ተደርጓል",
      importRowNumber: "ረድፍ",
      importStatus: "ሁኔታ",
      importDuplicateCandidates: "ድግግሞሽ ሊሆኑ የሚችሉ",
      importMatchedOn: "የተመሳሰለበት",
      importSkipRow: "ዝለል",
      importBackToUpload: "ወደ ጫን ተመለስ",
      importCommit: "ማስገባቱን አረጋግጥ",
      importNoRows: "ምንም ረድፍ የለም",
      importUploadFailed: "የገባውን ፋይል አስቀድሞ ማየት አልተሳካም",
      importBatchExpired: "የእርስዎ ግምገማ ጊዜ አልፎበታል። እባክዎ ፋይሉን እንደገና ይጫኑ።",
      importCommitFailed: "ማስገባቱን ማረጋገጥ አልተሳካም",
      importCreatedCount: "ተፈጥሯል",
      importSkippedCount: "ተዘሏል",
      importFailedCount: "አልተሳካም",
      importStartOver: "ሌላ ፋይል አስገባ",
      notAuthorized: "ይህን ገጽ ለማየት ፈቃድ የለዎትም።",
      of: "ከ",
      // rule as above, needs a native speaker, not a machine translation.
      duplicatesReview: "የድግግሞሽ ግምገማ",
      duplicatesSearch: "ፈልግ",
      duplicatesCompareSelected: "የተመረጡትን አወዳድር",
      duplicatesMatchScore: "የመመሳሰል ነጥብ",
      duplicatesMatchedFields: "የተመሳሰለበት መስክ",
      duplicatesNoResults: "ተዛማጅ አልተገኘም",
      duplicatesEnterCriteria: "ከላይ የፍለጋ መስፈርቶችን ያስገቡ",
      duplicatesSearchFailed: "ድግግሞሾችን መፈለግ አልተሳካም",
      duplicatesField: "መስክ",
      duplicatesSurvivor: "የሚቀመጠው",
      duplicatesLosing: "የሚጠፋው",
      duplicatesUseLosingValue: "የሚጠፋውን እሴት ተጠቀም",
      duplicatesSurvivorIs: "የሚቀመጠው፡",
      duplicatesPersonnummerNote: "ሁለቱም መዝገቦች ብሔራዊ መታወቂያ አላቸው። በመደበኛነት የሚቀመጠው መዝገብ እሴት ይያዛል፣ ነገር ግን የሚጠፋውን መዝገብ እሴት ለመጠቀም ከተመረጠ ለሚቀመጠው መዝገብ የብሔራዊ መታወቂያ ስምምነት ያስፈልጋል።",
      duplicatesReason: "ምክንያት (አማራጭ)",
      duplicatesMerge: "አዋህድ",
      duplicatesMergeSuccessful: "አባላት በተሳካ ሁኔታ ተዋህደዋል",
      duplicatesMergeFailed: "አባላትን ማዋሃድ አልተሳካም",
      duplicatesLoadFailed: "የአባል ዝርዝሮችን መጫን አልተሳካም",
      duplicatesBackToSearch: "ወደ ፍለጋ ተመለስ",
      originPlace: "መነሻ ቦታ",
      originChurch: "መነሻ ቤተ ክርስቲያን",
      auditLogs: "የኦዲት መዝገብ",
      auditLogTable: "ሰንጠረዥ",
      auditLogRecordId: "መዝገብ መለያ (ID)",
      auditLogActor: "የፈጻሚ አባል መለያ (ID)",
      auditLogAction: "ተግባር",
      auditLogAny: "ማናቸውም",
      auditLogFetchFailed: "የኦዲት መዝገቦችን መጫን አልተሳካም",
      auditLogTime: "ሰዓት",
      auditLogRecords: "መዝገብ(ቦች)",
      auditLogActorName: "ፈጻሚ",
      auditLogIp: "አይፒ (IP)",
      auditLogRevealExport: "ላክ (ያልተሸፈነ)",
      auditLogRevealSingle: "አሳይ",
      auditLogRecordsCount: "መዝገቦች",
      auditLogShowDetails: "ዝርዝሮች",
      auditLogHideDetails: "ደብቅ",
      auditLogBefore: "በፊት",
      auditLogAfter: "በኋላ",
      auditLogNoData: "(ምንም)",
      confessionGapReport: "የንስሐ አባት ክፍተት",
      reportIncludeMinors: "ታዳጊዎችን ያካትቱ (ዕድሜ ≤ 18)",
      reportFetchFailed: "ሪፖርቱን መጫን አልተሳካም",
      reportNeverCaptured: "ፈጽሞ አልተመዘገበም",
      reportNoneRecorded: "ምንም አልተመዘገበም",
      servantsRosterReport: "የአገልጋዮች ዝርዝር",
      reportAnyStatus: "ማናቸውም",
      reportStatusActive: "ንቁ",
      reportStatusOnLeave: "በዕረፍት ላይ",
      reportStatusReleased: "የተሰናበተ",
      reportCapacity: "ኃላፊነት / ድርሻ",
      reportDepartment: "ክፍል",
      reportOrdinationRank: "የማዕረግ ደረጃ / የክህነት ደረጃ",
      reportEmployed: "ተቀጣሪ",
      reportEmployer: "አሰሪ",
      reportYes: "አዎ",
      reportNo: "አይደለም",
      guestCountsReport: "የእንግዶች ብዛት",
      reportPeriodStart: "ጊዜው የሚጀመርበት",
      reportPeriodEnd: "ጊዜው የሚያበቃበት",
      reportRun: "ሪፖርት አዘጋጅ",
      reportRunPrompt: "ጊዜ ይምረጡና (አማራጭ) ሪፖርቱን ያዘጋጁ።",
      reportTotalGuests: "ጠቅላላ እንግዶች",
      reportConverted: "ወደ አባልነት የተቀየሩ",
      reportConversionRate: "የአባልነት ቅያሬ መጠን",
      reportByServiceType: "በአገልግሎት ዓይነት",
      reportServiceTypeNote: "ክፍፍል አይደለም - ከአንድ በላይ አገልግሎት ጋር የተገናኘ እንግዳ በእያንዳንዳቸው ውስጥ ይቆጠራል።",
      reportServicePriesthood: "ክህነት",
      reportServiceDeaconship: "ዲቁና",
      reportServiceSundaySchool: "ሰንበት ትምህርት ቤት",
      reportServiceParish: "አጥቢያ",
      reportServiceYouth: "ወጣቶች",
      reportUnassigned: "ያልተመደበ",
      reports: "ሪፖርቶች",
      directory: "ማውጫ",
      includeGuests: "እንግዶችን ያካትቱ",
      department: "ክፍል",
      ageBand: "የዕድሜ ክልል",
      ageBandAny: "ማንኛውም ዕድሜ",
      ageBandYouth: "ወጣት (0-18)",
      youngAdult: "ጎልማሳ ወጣት (19-35)",
      adult: "አዋቂ (36-59)",
      senior: "አረጋዊ (60+)",
      missingDateOfBirth: "የትውልድ ቀን የጎደለው",
      searchByPersonalNumber: "በግል መለያ ቁጥር ፈልግ",
      applyFilters: "ማጣሪያዎችን ተግብር",
      resetFilters: "ማጣሪያዎችን አጽዳ",
      totalResults: "ውጤቶች",
      anyChurch: "ሁሉም ቤተ ክርስቲያናት",
      anyStatus: "ማንኛውም ሁኔታ",
      anyMembershipType: "ማናቸውም",
      search: "ፈልግ",
      noResults: "ምንም ውጤት የለም",
      emailAddress: "ኢሜል አድራሻ",
      phoneNumber: "ስልክ ቁጥር",
      dateOfBirth: "የትውልድ ቀን",
      profession: "ሙያ",
      register: "መዝግብ",
      members: "አባላት",
      dashboard: "ዳሽቦርድ",
      churchService: "የቤተ ክርስቲያን አገልግሎት",
      baptisms: "ጥምቀት",
      maritalStatus: "የጋብቻ ሁኔታ",
      churchMemberInfoManagementSystem: "የአባል መረጃ አስተዳደር ሲስተም (CMMS)",
      registerMembers: "አባላትን መመዝግብ",
      registerEvents: "ክስተቶችን መመዝግብ (ጥምቀት፣ ሰርግ)",
      getStarted: "ጀምር",
      churchName: "የቤተ ክርስቲያን ስም",
      spiritualFathersName: "የንስሀ አባት ስም",
      address: "አድራሻ",
      city: "ከተማ",
      savingSuccessful: "መረጃው በሚገባ ታድሷል",
      savingError: "መረጃውን ማስቀመጥ አልተቻለም። እባክዎን የIT መምሪያውን ያነጋግሩ",
      membersTableTitle: "ቤተ ክርስቲያኑ አባላት ዝርዝር",
      apartmentNumber: "የአፓርታመንት ቁጥር",
      postCode: "ፖስታ ኮድ",
      country: "ሀገር",
      churchCode: "የቤተ ክርስቲያን ኮድ",
      churchCodeRequired: "ለአዲስ ቤተ ክርስቲያን የቤተ ክርስቲያን ኮድ ማካተት ያስፈልጋል - የምስክር ወረቀት ማጣቀሻ ቁጥር ቅድመ ቅጥያ ይሆናል",
      organisationsnummer: "የድርጅት ቁጥር",
      organisationsnummerRequired: "ለአዲስ ቤተ ክርስቲያን የድርጅት ቁጥር ማካተት ያስፈልጋል",
      telefon: "ስልክ",
      userNameIsRequired: "የተጠቃሚ ስም አስፈላጊ ነው!",
      passwordIsRequired: "የይለፍ ቃል አስፈላጊ ነው!",
      userNameOrPasswordIncorrect: "የተጠቃሚ ስም ወይም የይለፍ ቃል ትክክል አይደለም።",
      sessionExpired: "ክፍለ ጊዜዎ አልቋል። እባክዎ እንደገና ይግቡ።",
      login: "ግባ",
      dontHaveAnAccount:
        "እስካሁን አካውንት የሎትም ነበር? እባክዎን በ dev@medhanealem.se ኢሜል ይላኩ አካውንት ለማግኘት።",
      forgotPassword: "የይለፍ ቃል ረሳሁ?",
      userName: "የተጠቃሚ ስም",
      password: "የይለፍ ቃል",
      loginToSystem: "ወደ ሲስተሙ ግባ",
      accessDeniedTitle: "መግባት ተከልክሏል",
      accessDeniedBody: "ይህን ገጽ ለማየት ፈቃድ የለዎትም። ስህተት ነው ብለው ካሰቡ፣ አስተዳዳሪን ያነጋግሩ።",
      backToDashboard: "ወደ መቆጣጠሪያ ሰሌዳ ተመለስ",
      editMember: "አባልን ያርትዑ",
      memberDetails: "የአባል መረጃ",
      edit: "አርትዕ",
      update: "አድስ",
      updateSuccessful: "አባል በሚገባ ተስተካክሏል",
      updateError: "አባልን ማዘመን አልተቻለም",
      errorFetchingMember: "የአባል ዝርዝሮችን ማግኘት አልተቻለም",
      actions: "ተግባራት",
      registerChild: "ልጅ መዝግብ",
      showFamily: "ቤተሰብ አሳይ",
      familyTree: "የቤተሰብ ዝርዝር",
      spouse: "የትዳር ጓደኛ",
      children: "ልጆች",
      parents: "ወላጆች",
      noFamilyMembers: "ምንም የቤተሰብ አባላት አልተገኙም",
      searchingMembers: "አባላትን በመፈለግ ላይ...",
      selectExistingMember: "ያለውን አባል ምረጥ ወይም አዲስ መዝግብ",
      relationshipRegistered: "ግንኙነት በተሳካ ሁኔታ ተመዝግቧል",
      errorRegisteringRelationship: "ግንኙነት መመዝግብ አልተቻለም",
      confirmDeleteMember: "ይህን አባል መሰረዝ ይፈልጋሉ?",
      memberDeletedSuccessfully: "አባል በተሳካ ሁኔታ ተሰርዟል",
      errorDeletingMember: "አባልን መሰረዝ አልተቻለም",
      selectDate: "ቀን ይምረጡ",
      registerNewMember: "አዲስ አባል መዝግብ",
      close: "ዝጋ",
      cancel: "ሰርዝ",
      confirm: "አረጋግጥ",
      // Baptism translations
      registerBaptism: "ጥምቀት መዝግብ",
      editBaptism: "ጥምቀት አርትዕ",
      correctBaptism: "የጥምቀት መዝገብ አስተካክል",
      baptismsListTitle: "የጥምቀት ዝርዝር",
      registerNewBaptism: "አዲስ ጥምቀት መዝግብ",
      personBeingBaptized: "የሚጠመቅ ሰው",
      spiritualFather: "የክርስትና አባት",
      priest: "ካህን",
      selectPriest: "ካህን ይምረጡ",
      baptismDate: "የጥምቀት ቀን",
      pleaseSelectAllMembers: "እባክዎን ሁሉንም አስፈላጊ አባላት ይምረጡ",
      baptismCreatedSuccessfully: "ጥምቀት በተሳካ ሁኔታ ተመዝግቧል",
      baptismUpdatedSuccessfully: "ጥምቀት በተሳካ ሁኔታ ተስተካክሏል",
      errorSavingBaptism: "ጥምቀትን ማስቀመጥ አልተቻለም",
      errorFetchingBaptism: "የጥምቀት ዝርዝሮችን ማግኘት አልተቻለም",
      failedToFetchBaptisms: "ጥምቀቶችን ማምጣት አልተቻለም",
      confirmDeleteBaptism: "ይህን ጥምቀት መሰረዝ ይፈልጋሉ?",
      baptismDeletedSuccessfully: "ጥምቀት በተሳካ ሁኔታ ተሰርዟል",
      errorDeletingBaptism: "ጥምቀትን መሰረዝ አልተቻለም",
      baptismId: "የጥምቀት መለያ",
      personBaptized: "የተጠመቀ ሰው",
      errorFetchingCertificate: "ሰርተፊኬት ማምጣት አልተቻለም",
      // Marriage translations
      registerMarriage: "ጋብቻ መዝግብ",
      editMarriage: "ጋብቻ አርትዕ",
      correctMarriage: "የጋብቻ መዝገብ አስተካክል",
      marriagesListTitle: "የጋብቻ ዝርዝር",
      registerNewMarriage: "አዲስ ጋብቻ መዝግብ",
      firstSpouse: "ባል",
      secondSpouse: "ሚስት",
      marriageDate: "የጋብቻ ቀን",
      marriageCreatedSuccessfully: "ጋብቻ በተሳካ ሁኔታ ተመዝግቧል",
      marriageUpdatedSuccessfully: "ጋብቻ በተሳካ ሁኔታ ተስተካክሏል",
      errorSavingMarriage: "ጋብቻን ማስቀመጥ አልተቻለም",
      errorFetchingMarriage: "የጋብቻ ዝርዝሮችን ማግኘት አልተቻለም",
      failedToFetchMarriages: "ጋብቻዎችን ማምጣት አልተቻለም",
      confirmDeleteMarriage: "ይህን ጋብቻ መሰረዝ ይፈልጋሉ?",
      marriageDeletedSuccessfully: "ጋብቻ በተሳካ ሁኔታ ተሰርዟል",
      errorDeletingMarriage: "ጋብቻን መሰረዝ አልተቻለም",
      marriageId: "የጋብቻ መለያ",
      marriages: "ጋብቻዎች",
      certificates: "ሰርተፊኬቶች",
      englishCertificate: "እንግሊዝኛ ሰርተፊኬት",
      swedishCertificate: "ስዊድንኛ ሰርተፊኬት",
      amharicCertificate: "አማርኛ ሰርተፊኬት",
      currentMember: "የአሁኑ አባል",
      parent: "ወላጅ",
      child: "ልጅ",
      membershipCertificate: "የአባልነት ምስክር ወረቀት",
      fithat: "ፍትሐት",
      fithatRecords: "የፍትሐት መዝገቦች",
      fithatRecordsListTitle: "የፍትሐት መዝገቦች ዝርዝር",
      registerNewFithat: "አዲስ ፍትሐት መዝግብ",
      registerFithat: "ፍትሐት መዝግብ",
      correctFithat: "የፍትሐት መዝገብ አስተካክል",
      departedPerson: "ሟች",
      fithatDate: "የፍትሐት ቀን",
      fithatCreatedSuccessfully: "የፍትሐት መዝገብ በተሳካ ሁኔታ ተፈጥሯል",
      fithatUpdatedSuccessfully: "የፍትሐት መዝገብ በተሳካ ሁኔታ ታድሷል",
      errorSavingFithat: "የፍትሐት መዝገብ በማስቀመጥ ላይ ስህተት ተፈጥሯል",
      errorFetchingFithat: "የፍትሐት መዝገብ ዝርዝሮችን በማምጣት ላይ ስህተት ተፈጥሯል",
      failedToFetchFithatRecords: "የፍትሐት መዝገቦችን ማምጣት አልተሳካም",
      fithatRecordId: "የፍትሐት መዝገብ መለያ (ID)",

      officiatingChurch: "ሥርዓቱን የሚፈጽመው ቤተ ክርስቲያን",
      hostingChurch: "አስተናጋጅ ቤተ ክርስቲያን",
      spouse1HomeChurch: "የመጀመሪያው ተጋቢ ቤተ ክርስቲያን",
      spouse2HomeChurch: "የሁለተኛው ተጋቢ ቤተ ክርስቲያን",
      fatherName: "የአባት ስም",
      motherName: "የእናት ስም",
      baptizedPersonSection: "ተጠማቂው",
      godparentSection: "የክርስትና አባት/እናት",

      correctRecord: "አስተካክል",
      correctionReason: "የማስተካከያ ምክንያት",
      correctionReasonRequired: "ለዚህ ማስተካከያ ምክንያት ማካተት ያስፈልጋል",
      voidRecordAction: "ሰርዝ",
      voidReasonLabel: "የመሰረዝ ምክንያት",
      voidReasonRequired: "ይህን መዝገብ ለመሰረዝ ምክንያት ማካተት ያስፈልጋል",
      voidVsCorrectionExplanation: "መሰረዝ ከማስተካከያ የተለየ ነው። ዝርዝሮቹ ስህተት ሲሆኑ ክስተቱ ግን ሲፈጸም አስተካክል ይጠቀሙ። ይህ መዝገብ ጨርሶ መኖር ያልነበረበት ሲሆን ብቻ ሰርዝ ይጠቀሙ - ለኦዲት ዓላማ በስርዓቱ ውስጥ ይቆያል፣ ነገር ግን ከዝርዝሮች ይወገዳል እና ከዚያ በኋላ በምስክር ወረቀት ማረጋገጫ አይረጋገጥም።",
      recordVoidedSuccessfully: "መዝገቡ ተሰርዟል",
      errorVoidingRecord: "መዝገቡን በመሰረዝ ላይ ስህተት",
      certificateRecordVoided: "ይህ መዝገብ ተሰርዟል",
      viewHistory: "ታሪክ",
      correctionHistory: "የእርማት ታሪክ",
      errorFetchingCorrectionHistory: "የእርማት ታሪክ በማምጣት ላይ ስህተት ተፈጥሯል",
      noCorrectionsYet: "እስካሁን ምንም እርማቶች አልተመዘገቡም።",
      correctedBy: "አስተካካይ",
      correctedAt: "የተስተካከለበት",
      viewChanges: "ለውጦችን አሳይ",

      generateCertificate: "አዘጋጅ (Generate)",
      reissueCertificate: "እንደገና አውጣ",
      reissueReason: "እንደገና የሚወጣበት ምክንያት",
      reissueReasonRequired: "ይህን የምስክር ወረቀት እንደገና ለማውጣት ምክንያት ያስፈልጋል",
      reissueLanguage: "ቋንቋ",
      referenceNumberLabel: "የማጣቀሻ ቁጥር",
      certificateDisclaimerTitle: "ይህ የመጨረሻው የምስክር ወረቀት አይደለም",
      certificateDisclaimerBody: "ይህ ሰነድ በሰጪው ቤተ ክርስቲያን ማኅተም እና ፊርማ እስካልተረጋገጠ ድረስ ተቀባይነት የለውም። መዝገቡ መፈጠሩን ያረጋግጣል እንጂ የተሟላ የምስክር ወረቀት አይደለም።",
      confirmAndDownload: "ተረድቻለሁ - አውርድ",
      certificateReissuedSuccessfully: "የምስክር ወረቀቱ በተሳካ ሁኔታ እንደገና ወጥቷል",
      errorReissuingCertificate: "የምስክር ወረቀት እንደገና በማውጣት ላይ ስህተት ተፈጥሯል",

      verifyCertificateTitle: "የምስክር ወረቀት አረጋግጥ",
      certificateReferenceNumberInput: "የምስክር ወረቀት ማጣቀሻ ቁጥር",
      verifyCertificateButton: "አረጋግጥ",
      certificateRecordValid: "ይህ የምስክር ወረቀት መዝገብ ትክክለኛ ነው።",
      certificateRecordNotFound: "ለዚህ ማጣቀሻ ቁጥር ምንም የምስክር ወረቀት መዝገብ አልተገኘም።",
      issuingChurch: "ሰጪው ቤተ ክርስቲያን",
      issueDate: "የተሰጠበት ቀን",

      pendingReminders: "የሚጠባበቁ የጥምቀት ማሳሰቢያዎች",
      dueForReminder: "ለማሳሰቢያ የደረሰ",
      timingUnresolved: "ጊዜው ያልተወሰነ",
      timingUnresolvedNote: "ለእነዚህ አባላት ጾታ ስላልተመዘገበ፣ የ40ኛው/80ኛው ቀን ጊዜ ሊሰላ አይችልም። ይህን ለመፍታት ጾታቸውን ይመዝግቡ።",
      reminderDayCount: "ቀን",
      needsAttention: "ትኩረት ይፈልጋል",
      allRecords: "ሁሉም",

      pendingEndings: "የሚጠባበቁ የጋብቻ ፍጻሜዎች",
      endMarriageAction: "ጋብቻ አብቃ",
      endReasonLabel: "የሚያበቃበት ምክንያት",
      spouseDeceased: "ተጋቢ አርፏል/አርፋለች",
      dissolved: "ፈርሷል",
      endedAtLabel: "ያበቃበት",
      endNoteLabel: "ማስታወሻ (አማራጭ)",
      marriageEndedSuccessfully: "የጋብቻ ፍጻሜ በተሳካ ሁኔታ ተመዝግቧል",
      errorEndingMarriage: "የጋብቻ ፍጻሜን በመመዝገብ ላይ ስህተት ተፈጥሯል",
      spouseName: "ተጋቢ",
      deceasedMember: "ያረፈ አባል",

      pendingDeceasedStatus: "የሚጠባበቁ የዕረፍት-ሁኔታ ማሻሻያዎች",
      currentStatusLabel: "የአሁን ሁኔታ",
      updateMemberStatus: "የአባል ሁኔታ አዘምን",

      administration: "አስተዳደር",
      manageUsers: "ተጠቃሚዎችን ያስተዳድሩ",
        manageFamily: "ቤተሰብ አስተዳደር",
      users: "ተጠቃሚዎች",
      user: "ተጠቃሚ",
      createUser: "ተጠቃሚ መዝግብ",
      editUser: "ተጠቃሚን ያርትዑ",
      userCreatedSuccessfully: "ተጠቃሚ በተሳካ ሁኔታ ተፈጥሯል",
      userUpdatedSuccessfully: "ተጠቃሚ በተሳካ ሁኔታ ተስተካክሏል",
      errorCreatingUser: "ተጠቃሚ መመዝገብ አልተቻለም",
      errorUpdatingUser: "ተጠቃሚን ማዘመን አልተቻለም",
      associatedMember: "ተጓዳኝ አባል",
      accountStatus: "የመለያ ሁኔታ",
      roles: "ሚናዎች",
      failedToFetchCountries: "ሀገሮችን ማምጣት አልተቻለም",
      pleaseFieldAllRequired: "እባክዎን ሁሉንም አስፈላጊ መረጃዎች ይሙሉ",
      street: "መንገድ",
      countryName: "ሀገር",
      // Employee translations
      addEmployee: "ሰራተኛ መዝግብ",
      editEmployee: "ሰራተኛን ያርትዑ",
      manageEmployees: "የቤተ ክርስቲያን ሰራተኞችን ያስተዳድሩ",
      pendingMinutesApproval: "በመጠባበቅ ላይ ያሉ ቃለ ጉባኤዎች",
      failedToFetchPendingMinutes: "በመጠባበቅ ላይ ያሉ ቃለ ጉባኤዎችን ማምጣት አልተቻለም",
      noPendingMinutes: "ለመገምገም በመጠባበቅ ላይ ያሉ ቃለ ጉባኤዎች የሉም",
      submittedBy: "ያቀረበ",
      submittedAt: "የቀረበበት ጊዜ",
      meeting: "ስብሰባ",
      view: "ይመልከቱ",
      minutesApprovedSuccessfully: "ቃለ ጉባኤ በተሳካ ሁኔታ ጸድቋል",
      minutesRejectedSuccessfully: "ቃለ ጉባኤ ተደመውቋል",
      memberFullName: "የአባሉ ሙሉ ስም",
      professionType: "የሙያ አይነት",
      failedToFetchEmployees: "ሰራተኞችን ማምጣት አልተቻለም",
      failedToFetchEmployeeDetails: "የሰራተኛ ዝርዝሮችን ማምጣት አልተቻለም",
      employeeDeletedSuccessfully: "ሰራተኛ በተሳካ ሁኔታ ተሰርዟል",
      errorDeletingEmployee: "ሰራተኛን መሰረዝ አልተቻለም",
      employeeAddedSuccessfully: "ሰራተኛ በተሳካ ሁኔታ ታክሏል",
      employeeUpdatedSuccessfully: "ሰራተኛ በተሳካ ሁኔታ ተስተካክሏል",
      errorSavingEmployee: "ሰራተኛን ማስቀመጥ አልተቻለም",
      confirmDeleteEmployee: "ይህን ሰራተኛ መሰረዝ ይፈልጋሉ?",
      searchByName: "በስም ይፈልጉ...",
      // Service Member translations
      registerServiceMember: "የአገልግሎት አባል መዝግብ",
      manageServiceMembers: "የአገልግሎት አባላትን ያስተዳድሩ",
      serviceName: "የአገልግሎት ስም",
      selectService: "አገልግሎት ይምረጡ",
      failedToFetchServiceMembers: "የአገልግሎት አባላትን ማምጣት አልተቻለም",
      failedToFetchServices: "አገልግሎቶችን ማምጣት አልተቻለም",
      serviceMemberDeletedSuccessfully: "የአገልግሎት አባል በተሳካ ሁኔታ ተሰርዟል",
      errorDeletingServiceMember: "የአገልግሎት አባልን መሰረዝ አልተቻለም",
      serviceMemberRegisteredSuccessfully: "የአገልግሎት አባል በተሳካ ሁኔታ ተመዝግቧል",
      errorRegisteringServiceMember: "የአገልግሎት አባልን መመዝገብ አልተቻለም",
      confirmDeleteServiceMember: "ይህን የአገልግሎት አባል ምዝገባ መሰረዝ ይፈልጋሉ?",
      failedToFetchDetails: "ዝርዝሮችን ማምጣት አልተቻለም",
      memberServiceUpdated: "የአባል አገልግሎት በተሳካ ሁኔታ ተስተካክሏል",
      memberServiceAdded: "የአባል አገልግሎት በተሳካ ሁኔታ ታክሏል",
      errorSavingDetails: "ዝርዝሮችን ማስቀመጥ አልተቻለም",
      editServiceMember: "የአገልግሎት አባልን ያርትዑ",
      beginDate: "የመጀመሪያ ቀን",
      endDate: "የመጨረሻ ቀን",
      areYouSureYouWantToDeleteThisMember: "ይህን አባል መሰረዝ ይፈልጋሉ?",
      confirmDeleteUser: "ይህን ተጠቃሚ መሰረዝ ይፈልጋሉ?",
      userDeletedSuccessfully: "ተጠቃሚ በተሳካ ሁኔታ ተሰርዟል",
      errorDeletingUser: "ተጠቃሚን መሰረዝ አልተቻለም",
      // Church Service Management translations
      manageChurchServices: "የቤተ ክርስቲያን አገልግሎቶችን ያስተዳድሩ",
      addService: "አገልግሎት ይጨምሩ",
      editService: "አገልግሎት ያርትዑ",
      selectServiceType: "የአገልግሎት አይነት ይምረጡ",
      priesthood: "ክህነት",
      deaconship: "ዲቁና",
      sundaySchoolService: "የሰንበት ትምህርት ቤት",
      serviceAddedSuccessfully: "አገልግሎት በተሳካ ሁኔታ ታክሏል",
      serviceUpdatedSuccessfully: "አገልግሎት በተሳካ ሁኔታ ተስተካክሏል",
      serviceDeletedSuccessfully: "አገልግሎት በተሳካ ሁኔታ ተሰርዟል",
      errorAddingService: "አገልግሎት መጨመር አልተቻለም",
      errorUpdatingService: "አገልግሎት ማስተካከል አልተቻለም",
      errorDeletingService: "አገልግሎት መሰረዝ አልተቻለም",
      confirmDeleteService: "ይህን የቤተ ክርስቲያን አገልግሎት መሰረዝ ይፈልጋሉ?",
      failedToFetchChurchServices: "የቤተ ክርስቲያን አገልግሎቶችን ማምጣት አልተቻለም",
      noServicesFound: "አገልግሎቶች አልተገኙም",
      failedToFetchServiceDetails: "የአገልግሎት ዝርዝሮችን ማምጣት አልተቻለም",
      // Dashboard translations
      activeMembers: "መእመናን",
      employees: "ሰራተኞች",
      currentActiveMembers: "የአሁኑ መእመናን",
      totalEmployees: "ጠቅላላ ሰራተኞች",
      totalBaptisms: "ጠቅላላ ጥምቀቶች",
      totalMarriages: "ጠቅላላ ጋብቻዎች",
      sundaySchoolParticipants: "የሰንበት ትምህርት ቤት ተሳታፊዎች",
      totalDeacons: "ጠቅላላ ዲያቆናት",
      employeesByYear: "በየዓመቱ የተመዘገቡ ሰራተኞች",
      baptismsByYear: "በየዓመቱ የተመዘገቡ ጥምቀቶች",
      marriagesByYear: "በየዓመቱ የተመዘገቡ ጋብቻዎች",
      sundaySchoolByYear: "በየዓመቱ የተመዘገቡ የሰንበት ትምህርት ቤት አባላት",
      deaconsByYear: "በየዓመቱ የተመዘገቡ ዲያቆናት",
      failedToFetchDashboardStats: "የዳሽቦርድ ስታቲስቲክስ ማምጣት አልተቻለም",
      searchMember: "አባል ይፈልጉ...",
      passwordIsRequiredForNew: "ለአዲሱ ተጠቃሚ የይለፍ ቃል ያስፈልጋል",
      passwordMinLength: "የይለፍ ቃል ቢያንስ 6 ቁምፊዎች መሆን አለበት",
      usernameMinLength: "የተጠቃሚ ስም ቢያንስ 3 ቁምፊዎች መሆን አለበት",
      failedToFetchUsers: "ተጠቃሚዎችን ማምጣት አልተቻለም",
      failedToFetchRoles: "ሚናዎችን ማምጣት አልተቻለም",
      failedToFetchUserDetails: "የተጠቃሚ ዝርዝሮችን ማምጣት አልተቻለም",
      atLeastOneRoleRequired: "ቢያንስ አንድ ሚና ያስፈልጋል",
      emailIsRequired: "ኢሜል ያስፈልጋል",
      leaveBlankToKeepCurrent: "የአሁኑን የይለፍ ቃል ለመጠበቅ ባዶ ይተዉ",
      usernameIsRequired: "የተጠቃሚ ስም ያስፈልጋል",
      passwordsDoNotMatch: "የይለፍ ቃሎቹ አይመሳሰሉም",
      firstNameIsRequired: "ስም ያስፈልጋል",
      lastNameIsRequired: "የአባት ስም ያስፈልጋል",
      confirmPassword: "የይለፍ ቃል ያረጋግጡ",
      registerAccountTitle: "አካውንት ይፍጠሩ",
      registerAccountSubtitle: "ዝርዝርዎን ከዚህ በታች ያስገቡ - አስተዳዳሪ አካውንትዎን ገምግሞ ያጸድቃል።",
      personalNumberOptionalHelp: "አማራጭ። የስዊድን የግል መለያ ቁጥር (personal number) ገና ከሌለዎት ይህን ባዶ ይተዉት - አሁንም መመዝገብ ይችላሉ።",
      registrationSubmissionFailed: "ምዝገባው አልተሳካም - እባክዎ ቅጹን አረጋግጠው እንደገና ይሞክሩ።",
      backToLogin: "ወደ መግቢያ ተመለስ",
      alreadyHaveAnAccount: "ቀድሞውኑ አካውንት አለዎት?",
      pendingAccounts: "የሚጠባበቁ አካውንቶች",
      pendingAccountsSubtitle: "ግምገማ የሚጠብቁ የራስ-ምዝገባዎች",
      noPendingAccounts: "የሚጠባበቁ አካውንቶች የሉም",
      requestedIdentity: "የተጠየቀ ማንነት",
      requestedOn: "የተጠየቀበት",
      rejectAccount: "አካውንት ውድቅ አድርግ",
      accountApprovedSuccessfully: "አካውንቱ በተሳካ ሁኔታ ጸድቋል",
      accountRejectedSuccessfully: "አካውንቱ በተሳካ ሁኔታ ውድቅ ተደርጓል",
      errorApprovingAccount: "አካውንቱን ማጽደቅ አልተሳካም",
      errorRejectingAccount: "አካውንቱን ውድቅ ማድረግ አልተሳካም",
      failedToFetchPendingAccounts: "የሚጠባበቁ አካውንቶችን ማምጣት አልተሳካም",
      consentBasisLabel: "ይህን የግል መለያ ቁጥር ለማስቀመጥ ሕጋዊ መሠረት",
      consentBasisSelfSubmitted: "በራስ የቀረበ (አመልካቹ ራሱ ያቀረበው)",
      consentBasisReligiousBody: "በቤተ ክርስቲያኑ በአባሉ ስም የተመዘገበ",
      consentBasisRequiredForPersonalNumber: "የግል መለያ ቁጥር ሲሰጥ ሕጋዊ መሠረት ያስፈልጋል",
      myAccount: "የእኔ አካውንት",
      myProfile: "የኔ መገለጫ",
      myApplications: "የእኔ ማመልከቻዎች",
      myBookings: "የእኔ ቀጠሮዎች / ማስያዣዎች",
      myCertificates: "የእኔ ምስክር ወረቀቶች",
      failedToFetchMyProfile: "መገለጫዎን ማምጣት አልተሳካም",
      failedToFetchMyBookings: "ቀጠሮዎችዎን ማምጣት አልተሳካም",
      failedToFetchMyCertificates: "ምስክር ወረቀቶችዎን ማምጣት አልተሳካም",
      myProfileReadOnlyNote: "ይህ መረጃ ለማንበብ ብቻ ነው። እርማት ለመጠየቅ የቤተ ክርስቲያንዎን አስተዳዳሪ ያነጋግሩ።",
      fullName: "ሙሉ ስም",
      confessionFather: "የንስሐ አባት",
      confessionFatherNoneRecorded: "ምንም አልተመዘገበም",
      notificationChannelPreference: "የማሳወቂያ መንገድ ምርጫ",
      preferredLanguage: "የተመረጠ ቋንቋ",
      noBookingsYet: "እስካሁን ምንም ቀጠሮ አላስያዙም።",
      noCertificatesYet: "እስካሁን ምንም የምስክር ወረቀት የለዎትም።",
      applicationDate: "የማመልከቻ ቀን",
      clergyMember: "ካህን",
      certificateType: "የምስክር ወረቀት ዓይነት",
      download: "አውርድ",
      notAuthorizedToManageThisClergyMember: "የእርስዎን ተደራሽነት ብቻ ማስተዳደር ይችላሉ፣ ወይም የቤተ ክርስቲያን አስተዳዳሪ ከሆኑ የካህናትን ማስተዳደር ይችላሉ።",
      addChurch: "ቤተ ክርስቲያን መዝግብ",
      editChurch: "ቤተ ክርስቲያንን ያርትዑ",
      manageChurches: "አብያተ ክርስቲያናትን ያስተዳድሩ",
      failedToFetchChurches: "አብያተ ክርስቲያናትን ማምጣት አልተቻለም",
      failedToFetchChurchDetails: "የቤተ ክርስቲያን ዝርዝሮችን ማምጣት አልተቻለም",
      churchDeletedSuccessfully: "ቤተ ክርስቲያን በተሳካ ሁኔታ ተሰርዟል",
      selectCountry: "ሀገር ይምረጡ",
      errorDeletingChurch: "ቤተ ክርስቲያንን መሰረዝ አልተቻለም",
      churchAddedSuccessfully: "ቤተ ክርስቲያን በተሳካ ሁኔታ ታክሏል",
      churchUpdatedSuccessfully: "ቤተ ክርስቲያን በተሳካ ሁኔታ ተስተካክሏል",
      errorSavingChurch: "ቤተ ክርስቲያንን ማስቀመጥ አልተቻለም",
      confirmDeleteChurch: "ይህን ቤተ ክርስቲያን መሰረዝ ይፈልጋሉ?",
      export: "ወደ Excel አስቀምጥ",
      rowsPerPage: "ረድፎች በአንድ በገፅ",
      prev: "ቀዳሚ",
      next: "ቀጣይ",
      PROFILE_SETTINGS: "የፕሮፋይል አስተካክል",
      LOGOUT: "ውጣ",
      selectColumnsToExport: "ለመላክ አምዶችን ይምረጡ",
      exportSuccessful: "ወደ Excel መላክ ተሳክቷል",
      exportFailed: "ወደ Excel መላክ አልተሳካም",
      selectMaritalStatus: "የጋብቻ ሁኔታ ይምረጡ",
        selectGender: "ጾታ ይምረጡ",
        selectSpiritualFather: "የእምነት አባት ይምረጡ",
        selectChurch: "ቤተ ክርስቲያን ይምረጡ",
      selectStatus: "ሁኔታ ይምረጡ",
      status: "ሁኔታ",
      ACTIVE: "ንቁ",
      INACTIVE: "ንቁ ያልሆነ",
      MOVED: "ተዛውሯል",
      DECEASED: "ሞቷል",
      // Meeting Minutes translations (Amharic)
      meetings: "ስብሰባዎች",
      meetingsListTitle: "የስብሰባ ዝርዝር",
      addMeeting: "ስብሰባ ጨምር",
      editMeeting: "ስብሰባ አርትዕ",
      scheduleMeeting: "ስብሰባ ቀጠሮ",
      meetingTitle: "የስብሰባ ርዕስ",
      meetingDate: "የስብሰባ ቀን",
      startTime: "የመጀመሪያ ሰዓት",
      endTime: "የመጨረሻ ሰዓት",
      location: "ቦታ",
      chair: "ሰብሳቢ",
      secretary: "ጸሐፊ",
      selectChair: "ሰብሳቢ ይምረጡ",
      selectSecretary: "ጸሐፊ ይምረጡ",
      noMembersForServiceType: "ለዚህ የአገልግሎት ዓይነት የተመዘገቡ አባላት የሉም",
      enterDescription: "የስብሰባ መግለጫ ያስገቡ...",
      loading: "በመጫን ላይ",
      meetingCreatedSuccessfully: "ስብሰባ በተሳካ ሁኔታ ተፈጥሯል",
      meetingUpdatedSuccessfully: "ስብሰባ በተሳካ ሁኔታ ተስተካክሏል",
      meetingDeletedSuccessfully: "ስብሰባ በተሳካ ሁኔታ ተሰርዟል",
      errorSavingMeeting: "ስብሰባን ማስቀመጥ አልተቻለም",
      errorFetchingMeeting: "የስብሰባ ዝርዝሮችን ማምጣት አልተቻለም",
      failedToFetchMeetings: "ስብሰባዎችን ማምጣት አልተቻለም",
      confirmDeleteMeeting: "ይህን ስብሰባ መሰረዝ ይፈልጋሉ?",
      editMinutes: "ቃለ ጉባኤ አርትዕ",
      viewMinutes: "ቃለ ጉባኤ ይመልከቱ",
      meetingMinutes: "ቃለ ጉባኤ",
      attendance: "ተገኝነት",
      agenda: "አጀንዳ",
      decisions: "ውሳኔዎች",
      actionItems: "የድርጊት ዕቅዶች",
      addAttendee: "ተሳታፊ ጨምር",
      attendeeName: "የተሳታፊ ስም",
      attendeeRole: "ሚና",
      present: "ተገኝቷል",
      absent: "አልተገኘም",
      addAgendaItem: "የአጀንዳ ነጥብ ጨምር",
      showPreview: "ቅድመ እይታ አሳይ",
      hidePreview: "ቅድመ እይታ ደብቅ",
      agendaTitle: "የአጀንዳ ርዕስ",
      agendaNotes: "ማስታወሻዎች",
      agendaDecision: "ውሳኔ",
      addDecision: "ውሳኔ ጨምር",
      decisionText: "ውሳኔ",
      task: "ተግባር",
      responsible: "ሃላፊ",
      dueDate: "የማጠናቀቂያ ቀን",
      pending: "በመጠባበቅ ላይ",
      inProgress: "በሂደት ላይ",
      completed: "ተጠናቅቋል",
      nextMeetingDate: "የሚቀጥለው ስብሰባ ቀን",
      nextMeetingLocation: "የሚቀጥለው ስብሰባ ቦታ",
      print: "አትም",
      exportPdf: "PDF አውጣ",
      minutesLockedBy: "ቃለ ጉባኤው በ እየተስተካከለ ነው",
      lockExpired: "የማስተካከያ ቁልፍዎ ጊዜው አልፏል",
      acquiringLock: "የማስተካከያ ቁልፍ በማግኘት ላይ...",
      youHaveEditingLock: "የማስተካከያ ቁልፍ አለዎት",
      viewingOnly: "ማየት ብቻ - ሌላ ተጠቃሚ እያስተካከለ ነው",
      releaseLock: "ቁልፍ ልቀቅ",
      notEditing: "እያስተካከሉ አይደለም",
      clickToStartEditing: "ማስተካከል ለመጀመር ቁልፉን ጠቅ ያድርጉ",
      startEditing: "ማስተካከል ጀምር",
      viewOnly: "ማየት ብቻ",
      viewOnlyMode: "ሌላ ተጠቃሚ ይህን ሰነድ እያስተካከለ ነው",
      lockExpiresAt: "ቁልፉ የሚያልቅበት ጊዜ",
      youAreEditing: "እያስተካከሉ ነው",
      preview: "ቅድመ ዕይታ",
      editor: "አርታኢ",
      additionalNotes: "ተጨማሪ ማስታወሻዎች",
      signature: "ፊርማ",
      chairSignature: "የሰብሳቢ ፊርማ",
      secretarySignature: "የጸሐፊ ፊርማ",
      readOnlyAccess: "ለዚህ ገጽ የማንበብ ብቻ ፍቃድ አለዎት",
      backToList: "ወደ ዝርዝሩ ተመለስ",
      downloadPdf: "PDF አውርድ",
      pdfDownloaded: "PDF በተሳካ ሁኔታ ወርዷል",
      failedToDownloadPdf: "PDF ማውረድ አልተቻለም",
      connected: "ተገናኝቷል",
      disconnected: "ተቋርጧል",
      meetingNotFound: "ስብሰባው አልተገኘም",
      failedToReleaseLock: "ቁልፍ መልቀቅ አልተቻለም",
      lockLost: "የማስተካከያ ቁልፍዎ ጠፍቷል",
      titleAndDateRequired: "ርዕስ እና ቀን ያስፈልጋሉ",
      createMeeting: "ስብሰባ ፍጠር",
      minutesSaved: "ቃለ ጉባኤ በተሳካ ሁኔታ ተቀምጧል",
      errorSavingMinutes: "ቃለ ጉባኤ ማስቀመጥ አልተቻለም",
      // Approval workflow (Amharic)
      pendingApproval: "ፈቃድ በመጠባበቅ ላይ",
      approved: "ጸድቋል",
      rejected: "ተቀባይነት አላገኘም",
      approveMinutesConfirmation: "ይህን የስብሰባ ቃለ ጉባኤ ማፅደቅ ይፈልጋሉ?",
      rejectMinutesConfirmation: "ይህን የስብሰባ ቃለ ጉባኤ መቃወም ይፈልጋሉ?",
      approvedOn: "ጸድቋል በ",
      optional: "አማራጭ",
      addYourNotesHere: "ማስታወሻዎችዎን እዚህ ይጨምሩ",
      sendApprovalRequests: "የፈቃድ ጥያቄዎችን ላክ",
      sendApprovalEmails: "ለሁሉም ተሳታፊዎች የፈቃድ ኢሜል ላክ",
      // Service types (Amharic)
      serviceType: "የአገልግሎት ዓይነት",
      parish: "ቤተ ክርስቲያን",
      sundaySchool: "እሁድ ትምህርት ቤት",  
      youth: "ወጣቶች",
      choir: "መዘምራን",
      deacons: "ዲያቆናት",
      cancelled: "ተሰርዟል",
      allStatuses: "ሁሉም ሁኔታዎች",
      noMeetingsFound: "ስብሰባዎች አልተገኙም",
      date: "ቀን",
      time: "ሰዓት",
      showingOf: "{{count}} ከ {{total}} እያሳየ",
      page: "ገጽ",
      delete: "ሰርዝ",
      editing: "በማስተካከል ላይ",
      chooseMember: "አባል ምረጥ",
      role: "ሚና",
      attendee: "ተሳታፊ",
      chairperson: "ሰብሳቢ",
      invited: "ተጋብዟል",
      confirmed: "ተረጋግጧል",
      declined: "ተቀባይነት አልነበረውም",
      saveNow: "አሁን አስቀምጥ",
      startTypingMinutes: "ቃለ ጉባኤ መፃፍ ጀምር...",
      attendees: "ተሳታፊዎች",
      lockAcquired: "አሁን የማስተካከያ ቁልፍ አለዎት",
      lockReleased: "የማስተካከያ ቁልፍ ተለቅቋል",
      lockRefreshed: "ቁልፍ ታድሷል",
      errorAcquiringLock: "ቁልፍ ማግኘት አልተቻለም",
      errorReleasingLock: "ቁልፍ መልቀቅ አልተቻለም",
      errorRefreshingLock: "ቁልፍ ማደስ አልተቻለም",
      DRAFT: "ረቂቅ",
      IN_PROGRESS: "በሂደት ላይ",
      FINALIZED: "ተጠናቅቋል",
      APPROVED: "ጸድቋል",
      ARCHIVED: "ተመዝግቧል",
      // Action Items Manager translations (Amharic)
      total: "ጠቅላላ",
      overdue: "ጊዜው ያለፈባቸው",
      noActionItems: "ገና የድርጊት ዕቅዶች የሉም",
      titleAndAgendaRequired: "ርዕስ እና የአጀንዳ ነጥብ ያስፈልጋል",
      errorSavingActionItem: "የድርጊት ዕቅድ ማስቀመጥ አልተቻለም",
      confirmDeleteActionItem: "ይህን የድርጊት ዕቅድ መሰረዝ ይፈልጋሉ?",
      selectAgendaItem: "የአጀንዳ ነጥብ ይምረጡ",
      agendaItem: "የአጀንዳ ነጥብ",
      actionItemTitle: "የድርጊት ዕቅድ ርዕስ",
      actionItemDescription: "መግለጫ",
      assignee: "ሃላፊ",
      selectAssignee: "ሃላፊ ይምረጡ",
      priority: "ቅድሚያ",
      low: "ዝቅተኛ",
      medium: "መካከለኛ",
      high: "ከፍተኛ",
      urgent: "አጣዳፊ",
      startProgress: "መስራት ጀምር",
      markComplete: "እንደተጠናቀቀ ምልክት",
      editActionItem: "የድርጊት ዕቅድ አርትዕ",
      add: "ጨምር",
      save: "አስቀምጥ",
      // Meeting Minutes Approval translations (Amharic)
      minutesEditor: "ቃለ ጉባኤ አርታኢ",
      formalApproval: "ይፋዊ ፀድቋል",
      noFormalMinutesYet: "ይፋዊ ቃለ ጉባኤ አልተፈጠረም",
      createFormalMinutesDescription: "ለፀድቋል የሚቀርብ ይፋዊ ቃለ ጉባኤ ሰነድ ይፍጠሩ።",
      createDraftMinutes: "ረቂቅ ቃለ ጉባኤ ፍጠር",
      minutesPreview: "ቃለ ጉባኤ ቅድመ ዕይታ",
      version: "ስሪት",
      lastUpdated: "በመጨረሻ የተስተካከለበት",
      submitForApproval: "ለፀድቋል አቅርብ",
      approve: "አፅድቅ",
      reject: "ውድቅ አድርግ",
      approveMinutes: "ቃለ ጉባኤ አፅድቅ",
      rejectMinutes: "ቃለ ጉባኤ ውድቅ አድርግ",
      approvalNotes: "የፀድቋል ማስታወሻዎች",
      rejectionReason: "የመደመወድቅ ምክንያት",
      optionalApprovalNotes: "አማራጭ ማስታወሻዎች",
      provideRejectionReason: "እባክዎ የመደመወድቅ ምክንያት ያስገቡ",
      confirmApprove: "ፀድቋል አረጋግጥ",
      confirmReject: "ውድቅ አረጋግጥ",
      errorCreatingDraft: "ረቂቅ መፍጠር አልተቻለም",
      errorSubmittingApproval: "ለፀድቋል ማቅረብ አልተቻለም",
      errorProcessingApproval: "ፀድቋል ማስተናገድ አልተቻለም",
      aiGeneratedSummary: "በAI የተፈጠረ ማጠቃለያ",
      aiSummary: "AI ማጠቃለያ",
      generateAiSummary: "AI ማጠቃለያ አፍጥር",
      aiSummaryComingSoon: "AI ማጠቃለያ በቅርቡ ይመጣል!",
      by: "በ",
      "minutesStatus.DRAFT": "ረቂቅ - ለማስተካከል ዝግጁ",
      "minutesStatus.PENDING_APPROVAL": "ፀድቋል በመጠባበቅ ላይ",
      "minutesStatus.APPROVED": "ጸድቋል - የመጨረሻ ስሪት",
      "minutesStatus.REJECTED": "ተደመውቋል - ማስተካከል ያስፈልጋል",
      "minutesStatus.ARCHIVED": "ተመዝግቧል",
      // Meeting Enhancement Translations (SR-01 to SR-10) - Amharic
      // SR-01: Recurring Meeting
      recurringPattern: "የመደጋጋም ቅጥያ",
      weekly: "በየሳምንቱ",
      monthly: "በየወሩ",
      quarterly: "በየሩብ ዓመቱ",
      lastSundayOfMonth: "የወሩ የመጨረሻ እሁድ",
      firstSundayOfMonth: "የወሩ የመጀመሪያ እሁድ",
      suggestedNextDate: "የሚጠቆመው ቀጣይ የስብሰባ ቀን",
      // SR-02: Agenda Builder
      agendaItems: "የአጀንዳ ነጥቦች",
      addAgendaItemsOrUseTemplate: "የአጀንዳ ነጥቦችን ይጨምሩ ወይም አብነት ይጠቀሙ",
      noAgendaItemsYet: "ገና የአጀንዳ ነጥቦች የሉም",
      agendaItemTitle: "የአጀንዳ ነጥብ ርዕስ",
      agendaItemDescription: "መግለጫ/ዳራ",
      whoWillPresent: "ይህን ነጥብ ማን ያቀርባል?",
      addFromTemplate: "ከአብነት ጨምር",
      openingPrayer: "የመክፈቻ ጸሎት",
      approvalOfPreviousMinutes: "የቀደመው ቃለ ጉባኤ ፀድቋል",
      mattersArising: "የሚነሱ ጉዳዮች",
      reportsAndUpdates: "ሪፖርቶች እና ዜናዎች",
      newBusiness: "አዲስ ጉዳይ",
      anyOtherBusiness: "ሌላ ማናቸውም ጉዳይ",
      closingPrayerAndAdjournment: "የመዝጊያ ጸሎት እና ማስተላለፊያ",
      items: "ነጥቦች",
      minutes: "ደቂቃዎች",
      min: "ደቂቃ",
      presenter: "አቅራቢ",
      estimatedDuration: "የሚገመተው ጊዜ",
      // SR-03: Attachments
      attachments: "አባሪዎች",
      addAttachment: "አባሪ ጨምር",
      uploadFile: "ፋይል ስቀል",
      downloadFile: "ፋይል አውርድ",
      fileDescription: "የፋይል መግለጫ",
      // SR-04: Quorum
      quorumMet: "ምልዓተ ጉባኤ ተሟልቷል",
      quorumNotMet: "ምልዓተ ጉባኤ አልተሟላም",
      membersPresent: "አባላት ተገኝተዋል",
      totalMembers: "ጠቅላላ አባላት",
      quorumRequirement: "የምልዓተ ጉባኤ መስፈርት",
      needMoreMembers: "ለምልዓተ ጉባኤ {{count}} ተጨማሪ አባላት ያስፈልጋሉ",
      quorumType: "የምልዓተ ጉባኤ ዓይነት",
      percentage: "መቶኛ",
      fixedNumber: "የተወሰነ ቁጥር",
      // SR-06: Matters Arising
      loadingMattersArising: "የሚነሱ ጉዳዮችን በማስገባት ላይ",
      fromMeeting: "ከስብሰባ",
      noMattersArising: "የሚነሱ ጉዳዮች የሉም",
      allPreviousActionItemsResolved: "ሁሉም የቀደሙ የድርጊት ዕቅዶች ተፈጽመዋል",
      reviewStatus: "የግምገማ ሁኔታ",
      notReviewed: "ገና አልተገመገመም",
      stillPending: "አሁንም በመጠባበቅ ላይ",
      carryForward: "ወደፊት ይሂድ",
      markCompleted: "እንደተጠናቀቀ ምልክት",
      originalDescription: "የመጀመሪያ መግለጫ",
      updateStatus: "ሁኔታ አስተካክል",
      reviewNotes: "የግምገማ ማስታወሻዎች",
      addNotesAboutProgress: "ስለ ሂደት ማስታወሻዎች ይጨምሩ",
      // SR-07: Inline Action Items
      addActionItem: "የድርጊት ዕቅድ ጨምር",
      newActionItem: "አዲስ የድርጊት ዕቅድ",
      noActionItemsYet: "ለዚህ አጀንዳ ገና የድርጊት ዕቅዶች የሉም",
      assignTo: "ወደ ምደባ",
      selectMember: "አባል ይምረጡ",
      optionalDetails: "አማራጭ ዝርዝሮች",
      addItem: "ንጥል ጨምር",
      // SR-08: Decision/Voting
      decisionType: "የውሳኔ ዓይነት",
      consensus: "መግባባት",
      majorityVote: "የብዙሃን ድምጽ",
      unanimous: "በሙሉ ድምጽ",
      deferred: "ተራዝሟል",
      consensusDescription: "ሁሉም አባላት ያለ ድምጽ ተስማሙ",
      majorityVoteDescription: "ውሳኔ በብዙሃን ድምጽ ተደረገ",
      unanimousDescription: "ሁሉም አባላት ድምጽ ሰጥተዋል",
      deferredDescription: "ውሳኔ ለወደፊት ስብሰባ ተላልፏል",
      voteCount: "የድምጽ ቆጠራ",
      totalVoters: "ጠቅላላ ድምጽ ሰጪዎች",
      votesFor: "የደጋፊ ድምጽ",
      votesAgainst: "የተቃዋሚ ድምጽ",
      abstentions: "ድምጽ ያልሰጡ",
      motionBy: "ሀሳብ ያቀረበው",
      secondedBy: "ደገፈ",
      whoProposedMotion: "ሀሳቡን ማን አቀረበ?",
      whoSecondedMotion: "ሀሳቡን ማን ደገፈ?",
      motionPassed: "ሀሳቡ ተቀባይነት አግኝቷል",
      motionFailed: "ሀሳቡ ተቀባይነት አላገኘም",
      inFavor: "ደጋፊ",
      votes: "ድምጾች",
      // SR-09: Dynamic Header
      parishHeader: "የቤተ ክርስቲያን ራስጌ",
      // SR-10: Digital Signatures
      signatures: "ፊርማዎች",
      signedBy: "የፈረመው",
      signatureNotYetProvided: "ፊርማ ገና አልተሰጠም",
      waitingForSignature: "ፊርማ በመጠባበቅ ላይ",
      clickToSign: "ለመፈረም ጠቅ ያድርጉ",
      signNow: "አሁን ፈርም",
      signAbovePrompt: "በማውስ ወይም በመነካካት ከላይ ባለው ሳጥን ውስጥ ይፈርሙ",
      saveSignature: "ፊርማ አስቀምጥ",
      clearSignature: "ፊርማ ደምስስ",
      toBeSignedBy: "የሚፈርመው",
      signedOn: "የፈረመበት ቀን",
      required: "ያስፈልጋል",
      fullyApproved: "ሙሉ በሙሉ ጸድቋል",
      bothSignaturesRequired: "የሰብሳቢ እና የጸሐፊ ፊርማዎች ያስፈልጋሉ",
      chairSignatureRequired: "የሰብሳቢ ፊርማ ያስፈልጋል",
      secretarySignatureRequired: "የጸሐፊ ፊርማ ያስፈልጋል",
      // Meeting Status
      scheduled: "ታቅዷል",
      liveInSession: "በቀጥታ / በስብሰባ ላይ",
      review: "በግምገማ ላይ",
      archived: "ተመዝግቧል",
      discussed: "ተወያይቷል",
      subject: "ርዕሰ ጉዳይ / ግለሰብ",
      service: "አገልግሎት",
      telephone: "ስልክ",
      email: "ኢሜል",
      province: "ክፍለ ሀገር/ክልል",
      postalCode: "የፖስታ ቁጥር",
      apartmentNo: "የአፓርታማ ቁጥር",
      referenceCode: "የማጣቀሻ ኮድ",
      decisionReason: "የውሳኔ ምክንያት",
      requestedBy: "ጠያቂ",
      requestedClergy: "የተጠየቀው ካህን",
      scheduledAt: "የተቀጠረበት ጊዜ",
      logisticsNote: "የዝግጅት ማስታወሻ",
      dayOfWeekLabel: "የሳምንቱ ቀን",
      applyForServiceTitle: "ለቤተ ክርስቲያን አገልግሎት ያመልክቱ",
      applyForServiceSubtitle: "ጥምቀት፣ ተክሊል፣ ፍትሐት፣ ንስሐ፣ ምክክር እና ሌሎችም።",
      churchIsRequired: "እባክዎ ቤተ ክርስቲያን ይምረጡ",
      applicantNameIsRequired: "የእርስዎ ስም ያስፈልጋል",
      serviceIsRequired: "እባክዎ አገልግሎት ይምረጡ ወይም የሚፈልጉትን ይግለጹ",
      consentIsRequired: "ይህን ማመልከቻ ለማስገባት የስምምነት መግለጫውን መቀበል አለብዎት",
      childNameIsRequired: "የልጁ ስም ያስፈልጋል",
      childDobIsRequired: "የልጁ የትውልድ ቀን ያስፈልጋል",
      godparentNameIsRequired: "የክርስትና አባት/እናት ስም ያስፈልጋል",
      atLeastOneSubjectIsRequired: "ቢያንስ አንድ ግለሰብ ያስፈልጋል",
      subjectFullNameIsRequired: "የእያንዳንዱ ግለሰብ ሙሉ ስም ያስፈልጋል",
      weddingPartyContactMethodIsRequired: "እባክዎ ለእያንዳንዱ ወገን ቢያንስ አንድ የመገናኛ መንገድ (ስልክ፣ ኢሜል ወይም አድራሻ) ያስገቡ",
      weddingPartyContactMethodHelp: "ለዚህ ወገን ከስልክ፣ ኢሜል ወይም አድራሻ ቢያንስ አንዱን ያስገቡ።",
      phoneNumberIsInvalid: "እባክዎ ትክክለኛ የስልክ ቁጥር ያስገቡ",
      weddingApplicantRoleTitle: "እርስዎ ከሚጋቡት ሁለት ሰዎች አንዱ ነዎት?",
      iAmNotDirectlyAParty: "አይደለሁም፣ በእነሱ ስም እያመለከትኩ ነው",
      iAmPartyOne: "አዎ፣ እኔ የመጀመሪያው ወገን ነኝ",
      iAmPartyTwo: "አዎ፣ እኔ ሁለተኛው ወገን ነኝ",
      iAmAlsoASubject: "እኔም ይህ ጥያቄ ከሚመለከታቸው ሰዎች አንዱ ነኝ",
      applicationSubmissionFailed: "ማመልከቻዎን ማስገባት አልቻልንም። እባክዎ እንደገና ይሞክሩ።",
      applicationSubmittedTitle: "ማመልከቻው ገብቷል",
      applicationSubmittedCheckEmail: "እባክዎ ማመልከቻዎን ለማረጋገጥ ኢሜልዎን ያረጋግጡ።",
      otherServiceNotListed: "የምፈልገው አገልግሎት በዝርዝሩ ውስጥ የለም",
      serviceSectionDetailsTitle: "{{service}} ዝርዝር",
      baptismDetailsTitle: "የጥምቀት ዝርዝሮች",
      weddingDetailsTitle: "የጋብቻ ዝርዝሮች",
      partyOneTitle: "የመጀመሪያው ወገን",
      partyTwoTitle: "የሁለተኛው ወገን",
      subjectsSectionTitle: "ጉዳዩ የሚመለከታቸው",
      applicantChristianNameHelp: "እርስዎም በጸሎቱ ውስጥ እንደ ጠያቂ ይጠቀሳሉ - ይህ ለእርስዎ የሚነበበው ስም (የክርስትና ስም) ነው።",
      subjectFullName: "ሙሉ ስም",
      livingOrDeparted: "በሕይወት ያለ ወይም ያረፈ",
      living: "በሕይወት ያለ",
      departed: "ያረፈ",
      addAnotherSubject: "ሌላ ግለሰብ ያክሉ",
      remove: "አስወግድ",
      paymentSectionTitle: "ክፍያ",
      amountPaid: "የተከፈለው መጠን",
      payerName: "የከፋዩ ስም",
      estimatedTotal: "ግምታዊ ጠቅላላ",
      estimatedTotalHelp: "በእንግዳ ታሪፍ የተገመተ - የተረጋገጠው መጠን በአባልነት ላይ የተመሰረተ ሲሆን፣ አጥቢያው ማመልከቻዎን ሲገመግም ይወሰናል።",
      consentStatement: "በዚህ ቅጽ ውስጥ የተሰጡት የግል መረጃዎች ለዚህ ማመልከቻ ዓላማ ጥቅም ላይ እንዲውሉ እስማማለሁ።",
      submitApplication: "ማመልከቻ አስገባ",
      applicantName: "የአመልካች ስም",
      applicantEmail: "የአመልካች ኢሜል",
      applicantRelation: "ከግለሰቡ ጋር ያለው ዝምድና",
      serviceNeededDate: "የሚፈለግበት ቀን",
      serviceOtherText: "የሚፈለገውን አገልግሎት ይግለጹ",
      childName: "የልጁ ስም",
      childDob: "የልጁ የትውልድ ቀን",
      godparentName: "የክርስትና አባት/እናት ስም",
      selectChurchFirst: "መጀመሪያ ቤተ ክርስቲያን ይምረጡ",
      checkApplicationStatus: "የማመልከቻ ሁኔታን ያረጋግጡ",
      verificationTokenMissing: "የዚህ ማረጋገጫ ሊንክ ቶከን (token) ጠፍቷል",
      verifyingApplication: "ማመልከቻዎን በማረጋገጥ ላይ...",
      applicationVerifiedTitle: "ማመልከቻው ተረጋግጧል",
      applicationVerifiedBody: "እናመሰግናለን - ማመልከቻዎ የተረጋገጠ ሲሆን አሁን ለግምገማ በአጥቢያው እጅ ይገኛል።",
      verificationFailedTitle: "ማረጋገጥ አልተሳካም",
      verificationFailed: "ይህ ማረጋገጫ ሊንክ ትክክል አይደለም ወይም ጊዜው አልፎበታል",
      referenceCodeIsRequired: "እባክዎ የማጣቀሻ ኮድዎን ያስገቡ",
      applicationNotFound: "ለዚያ ማጣቀሻ ኮድ ምንም ማመልከቻ አልተገኘም",
      checkStatus: "ሁኔታን ያረጋግጡ",
      yourResponse: "የእርስዎ ምላሽ",
      responseRequired: "እባክዎ ምላሽ ያስገቡ",
      responseFailed: "ምላሽዎን ማስገባት አልተሳካም",
      submitResponse: "ምላሽ አስገባ",
      applicationStatus: {
        PENDING_VERIFICATION: "ማረጋገጫ በመጠባበቅ ላይ",
        SUBMITTED: "ገብቷል",
        UNDER_REVIEW: "በግምገማ ላይ",
        INFO_REQUESTED: "ተጨማሪ መረጃ ተጠይቋል",
        APPROVED: "ጸድቋል",
        REJECTED: "ውድቅ ተደርጓል",
        ABANDONED: "የተተወ",
      },
      serviceApplications: "የአገልግሎት ማመልከቻዎች",
      failedToFetchApplications: "የአገልግሎት ማመልከቻዎችን መጫን አልተሳካም",
      queueWarningsColumn: "ማስጠንቀቂያዎች",
      subjectCountBadge: "{{count}} ግለሰቦች",
      subjectCountBadgeHelp: "ይህ ማመልከቻ ከአንድ በላይ ግለሰብን ይገልጻል",
      queueWarningConfessionFather: "የንስሐ አባት የለም",
      queueWarningConfessionFatherHelp: "አመልካቹ ወይም የተጋቢ ወገን የንስሐ አባት በመዝገብ ላይ እንደሌላቸው ገልጸዋል",
      queueWarningConfessionNeedsResolution: "የንስሐ አባት መለየት ያስፈልጋል",
      queueWarningConfessionNeedsResolutionHelp: "\"በዚህ ቤተ ክርስቲያን\" ተብሎ ተገልጿል - ከማጽደቅ በፊት ወይም በሚደረግበት ጊዜ ትክክለኛውን ካህን ይምረጡ",
      queueWarningPaymentNotDeclared: "ምንም ክፍያ አልተገለጸም",
      queueWarningPaymentNotDeclaredHelp: "ይህ ክፍያ የሚጠይቅ አገልግሎት ሲሆን ምንም ክፍያ ገና አልተገለጸም",
      queueWarningPaymentUnderpaid: "ያልተሟላ ክፍያ ሊሆን ይችላል",
      queueWarningPaymentUnderpaidHelp: "የተገለጸው ክፍያ ለዚህ ማመልከቻ ከሚቻለው ዝቅተኛ ታሪፍ በታች ነው",
      queueWarningPossibleDuplicate: "ድግግሞሽ ሊሆን ይችላል",
      queueWarningPossibleDuplicateHelp: "የአመልካቹ ስም ከነባር አባል ጋር በጣም ይመሳሰላል",
      reviewApplication: "ማመልከቻውን ይገምግሙ",
      failedToFetchApplication: "ማመልከቻውን መጫን አልተሳካም",
      applicationDetails: "የማመልከቻ ዝርዝሮች",
      applicantResolutionTitle: "አመልካች",
      subjectResolutionTitle: "ርዕሰ ጉዳይ/ግለሰብ",
      godparentResolutionTitle: "የክርስትና አባት/እናት",
      partyOneResolutionTitle: "የመጀመሪያው ወገን",
      partyTwoResolutionTitle: "የሁለተኛው ወገን",
      bookingDetailsTitle: "የቀጠሮ ዝርዝሮች",
      applicantResolutionRequired: "እባክዎ ከማጽደቅዎ በፊት አመልካቹን ይወስኑ/ያረጋግጡ",
      subjectResolutionRequired: "እባክዎ ከማጽደቅዎ በፊት ግለሰቡን ይወስኑ/ያረጋግጡ",
      godparentResolutionRequired: "እባክዎ ከማጽደቅዎ በፊት የክርስትና አባት/እናቱን ይወስኑ/ያረጋግጡ",
      weddingPartyResolutionRequired: "እባክዎ ከማጽደቅዎ በፊት ሁለቱንም ወገኖች ይወስኑ/ያረጋግጡ",
      serviceOverride: "የሚመዘገብ አገልግሎት",
      serviceOverrideHelp: "ይህ ማመልከቻ የ'ሌሎች አገልግሎቶች' ነጻ ጽሑፍ አማራጭን ተጠቅሟል - የትኛው የካታሎግ አገልግሎት መመዝገብ እንዳለበት ይምረጡ።",
      serviceOverrideRequired: "እባክዎ የትኛው አገልግሎት መመዝገብ እንዳለበት ይምረጡ",
      confirmedTotalTitle: "የክፍያ ማስታረቂያ",
      resolvedTotalLabel: "የተረጋገጠ ጠቅላላ መጠን",
      paymentStatusLabel: "የክፍያ ሁኔታ",
      paymentStatusConfirmed: "ተረጋግጧል",
      paymentStatusUnderpaid: "ያልተሟላ ክፍያ",
      paymentStatusUnpaid: "ያልተከፈለ",
      selectPaymentStatus: "የክፍያ ሁኔታ ይምረጡ",
      paymentStatusRequired: "እባክዎ ከማጽደቅዎ በፊት የክፍያ ሁኔታን ይመዝግቡ",
      paymentMismatchWarning: "የተገለጸ {{declared}} - ጠቅላላ የሚፈለግ {{total}} - በ {{difference}} አንሷል",
      noPriceSnapshotNote: "ይህ ማመልከቻ በመዝገብ ላይ የዋጋ መረጃ የለውም - ማስታረቁ በእጅ መደረግ አለበት።",
      selectClergyOptional: "ካህን ይምረጡ (አማራጭ)",
      applicationApproved: "ማመልከቻው ጸድቋል",
      approvalFailed: "ማመልከቻውን ማጽደቅ አልተሳካም",
      rejectionReasonRequired: "ውድቅ የተደረገበት ምክንያት ያስፈልጋል",
      applicationRejected: "ማመልከቻው ውድቅ ተደርጓል",
      rejectionFailed: "ማመልከቻውን ውድቅ ማድረግ አልተሳካም",
      requestInfo: "መረጃ ይጠይቁ",
      requestInfoNote: "የጎደለው ምንድን ነው",
      requestInfoNoteRequired: "እባክዎ ምን መረጃ እንደሚያስፈልግ ይግለጹ",
      confirmRequestInfo: "ጥያቄ ላክ",
      applicationInfoRequested: "ተጨማሪ መረጃ ጥያቄ ተልኳል",
      requestInfoFailed: "ተጨማሪ መረጃ መጠየቅ አልተሳካም",
      requestInfoSectionTitle: "የተጠየቀ መረጃ",
      applicantResponseLabel: "የአመልካች ምላሽ",
      noResponseYet: "እስካሁን ምላሽ የለም",
      selected: "ተመርጧል",
      changeSelection: "ቀይር",
      noPersonSelectedYet: "እስካሁን የተመረጠ ሰው የለም - ጥቆማዎችን ይመልከቱ ወይም ከታች ይፈልጉ።",
      possibleMatches: "ሊመሳሰሉ የሚችሉ",
      searchAgain: "እንደገና ፈልግ",
      searchExistingPeople: "ነባር ሰውን ይፈልጉ",
      noResultsFound: "ምንም ውጤት አልተገኘም",
      resolutionActionLabel: "ይህ ሰው እንዴት መስተናገድ አለበት?",
      selectAction: "ተግባር ይምረጡ",
      resolutionAction: {
        LINK_EXISTING: "ከነባር አባል ጋር ያገናኙ",
        CREATE_MEMBER: "አዲስ አባል ይፍጠሩ",
        CREATE_GUEST: "አዲስ እንግዳ ይፍጠሩ",
        DESCRIPTIVE_ONLY: "ገላጭ በሆነ መንገድ ይመዝግቡ (የአባል/እንግዳ መዝገብ የለም)",
      },
      existingMemberId: "ነባር የአባል መለያ (ID)",
      descriptiveName: "ገላጭ ስም",
      descriptiveDob: "ገላጭ የትውልድ ቀን",
      descriptiveNote: "ገላጭ ማስታወሻ",
      checkForDuplicates: "ድግግሞሾችን ያረጋግጡ",
      nameRequiredForDuplicateCheck: "ድግግሞሾችን ከማረጋገጥዎ በፊት ስም ያስገቡ",
      nameAsGiven: "ስም እንደተሰጠው፡ {{name}} - እያንዳንዱን ክፍል ከታች ባሉት መስኮች ያስገቡ",
      duplicateCheckFailed: "ድግግሞሽ ሊሆኑ የሚችሉትን ማረጋገጥ አልተሳካም",
      noDuplicateCandidatesFound: "ምንም ዓይነት ድግግሞሽ አልተገኘም",
      matchScore: "የመመሳሰል ነጥብ",
      possibleMatchElsewhere: "በሌላ ቤተ ክርስቲያን ሊኖር የሚችል መመሳሰል",
      contactDioceseToConfirm: "ለማረጋገጥ የሀገረ ስብከት አስተዳዳሪን ያነጋግሩ",
      bookings: "ቀጠሮዎች",
      failedToFetchBookings: "ቀጠሮዎችን ማምጣት አልተሳካም",
      bookingStatus: {
        REQUESTED: "ተጠይቋል",
        CONFIRMED: "ተረጋግጧል",
        COMPLETED: "ተጠናቋል",
        CANCELLED: "ተሰርዟል",
      },
      needsMarriageRecorded: "ጋብቻውን ይመዝግቡ",
      needsMarriageRecordedHelp: "የዚህ ሰርግ ቀን አልፏል ነገር ግን ጋብቻው እስካሁን አልተመዘገበም - ለመመዝገብ እዚህ ጠቅ ያድርጉ።",
      editBooking: "ቀጠሮን ያርትዑ",
      failedToFetchBooking: "ቀጠሮውን ማምጣት አልተሳካም",
      bookingUpdatedSuccessfully: "ቀጠሮው በተሳካ ሁኔታ ታድሷል",
      errorUpdatingBooking: "ቀጠሮውን ማደስ አልተሳካም",
      clergyQueue: "የካህናት ተራ",
      failedToFetchClergyQueue: "የካህናት ተራዎን ማምጣት አልተሳካም",
      clergyQueueEmpty: "እርምጃ የሚጠብቁ ምንም ቀጠሮዎች የሉዎትም",
      respond: "ምላሽ ይስጡ",
      manageClergyAvailability: "የካህናት ክፍት ጊዜ",
      searchClergyMember: "ካህን ይፈልጉ",
      newAvailabilityWindow: "አዲስ ክፍት ጊዜ",
      addAvailabilityWindow: "ክፍት ጊዜ ይጨምሩ",
      startAndEndTimeRequired: "የመጀመሪያ እና የመጨረሻ ሰዓት ያስፈልጋል",
      availabilityWindowAdded: "ክፍት ጊዜ ተጨምሯል",
      errorAddingAvailabilityWindow: "ክፍት ጊዜ መጨመር አልተሳካም",
      errorDeletingAvailabilityWindow: "የተደራሽነት ጊዜ መሰረዝ አልተሳካም",
      failedToFetchAvailability: "ክፍት ጊዜዎችን ማምጣት አልተሳካም",
      dayOfWeek: {
        MONDAY: "ሰኞ",
        TUESDAY: "ማክሰኞ",
        WEDNESDAY: "ረቡዕ",
        THURSDAY: "ሐሙስ",
        FRIDAY: "አርብ",
        SATURDAY: "ቅዳሜ",
        SUNDAY: "እሑድ",
      },
      bookConsultation: "ምክክር ያስይዙ",
      failedToLoadBookingSetup: "የማስያዣ ቅንብሮችን ማምጣት አልተሳካም",
      consultationServiceNotConfigured: "የምክክር አገልግሎት ለቤተ ክርስቲያንዎ ገና አልተዋቀረም",
      failedToFetchBookableClergy: "የሚገኙ ካህናትን ማምጣት አልተሳካም",
      failedToFetchSlots: "ክፍት የጊዜ ሰሌዳዎችን ማምጣት አልተሳካም",
      selectAClergyMember: "ካህን ይምረጡ",
      selectClergy: "ካህን ይምረጡ",
      noBookableClergyAtYourChurch: "በቤተ ክርስቲያንዎ ውስጥ በአሁኑ ጊዜ ክፍት የምክክር ጊዜ ያላቸው ካህናት የሉም",
      previousWeek: "ያለፈው ሳምንት",
      nextWeek: "ቀጣዩ ሳምንት",
      noOpenSlotsThisWeek: "በዚህ ሳምንት ምንም ክፍት የጊዜ ሰሌዳ የለም",
      consultationBooked: "ምክክር ተይዟል",
      consultationBookedTitle: "ምክክር ተይዟል",
      consultationBookedBody: "ከ{{clergy}} ጋር የሚያደርጉት ምክክር ለ{{date}} ተረጋግጧል።",
      bookAnother: "ሌላ ያስይዙ",
      bookingFailed: "ይህን ጊዜ ማስያዝ አልተሳካም - ምናልባት አሁን ተይዞ ሊሆን ይችላል",
      servicesAndBookings: "አገልግሎቶች እና ቀጠሮዎች",
      serviceCatalog: "የአገልግሎት ማውጫ",
      addServiceCatalogEntry: "አገልግሎት ጨምር",
      editServiceCatalogEntry: "አገልግሎት ያርትዑ",
      failedToFetchServiceCatalog: "የአገልግሎት ማውጫውን ማምጣት አልተሳካም",
      failedToFetchServiceCatalogEntry: "ይህን የአገልግሎት ማውጫ መረጃ ማምጣት አልተሳካም",
      serviceCatalogCode: "ኮድ",
      serviceCatalogPrice: "የአባል / የእንግዳ ዋጋ",
      serviceCatalogPricingBasis: "የዋጋ መሠረት",
      serviceCatalogCurrency: "የገንዘብ ዓይነት",
      serviceCatalogMemberPrice: "የአባል ዋጋ",
      serviceCatalogGuestPrice: "የእንግዳ ዋጋ",
      serviceCatalogIsFree: "ይህ አገልግሎት ነጻ ነው",
      selectPricingBasis: "የዋጋ መሠረት ይምረጡ",
      pricingBasis: {
        PER_SUBJECT: "በግለሰብ",
        PER_APPLICATION: "በማመልከቻ",
      },
      serviceCatalogSubjectMode: "የተገልጋይ ሁኔታ",
      selectSubjectMode: "የተገልጋይ ሁኔታ ይምረጡ",
      subjectMode: {
        SELF_ONLY: "አመልካች ብቻ - ምንም የግለሰብ ክፍል የለም",
        SINGLE: "አንድ ግለሰብ",
        MANY: "በርካታ ግለሰቦች",
      },
      free: "ነጻ",
      active: "ንቁ",
      retired: "ከአገልግሎት ውጪ",
      retire: "ከአገልግሎት ውጪ አድርግ",
      activate: "አንቃ",
      confirmRetireServiceCatalogEntry: "ይህን አገልግሎት ከአገልግሎት ውጪ ማድረግ ይፈልጋሉ? በአዳዲስ ማመልከቻዎች ላይ አይቀርብም፣ ነገር ግን ነባር ቀጠሮዎች እና ማመልከቻዎች አሁንም ይጠቀሙበታል።",
      confirmActivateServiceCatalogEntry: "ይህን አገልግሎት እንደገና ማንቃት ይፈልጋሉ? ለአዳዲስ ማመልከቻዎች ክፍት ይሆናል።",
      serviceCatalogEntryRetired: "አገልግሎት ከአገልግሎት ውጪ ሆኗል",
      serviceCatalogEntryActivated: "አገልግሎት ነቅቷል",
      serviceCatalogEntryUpdated: "የአገልግሎት ማውጫ መረጃ ታድሷል",
      serviceCatalogEntryAdded: "የአገልግሎት ማውጫ መረጃ ታክሏል",
      errorUpdatingServiceCatalogEntry: "ይህን የአገልግሎት ማውጫ መረጃ ማደስ አልተሳካም",
      errorAddingServiceCatalogEntry: "ይህን የአገልግሎት ማውጫ መረጃ መጨመር አልተሳካም",
      errorFetchingFamilyData: "የቤተሰብ መረጃን ማምጣት አልተሳካም",
      familyTreeDepth: "ጥልቀት",
      showGodparents: "የክርስትና አባት/እናትን አሳይ",
      showConfessionFather: "የንስሐ አባትን አሳይ",
      familyLegendParent: "ወላጅ",
      familyLegendChild: "ልጅ",
      familyLegendSpouse: "የትዳር ጓደኛ",
      familyLegendSibling: "እህት/ወንድም",
      familyLegendPending: "በመጠባበቅ ላይ",
      familyLegendDisputed: "የተስተባበለ",
      "familyEdgeStatus.pending": "በመጠባበቅ ላይ",
      "familyEdgeStatus.disputed": "የተስተባበለ",
      pendingConfirmations: "የሚጠባበቁ ማረጋገጫዎች",
      pendingConfirmationsHelp: "ሌሎች አባላት እርስዎን እንደ ወላጃቸው አሳውቀዋል። ይህ ትክክል ከሆነ ያረጋግጡ፣ ወይም ካልሆነ ያስተባብሉ።",
      noPendingConfirmations: "ምንም የሚጠባበቁ ማረጋገጫዎች የሉዎትም።",
      householdPendingClaim: "{{name}} እርስዎን እንደ ወላጃቸው አሳውቀዋል",
      householdConfirmSuccessful: "ተረጋግጧል",
      householdDisputeSuccessful: "ተስተባብሏል",
      householdActionFailed: "ምላሽዎን መመዝገብ አልተሳካም",
      dispute: "አስተባብል",
      proceedWithReasons: "በምክንያቶች ይቀጥሉ",
      impedimentAbsoluteTitle: "ይህ ጋብቻ ሊመዘገብ አይችልም",
      impedimentAbsoluteBody: "የሚከተሉት በምንም ዓይነት ሁኔታ በምክንያት ሊታለፉ አይችሉም፡",
      impedimentInsufficientTitle: "ማረጋገጥ አልተቻለም",
      impedimentFoundTitle: "እንቅፋት ተገኝቷል",
      impedimentReviewTitle: "የካህን ግምገማ ይፈልጋል",
      impedimentSpouse1EdgesKnown: "ተጋቢ 1 - በስርዓቱ ውስጥ የሚታወቁ የቤተሰብ ግንኙነቶች",
      impedimentSpouse2EdgesKnown: "ተጋቢ 2 - በስርዓቱ ውስጥ የሚታወቁ የቤተሰብ ግንኙነቶች",
      impedimentOverrideReasonLabel: "ይህ ቢኖርም ለመቀጠል ምክንያት",
      "impedimentType.ACTIVE_MARRIAGE": "ገባሪ ጋብቻ ተመዝግቧል",
      "impedimentType.UNDER_CIVIL_AGE": "ከሕጋዊ የጋብቻ ዕድሜ በታች",
      "impedimentType.SAME_SEX": "ተመሳሳይ ጾታ",
      "impedimentType.CONSANGUINITY": "የሥጋ ዝምድና",
      "impedimentType.SPIRITUAL_KINSHIP": "የመንፈስ ዝምድና (የክርስትና ዝምድና)",
      "impedimentType.UNCONFIRMED_DECLARED_LINK": "ያልተረጋገጠ የዝምድና ግንኙነት ላይ የተመሠረተ",
      "impedimentType.CATECHUMEN": "የጥምቀት ሁኔታ አልተረጋገጠም",
      "impedimentType.PRIOR_MARRIAGE_NOT_PROPERLY_CLOSED": "የቀድሞ ጋብቻ በአግባቡ አልተዘጋም",
      "impedimentType.MISSING_DATA": "የጎደለ መረጃ",
      consentPageGuardianTitle: "{{child}} በ{{className}}",
      consentPageMembershipTitle: "የ{{child}} የአባልነት መዝገብ",
      consentPageChangedMeanwhile: "እርስዎ ምላሽ እየሰጡ እያለ ይህ ጥያቄ ስለተለወጠ፣ ምላሽዎ አልተወሰደም። ገጹ አሁን ያለበትን ሁኔታ ያሳያል።",
      consentPageOffline: "የቤተ ክርስቲያኑን ሲስተም ማግኘት አልቻልንም። የኢንተርኔት ግንኙነትዎን ያረጋግጡና እንደገና ይሞክሩ።",
      consentPageTooManyTries: "በአጭር ጊዜ ውስጥ ብዙ ሙከራዎች ተደርገዋል። ጥቂት ደቂቃዎች ጠብቀው እንደገና ይሞክሩ።",
      consentPageServerTrouble: "የቤተ ክርስቲያኒቱ ሲስተም ምላሽ ሰጥቷል፣ ነገር ግን ይህን አሁን ማድረግ አልቻለም። ከጥቂት ጊዜ በኋላ እንደገና ይሞክሩ። ይህ ችግር ከቀጠለ ቤተ ክርስቲያኒቱን ያነጋግሩ።",
      consentPageNotShownTitle: "ይህ ገጽ በአሁኑ ጊዜ ሊታይ አይችልም",
      consentPageRefusedTitle: "ይህ ገጽ ሊታይ አይችልም",
      consentPageRefused: "የቤተ ክርስቲያኗ ሲስተም ይህን ሊንክ አልተቀበለውም። ቤተ ክርስቲያኗን ያነጋግሩ።",
      consentPageUnknownStateTitle: "ይህ ጥያቄ እዚህ ሊታይ አይችልም",
      consentPageRejected: "ምላሾችዎን ማስቀመጥ አልተቻለም። እባክዎ ሊንኩን ከኢሜልዎ እንደገና ይክፈቱትና እንደገና ይጀምሩ። ይህ ችግር ከቀጠለ ቤተ ክርስቲያኒቱን ያነጋግሩ።",
      consentPageNoTokenTitle: "ይህ ሊንክ ያልተሟላ ነው",
      consentPageNoTokenBody: "የሊንኩ የተወሰነ ክፍል ጠፍቷል። ኢሜሉን ከቤተ ክርስቲያኒቱ ከፍተው ሊንኩን እንደገና ይጫኑ። ሊንኩን ኮፒ አድርገውት ከሆነ፣ ሙሉውን ኮፒ ማድረግዎን ያረጋግጡ።",
      consentPageNormalBrowser: "አሁንም ካልሰራ፣ ሊንኩን በመደበኛ የድር አሳሽዎ (እንደ Safari ወይም Chrome) ይክፈቱ።",
      consentPageLoading: "አንድ አፍታ",
      consentPageLoadingBody: "ጥያቄዎን በመክፈት ላይ...",
      consentPageUnreachableTitle: "የቤተ ክርስቲያኑን ሲስተም ማግኘት አልቻልንም",
      consentPageTryAgain: "እንደገና ይሞክሩ",
      consentPageInvalidTitle: "ይህ ሊንክ አይሰራም",
      consentPageInvalidBody: "ሊንኩ በከፊል ብቻ ተገልብጦ ሊሆን ይችላል፣ ወይም ከቤተ ክርስቲያን የመጣ ሊንክ አይደለም። ኢሜሉን ከቤተ ክርስቲያኒቱ ከፍተው ሊንኩን እንደገና ይጫኑ። አሁንም ካልሰራ ቤተ ክርስቲያኒቱን ያነጋግሩ።",
      consentPageClosedTitle: "ይህ ጥያቄ ተዘግቷል",
      consentPageClosedBody: "{{parish}} ይህን ጥያቄ ሰርዞታል። ከእርስዎ ተጨማሪ ምንም አይጠበቅም።",
      consentPageSupersededTitle: "አዲስ ሊንክ ተልኳል",
      consentPageSupersededBody: "{{parish}} ስለዚህ ጉዳይ አዲስ ኢሜል ልኮልዎታል። እባክዎ በቅርብ ኢሜል ውስጥ ያለውን ሊንክ ይጠቀሙ።",
      consentPageSentElsewhereTitle: "ይህ ሊንክ ከአሁን በኋላ ንቁ አይደለም",
      consentPageSentElsewhereBody: "ይህ ጥያቄ ወደ ሌላ አሳዳጊ ተልኳል። እርስዎ እራስዎ መመለስ ከፈለጉ ቤተ ክርስቲያኗን ያነጋግሩ።",
      consentWithdrawTitle: "ስምምነትዎን ማቋረጥ ይፈልጋሉ?",
      consentWithdrawGivenOn: "የተሰጠበት ቀን",
      consentWithdrawWhatHappensGuardian: "ካቋረጡት፣ {{child}} በዚህ ስምምነት መሠረት ከእንግዲህ በ{{className}} አይሳተፍም። እስካሁን የተመዘገበ ተገኝነት እንዳለ ይቆያል።",
      consentWithdrawConfirm: "ስምምነቴን አቋርጣለሁ",
      consentWithdrawKeep: "ስምምነቴን እጠብቃለሁ",
      consentWithdrawWithdrawing: "በማቋረጥ ላይ...",
      consentWithdrawFailed: "ማቋረጡን ማስቀመጥ አልተቻለም። የኢንተርኔት ግንኙነትዎን ያረጋግጡና እንደገና ይሞክሩ። ይህ ችግር ከቀጠለ ቤተ ክርስቲያኒቱን ያነጋግሩ።",
      consentPageThanksTitle: "እናመሰግናለን። ምላሾችዎ ተቀምጠዋል",
      consentPageIntroTitle: "ስለ {{child}} ጥያቄ",
      consentPageIntroAsking: "{{parish}} ስለ {{child}} የእርስዎን ምላሽ እየጠየቀ ነው።",
      consentPageIntroOne: "አንድ ጥያቄ አለ።",
      consentPageIntroMany: "{{count}} ጥያቄዎች አሉ። እያንዳንዱን ለየብቻ ይመልሳሉ፡ ለአንዱ አዎ ለሌላው አይ ማለት ይችላሉ።",
      consentPageBegin: "ጀምር",
      consentPageQuestionOf: "ጥያቄ {{number}} ከ{{count}}",
      consentPageDoYouAgree: "ይስማማሉ?",
      consentPageYes: "አዎ፣ እስማማለሁ",
      consentPageNo: "አይ፣ አልስማማም",
      consentPageContinue: "ቀጥል",
      consentPageBack: "ወደኋላ",
      consentPageReviewTitle: "ምላሾችዎን ያረጋግጡ",
      consentPageSendAnswers: "ምላሾቼን ላክ",
      consentPageDocReceipt: "የምላሾችዎ ማረጋገጫ (ደረሰኝ)",
      errorBoundaryTitle: "ችግር ተከስቷል",
      errorBoundaryBody: "ያልተጠበቀ ስህተት ተከስቷል። እንደገና ይሞክሩ፣ ወይም ገጹን ያድሱ።",
      chaseTitle: "የስምምነት ሁኔታ",
      chaseSendFailed: "ምንም አልተላከም። እንደገና ይሞክሩ።",
      chaseStatusNotAsked: "አልተጠየቀም",
      chaseStatusAsked: "ተጠይቋል፣ ሊንኩ አሁንም ይሰራል",
      chaseStatusNoResponse: "ምላሽ የለም፣ ሊንኩ ጊዜው አልፎበታል",
      chaseStatusGranted: "ስምምነት ተሰጥቷል",
      chaseStatusRefused: "አልተስማሙም",
      chaseActionSend: "ሊንክ ላክ",
      chaseActionResend: "ሊንኩን እንደገና ላክ",
      churchDpContactTitle: "የመረጃ ጥበቃ አድራሻ (Data-protection contact)",
      churchDpContactHelp: "ሰዎች ስምምነታቸውን ለማቋረጥ ወይም ስለግል መረጃቸው ለመጠየቅ የሚያነጋግሩት ሰው። የስምምነት ጽሑፎች [contact] በሚለው ቦታ ኢሜል (እና ስልክ) ያሳያሉ፤ እያንዳንዱ ጽሑፍ ኃላፊነቱን በራሱ ቃላት ይጠራል። ኃላፊነት እና ኢሜል አብረው ያስፈልጋሉ። ባዶ ከተተወ፣ [contact] በምትኩ የቤተ ክርስቲያኒቱን ስልክ ቁጥር ያሳያል።",
      churchDpContactRole: "ኃላፊነት",
      churchDpContactEmail: "ኢሜል",
      churchDpContactPhone: "ስልክ (አማራጭ)",
      activityTryAgain: "እንደገና ይሞክሩ",
      loadFailedTitle: "ይህን መጫን አልተቻለም",
      loadRefusedTitle: "ይህን ለማየት ፈቃድ የለዎትም",
      loadNotFoundTitle: "ይህ የለም፣ ወይም አሁን አይገኝም",
      loadingMemberData: "የአባል መረጃን በመጫን ላይ...",
      memberNotFound: "አባል አልተገኘም",
      memberNotFoundMessage: "ይህ አባል የለም፣ ወይም አሁን አይገኝም።",
      nonSessionDatesTitle: "ያለ ፕሮግራም ያሉ ቀናት",
      nonSessionDatesHelp: "እንደ ገና፣ ፋሲካ ወይም ብሔራዊ በዓላት ያሉ የቤተ ክርስቲያኒቱ ፕሮግራሞች የማይካሄዱባቸው ቀናት። ፕሮግራሞች ሲመነጩ እነዚህ ቀናት ይዘለላሉ፣ ስለዚህ ማንም እንዳላካሄዳቸው ፕሮግራሞች ሆነው አይታዩም። የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተ ክርስቲያን በዓላት ስለሚንቀሳቀሱ የእያንዳንዱን ዓመት ቀናት ይጨምሩ።",
      nonSessionDateAdd: "ጨምር",
      nonSessionDateRemove: "አስወግድ",
      consentRequestsTitle: "የስምምነት ጥያቄዎች",
      consentRequestAsk: "ስምምነት ይጠይቁ",
      consentRequestMakeAndSend: "ጥያቄውን ያዘጋጁ",
    },
  },
};

i18n
  .use(initReactI18next) // Passes i18n down to react-i18next
  .use(LanguageDetector) // Detects user language
  .init({
    resources,
    // A key with no entry in the reader's language: Amharic is shown in Swedish, then English (Adisu; owner, 16 Oct
    // 2026); Swedish, and any language not offered, in English. An untranslated key has no entry - never another
    // language's text copied in, which can't later be told from a translation (scripts/check-i18n.js).
    fallbackLng: { am: ["sv", "en"], default: ["en"] },
    detection: {
      order: [
        "queryString",
        "cookie",
        "localStorage",
        "navigator",
        "htmlTag",
        "path",
        "subdomain",
      ],
      // On the public page the path alone decides its language, and nothing is kept (cmms public-page-plan.md, 5);
      // keepLanguageOn follows the path from there.
      caches: cachesOn(window.location.pathname),
    },
    interpolation: {
      escapeValue: false, // React already safes from XSS
    },
  });

// Dates follow the interface language (M12 screen batch, 12 Oct 2026): a Swedish recorder read "Friday 16 October
// 2026" on the attendance sheet because moment's locale was never set. Swedish has moment's own locale. moment has
// none for Amharic, so under Amharic it follows the interface's fallback - Swedish (owner, 16 Oct 2026); the recorder
// screens format Amharic dates through features/activities/sessionDate.js - both calendars, with the Ethiopian names
// from the browser's calendar data.
const momentLocale = (language) => (/^(sv|am)/.test(String(language || "")) ? "sv" : "en");
moment.locale(momentLocale(i18n.language));
i18n.on("languageChanged", (language) => moment.locale(momentLocale(language)));

/** Where the chosen language is kept: nowhere on the public page, else in local storage and a cookie. */
function cachesOn(pathname) {
  return isPublicParishPath(pathname) ? [] : ["localStorage", "cookie"];
}

/**
 * The language is kept again once the reader leaves the public page: someone who arrives at the root and is sent on
 * to sign-in, with no reload, has the staff app keep the language chosen there (session 6's reviewer pass). App.js
 * calls this on every route change.
 */
export function keepLanguageOn(pathname) {
  const detector = i18n.services.languageDetector;
  if (detector?.options) detector.options.caches = cachesOn(pathname);
}

export default i18n;
